export * from "./models/auth";
export * from "./nano-banana-receipts";
export * from "./square-purchases-tables";
export * from "./ads-manager-refresh-tables";
export * from "./account-performance-alert-tables";
export * from "./adImage";
export * from "./image-layer-tables";
export * from "./video-tables";
export * from "./client-intake-tables";
export { VIDEO_BLUEPRINT_SYSTEM_FLAG } from "./video-contracts";
import { VIDEO_BLUEPRINT_SYSTEM_FLAG } from "./video-contracts";
import { PORTRAIT_BOTTOM_CONTENT, PORTRAIT_ADMISSION_DECISIONS, PORTRAIT_BOUNDARY_OBJECT_KINDS } from "./safe-zone-portrait";

import { sql } from "drizzle-orm";
import {
  pgTable,
  varchar,
  text,
  timestamp,
  jsonb,
  integer,
  bigint,
  date,
  bigserial,
  numeric,
  real,
  boolean,
  index,
  uniqueIndex,
  primaryKey,
  check,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { photoSlotsSchema, type PhotoSlots } from "./template-photo-slots";
export { photoSlotsSchema, photoSlotSchema } from "./template-photo-slots";
export type { PhotoSlots, PhotoSlot } from "./template-photo-slots";
import { users } from "./models/auth";
import { textEngineStateSchema } from "./image-edit-engines";
import { exampleImageEntrySchema } from "./adImage";
import { productVariantSchema, type ProductVariant } from "./product-variant";
import type { AdsManagerSnapshotPayload } from "./ads-manager";
import {
  SMB_AUTOMATION_FLAG_KEY, smbOrderStatusSchema, smbManifestSchema,
  smbJsonObjectSchema, smbMoneySchema, smbTemplateOverridesSchema,
  smbConfidenceFlagsSchema, smbStepStatesSchema,
} from "./smb";
import type { SmbOrderStatus, SmbManifest, SmbTemplateOverrides } from "./smb";
import { portalBrandingSchema, portalGroupsSchema, portalContactSchema } from "./portal";
import { direct916GuardStateSchema } from "./direct-916-guard";
import { nativePromptSnapshotSchema } from "./ads-express-native-prompts";
import { portraitReframeStateSchema } from "./ads-express-portrait-reframe";
import { campaignImageJournalSchema } from "./campaign-gpt-image";

// Direct campaign requests need recovery even without an Ads Express parent.
export const campaignImageRecoveries = pgTable("campaign_image_recoveries", {
  id: varchar("id", { length: 64 }).primaryKey(),
  userId: varchar("user_id").notNull(),
  inputHash: varchar("input_hash", { length: 64 }).notNull(),
  inputSnapshot: jsonb("input_snapshot").$type<Record<string, unknown>>(),
  prompt: text("prompt"),
  falRequestId: text("fal_request_id"),
  falEndpoint: text("fal_endpoint"),
  renderedEngine: text("rendered_engine"),
  journal: jsonb("journal").$type<import("./campaign-gpt-image").CampaignImageJournal>(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Stable, brand-independent keys for the tools users can pin in the sidebar.
// Keep this registry independent from labels and URLs: labels/routes may be
// reorganized without invalidating a user's saved pins.
export const workspaces = pgTable("workspaces", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 255 }).notNull().unique(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const workspaceMembers = pgTable(
  "workspace_members",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id),
    userId: varchar("user_id").notNull().references(() => users.id),
    role: varchar("role", { length: 50 }).notNull().default("viewer"),
    invitedBy: varchar("invited_by"),
    joinedAt: timestamp("joined_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("workspace_members_unique").on(table.workspaceId, table.userId),
  ]
);

export const BRAND_CLIENT_CATEGORIES = [
  "trial_users",
  "demos",
  "current_clients",
  "previous_clients",
  "smb_automated",
] as const;
export const brands = pgTable("brands", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id),
  name: varchar("name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 255 }).notNull(),
  // Authenticated PersonaOS routes only; independent of legacy/public slugs.
  urlName: varchar("url_name", { length: 60 }),
  category: varchar("category", { length: 255 }),
  url: varchar("url", { length: 2048 }),
  logoUrl: varchar("logo_url", { length: 2048 }),
  adgenLogoUrl: varchar("adgen_logo_url", { length: 2048 }),
  status: varchar("status", { length: 50 }).notNull().default("active"),
  summary: text("summary"),
  founderStory: text("founder_story"),
  mission: text("mission"),
  positioning: text("positioning"),
  worldview: text("worldview"),
  enemy: text("enemy"),
  promise: text("promise"),
  toneSummary: text("tone_summary"),
  proofHierarchy: jsonb("proof_hierarchy"),
  approvedPhrases: jsonb("approved_phrases"),
  bannedPhrases: jsonb("banned_phrases"),
  colorPalette: jsonb("color_palette"),
  visualRules: jsonb("visual_rules"),
  claimGuardrails: jsonb("claim_guardrails"),
  strategicPriorities: jsonb("strategic_priorities"),
  categoryMap: jsonb("category_map"),
  nonNegotiableTruths: jsonb("non_negotiable_truths"),
  categoryLanguage: jsonb("category_language"),
  pricePositioning: text("price_positioning"),
  targetAudienceSignals: jsonb("target_audience_signals"),
  keyProducts: jsonb("key_products"),
  socialProfiles: jsonb("social_profiles"),
  researchSources: jsonb("research_sources"),
  researchNotes: text("research_notes"),
  researchConfidence: integer("research_confidence"),
  lastEnrichedAt: timestamp("last_enriched_at"),
  aiResearchStatus: text("ai_research_status"),
  aiResearchStartedAt: timestamp("ai_research_started_at"),
  aiResearchError: text("ai_research_error"),
  aiResearchResult: jsonb("ai_research_result"),
  settings: jsonb("settings"),
  brandDesignSystem: jsonb("brand_design_system"),
  // Email Express 2.0 only: ID of an existing Magic Patterns dashboard
  // design system. Magic Patterns does not expose an API to create one.
  magicPatternsDesignSystemId: text("magic_patterns_design_system_id"),
  // Durable (R2-mirrored) brand Instagram video URLs, auto-fetched on brand
  // setup/refresh and rendered by the LP Express long-form template.
  //   videoMakingUrl    → "See Us Make It From Scratch" slot
  //   videoLifestyleUrl → "Life Around the Farm" slot
  // Empty when no Instagram videos are found; the template falls back to its
  // hardcoded /farm-video-*.mp4 placeholders.
  videoMakingUrl: varchar("video_making_url", { length: 2048 }),
  videoLifestyleUrl: varchar("video_lifestyle_url", { length: 2048 }),
  // Brand-level Meta (Facebook) Pixel ID, entered once inside LP Express and
  // shared by all of the brand's landing pages. Numeric-only, 10–20 digits
  // (validated server-side). NULL = not configured. Each LP Express page still
  // opts in individually via lp_express_pages.meta_pixel_enabled.
  metaPixelId: varchar("meta_pixel_id", { length: 32 }),
  // Master-only internal client classification (Trial Users / Demos / Current
  // Clients / Previous Clients). Distinct from the AI-researched market
  // `category` above. NULL = uncategorized. Never sent to non-master users —
  // the API strips it from brand responses for them.
  clientCategory: varchar("client_category", { length: 32 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by"),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  uniqueIndex("brands_url_name_lower_unique").on(sql`lower(${table.urlName})`),
  index("brands_workspace_active_idx").on(table.workspaceId).where(sql`${table.archivedAt} is null`),
]);

// One-shot data-fix markers: each key records that a boot-time data repair has
// already been applied in THIS environment (dev and prod each claim their own
// row), so the fix runs exactly once per environment and never re-clobbers
// later user edits.
// Migration ledger: records which numbered migrations/NNNN_*.sql files have
// been applied in THIS database (dev and prod each maintain their own rows).
// Managed by scripts/migration-ledger.ts (status / seed / apply / mark).
export const schemaMigrations = pgTable("schema_migrations", {
  filename: varchar("filename").primaryKey(),
  appliedAt: timestamp("applied_at").defaultNow(),
  // 'seed' (historical detection), 'apply' (helper ran it), 'manual'
  appliedBy: varchar("applied_by", { length: 20 }).notNull().default("apply"),
});

export const dataFixMarkers = pgTable("data_fix_markers", {
  key: varchar("key").primaryKey(),
  appliedAt: timestamp("applied_at").defaultNow(),
});

export const adLauncherDrafts = pgTable("ad_launcher_drafts", {
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  id: varchar("id", { length: 160 }).notNull(),
  name: varchar("name", { length: 120 }).notNull(),
  payload: jsonb("payload").$type<Record<string, unknown>>().notNull(),
  revision: integer("revision").notNull().default(1),
  createdBy: varchar("created_by").notNull().references(() => users.id),
  updatedBy: varchar("updated_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  // Retain identity tombstones: stale autosave/import cannot recreate a deletion.
  deletedAt: timestamp("deleted_at"),
}, t => [
  primaryKey({ columns: [t.brandId, t.id] }),
  index("ad_launcher_drafts_brand_updated_idx").on(t.brandId, t.updatedAt),
  check("ad_launcher_drafts_revision_check", sql`${t.revision} > 0`),
]);

export const smbPackages = pgTable("smb_packages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: varchar("slug", { length: 160 }).notNull().unique(),
  name: varchar("name", { length: 255 }).notNull(),
  active: boolean("active").notNull().default(true),
  isDefault: boolean("is_default").notNull().default(false),
  manifestJson: jsonb("manifest_json").$type<SmbManifest>().notNull(),
  costCapUsd: numeric("cost_cap_usd", { precision: 14, scale: 6 }).notNull(),
  maxRevisions: integer("max_revisions").notNull().default(2),
  humanReviewMode: text("human_review_mode").notNull().default("always"),
  qaPolicyJson: jsonb("qa_policy_json").$type<Record<string, unknown>>(),
  pausedReason: text("paused_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("smb_packages_active_default_unique").on(t.isDefault).where(sql`${t.active} AND ${t.isDefault}`),
  index("smb_packages_active_idx").on(t.active),
  check("smb_packages_cost_check", sql`${t.costCapUsd} >= 0`),
  check("smb_packages_revisions_check", sql`${t.maxRevisions} >= 0`),
  check("smb_packages_review_check", sql`${t.humanReviewMode} = 'always'`),
]);

export const smbTemplateTests = pgTable("smb_template_tests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  requestId: varchar("request_id").notNull(),
  inputDigest: text("input_digest").notNull(),
  templateId: varchar("template_id").notNull().references(() => adUnitTypes.id),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  adGenerationId: varchar("ad_generation_id").references(() => adGenerations.id),
  admissionStatus: text("admission_status").notNull().default("submitting"),
  verdict: text("verdict").$type<"pending" | "pass" | "fail">().notNull().default("pending"),
  notes: text("notes").notNull().default(""),
  createdBy: varchar("created_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("smb_template_tests_request_idx").on(t.createdBy, t.requestId),
  index("smb_template_tests_template_idx").on(t.templateId, t.createdAt),
  index("smb_template_tests_brand_idx").on(t.brandId),
  index("smb_template_tests_generation_idx").on(t.adGenerationId),
  check("smb_template_tests_verdict_check", sql`${t.verdict} IN ('pending', 'pass', 'fail')`),
  check("smb_template_tests_admission_check", sql`${t.admissionStatus} IN ('submitting', 'submitted', 'failed')`),
]);
export const brandCustomerEvidence = pgTable("brand_customer_evidence", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  label: varchar("label", { length: 120 }).notNull(),
  sourcePreset: varchar("source_preset", { length: 16 }).notNull(),
  columnMapping: jsonb("column_mapping").$type<import("./customer-evidence").EvidenceMapping>().notNull(),
  rowCount: integer("row_count").notNull(),
  usableRowCount: integer("usable_row_count").notNull(),
  aggregate: jsonb("aggregate").$type<import("./customer-evidence").CustomerEvidenceAggregate>().notNull(),
  status: varchar("status", { length: 16 }).$type<"active" | "archived">().notNull().default("active"),
  importedAt: timestamp("imported_at").notNull().defaultNow(),
  importedBy: varchar("imported_by").notNull().references(() => users.id),
}, (t) => [
  index("brand_customer_evidence_brand_status_idx").on(t.brandId, t.status),
  check("brand_customer_evidence_status_check", sql`${t.status} IN ('active', 'archived')`),
]);
export const creativePerformanceSnapshotScopes = pgTable("creative_performance_snapshot_scopes", {
  brandId: varchar("brand_id").primaryKey().references(() => brands.id, { onDelete: "cascade" }),
  fingerprint: text("fingerprint").notNull(),
  generation: integer("generation").notNull().default(0),
  blockedUntil: timestamp("blocked_until"),
  blockedError: jsonb("blocked_error").$type<Record<string, unknown>>(),
});
export const creativePerformanceSnapshots = pgTable("creative_performance_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  feedKey: text("feed_key").notNull(),
  generation: integer("generation").notNull(),
  contractVersion: integer("contract_version").notNull().default(1),
  payload: jsonb("payload").$type<unknown>(),
  fetchedAt: timestamp("fetched_at"),
  version: integer("version").notNull().default(0),
  refreshToken: varchar("refresh_token"),
  leaseExpiresAt: timestamp("lease_expires_at"),
  retryAt: timestamp("retry_at"),
  lastError: jsonb("last_error").$type<Record<string, unknown>>(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("creative_performance_snapshots_feed_unique").on(table.brandId, table.feedKey),
  index("creative_performance_snapshots_cleanup_idx").on(table.updatedAt),
  index("creative_performance_snapshots_lease_idx").on(table.leaseExpiresAt),
]);

export const userNavPins = pgTable(
  "user_nav_pins",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    itemKey: varchar("item_key", { length: 100 }).notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("user_nav_pins_user_item_unique").on(table.userId, table.itemKey),
    index("user_nav_pins_user_idx").on(table.userId),
  ],
);
export const personas = pgTable(
  "personas",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    slug: varchar("slug", { length: 255 }).notNull(),
    status: varchar("status", { length: 50 }).notNull().default("draft"),
    awarenessStage: varchar("awareness_stage_default", { length: 50 }).default("problem_aware"),
    summary: text("summary"),
    worldview: text("worldview"),
    evidenceLink: jsonb("evidence_link").$type<import("./persona-evidence").PersonaEvidenceLink>(),
    // Admission marker keeps legacy personas out of automatic matching.
    evidenceGeneration: jsonb("evidence_generation").$type<import("./persona-evidence").PersonaEvidenceGenerationCapture>(),
    evidenceMatchToken: varchar("evidence_match_token", { length: 64 }),
    primaryPain: text("primary_pain"),
    desiredOutcome: text("desire"),
    deepFear: text("deep_fear"),
    // Chip-length condensed phrasings of the card traits (Fear / Desire /
    // Trigger) shown on the Personas grid. System-managed: generated by a
    // cheap text model after profile creation and cleared for regeneration
    // whenever the underlying full field is edited. NULL/empty = not
    // generated yet — the card falls back to clamping the full text.
    deepFearShort: text("deep_fear_short"),
    desiredOutcomeShort: text("desire_short"),
    triggerShort: text("trigger_short"),
    coreBeliefsJson: jsonb("core_beliefs"),
    objectionsJson: jsonb("objections"),
    trustTriggersJson: jsonb("trust_triggers"),
    purchaseDriversJson: jsonb("purchase_drivers"),
    emotionalHooksJson: jsonb("emotional_levers"),
    languagePatternsJson: jsonb("vocabulary"),
    customerVoiceQuotesJson: jsonb("customer_voice_quotes"),
    secondaryPainsJson: jsonb("secondary_pains"),
    exclusionsJson: jsonb("exclusions"),
    notes: text("persona_notes"),
    notesLegacy: jsonb("notes"),
    oldSolution: text("old_solution"),
    currentFrustration: text("current_frustration"),
    proofRequired: jsonb("proof_required"),
    preferredTone: jsonb("preferred_tone"),
    preferredChannels: jsonb("preferred_channels"),
    visualCues: jsonb("visual_cues"),
    fingerprint: jsonb("fingerprint"),
    imageUrl: text("image_url"),
    // Explicit lifecycle for user-requested cover regeneration. This must not
    // be inferred from imageUrl because established personas may legitimately
    // have no cover. "generating" is the only in-flight state; "ready" and
    // "failed" are terminal outcomes used to release cross-navigation UI.
    coverRegenerationStatus: varchar("cover_regeneration_status", { length: 20 }),
    // Per-request ownership token for the atomic regeneration claim. Terminal
    // writers must match it so stale failures cannot overwrite a newer cover.
    coverRegenerationToken: varchar("cover_regeneration_token", { length: 64 }),
    avatarName: varchar("avatar_name", { length: 255 }),
    // Cinematic landscape hero image for the persona cards (image-to-image
    // from the effective profile picture, rendered 1536x1024 and mirrored to
    // R2). status: null = never attempted, "generating" = claimed/in flight,
    // "ready" = heroImageUrl durable, "failed" = terminal error (see
    // heroImageError). startedAt lets restarts reclaim stale claims.
    heroImageUrl: text("hero_image_url"),
    heroImageStatus: varchar("hero_image_status", { length: 20 }),
    heroImagePrompt: text("hero_image_prompt"),
    heroImageError: text("hero_image_error"),
    heroImageStartedAt: timestamp("hero_image_started_at"),
    // Customer quote for the card's quote strip, captured from the Sim
    // starter-personas (10-persona) generator. NULL = not captured — the card
    // falls back to the first customer_voice_quotes entry.
    cardQuote: text("card_quote"),
    // Small hand-drawn line-art sketch shown between the quote and the CORE
    // TENSION block on the persona card. One-shot text-to-image in a locked
    // ink-sketch style (brand/persona context only steers the subject),
    // mirrored to R2. Same lifecycle semantics as the hero image:
    // status NULL = never attempted, "generating" = claimed/in flight,
    // "ready" = sketchImageUrl durable, "failed" = terminal error (see
    // sketchImageError). startedAt lets restarts reclaim stale claims.
    sketchImageUrl: text("sketch_image_url"),
    sketchImageStatus: varchar("sketch_image_status", { length: 20 }),
    sketchImagePrompt: text("sketch_image_prompt"),
    sketchImageError: text("sketch_image_error"),
    sketchImageStartedAt: timestamp("sketch_image_started_at"),
    referenceSheetUrl: text("reference_sheet_url"),
    referenceSheetStatus: varchar("reference_sheet_status", { length: 20 }),
    referenceSheetError: text("reference_sheet_error"),
    primaryCharacterId: varchar("primary_character_id"),
    charactersGenerationStatus: varchar("characters_generation_status", { length: 20 }),
    charactersGenerationStartedAt: timestamp("characters_generation_started_at"),
    charactersGenerationError: text("characters_generation_error"),
    // Full-profile enrichment (Sim workflow that fills the psychology fields)
    // for slim personas, e.g. Ideator saves. null = never run,
    // "generating" = in flight, "complete" = done, "failed" = retryable error.
    profileEnrichmentStatus: varchar("profile_enrichment_status", { length: 20 }),
    profileEnrichmentError: text("profile_enrichment_error"),
    // Auto persona research (topics + sourced findings, reusing the Persona
    // Research Lab's lead/topics/specialist-research Sim workflows).
    // null = never run, "generating" = in flight, "complete" = done
    // (individual topics may still be failed), "failed" = retryable error.
    researchStatus: varchar("research_status", { length: 20 }),
    researchError: text("research_error"),
    // Aggregate build rollup for AI-generated personas. null = manual/legacy
    // persona (always treated as done), "building" = background work
    // (profile, fingerprint, characters, research) still in flight,
    // "done" = every sub-step reached a terminal state. Flipping
    // building→done is the atomic claim that fires the "persona is ready"
    // notification exactly once.
    buildStatus: varchar("build_status", { length: 20 }),
    // Groups starter-pack siblings so the batch notification fires once,
    // when the LAST sibling of the batch resolves.
    buildBatchId: varchar("build_batch_id"),
    // Bounded self-heal: how many times the build sweep re-ran missing
    // sub-steps (fingerprint/characters) for this persona. Capped so a
    // persistently failing provider can't loop spend forever.
    buildRepairAttempts: integer("build_repair_attempts").notNull().default(0),
    sourceSessionId: varchar("source_session_id"),
    sourceExistingPersonaId: varchar("source_existing_persona_id"),
    linkedProductOrOfferId: varchar("linked_product_or_offer_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("personas_brand_idx").on(table.brandId)]
);

export const starterPersonaBatches = pgTable(
  "starter_persona_batches",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    createdBy: varchar("created_by").notNull().references(() => users.id),
    status: varchar("status", { length: 24 }).notNull().default("queued"),
    capturedInput: jsonb("captured_input").$type<Record<string, unknown>>().notNull(),
    ideationResult: jsonb("ideation_result"),
    ideationExecutionId: varchar("ideation_execution_id"),
    providerSubmissionState: varchar("provider_submission_state", { length: 24 }),
    ideationUsageSettledAt: timestamp("ideation_usage_settled_at"),
    attempt: integer("attempt").notNull().default(0),
    infrastructureAttempt: integer("infrastructure_attempt").notNull().default(0),
    leaseOwner: varchar("lease_owner", { length: 100 }),
    leaseExpiresAt: timestamp("lease_expires_at"),
    errorCode: varchar("error_code", { length: 80 }),
    errorMessage: text("error_message"),
    notificationClaimedAt: timestamp("notification_claimed_at"),
    startedAt: timestamp("started_at"),
    settledAt: timestamp("settled_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("starter_persona_batches_brand_idx").on(table.brandId),
    index("starter_persona_batches_claim_idx").on(table.status, table.leaseExpiresAt, table.createdAt),
    uniqueIndex("starter_persona_batches_one_active_brand")
      .on(table.brandId)
      .where(sql`${table.status} IN ('queued', 'ideating', 'profiles')`),
    check(
      "starter_persona_batches_status_check",
      sql`${table.status} IN ('queued', 'ideating', 'profiles', 'completed', 'failed', 'interrupted', 'discarded')`,
    ),
  ],
);
export const personaCharacters = pgTable(
  "persona_characters",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    personaId: varchar("persona_id").notNull().references(() => personas.id, { onDelete: "cascade" }),
    charKey: varchar("char_key", { length: 50 }),
    name: varchar("name", { length: 255 }).notNull(),
    age: integer("age"),
    genderPresentation: varchar("gender_presentation", { length: 100 }),
    ethnicityHeritage: text("ethnicity_heritage"),
    regionOrSetting: text("region_or_setting"),
    physicalLook: jsonb("physical_look"),
    styleAndWardrobe: jsonb("style_and_wardrobe"),
    cameraPresence: jsonb("camera_presence"),
    visualDistinction: jsonb("visual_distinction"),
    imagePromptSummary: text("image_prompt_summary"),
    rawData: jsonb("raw_data"),
    avatarUrl: text("avatar_url"),
    avatarPrompt: text("avatar_prompt"),
    avatarStatus: varchar("avatar_status", { length: 20 }).default("pending"),
    avatarError: text("avatar_error"),
    avatarAttemptId: varchar("avatar_attempt_id", { length: 64 }),
    avatarAttemptStartedAt: timestamp("avatar_attempt_started_at"),
    referenceSheetUrl: text("reference_sheet_url"),
    referenceSheetStatus: varchar("reference_sheet_status", { length: 20 }),
    referenceSheetError: text("reference_sheet_error"),
    // Exact avatar input that produced referenceSheetUrl. A ready sheet is
    // reusable only while this still matches avatarUrl.
    referenceSheetAvatarUrl: text("reference_sheet_avatar_url"),
    adIdentityPackUrls: jsonb("ad_identity_pack_urls").$type<string[]>(),
    adIdentityPackStatus: varchar("ad_identity_pack_status", { length: 20 }),
    adIdentityPackError: text("ad_identity_pack_error"),
    adIdentityPackAvatarUrl: text("ad_identity_pack_avatar_url"),
    adIdentityPackAttemptId: varchar("ad_identity_pack_attempt_id", { length: 64 }),
    adIdentityPackStartedAt: timestamp("ad_identity_pack_started_at"),
    // For char #1 derived from a persona's existing avatar via the Sim
    // vision→bio workflow. Null on legacy / Sim-generated character rows
    // (those arrive with their bio already populated).
    bioStatus: varchar("bio_status", { length: 20 }),
    bioError: text("bio_error"),
    bioRetries: integer("bio_retries").default(0),
    userCharacterDirection: text("user_character_direction"),
    sortOrder: integer("sort_order").default(0),
    createdBy: varchar("created_by"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [index("persona_characters_persona_idx").on(table.personaId)],
);

// Per-persona research topics with sourced findings — the durable, persona-
// attached version of the Persona Research Lab's topics + specialist research
// modules. Rows are created when topic generation completes (status
// "pending"), then advance pending → researching → done | failed as each
// topic's specialist research lands.
export const personaResearchTopics = pgTable(
  "persona_research_topics",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    personaId: varchar("persona_id").notNull().references(() => personas.id, { onDelete: "cascade" }),
    sortOrder: integer("sort_order").default(0),
    topic: text("topic").notNull(),
    description: text("description"),
    knowledgeGap: text("knowledge_gap"),
    commercialUse: text("commercial_use"),
    priority: varchar("priority", { length: 50 }),
    suggestedSourcesJson: jsonb("suggested_sources"),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    findings: text("findings"),
    // Normalized source list (array of URL/citation strings) extracted from
    // the specialist research output.
    sourcesJson: jsonb("sources"),
    error: text("error"),
    // Raw topic object as returned by the topics workflow (also reused to
    // build faithful retry payloads for the specialist research workflow).
    rawData: jsonb("raw_data"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [index("persona_research_topics_persona_idx").on(table.personaId)],
);

export const products = pgTable(
  "products",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    slug: varchar("slug", { length: 255 }).notNull(),
    sku: varchar("sku", { length: 100 }),
    status: varchar("status", { length: 50 }).notNull().default("active"),
    category: varchar("category", { length: 255 }),
    productRole: varchar("product_role", { length: 50 }),
    summary: text("summary"),
    description: text("description"),
    jobToBeDone: text("job_to_be_done"),
    ingredientsOrFeatures: jsonb("ingredients_or_features"),
    useCases: jsonb("use_cases"),
    objections: jsonb("objections"),
    proofPoints: jsonb("proof_points"),
    priceText: varchar("price_text", { length: 100 }),
    subscriptionAvailable: boolean("subscription_available").default(false),
    isBundle: boolean("is_bundle").default(false),
    bundleProductIds: jsonb("bundle_product_ids"),
    bundleRole: varchar("bundle_role", { length: 100 }),
    claimNotes: jsonb("claim_notes"),
    pdpUrl: varchar("pdp_url", { length: 500 }),
    mediaJson: jsonb("media_json"),
    variantOptionsJson: jsonb("variant_options_json"),
    variantsJson: jsonb("variants_json"),
    notes: jsonb("notes"),
    adgenImageRefs: jsonb("adgen_image_refs"),
    // Shopify-owned source URLs retained only for diagnostics and refresh
    // comparison. User-facing/generation image fields contain durable URLs.
    shopifyImageSources: jsonb("shopify_image_sources"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("products_brand_idx").on(table.brandId)]
);

export const shopifyProductImageCleanup = pgTable(
  "shopify_product_image_cleanup",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    objectKey: varchar("object_key", { length: 1024 }).notNull().unique(),
    durableUrl: text("durable_url").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    attempts: integer("attempts").notNull().default(0),
    lastError: text("last_error"),
    nextAttemptAt: timestamp("next_attempt_at").defaultNow(),
    deletedAt: timestamp("deleted_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("shopify_product_image_cleanup_retry_idx").on(table.status, table.nextAttemptAt),
  ],
);
export const shopifyImportRuns = pgTable(
  "shopify_import_runs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    createdBy: varchar("created_by"),
    storeUrl: text("store_url").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("running"),
    phase: varchar("phase", { length: 24 }).notNull().default("starting"),
    imported: integer("imported").notNull().default(0),
    skipped: integer("skipped").notNull().default(0),
    processed: integer("processed").notNull().default(0),
    failed: integer("failed").notNull().default(0),
    total: integer("total").notNull().default(0),
    pagesFetched: integer("pages_fetched").notNull().default(0),
    catalogComplete: boolean("catalog_complete").notNull().default(false),
    partialReason: text("partial_reason"),
    variantsAdded: integer("variants_added").notNull().default(0),
    variantImagesFilled: integer("variant_images_filled").notNull().default(0),
    galleriesFilled: integer("galleries_filled").notNull().default(0),
    siblingsMerged: integer("siblings_merged").notNull().default(0),
    siblingColorsAdded: integer("sibling_colors_added").notNull().default(0),
    variantFailures: integer("variant_failures").notNull().default(0),
    mediaTotal: integer("media_total").notNull().default(0),
    mediaCompleted: integer("media_completed").notNull().default(0),
    mediaFailed: integer("media_failed").notNull().default(0),
    error: text("error"),
    leaseOwner: varchar("lease_owner", { length: 120 }),
    leaseExpiresAt: timestamp("lease_expires_at"),
    heartbeatAt: timestamp("heartbeat_at"),
    lastProgressAt: timestamp("last_progress_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    completedAt: timestamp("completed_at"),
  },
  (table) => [
    index("shopify_import_runs_brand_idx").on(table.brandId, table.createdAt),
    index("shopify_import_runs_claim_idx").on(table.status, table.leaseExpiresAt),
    uniqueIndex("shopify_import_runs_one_active_brand")
      .on(table.brandId)
      .where(sql`${table.status} = 'running'`),
  ],
);
export const productReviews = pgTable(
  "product_reviews",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    productId: varchar("product_id").notNull().references(() => products.id),
    externalId: integer("external_id"),
    reviewer: varchar("reviewer", { length: 255 }),
    date: varchar("date", { length: 50 }),
    dateApproximate: boolean("date_approximate").default(false),
    scent: varchar("scent", { length: 255 }),
    rating: integer("rating"),
    reviewText: text("review_text"),
    sentiment: varchar("sentiment", { length: 50 }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [index("product_reviews_product_idx").on(table.productId)]
);

export const adUnitTypes = pgTable(
  "ad_unit_types",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").references(() => brands.id),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    status: text("status").notNull().default("active"),
    formatType: text("format_type").default("static"),
    formatIndex: integer("format_index"),
    adsExpressBucket: text("ads_express_bucket"),
    // Admin-managed custom category for the Ads Express picker. Nullable:
    // unassigned templates fall back to the legacy `adsExpressBucket`/heuristic
    // grouping. One category per template (no multi-category by design).
    categoryId: varchar("category_id").references((): any => templateCategories.id),
    platform: jsonb("platform"),
    objective: text("objective"),
    funnelStage: text("funnel_stage"),
    idealLengthSeconds: integer("ideal_length_seconds"),
    strategicRole: text("strategic_role"),
    funnelDeployment: jsonb("funnel_deployment"),
    psychologicalFunction: jsonb("psychological_function"),
    structuralDrivers: jsonb("structural_drivers"),
    copyFramework: jsonb("copy_framework"),
    exampleImages: jsonb("example_images"),
    photoSlots: jsonb("photo_slots").$type<PhotoSlots>(),
    hookStyle: jsonb("hook_style"),
    structureTemplate: jsonb("structure_template"),
    proofPatterns: jsonb("proof_patterns"),
    ctaPatterns: jsonb("cta_patterns"),
    visualGrammar: jsonb("visual_grammar"),
    useWhen: jsonb("use_when"),
    avoidWhen: jsonb("avoid_when"),
    notes: jsonb("notes"),
    visualIdentification: jsonb("visual_identification"),
    secondaryFunnel: text("secondary_funnel"),
    scrollStopper: text("scroll_stopper"),
    coreMessage: text("core_message"),
    psychologicalLevers: jsonb("psychological_levers"),
    creativeElements: jsonb("creative_elements"),
    reusabilityScore: integer("reusability_score"),
    imagePromptTemplate: text("image_prompt_template"),
    storyboardPromptTemplate: text("storyboard_prompt_template"),
    seedancePromptTemplate: text("seedance_prompt_template"),
    startingFramePrompt: text("starting_frame_prompt"),
    simStartingFrameUrl: text("sim_starting_frame_url"),
    simScriptUrl: text("sim_script_url"),
    simScriptConceptUrl: text("sim_script_concept_url"),
    simScenePromptUrl: text("sim_scene_prompt_url"),
    simCreativeStrategistUrl: text("sim_creative_strategist_url"),
    simStoryboardMakerUrl: text("sim_storyboard_maker_url"),
    simMiniStoryboardMakerUrl: text("sim_mini_storyboard_maker_url"),
    simCustomConceptUrl: text("sim_custom_concept_url"),
    simSeedanceVideoUrl: text("sim_seedance_video_url"),
    adgenExampleImageUrl: text("adgen_example_image_url"),
    adgenIncludeProductImage: boolean("adgen_include_product_image").default(true),
    isMeme: boolean("is_meme").default(false),
    memeReferenceImageUrl: text("meme_reference_image_url"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("ad_unit_types_brand_idx").on(table.brandId)]
);

// Master-managed global library of narrator-led video-ad storytelling
// templates ("Story Categories"). Mirrors the adUnitTypes template pattern:
// brandId is nullable (null => global), structured/list fields are jsonb,
// and rows are archived (status="archived" + archivedAt) rather than deleted.
// Stores story DNA only — generation prompts, campaign routing, and scoring
// live elsewhere.
export const storyCategories = pgTable(
  "story_categories",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // Null brandId => global, master-managed library entry.
    brandId: varchar("brand_id").references(() => brands.id),
    // Identity
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    status: text("status").notNull().default("active"),
    version: text("version"),
    familyTags: jsonb("family_tags").$type<string[]>(),
    description: text("description"),
    // 500x500 on-brand graphic shown in the picker + admin page (durable R2 URL).
    imageUrl: text("image_url"),
    // Story method
    coreStoryMethod: text("core_story_method"),
    storyQuestion: text("story_question"),
    storyMovement: text("story_movement"),
    viewerRealization: text("viewer_realization"),
    // Persuasion logic
    persuasionGoal: text("persuasion_goal"),
    persuasionTags: jsonb("persuasion_tags").$type<string[]>(),
    emotionArc: jsonb("emotion_arc").$type<string[]>(),
    // Narrative shape
    typicalArc: jsonb("typical_arc").$type<string[]>(),
    // Visual language
    metaphorBank: jsonb("metaphor_bank").$type<string[]>(),
    scenePatterns: jsonb("scene_patterns").$type<string[]>(),
    motionVerbs: jsonb("motion_verbs").$type<string[]>(),
    visualSymbols: jsonb("visual_symbols").$type<string[]>(),
    visualsToAvoid: jsonb("visuals_to_avoid").$type<string[]>(),
    commonFailureModes: jsonb("common_failure_modes").$type<string[]>(),
    // CTA landing
    ctaLandingStyle: text("cta_landing_style"),
    ctaBridgeExamples: jsonb("cta_bridge_examples").$type<string[]>(),
    // Examples
    exampleConcepts: jsonb("example_concepts").$type<{ industry: string; concept: string }[]>(),
    badExamples: jsonb("bad_examples").$type<{ bad_example: string; why_bad: string }[]>(),
    // Admin / versioning
    editNotes: text("edit_notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    lastEditedBy: varchar("last_edited_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("story_categories_brand_idx").on(table.brandId)]
);

export const brandAdUnitAccess = pgTable(
  "brand_ad_unit_access",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    adUnitTypeId: varchar("ad_unit_type_id").notNull().references(() => adUnitTypes.id),
    assignedAt: timestamp("assigned_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("brand_ad_unit_access_unique").on(table.brandId, table.adUnitTypeId),
  ]
);

export const insertBrandAdUnitAccessSchema = createInsertSchema(brandAdUnitAccess).omit({
  id: true,
  assignedAt: true,
});
export type InsertBrandAdUnitAccess = z.infer<typeof insertBrandAdUnitAccessSchema>;
export type BrandAdUnitAccess = typeof brandAdUnitAccess.$inferSelect;

export const brandAdUnitApprovals = pgTable(
  "brand_ad_unit_approvals",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    adUnitTypeId: varchar("ad_unit_type_id").notNull().references(() => adUnitTypes.id),
    approvedAt: timestamp("approved_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("brand_ad_unit_approvals_unique").on(table.brandId, table.adUnitTypeId),
  ]
);
export const templateCategories = pgTable("template_categories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  displayOrder: integer("display_order").default(0),
  color: text("color"),
  icon: text("icon"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTemplateCategorySchema = createInsertSchema(templateCategories).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertTemplateCategory = z.infer<typeof insertTemplateCategorySchema>;
export type TemplateCategory = typeof templateCategories.$inferSelect;

// Brand-owned, ordered collections used to save a reusable selection in the
// Ads Express static-template picker. Membership is intentionally separate
// from ad_unit_types: removing a template from a set never archives or deletes
// the underlying template.
export const staticTemplateSets = pgTable(
  "static_template_sets",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    displayOrder: integer("display_order").notNull().default(0),
    createdBy: varchar("created_by").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("static_template_sets_brand_name_unique").on(table.brandId, table.name),
    index("static_template_sets_brand_order_idx").on(table.brandId, table.displayOrder),
  ],
);

export const staticTemplateSetMembers = pgTable(
  "static_template_set_members",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    setId: varchar("set_id").notNull().references(() => staticTemplateSets.id, { onDelete: "cascade" }),
    adUnitTypeId: varchar("ad_unit_type_id").notNull().references(() => adUnitTypes.id),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("static_template_set_members_set_template_unique").on(table.setId, table.adUnitTypeId),
    index("static_template_set_members_set_order_idx").on(table.setId, table.sortOrder),
  ],
);

// Standalone Ad Concept Board records. These do not share the LP Studio,
// Brand Spy, or ad-unit template lifecycle. Jobs freeze their type version and
// inputSnapshot at admission; consumers must not reconstruct them from live rows.
export const templateStudioTypes = pgTable("template_studio_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  name: text("name").notNull(),
  definition: text("definition").notNull(),
  formatRules: text("format_rules").notNull(),
  allowedVariationDimensions: jsonb("allowed_variation_dimensions").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  requiredVisualElements: jsonb("required_visual_elements").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  forbiddenPatterns: jsonb("forbidden_patterns").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  version: integer("version").notNull().default(1),
  enabled: boolean("enabled").notNull().default(true),
  brandId: varchar("brand_id").references(() => brands.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [index("template_studio_types_brand_idx").on(table.brandId)]);

export type BoardReferencePerformanceSnapshot = {
  metric: "leads" | "checkouts" | "purchases";
  value: number;
  spend: number;
  costPer: number | null;
  since: string;
  until: string;
  capturedAt: string;
};
export const templateStudioLibraryImages = pgTable("template_studio_library_images", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  objectKey: text("object_key").notNull(),
  previewObjectKey: text("preview_object_key").notNull(),
  mimeType: text("mime_type").notNull(),
  width: integer("width").notNull(),
  height: integer("height").notNull(),
  contentHash: text("content_hash").notNull(),
  source: text("source").$type<"upload" | "performance" | "saved-ad">().notNull().default("upload"),
  // Additive lineage: content deduplication can associate multiple saved ads.
  // No FK: removing a source ad must not remove its independent reference copy.
  savedAdIds: jsonb("saved_ad_ids").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  externalAdId: text("external_ad_id"),
  performanceSnapshot: jsonb("performance_snapshot").$type<BoardReferencePerformanceSnapshot>(),
  version: integer("version").notNull().default(1),
  typeIds: jsonb("type_ids").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  enabled: boolean("enabled").notNull().default(true),
  uploadedBy: varchar("uploaded_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("template_studio_library_images_brand_idx").on(table.brandId),
  uniqueIndex("template_studio_library_images_brand_external_ad_unique")
    .on(table.brandId, table.externalAdId).where(sql`${table.externalAdId} IS NOT NULL`),
]);

export const templateStudioJobs = pgTable("template_studio_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  typeId: varchar("type_id").notNull().references(() => templateStudioTypes.id),
  typeVersion: integer("type_version").notNull(),
  productId: varchar("product_id").references(() => products.id),
  offerId: varchar("offer_id").references(() => offers.id),
  brief: text("brief"),
  columns: integer("columns").notNull().default(4),
  rows: integer("rows").notNull().default(2),
  status: text("status").notNull().default("queued"),
  stage: text("stage").notNull().default("queued"),
  requestHash: text("request_hash").notNull(),
  idempotencyKey: text("idempotency_key").notNull(),
  inputSnapshot: jsonb("input_snapshot").$type<Record<string, unknown>>().notNull(),
  plan: jsonb("plan").$type<Record<string, unknown>>(),
  review: jsonb("review").$type<Record<string, unknown>>(),
  imageObjectKey: text("image_object_key"),
  cancelledAt: timestamp("cancelled_at"),
  correctionUsed: boolean("correction_used").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("template_studio_jobs_brand_idempotency_uniq").on(table.brandId, table.idempotencyKey),
  index("template_studio_jobs_brand_created_idx").on(table.brandId, table.createdAt),
  index("template_studio_jobs_status_idx").on(table.status),
  check("template_studio_jobs_status_check", sql`${table.status} IN ('queued', 'planning', 'rendering', 'reviewing', 'repairing', 'ready', 'needs_review', 'failed', 'cancelled')`),
]);

export const templateStudioPanels = pgTable("template_studio_panels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: varchar("job_id").notNull().references(() => templateStudioJobs.id),
  position: integer("position").notNull(),
  name: text("name").notNull(),
  concept: text("concept").notNull(),
  structureTemplate: text("structure_template").notNull(),
  visualGrammar: text("visual_grammar").notNull(),
  hookStyle: text("hook_style").notNull(),
  copyFramework: text("copy_framework").notNull(),
  scene: text("scene").notNull(),
  composition: text("composition").notNull(),
  referenceIds: jsonb("reference_ids").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  approvedAssetIds: jsonb("approved_asset_ids").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  distinction: text("distinction").notNull(),
  typeFit: text("type_fit").notNull(),
  promotedTemplateId: varchar("promoted_template_id").references(() => adUnitTypes.id),
  promotedAt: timestamp("promoted_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("template_studio_panels_job_position_uniq").on(table.jobId, table.position),
  check("template_studio_panels_position_check", sql`${table.position} BETWEEN 1 AND 8`),
]);

export const templateStudioStageAttempts = pgTable("template_studio_stage_attempts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: varchar("job_id").notNull().references(() => templateStudioJobs.id),
  stage: text("stage").notNull(),
  attemptId: varchar("attempt_id").notNull().unique().default(sql`gen_random_uuid()`),
  leaseOwner: text("lease_owner"),
  leaseExpiresAt: timestamp("lease_expires_at"),
  provider: text("provider").notNull(),
  model: text("model"),
  requestId: text("request_id"),
  responseId: text("response_id"),
  status: text("status").notNull(),
  errorCategory: text("error_category"),
  outputCheckpoint: jsonb("output_checkpoint").$type<Record<string, unknown>>(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("template_studio_stage_attempts_job_stage_idx").on(table.jobId, table.stage),
  index("template_studio_stage_attempts_lease_idx").on(table.status, table.leaseExpiresAt),
  check("template_studio_stage_attempts_provider_check", sql`${table.provider} IN ('sim', 'fal')`),
]);

export const templateStudioUsageRecords = pgTable("template_studio_usage_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  attemptId: varchar("attempt_id").notNull().references(() => templateStudioStageAttempts.attemptId),
  provider: text("provider").notNull(),
  model: text("model"),
  pricingVersion: text("pricing_version"),
  estimatedCostUsd: numeric("estimated_cost_usd"),
  actualCostUsd: numeric("actual_cost_usd"),
  settlementStatus: text("settlement_status").notNull(),
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [index("template_studio_usage_records_attempt_idx").on(table.attemptId)]);

export const insertTemplateStudioTypeSchema = createInsertSchema(templateStudioTypes, {
  allowedVariationDimensions: z.array(z.string()),
  requiredVisualElements: z.array(z.string()),
  forbiddenPatterns: z.array(z.string()),
}).omit({ id: true, createdAt: true, updatedAt: true });
export const insertTemplateStudioLibraryImageSchema = createInsertSchema(templateStudioLibraryImages, {
  typeIds: z.array(z.string()),
  savedAdIds: z.array(z.string()).optional(),
  source: z.enum(["upload", "performance", "saved-ad"]).optional(),
  performanceSnapshot: z.object({
    metric: z.enum(["leads", "purchases", "checkouts"]),
    value: z.number(),
    spend: z.number(),
    costPer: z.number().nullable(),
    since: z.string(),
    until: z.string(),
    capturedAt: z.string(),
  }).nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export const insertTemplateStudioJobSchema = createInsertSchema(templateStudioJobs).omit({ id: true, createdAt: true, updatedAt: true });
export const insertTemplateStudioPanelSchema = createInsertSchema(templateStudioPanels, {
  referenceIds: z.array(z.string()),
  approvedAssetIds: z.array(z.string()),
}).omit({ id: true, createdAt: true, updatedAt: true });
export const insertTemplateStudioStageAttemptSchema = createInsertSchema(templateStudioStageAttempts).omit({ id: true, createdAt: true, updatedAt: true });
export const insertTemplateStudioUsageRecordSchema = createInsertSchema(templateStudioUsageRecords).omit({ id: true, createdAt: true, updatedAt: true });
export type TemplateStudioType = typeof templateStudioTypes.$inferSelect;
export type TemplateStudioLibraryImage = typeof templateStudioLibraryImages.$inferSelect;
export type TemplateStudioJob = typeof templateStudioJobs.$inferSelect;
export type TemplateStudioPanel = typeof templateStudioPanels.$inferSelect;
export type TemplateStudioStageAttempt = typeof templateStudioStageAttempts.$inferSelect;
export type TemplateStudioUsageRecord = typeof templateStudioUsageRecords.$inferSelect;
export type InsertTemplateStudioType = z.infer<typeof insertTemplateStudioTypeSchema>;
export type InsertTemplateStudioLibraryImage = z.infer<typeof insertTemplateStudioLibraryImageSchema>;
export type InsertTemplateStudioJob = z.infer<typeof insertTemplateStudioJobSchema>;
export type InsertTemplateStudioPanel = z.infer<typeof insertTemplateStudioPanelSchema>;
export type InsertTemplateStudioStageAttempt = z.infer<typeof insertTemplateStudioStageAttemptSchema>;
export type InsertTemplateStudioUsageRecord = z.infer<typeof insertTemplateStudioUsageRecordSchema>;

export const insertStaticTemplateSetSchema = createInsertSchema(staticTemplateSets).omit({
  id: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertStaticTemplateSet = z.infer<typeof insertStaticTemplateSetSchema>;
export type StaticTemplateSet = typeof staticTemplateSets.$inferSelect;
export type StaticTemplateSetMember = typeof staticTemplateSetMembers.$inferSelect;

export const offers = pgTable(
  "offers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    slug: varchar("slug", { length: 255 }).notNull(),
    status: varchar("status", { length: 50 }).notNull().default("active"),
    offerType: varchar("offer_type", { length: 50 }),
    summary: text("summary"),
    valueFraming: text("value_framing"),
    urgencyFraming: text("urgency_framing"),
    includedProducts: jsonb("included_products"),
    targetFunnelStage: jsonb("target_funnel_stage"),
    targetPersonas: jsonb("target_personas"),
    copyRules: jsonb("copy_rules"),
    notes: jsonb("notes"),
    // Expanded offer model (additive — legacy rows have these as NULL).
    offerCategory: varchar("offer_category", { length: 255 }),
    shortDescription: text("short_description"),
    buyerProblem: jsonb("buyer_problem"),
    desiredOutcome: jsonb("desired_outcome"),
    valueProposition: jsonb("value_proposition"),
    offerDetails: jsonb("offer_details"),
    fulfillment: jsonb("fulfillment"),
    proof: jsonb("proof"),
    riskReversal: jsonb("risk_reversal"),
    objections: jsonb("objections"),
    conversion: jsonb("conversion"),
    personaFit: jsonb("persona_fit"),
    creativeInputs: jsonb("creative_inputs"),
    serviceDetails: jsonb("service_details"),
    // Reference images for AI ad generation (mirrors products.adgenImageRefs).
    // Stored as string[] of /uploads/... or fal.media URLs.
    adgenImageRefs: jsonb("adgen_image_refs"),
    // Small hand-drawn line-art sketch shown in the offering card's quote
    // strip (mirrors personas.sketch_image_* exactly). One-shot text-to-image
    // in the locked ink-sketch style (brand/offering context only steers the
    // subject), mirrored to R2 under offer-sketches/. Lifecycle semantics:
    // status NULL = never attempted, "generating" = claimed/in flight
    // (stale-reclaimable via startedAt), "ready" = sketchImageUrl durable,
    // "failed" = TERMINAL error (see sketchImageError) — the sweep never
    // retries it.
    sketchImageUrl: text("sketch_image_url"),
    sketchImageStatus: varchar("sketch_image_status", { length: 20 }),
    sketchImagePrompt: text("sketch_image_prompt"),
    sketchImageError: text("sketch_image_error"),
    sketchImageStartedAt: timestamp("sketch_image_started_at"),
    // Product-based AI hero image for the offering card's dark band and the
    // detail page's image panel (mirrors personas.hero_image_* exactly).
    // Rendered 1536x1024 on fal gpt-image-2 image-to-image with the
    // offering's best product reference photo as the input image (falls back
    // to text-to-image from the offering details when no usable http photo
    // exists), mirrored to R2 under offer-heroes/. Lifecycle semantics:
    // status NULL = never attempted, "generating" = claimed/in flight
    // (stale-reclaimable via startedAt), "ready" = heroImageUrl durable,
    // "failed" = TERMINAL error (see heroImageError) — the sweep never
    // retries it. A meaningful product-linkage change resets the lifecycle
    // so the hero regenerates featuring the new product.
    heroImageUrl: text("hero_image_url"),
    heroImageStatus: varchar("hero_image_status", { length: 20 }),
    heroImagePrompt: text("hero_image_prompt"),
    heroImageError: text("hero_image_error"),
    heroImageStartedAt: timestamp("hero_image_started_at"),
    // Build-with-AI persist-first lifecycle (mirrors the persona build-card
    // flow): the builder creates the row up front as a generating draft so
    // the grid can show an animated build card. NULL = normal saved row,
    // 'building' = builder job in flight, 'failed' = builder errored (retry
    // from buildInput or discard). Reset to NULL on success so every existing
    // consumer's "complete rows only" filter is simply build_status IS NULL.
    buildStatus: varchar("build_status", { length: 20 }),
    buildError: text("build_error"),
    buildStartedAt: timestamp("build_started_at"),
    // { description, draft?, answers? } — everything needed to retry a
    // failed build without re-asking the user.
    buildInput: jsonb("build_input"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("offers_brand_idx").on(table.brandId)]
);

// Brand-owned customer problem library. These records are intentionally
// separate from persona research and campaign data: a pillar is reusable
// relationship-layer context that can be safely archived without touching
// any of the linked catalog or persona records.
export const painPillarCategories = pgTable(
  "pain_pillar_categories",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 100 }).notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    status: varchar("status", { length: 20 }).notNull().default("active"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("pain_pillar_categories_brand_idx").on(table.brandId),
    uniqueIndex("pain_pillar_categories_brand_name_idx").on(table.brandId, sql`lower(${table.name})`),
  ],
);

export const painPillars = pgTable(
  "pain_pillars",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 100 }).notNull(),
    description: varchar("description", { length: 500 }).notNull(),
    categoryId: varchar("category_id").notNull().references(() => painPillarCategories.id),
    supportingPains: jsonb("supporting_pains").notNull().default(sql`'[]'::jsonb`),
    internalNotes: varchar("internal_notes", { length: 1000 }),
    status: varchar("status", { length: 20 }).notNull().default("active"),
    createdBy: varchar("created_by").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [
    index("pain_pillars_brand_idx").on(table.brandId),
    index("pain_pillars_category_idx").on(table.categoryId),
    index("pain_pillars_status_idx").on(table.brandId, table.status),
  ],
);

export const painPillarPersonas = pgTable(
  "pain_pillar_personas",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    painPillarId: varchar("pain_pillar_id").notNull().references(() => painPillars.id, { onDelete: "cascade" }),
    personaId: varchar("persona_id").notNull().references(() => personas.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("pain_pillar_personas_unique_idx").on(table.painPillarId, table.personaId),
    index("pain_pillar_personas_brand_idx").on(table.brandId),
    index("pain_pillar_personas_persona_idx").on(table.personaId),
  ],
);

export const painPillarProducts = pgTable(
  "pain_pillar_products",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    painPillarId: varchar("pain_pillar_id").notNull().references(() => painPillars.id, { onDelete: "cascade" }),
    productId: varchar("product_id").references(() => products.id, { onDelete: "cascade" }),
    offerId: varchar("offer_id").references(() => offers.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("pain_pillar_products_product_unique_idx").on(table.painPillarId, table.productId),
    uniqueIndex("pain_pillar_products_offer_unique_idx").on(table.painPillarId, table.offerId),
    index("pain_pillar_products_brand_idx").on(table.brandId),
    index("pain_pillar_products_product_idx").on(table.productId),
    index("pain_pillar_products_offer_idx").on(table.offerId),
  ],
);

// Sales & Discounts: a reusable promotion module that attaches to
// products, offers, landing pages, and Ads Express campaigns. Distinct
// from Offers — Offers are *what* you sell, sales/discounts are *how*
// you promote them. See SALE_TYPES / URGENCY_MECHANISMS in
// client/src/pages/sales-discount-form.tsx for the canonical option
// lists driving the dropdowns.
export const salesDiscounts = pgTable(
  "sales_discounts",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    saleType: varchar("sale_type", { length: 50 }).notNull().default("percentage_off"),
    discountValue: varchar("discount_value", { length: 255 }),
    // appliesTo: { scope: "products" | "offers" | "brand", productIds: string[], offerIds: string[] }
    appliesTo: jsonb("applies_to"),
    primaryProductId: varchar("primary_product_id").references(() => products.id),
    primaryOfferId: varchar("primary_offer_id").references(() => offers.id),
    promotionSummary: text("promotion_summary"),
    customerFacingCopy: text("customer_facing_copy"),
    whyThisSaleExists: text("why_this_sale_exists"),
    urgencyMechanism: varchar("urgency_mechanism", { length: 50 }).default("no_urgency"),
    expirationDate: timestamp("expiration_date"),
    promoCode: varchar("promo_code", { length: 100 }),
    termsConditions: text("terms_conditions"),
    ctaLanguage: text("cta_language"),
    creativeNotes: text("creative_notes"),
    status: varchar("status", { length: 50 }).notNull().default("draft"),
    // Small hand-drawn line-art sketch shown in the sale card's quote strip
    // (mirrors offers.sketch_image_* / personas.sketch_image_* exactly).
    // One-shot text-to-image in the locked ink-sketch style (brand/sale
    // context only steers the subject), mirrored to R2 under sale-sketches/.
    // Lifecycle semantics: status NULL = never attempted, "generating" =
    // claimed/in flight (stale-reclaimable via startedAt), "ready" =
    // sketchImageUrl durable, "failed" = TERMINAL error (see
    // sketchImageError) — the sweep never retries it.
    sketchImageUrl: text("sketch_image_url"),
    sketchImageStatus: varchar("sketch_image_status", { length: 20 }),
    sketchImagePrompt: text("sketch_image_prompt"),
    sketchImageError: text("sketch_image_error"),
    sketchImageStartedAt: timestamp("sketch_image_started_at"),
    // Product-based AI hero image for the sale card's dark band and the
    // detail page's image panel (mirrors offers.hero_image_* /
    // personas.hero_image_* exactly). Rendered 1536x1024 on fal gpt-image-2
    // image-to-image with the sale's best product reference photo (primary
    // product → first appliesTo product → primary offer's chain) as the
    // input image (falls back to text-to-image from the sale details when no
    // usable http photo exists), mirrored to R2 under sale-heroes/.
    // Lifecycle semantics: status NULL = never attempted, "generating" =
    // claimed/in flight (stale-reclaimable via startedAt), "ready" =
    // heroImageUrl durable, "failed" = TERMINAL error (see heroImageError) —
    // the sweep never retries it. A meaningful product-linkage change resets
    // the lifecycle so the hero regenerates featuring the new product.
    heroImageUrl: text("hero_image_url"),
    heroImageStatus: varchar("hero_image_status", { length: 20 }),
    heroImagePrompt: text("hero_image_prompt"),
    heroImageError: text("hero_image_error"),
    heroImageStartedAt: timestamp("hero_image_started_at"),
    // Build-with-AI persist-first lifecycle (mirrors offers.build_* exactly —
    // see the comment there). buildInput here is { description }.
    buildStatus: varchar("build_status", { length: 20 }),
    buildError: text("build_error"),
    buildStartedAt: timestamp("build_started_at"),
    buildInput: jsonb("build_input"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("sales_discounts_brand_idx").on(table.brandId)]
);

export const strategyNodes = pgTable(
  "strategy_nodes",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    personaId: varchar("persona_id").notNull().references(() => personas.id),
    productId: varchar("product_id").notNull().references(() => products.id),
    adUnitTypeId: varchar("ad_unit_type_id").notNull().references(() => adUnitTypes.id),
    offerId: varchar("offer_id").references(() => offers.id),
    status: varchar("status", { length: 50 }).notNull().default("idea"),
    priorityScore: integer("priority_score").default(50),
    confidenceScore: numeric("confidence_score", { precision: 5, scale: 2 }).default("0.5"),
    funnelStage: varchar("funnel_stage", { length: 50 }).notNull(),
    awarenessStage: varchar("awareness_stage", { length: 50 }).notNull(),
    angleTitle: varchar("angle_title", { length: 500 }).notNull(),
    angleSummary: text("angle_summary"),
    enemy: text("enemy"),
    promise: text("promise"),
    emotionalTrigger: text("emotional_trigger"),
    mainObjection: text("main_objection"),
    proofType: jsonb("proof_type"),
    ctaType: varchar("cta_type", { length: 50 }).default("shop_now"),
    claimRisk: varchar("claim_risk", { length: 20 }).default("green"),
    rationaleMarkdown: text("rationale_markdown"),
    hypothesis: text("hypothesis"),
    notes: jsonb("notes"),
    lastTestedAt: timestamp("last_tested_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [
    index("strategy_nodes_brand_idx").on(table.brandId),
    index("strategy_nodes_persona_idx").on(table.personaId),
    index("strategy_nodes_product_idx").on(table.productId),
    index("strategy_nodes_ad_unit_idx").on(table.adUnitTypeId),
  ]
);

export const narratives = pgTable(
  "narratives",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    slug: varchar("slug", { length: 255 }).notNull(),
    description: text("description"),
    enemy: text("enemy"),
    promise: text("promise"),
    emotionalCore: text("emotional_core"),
    proofTypesJson: jsonb("proof_types_json"),
    compatiblePersonasJson: jsonb("compatible_personas_json"),
    compatibleProductsJson: jsonb("compatible_products_json"),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("narratives_brand_idx").on(table.brandId)]
);

export const campaigns = pgTable(
  "campaigns",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    slug: varchar("slug", { length: 255 }).notNull(),
    personaId: varchar("persona_id").references(() => personas.id),
    productId: varchar("product_id").references(() => products.id),
    narrativeId: varchar("narrative_id").references(() => narratives.id),
    objective: text("objective"),
    campaignType: varchar("campaign_type", { length: 50 }).notNull().default("standard"),
    status: varchar("status", { length: 50 }).notNull().default("draft"),
    priority: varchar("priority", { length: 50 }).default("medium"),
    owner: varchar("owner", { length: 255 }),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [
    index("campaigns_brand_idx").on(table.brandId),
    index("campaigns_persona_idx").on(table.personaId),
    index("campaigns_product_idx").on(table.productId),
    index("campaigns_narrative_idx").on(table.narrativeId),
  ]
);

export const funnelPlans = pgTable(
  "funnel_plans",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    campaignId: varchar("campaign_id").notNull().references(() => campaigns.id),
    funnelLayer: varchar("funnel_layer", { length: 50 }).notNull(),
    stageGoal: text("stage_goal"),
    primaryMessage: text("primary_message"),
    secondaryMessage: text("secondary_message"),
    keyObjection: text("key_objection"),
    desiredAction: text("desired_action"),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [index("funnel_plans_campaign_idx").on(table.campaignId)]
);

export const persuasionLines = pgTable(
  "persuasion_lines",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    campaignId: varchar("campaign_id").notNull().references(() => campaigns.id),
    funnelLayer: varchar("funnel_layer", { length: 50 }).notNull(),
    persuasionStage: varchar("persuasion_stage", { length: 50 }).notNull(),
    angle: text("angle"),
    enemy: text("enemy"),
    promise: text("promise"),
    proofType: varchar("proof_type", { length: 100 }),
    emotionalTrigger: text("emotional_trigger"),
    objectionAddressed: text("objection_addressed"),
    priority: integer("priority").default(50),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("persuasion_lines_campaign_idx").on(table.campaignId),
    index("persuasion_lines_funnel_idx").on(table.funnelLayer),
  ]
);

export const mediaMixEntries = pgTable(
  "media_mix_entries",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    campaignId: varchar("campaign_id").notNull().references(() => campaigns.id),
    funnelLayer: varchar("funnel_layer", { length: 50 }).notNull(),
    persuasionLineId: varchar("persuasion_line_id").references(() => persuasionLines.id),
    platform: varchar("platform", { length: 100 }),
    adUnitType: varchar("ad_unit_type", { length: 100 }),
    contentFormat: varchar("content_format", { length: 100 }),
    durationSeconds: integer("duration_seconds"),
    priority: integer("priority").default(50),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("media_mix_entries_campaign_idx").on(table.campaignId),
    index("media_mix_entries_persuasion_line_idx").on(table.persuasionLineId),
  ]
);

export const assets = pgTable(
  "assets",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    assetType: varchar("asset_type", { length: 100 }),
    fileUrl: varchar("file_url", { length: 1000 }),
    transcript: text("transcript"),
    productTagsJson: jsonb("product_tags_json"),
    personaTagsJson: jsonb("persona_tags_json"),
    narrativeTagsJson: jsonb("narrative_tags_json"),
    funnelTagsJson: jsonb("funnel_tags_json"),
    proofType: varchar("proof_type", { length: 100 }),
    visualDescriptorsJson: jsonb("visual_descriptors_json"),
    emotionalTone: varchar("emotional_tone", { length: 100 }),
    usageRights: varchar("usage_rights", { length: 100 }),
    performanceStatus: varchar("performance_status", { length: 50 }),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("assets_brand_idx").on(table.brandId)]
);

export const briefs = pgTable(
  "briefs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    strategyNodeId: varchar("strategy_node_id").references(() => strategyNodes.id),
    campaignId: varchar("campaign_id").references(() => campaigns.id),
    personaId: varchar("persona_id").references(() => personas.id),
    productId: varchar("product_id").references(() => products.id),
    narrativeId: varchar("narrative_id").references(() => narratives.id),
    funnelLayer: varchar("funnel_layer", { length: 50 }),
    persuasionStage: varchar("persuasion_stage", { length: 50 }),
    persuasionLineId: varchar("persuasion_line_id").references(() => persuasionLines.id),
    mediaMixEntryId: varchar("media_mix_entry_id").references(() => mediaMixEntries.id),
    title: varchar("title", { length: 500 }).notNull(),
    status: varchar("status", { length: 50 }).notNull().default("draft"),
    briefText: text("brief_text"),
    hookDirection: text("hook_direction"),
    visualDirection: text("visual_direction"),
    proofRequired: text("proof_required"),
    assetsToPullJson: jsonb("assets_to_pull_json"),
    cta: varchar("cta", { length: 255 }),
    narrativeSnapshotJson: jsonb("narrative_snapshot_json"),
    doctrineSnapshot: jsonb("doctrine_snapshot"),
    personaSnapshot: jsonb("persona_snapshot"),
    productSnapshot: jsonb("product_snapshot"),
    adUnitSnapshot: jsonb("ad_unit_snapshot"),
    offerSnapshot: jsonb("offer_snapshot"),
    briefMarkdown: text("brief_markdown"),
    generationNotes: jsonb("generation_notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [
    index("briefs_brand_idx").on(table.brandId),
    index("briefs_campaign_idx").on(table.campaignId),
  ]
);

export const aiJobs = pgTable(
  "ai_jobs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    strategyNodeId: varchar("strategy_node_id").references(() => strategyNodes.id),
    briefId: varchar("brief_id").references(() => briefs.id),
    jobType: varchar("job_type", { length: 100 }).notNull(),
    provider: varchar("provider", { length: 50 }).notNull().default("stub"),
    status: varchar("status", { length: 50 }).notNull().default("queued"),
    requestPayload: jsonb("request_payload"),
    responsePayload: jsonb("response_payload"),
    externalJobId: varchar("external_job_id", { length: 255 }),
    errorMessage: text("error_message"),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
  },
  (table) => [
    index("ai_jobs_brand_idx").on(table.brandId),
    index("ai_jobs_status_idx").on(table.status),
  ]
);

export const creativeOutputs = pgTable(
  "creative_outputs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    strategyNodeId: varchar("strategy_node_id").references(() => strategyNodes.id),
    campaignId: varchar("campaign_id").references(() => campaigns.id),
    briefId: varchar("brief_id").references(() => briefs.id),
    aiJobId: varchar("ai_job_id").references(() => aiJobs.id),
    outputType: varchar("output_type", { length: 100 }).notNull(),
    title: varchar("title", { length: 500 }).notNull(),
    contentMarkdown: text("content_markdown"),
    contentJson: jsonb("content_json"),
    funnelLayer: varchar("funnel_layer", { length: 50 }),
    persuasionStage: varchar("persuasion_stage", { length: 50 }),
    assetType: varchar("asset_type", { length: 100 }),
    imageUrl: text("image_url"),
    adUnitName: varchar("ad_unit_name", { length: 500 }),
    angleName: varchar("angle_name", { length: 500 }),
    prompt: text("prompt"),
    status: varchar("status", { length: 50 }).notNull().default("draft"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [
    index("creative_outputs_brand_idx").on(table.brandId),
    index("creative_outputs_campaign_idx").on(table.campaignId),
  ]
);

export const performanceLearnings = pgTable(
  "performance_learnings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    strategyNodeId: varchar("strategy_node_id").references(() => strategyNodes.id),
    campaignId: varchar("campaign_id").references(() => campaigns.id),
    personaId: varchar("persona_id").references(() => personas.id),
    productId: varchar("product_id").references(() => products.id),
    narrativeId: varchar("narrative_id").references(() => narratives.id),
    briefId: varchar("brief_id").references(() => briefs.id),
    creativeOutputId: varchar("creative_output_id").references(() => creativeOutputs.id),
    funnelLayer: varchar("funnel_layer", { length: 50 }),
    persuasionStage: varchar("persuasion_stage", { length: 50 }),
    channel: varchar("channel", { length: 100 }),
    platform: varchar("platform", { length: 100 }),
    testName: varchar("test_name", { length: 500 }),
    dateStarted: timestamp("date_started"),
    dateEnded: timestamp("date_ended"),
    spend: numeric("spend", { precision: 12, scale: 2 }),
    impressions: integer("impressions"),
    clicks: integer("clicks"),
    ctr: numeric("ctr", { precision: 8, scale: 4 }),
    holdRate: numeric("hold_rate", { precision: 8, scale: 4 }),
    lpv: integer("lpv"),
    atc: integer("atc"),
    purchases: integer("purchases"),
    cvr: numeric("cvr", { precision: 8, scale: 4 }),
    cpa: numeric("cpa", { precision: 12, scale: 2 }),
    aov: numeric("aov", { precision: 12, scale: 2 }),
    roas: numeric("roas", { precision: 12, scale: 4 }),
    fatigueDate: timestamp("fatigue_date"),
    commentSummary: text("comment_summary"),
    rawMetrics: jsonb("raw_metrics"),
    nextHypothesis: text("next_hypothesis"),
    insight: text("insight"),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
  },
  (table) => [
    index("performance_learnings_brand_idx").on(table.brandId),
    index("performance_learnings_campaign_idx").on(table.campaignId),
  ]
);

export const learningEntries = pgTable(
  "learning_entries",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    strategyNodeId: varchar("strategy_node_id").references(() => strategyNodes.id),
    campaignId: varchar("campaign_id").references(() => campaigns.id),
    personaId: varchar("persona_id").references(() => personas.id),
    productId: varchar("product_id").references(() => products.id),
    narrativeId: varchar("narrative_id").references(() => narratives.id),
    funnelLayer: varchar("funnel_layer", { length: 50 }),
    persuasionStage: varchar("persuasion_stage", { length: 50 }),
    performanceEntryId: varchar("performance_entry_id").references(() => performanceLearnings.id),
    title: varchar("title", { length: 500 }).notNull(),
    status: varchar("status", { length: 50 }).notNull().default("draft"),
    insightType: varchar("insight_type", { length: 50 }),
    summary: text("summary"),
    whyItWorked: text("why_it_worked"),
    whyItFailed: text("why_it_failed"),
    nextHypothesis: text("next_hypothesis"),
    evidence: jsonb("evidence"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [
    index("learning_entries_brand_idx").on(table.brandId),
    index("learning_entries_campaign_idx").on(table.campaignId),
  ]
);

// === PERSONA IDEATION ===

export const personaIdeationRuns = pgTable("persona_ideation_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 255 }).notNull(),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  briefDescription: text("brief_description").notNull(),
  brandId: varchar("brand_id").references(() => brands.id),
  productId: varchar("product_id").references(() => products.id),
  offerId: varchar("offer_id").references(() => offers.id),
  narrativeId: varchar("narrative_id").references(() => narratives.id),
  category: varchar("category", { length: 255 }),
  sourceNotes: text("source_notes"),
  sourceCustomerLanguageJson: jsonb("source_customer_language_json"),
  generationMode: varchar("generation_mode", { length: 50 }).notNull().default("stub"),
  totalCandidates: integer("total_candidates").default(0),
  keptCount: integer("kept_count").default(0),
  mergedCount: integer("merged_count").default(0),
  rejectedCount: integer("rejected_count").default(0),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("ideation_runs_brand_idx").on(table.brandId),
]);

export const personaIdeationCandidates = pgTable("persona_ideation_candidates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ideationRunId: varchar("ideation_run_id").notNull().references(() => personaIdeationRuns.id),
  rankOrder: integer("rank_order"),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  name: varchar("name", { length: 255 }).notNull(),
  summary: text("summary"),
  worldview: text("worldview"),
  primaryPain: text("primary_pain"),
  desiredOutcome: text("desired_outcome"),
  deepFear: text("deep_fear"),
  coreBeliefsJson: jsonb("core_beliefs_json"),
  objectionsJson: jsonb("objections_json"),
  trustTriggersJson: jsonb("trust_triggers_json"),
  purchaseDriversJson: jsonb("purchase_drivers_json"),
  emotionalHooksJson: jsonb("emotional_hooks_json"),
  languagePatternsJson: jsonb("language_patterns_json"),
  whyThisPersonaMatters: text("why_this_persona_matters"),
  differentiationReason: text("differentiation_reason"),
  messagingImplicationsJson: jsonb("messaging_implications_json"),
  proofImplicationsJson: jsonb("proof_implications_json"),
  creativeTreatmentImplicationsJson: jsonb("creative_treatment_implications_json"),
  confidenceScore: integer("confidence_score"),
  mergeTargetCandidateId: varchar("merge_target_candidate_id"),
  promotedPersonaId: varchar("promoted_persona_id").references(() => personas.id),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("ideation_candidates_run_idx").on(table.ideationRunId),
]);

export const narrativeAngleWorkflowConfigs = pgTable("narrative_angle_workflow_configs", {
  brandId: varchar("brand_id").primaryKey().references(() => brands.id, { onDelete: "cascade" }),
  productAngleSimUrl: text("product_angle_sim_url"),
  offeringAngleSimUrl: text("offering_angle_sim_url"),
  createdBy: varchar("created_by"),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const narrativeV2Settings = pgTable("narrative_v2_settings", {
  id: integer("id").primaryKey(),
  productAngleSimUrl: text("product_angle_sim_url"),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => [check("narrative_v2_settings_singleton", sql`${t.id} = 1`)]);
export const narrativeGenerations = pgTable("narrative_generations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  personaId: varchar("persona_id").references(() => personas.id, { onDelete: "set null" }),
  // Either productId or offeringId is set depending on promotedItemType.
  // Both are nullable so offering-based generations don't need a product.
  promotedItemType: varchar("promoted_item_type", { length: 20 }).notNull().default("product"),
  productId: varchar("product_id").references(() => products.id),
  offeringId: varchar("offering_id").references(() => offers.id),
  status: varchar("status", { length: 50 }).notNull().default("queued"),
  requestPayloadJson: jsonb("request_payload_json"),
  responsePayloadJson: jsonb("response_payload_json"),
  parsedOutputJson: jsonb("parsed_output_json"),
  parseError: text("parse_error"),
  workflowProvider: varchar("workflow_provider", { length: 100 }),
  workflowName: varchar("workflow_name", { length: 255 }),
  workflowRunId: varchar("workflow_run_id", { length: 255 }),
  // Durable execution lease. Only the owner of leaseToken may settle a run;
  // lease expiry lets the reconciler detect work abandoned by a dead process.
  leaseToken: varchar("lease_token", { length: 255 }),
  leaseExpiresAt: timestamp("lease_expires_at"),
  attemptCount: integer("attempt_count").notNull().default(0),
  diagnosticsJson: jsonb("diagnostics_json"),
  errorMessage: text("error_message"),
  angleCount: integer("angle_count").default(0),
  angleGroupingJson: jsonb("angle_grouping_json"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("narr_gen_brand_idx").on(table.brandId),
  index("narr_gen_persona_idx").on(table.personaId),
  index("narr_gen_product_idx").on(table.productId),
  index("narr_gen_offering_idx").on(table.offeringId),
  index("narr_gen_recovery_idx").on(table.status, table.leaseExpiresAt),
]);

export const narrativeAngles = pgTable("narrative_angles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  generationId: varchar("generation_id").notNull().references(() => narrativeGenerations.id),
  personaId: varchar("persona_id").references(() => personas.id, { onDelete: "set null" }),
  productId: varchar("product_id").references(() => products.id),
  offeringId: varchar("offering_id").references(() => offers.id),
  name: varchar("name", { length: 255 }).notNull(),
  family: varchar("family", { length: 100 }),
  subtype: varchar("subtype", { length: 100 }),
  funnelStage: varchar("funnel_stage", { length: 10 }),
  summary: text("summary"),
  coreTension: text("core_tension"),
  personaRelevance: text("persona_relevance"),
  productTruth: text("product_truth"),
  beliefShift: text("belief_shift"),
  promise: text("promise"),
  proofAnchorsJson: jsonb("proof_anchors_json"),
  angleStemsJson: jsonb("angle_stems_json"),
  resonanceScore: integer("resonance_score"),
  proofScore: integer("proof_score"),
  distinctivenessScore: integer("distinctiveness_score"),
  emotionalScore: integer("emotional_score"),
  notes: text("notes"),
  status: varchar("status", { length: 50 }).notNull().default("active"),
  isFavorite: boolean("is_favorite").default(false),
  editedByUser: boolean("edited_by_user").default(false),
  sortOrder: integer("sort_order"),
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("narr_angles_gen_idx").on(table.generationId),
  index("narr_angles_brand_idx").on(table.brandId),
  index("narr_angles_persona_idx").on(table.personaId),
  index("narr_angles_product_idx").on(table.productId),
  index("narr_angles_family_idx").on(table.family),
]);

// One entry in the brand's font list. Two sources:
//   "google" — a curated free Google Font; `family` is the Google Fonts family
//              name (no file storage; previews load via the Google Fonts CSS API).
//   "upload" — a user-uploaded font file stored durably in R2; `url` is the
//              public R2 URL, `fileName`/`format` describe the original file.
// `label` is an optional user-editable role (e.g. "Headline", "Body").
export const brandFontSchema = z.object({
  id: z.string(),
  source: z.enum(["google", "upload"]),
  family: z.string(),
  label: z.string().default(""),
  url: z.string().nullable().default(null),
  fileName: z.string().nullable().default(null),
  format: z.string().nullable().default(null),
  // Licensing / copyright status (soft compliance signal for UPLOADED fonts).
  // Google fonts are inherently open-licensed and are never flagged (the
  // `isBrandFontLicenseFlagged` helper short-circuits on source==="google"),
  // so existing brands stay warning-free. Uploads default to "unknown" +
  // unconfirmed until the uploader confirms, so pre-existing uploaded fonts
  // (no field) parse to the flagged state and surface a warning rather than
  // being silently used. Defaults keep every existing blob parsing unchanged.
  licenseStatus: z.enum(["licensed", "open_source", "unknown"]).default("unknown"),
  rightsConfirmed: z.boolean().default(false),
});
export type BrandFont = z.infer<typeof brandFontSchema>;

export const brandDesignSystemSchema = z.object({
  designPositioning: z.string().default(""),
  styleRules: z.string().trim().max(4000).default(""),
  styleRulesFont: z.string().trim().max(4000).default(""),
  visualMood: z.array(z.string()).default([]),
  primaryColors: z.array(z.string()).default([]),
  secondaryColors: z.array(z.string()).default([]),
  accentColors: z.array(z.string()).default([]),
  typographyDirection: z.object({
    headlineStyle: z.string().default(""),
    bodyStyle: z.string().default(""),
    accentStyle: z.string().default(""),
  }).default({ headlineStyle: "", bodyStyle: "", accentStyle: "" }),
  // Brand fonts (picked free fonts + uploaded font files). Defaults to [] so
  // existing brands with no fonts parse unchanged.
  fonts: z.array(brandFontSchema).default([]),
  // NOTE (Task #1115 removal): the per-brand `adFont` ("apply brand font to
  // generated ads") setting was removed along with the whole brand-font-in-ads
  // feature. Any `adFont` key still present in stored design-system blobs is
  // simply ignored by this schema (unknown keys are stripped on parse).
  // Task #1233 — brand CTA/button style (fill color, optional text color,
  // corner shape). This is the HARD gate for CTA styling across LP Express,
  // Email Express, and the Ads Express prompt directive: when `enabled` is
  // false (or fillColor is unset/invalid) every consumer resolves null and the
  // output stays byte-for-byte identical to today. `textColor` null = automatic
  // contrast (white/near-black by fill luminance). `shape` null = keep each
  // surface's existing default radius. Defaults keep every existing
  // design-system blob parsing unchanged.
  // `fill` "outline" = transparent button with a border in the fill color
  // (auto text = the fill color itself). `allCaps` uppercases the button label.
  // `fontId` points at an entry in `fonts` above for the button label typeface;
  // null = keep each surface's default. Defaults keep legacy blobs unchanged.
  ctaStyle: z
    .object({
      enabled: z.boolean().default(false),
      fillColor: z.string().nullable().default(null),
      textColor: z.string().nullable().default(null),
      shape: z.enum(["square", "rounded", "pill"]).nullable().default(null),
      fill: z.enum(["solid", "outline"]).default("solid"),
      allCaps: z.boolean().default(false),
      fontId: z.string().nullable().default(null),
    })
    .default({
      enabled: false,
      fillColor: null,
      textColor: null,
      shape: null,
      fill: "solid",
      allCaps: false,
      fontId: null,
    }),
  layoutDirection: z.object({
    overallStyle: z.string().default(""),
    sectionDensity: z.string().default(""),
    preferredLayouts: z.array(z.string()).default([]),
    avoidLayouts: z.array(z.string()).default([]),
  }).default({ overallStyle: "", sectionDensity: "", preferredLayouts: [], avoidLayouts: [] }),
  componentStyle: z.object({
    buttons: z.string().default(""),
    cards: z.string().default(""),
    badges: z.string().default(""),
    forms: z.string().default(""),
    offerBoxes: z.string().default(""),
    comparisonTables: z.string().default(""),
    testimonials: z.string().default(""),
    faqAccordions: z.string().default(""),
  }).default({ buttons: "", cards: "", badges: "", forms: "", offerBoxes: "", comparisonTables: "", testimonials: "", faqAccordions: "" }),
  photographyDirection: z.object({
    style: z.string().default(""),
    lighting: z.string().default(""),
    settings: z.array(z.string()).default([]),
    models: z.string().default(""),
    productShots: z.string().default(""),
    lifestyleShots: z.string().default(""),
    avoid: z.array(z.string()).default([]),
  }).default({ style: "", lighting: "", settings: [], models: "", productShots: "", lifestyleShots: "", avoid: [] }),
  imageGenerationRules: z.object({
    mustInclude: z.array(z.string()).default([]),
    avoid: z.array(z.string()).default([]),
    styleKeywords: z.array(z.string()).default([]),
    negativePromptRules: z.array(z.string()).default([]),
  }).default({ mustInclude: [], avoid: [], styleKeywords: [], negativePromptRules: [] }),
  brandMotifs: z.array(z.string()).default([]),
  trustVisuals: z.array(z.string()).default([]),
  mobileDesignRules: z.array(z.string()).default([]),
  pageDesignRules: z.object({
    heroRules: z.array(z.string()).default([]),
    offerRules: z.array(z.string()).default([]),
    proofRules: z.array(z.string()).default([]),
    comparisonRules: z.array(z.string()).default([]),
    ctaRules: z.array(z.string()).default([]),
  }).default({ heroRules: [], offerRules: [], proofRules: [], comparisonRules: [], ctaRules: [] }),
});
export type BrandDesignSystem = z.infer<typeof brandDesignSystemSchema>;
export const emptyBrandDesignSystem = (): BrandDesignSystem => brandDesignSystemSchema.parse({});

export const insertWorkspaceSchema = createInsertSchema(workspaces).omit({ id: true, createdAt: true, updatedAt: true });
export const insertBrandSchema = createInsertSchema(brands)
  .omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true })
  .extend({
    brandDesignSystem: brandDesignSystemSchema.partial().optional().nullable(),
  });
export const insertPersonaSchema = createInsertSchema(personas).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true, evidenceLink: true, evidenceGeneration: true, evidenceMatchToken: true });

export const insertStarterPersonaBatchSchema = createInsertSchema(starterPersonaBatches).omit({ id: true, createdAt: true, updatedAt: true });
export const insertPersonaCharacterSchema = createInsertSchema(personaCharacters, {
  adIdentityPackUrls: z.array(z.string()).nullable().optional(),
}).omit({ id: true, createdAt: true });
export type InsertPersonaCharacter = z.infer<typeof insertPersonaCharacterSchema>;
export type PersonaCharacter = typeof personaCharacters.$inferSelect;
export const insertPersonaResearchTopicSchema = createInsertSchema(personaResearchTopics).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPersonaResearchTopic = z.infer<typeof insertPersonaResearchTopicSchema>;
export type PersonaResearchTopic = typeof personaResearchTopics.$inferSelect;
export const insertProductSchema = createInsertSchema(products).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertAdUnitTypeSchema = createInsertSchema(adUnitTypes)
  .omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true })
  .extend({
    exampleImages: z.array(exampleImageEntrySchema).optional().nullable(),
    photoSlots: photoSlotsSchema.optional().nullable(),
  });

export const storyExampleConceptSchema = z.object({
  industry: z.string(),
  concept: z.string(),
});
export const storyBadExampleSchema = z.object({
  bad_example: z.string(),
  why_bad: z.string(),
});
export const insertStoryCategorySchema = createInsertSchema(storyCategories)
  .omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true })
  .extend({
    familyTags: z.array(z.string()).optional().nullable(),
    persuasionTags: z.array(z.string()).optional().nullable(),
    emotionArc: z.array(z.string()).optional().nullable(),
    typicalArc: z.array(z.string()).optional().nullable(),
    metaphorBank: z.array(z.string()).optional().nullable(),
    scenePatterns: z.array(z.string()).optional().nullable(),
    motionVerbs: z.array(z.string()).optional().nullable(),
    visualSymbols: z.array(z.string()).optional().nullable(),
    visualsToAvoid: z.array(z.string()).optional().nullable(),
    commonFailureModes: z.array(z.string()).optional().nullable(),
    ctaBridgeExamples: z.array(z.string()).optional().nullable(),
    exampleConcepts: z.array(storyExampleConceptSchema).optional().nullable(),
    badExamples: z.array(storyBadExampleSchema).optional().nullable(),
  });
export type InsertStoryCategory = z.infer<typeof insertStoryCategorySchema>;
export type StoryCategory = typeof storyCategories.$inferSelect;
export const insertOfferSchema = createInsertSchema(offers).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertPainPillarCategorySchema = createInsertSchema(painPillarCategories).omit({ id: true, createdAt: true, updatedAt: true });
export const insertPainPillarSchema = createInsertSchema(painPillars).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertPainPillarPersonaSchema = createInsertSchema(painPillarPersonas).omit({ id: true, createdAt: true });
export const insertPainPillarProductSchema = createInsertSchema(painPillarProducts).omit({ id: true, createdAt: true });
export const insertSalesDiscountSchema = createInsertSchema(salesDiscounts).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertStrategyNodeSchema = createInsertSchema(strategyNodes).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertNarrativeSchema = createInsertSchema(narratives).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertCampaignSchema = createInsertSchema(campaigns).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertFunnelPlanSchema = createInsertSchema(funnelPlans).omit({ id: true, createdAt: true, updatedAt: true });
export const insertPersuasionLineSchema = createInsertSchema(persuasionLines).omit({ id: true, createdAt: true, updatedAt: true });
export const insertMediaMixEntrySchema = createInsertSchema(mediaMixEntries).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAssetSchema = createInsertSchema(assets).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertBriefSchema = createInsertSchema(briefs).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertAiJobSchema = createInsertSchema(aiJobs).omit({ id: true, createdAt: true, updatedAt: true });
export const insertCreativeOutputSchema = createInsertSchema(creativeOutputs).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertPerformanceLearningSchema = createInsertSchema(performanceLearnings).omit({ id: true, createdAt: true, updatedAt: true });
export const insertLearningEntrySchema = createInsertSchema(learningEntries).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertIdeationRunSchema = createInsertSchema(personaIdeationRuns).omit({ id: true, createdAt: true, updatedAt: true });
export const insertIdeationCandidateSchema = createInsertSchema(personaIdeationCandidates).omit({ id: true, createdAt: true, updatedAt: true });
export const insertProductReviewSchema = createInsertSchema(productReviews).omit({ id: true, createdAt: true });
export const savedOpportunities = pgTable("saved_opportunities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  generationId: varchar("generation_id").notNull().references(() => narrativeGenerations.id),
  title: varchar("title", { length: 500 }).notNull(),
  opportunityType: varchar("opportunity_type", { length: 100 }),
  coreTension: text("core_tension"),
  personaReason: text("persona_reason"),
  productAnchor: text("product_anchor"),
  whyPromising: text("why_promising"),
  strength: integer("strength"),
  opportunityIndex: integer("opportunity_index"),
  isFavorite: boolean("is_favorite").default(false),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("saved_opp_brand_idx").on(table.brandId),
  index("saved_opp_gen_idx").on(table.generationId),
]);

export const insertNarrativeGenerationSchema = createInsertSchema(narrativeGenerations).omit({ id: true, createdAt: true, updatedAt: true }).extend({ personaId: z.string().min(1) });
export const insertNarrativeAngleSchema = createInsertSchema(narrativeAngles).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertSavedOpportunitySchema = createInsertSchema(savedOpportunities).omit({ id: true, createdAt: true, updatedAt: true });

export type Workspace = typeof workspaces.$inferSelect;
export type InsertWorkspace = z.infer<typeof insertWorkspaceSchema>;
export type Brand = typeof brands.$inferSelect;
export type InsertBrand = z.infer<typeof insertBrandSchema>;
export type Persona = typeof personas.$inferSelect;
export type InsertPersona = z.infer<typeof insertPersonaSchema>;

export type StarterPersonaBatch = typeof starterPersonaBatches.$inferSelect;
export type Product = typeof products.$inferSelect;
export type InsertProduct = z.infer<typeof insertProductSchema>;
export type AdUnitType = typeof adUnitTypes.$inferSelect;
export type InsertAdUnitType = z.infer<typeof insertAdUnitTypeSchema>;

export type AdUnitCatalogItem = AdUnitType & {
  catalogScope: "global" | "brand";
  isApprovedForBrand: boolean;
  approvedAt: Date | null;
};
export type Offer = typeof offers.$inferSelect;
export type InsertOffer = z.infer<typeof insertOfferSchema>;
export type PainPillarCategory = typeof painPillarCategories.$inferSelect;
export type InsertPainPillarCategory = z.infer<typeof insertPainPillarCategorySchema>;
export type PainPillar = typeof painPillars.$inferSelect;
export type InsertPainPillar = z.infer<typeof insertPainPillarSchema>;
export type PainPillarPersona = typeof painPillarPersonas.$inferSelect;
export type InsertPainPillarPersona = z.infer<typeof insertPainPillarPersonaSchema>;
export type PainPillarProduct = typeof painPillarProducts.$inferSelect;
export type InsertPainPillarProduct = z.infer<typeof insertPainPillarProductSchema>;
export type SalesDiscount = typeof salesDiscounts.$inferSelect;
export type InsertSalesDiscount = z.infer<typeof insertSalesDiscountSchema>;
export type StrategyNode = typeof strategyNodes.$inferSelect;
export type InsertStrategyNode = z.infer<typeof insertStrategyNodeSchema>;
export type Narrative = typeof narratives.$inferSelect;
export type InsertNarrative = z.infer<typeof insertNarrativeSchema>;
export type Campaign = typeof campaigns.$inferSelect;
export type InsertCampaign = z.infer<typeof insertCampaignSchema>;
export type FunnelPlan = typeof funnelPlans.$inferSelect;
export type InsertFunnelPlan = z.infer<typeof insertFunnelPlanSchema>;
export type PersuasionLine = typeof persuasionLines.$inferSelect;
export type InsertPersuasionLine = z.infer<typeof insertPersuasionLineSchema>;
export type MediaMixEntry = typeof mediaMixEntries.$inferSelect;
export type InsertMediaMixEntry = z.infer<typeof insertMediaMixEntrySchema>;
export type Asset = typeof assets.$inferSelect;
export type InsertAsset = z.infer<typeof insertAssetSchema>;
export type Brief = typeof briefs.$inferSelect;
export type InsertBrief = z.infer<typeof insertBriefSchema>;
export type AiJob = typeof aiJobs.$inferSelect;
export type InsertAiJob = z.infer<typeof insertAiJobSchema>;
export type CreativeOutput = typeof creativeOutputs.$inferSelect;
export type InsertCreativeOutput = z.infer<typeof insertCreativeOutputSchema>;
export type PerformanceLearning = typeof performanceLearnings.$inferSelect;
export type InsertPerformanceLearning = z.infer<typeof insertPerformanceLearningSchema>;
export type LearningEntry = typeof learningEntries.$inferSelect;
export type InsertLearningEntry = z.infer<typeof insertLearningEntrySchema>;
export type WorkspaceMember = typeof workspaceMembers.$inferSelect;

export type PerformanceEntry = typeof performanceLearnings.$inferSelect;
export type InsertPerformanceEntry = z.infer<typeof insertPerformanceLearningSchema>;
export const performanceEntries = performanceLearnings;
export const insertPerformanceEntrySchema = insertPerformanceLearningSchema;
export type IdeationRun = typeof personaIdeationRuns.$inferSelect;
export type InsertIdeationRun = z.infer<typeof insertIdeationRunSchema>;
export type IdeationCandidate = typeof personaIdeationCandidates.$inferSelect;
export type InsertIdeationCandidate = z.infer<typeof insertIdeationCandidateSchema>;
export type ProductReview = typeof productReviews.$inferSelect;
export type InsertProductReview = z.infer<typeof insertProductReviewSchema>;
export type NarrativeGeneration = typeof narrativeGenerations.$inferSelect;
export type InsertNarrativeGeneration = z.infer<typeof insertNarrativeGenerationSchema>;
export type NarrativeAngle = typeof narrativeAngles.$inferSelect;
export type InsertNarrativeAngle = z.infer<typeof insertNarrativeAngleSchema>;
export type SavedOpportunity = typeof savedOpportunities.$inferSelect;
export type InsertSavedOpportunity = z.infer<typeof insertSavedOpportunitySchema>;

export const spyBrands = pgTable("spy_brands", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  name: varchar("name", { length: 255 }).notNull(),
  facebookPageUrl: text("facebook_page_url").notNull(),
  logoUrl: text("logo_url"),
  status: varchar("status", { length: 50 }).notNull().default("active"),
  archivedAt: timestamp("archived_at"),
  schedule: varchar("schedule", { length: 16 }).notNull().default("manual"),
  schedulePausedAt: timestamp("schedule_paused_at"),
  nextSyncAt: timestamp("next_sync_at"),
  lastSuccessfulSyncAt: timestamp("last_successful_sync_at"),
  syncMessage: text("sync_message"),
  lastSyncAt: timestamp("last_synced_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("spy_brands_brand_idx").on(table.brandId),
]);

export const spyAds = pgTable("spy_ads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spyBrandId: varchar("spy_brand_id").notNull().references(() => spyBrands.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  externalId: varchar("external_id", { length: 255 }),
  /** Facebook Ad Library identity when supplied. Rows without one retain their
   * legacy id and are never heuristically merged across collections. */
  sourceIdentity: varchar("source_identity", { length: 255 }),
  permalink: text("permalink"),
  title: text("title"),
  body: text("body"),
  format: varchar("format", { length: 50 }),
  thumbnailUrl: text("thumbnail_url"),
  sourceThumbnailUrl: text("source_thumbnail_url"),
  mediaUrl: text("media_url"),
  sourceMediaUrl: text("source_media_url"),
  mediaType: varchar("media_type", { length: 50 }),
  mediaStatus: varchar("media_status", { length: 32 }).notNull().default("unknown"),
  mediaFingerprint: varchar("media_fingerprint", { length: 128 }),
  landingPageUrl: text("landing_page_url"),
  ctaText: varchar("cta_text", { length: 255 }),
  startedAt: timestamp("started_at"),
  platformStartAt: timestamp("platform_start_at"),
  videoDurationSeconds: integer("video_duration_seconds"),
  isActive: boolean("is_active").default(true),
  latestExplicitStatus: varchar("latest_explicit_status", { length: 32 }),
  latestExplicitStatusAt: timestamp("latest_explicit_status_at"),
  lastObservedAt: timestamp("last_observed_at"),
  missingFromLastRunAt: timestamp("missing_from_last_run_at"),
  isFavorite: boolean("is_favorite").default(false),
  platforms: text("platforms").array(),
  notes: jsonb("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("spy_ads_spy_brand_idx").on(table.spyBrandId),
  index("spy_ads_brand_idx").on(table.brandId),
  index("spy_ads_external_idx").on(table.externalId),
  uniqueIndex("spy_ads_source_identity_uq").on(table.spyBrandId, table.sourceIdentity),
]);

/** A durable, inspectable collection operation. A non-terminal partial index
 * in the migration enforces one collection per watched competitor. */
export const spySyncRuns = pgTable("spy_sync_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spyBrandId: varchar("spy_brand_id").notNull().references(() => spyBrands.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  requestedBy: varchar("requested_by").references(() => users.id),
  idempotencyKey: varchar("idempotency_key", { length: 160 }),
  status: varchar("status", { length: 24 }).notNull().default("queued"),
  stage: varchar("stage", { length: 24 }).notNull().default("queued"),
  apifyRunId: varchar("apify_run_id", { length: 255 }),
  requestedCoverage: jsonb("requested_coverage"),
  limitations: jsonb("limitations"),
  counts: jsonb("counts"),
  error: text("error"),
  leaseOwner: varchar("lease_owner", { length: 255 }),
  leaseExpiresAt: timestamp("lease_expires_at"),
  heartbeatAt: timestamp("heartbeat_at"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("spy_sync_runs_brand_status_idx").on(table.spyBrandId, table.status),
  uniqueIndex("spy_sync_runs_brand_idempotency_uq").on(table.brandId, table.idempotencyKey),
]);
export const insertSpyBrandSchema = createInsertSchema(spyBrands).omit({ id: true, createdAt: true, updatedAt: true, lastSyncAt: true });
export const insertSpyAdSchema = createInsertSchema(spyAds).omit({ id: true, createdAt: true, updatedAt: true });

export type SpyBrand = typeof spyBrands.$inferSelect;
export type InsertSpyBrand = z.infer<typeof insertSpyBrandSchema>;
export type SpyAd = typeof spyAds.$inferSelect;
export type InsertSpyAd = z.infer<typeof insertSpyAdSchema>;

export type SpySyncRun = typeof spySyncRuns.$inferSelect;
export const ugcCampaigns = pgTable("ugc_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  name: varchar("name", { length: 255 }).notNull(),
  personaId: varchar("persona_id").references(() => personas.id),
  productId: varchar("product_id").references(() => products.id),
  narrativeId: varchar("narrative_id").references(() => narratives.id),
  narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
  adUnitTypeId: varchar("ad_unit_type_id").references(() => adUnitTypes.id),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  concept: text("concept"),
  targetAudience: text("target_audience"),
  videoStyle: varchar("video_style", { length: 100 }),
  duration: varchar("duration", { length: 50 }),
  // UGC scene clips render on kie.ai Kling 3.0. The ad-length selector on the
  // create page sets `secondsPerScene` (3/6/9 → 27/54/81s over a fixed 9 scenes);
  // script length, clip duration, and voiceover length all follow it.
  videoModel: varchar("video_model", { length: 50 }).default("kling-3.0"),
  secondsPerScene: integer("seconds_per_scene").default(3),
  videoCount: integer("video_count").default(1),
  avatarCount: integer("avatar_count").default(1),
  campaignMode: varchar("campaign_mode", { length: 50 }).default("oneshot"),
  notes: jsonb("notes"),
  // Task #744 — the full ProductVariant object chosen at create time (or null
  // for "no specific variant"). Snapshotted so the script narration + storyboard
  // product reference reuse the same picture + ingredients/description across
  // regenerations and server restarts. null = legacy product-level behaviour.
  selectedVariantSnapshot: jsonb("selected_variant_snapshot"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  index("ugc_campaigns_brand_idx").on(table.brandId),
]);

export const ugcScriptPlans = pgTable("ugc_script_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ugcCampaignId: varchar("ugc_campaign_id").notNull().references(() => ugcCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  scriptNumber: integer("script_number").notNull(),
  title: text("title").notNull(),
  conceptDescription: text("concept_description"),
  hookStyle: text("hook_style"),
  storyType: text("story_type"),
  primaryEmotion: text("primary_emotion"),
  primaryObjectionOrDesire: text("primary_objection_or_desire"),
  setting: text("setting"),
  keyDistinction: text("key_distinction"),
  avatarId: varchar("avatar_id"),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("ugc_script_plans_campaign_idx").on(table.ugcCampaignId),
]);

export const ugcScripts = pgTable("ugc_scripts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ugcCampaignId: varchar("ugc_campaign_id").notNull().references(() => ugcCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  avatarId: varchar("avatar_id"),
  title: varchar("title", { length: 255 }),
  hook: text("hook"),
  body: text("body"),
  cta: text("cta"),
  fullScript: text("full_script"),
  talkingPoints: jsonb("talking_points"),
  visualDirections: jsonb("visual_directions"),
  thumbnailUrl: text("thumbnail_url"),
  videoUrl: text("video_url"),
  scenePrompts: jsonb("scene_prompts"),
  // Server-side status for batch scene-prompt generation (idle | pending |
  // ready | error). Lets the client reflect completion/failure deterministically
  // and offer a cancel action instead of inferring progress from a client timer.
  scenePromptStatus: varchar("scene_prompt_status", { length: 20 }).default("idle"),
  scenePromptError: text("scene_prompt_error"),
  sceneVideos: jsonb("scene_videos"),
  // Per-panel images sliced from the storyboard grid(s). Each grid is a 7x2
  // (14-panel) 9:16 image; a 30s/two-grid script yields 28 panels. Shape:
  // [{ scene_number, storyboard_index, image_url }]. Used as the per-clip
  // start/reference frame and as the input image for per-panel scene prompts.
  panelImages: jsonb("panel_images"),
  panelStatus: varchar("panel_status", { length: 20 }).default("idle"),
  panelError: text("panel_error"),
  // Pre-storyboard UGC selfie: the character reference headshot transformed into
  // a realistic iPhone UGC selfie (KIE Nano Banana 2) before storyboard render,
  // then reused as the character reference throughout the pipeline. The source
  // column tracks the base headshot it was derived from so a changed avatar
  // headshot regenerates the selfie.
  characterSelfieUrl: text("character_selfie_url"),
  characterSelfieSourceUrl: text("character_selfie_source_url"),
  storyboardPrompt: text("storyboard_prompt"),
  storyboardImageUrl: text("storyboard_image_url"),
  storyboardStatus: varchar("storyboard_status", { length: 20 }).default("idle"),
  storyboardError: text("storyboard_error"),
  // Second storyboard for 30s videos: scenes 15-28 (last 15s). The columns
  // above hold storyboard 1 (scenes 1-14, first 15s). Each storyboard renders
  // its own 9:16 grid image and later its own 15s video; the two videos are
  // stitched together before the voice-over pass.
  storyboardPrompt2: text("storyboard_prompt_2"),
  storyboardImageUrl2: text("storyboard_image_url_2"),
  storyboardStatus2: varchar("storyboard_status_2", { length: 20 }).default("idle"),
  storyboardError2: text("storyboard_error_2"),
  // Third storyboard for 81s videos. Each storyboard covers ~27s (9 panels at
  // 3s): 27s → 1 storyboard, 54s → 2, 81s → 3. Slots beyond the selected ad
  // length stay "idle" and never count toward the storyboard progress total.
  storyboardPrompt3: text("storyboard_prompt_3"),
  storyboardImageUrl3: text("storyboard_image_url_3"),
  storyboardStatus3: varchar("storyboard_status_3", { length: 20 }).default("idle"),
  storyboardError3: text("storyboard_error_3"),
  // KIE task ids for in-flight storyboard grid renders (one per slot).
  // Persisted at createTask time so a server restart can resume polling the
  // existing task instead of failing the slot; cleared on terminal states.
  storyboardTaskId: text("storyboard_task_id"),
  storyboardTaskId2: text("storyboard_task_id_2"),
  storyboardTaskId3: text("storyboard_task_id_3"),
  voiceOverUrl: text("voice_over_url"),
  voiceOverStatus: varchar("voice_over_status", { length: 20 }).default("idle"),
  voiceOverError: text("voice_over_error"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("ugc_scripts_campaign_idx").on(table.ugcCampaignId),
  index("ugc_scripts_brand_idx").on(table.brandId),
]);

export const ugcAvatars = pgTable("ugc_avatars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ugcCampaignId: varchar("ugc_campaign_id").notNull().references(() => ugcCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  imageUrl: varchar("image_url").notNull(),
  resolvedPrompt: text("resolved_prompt"),
  status: varchar("status", { length: 20 }).default("generated").notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("ugc_avatars_campaign_idx").on(table.ugcCampaignId),
]);

export const insertUgcCampaignSchema = createInsertSchema(ugcCampaigns).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertUgcScriptPlanSchema = createInsertSchema(ugcScriptPlans).omit({ id: true, createdAt: true });
export const insertUgcScriptSchema = createInsertSchema(ugcScripts).omit({ id: true, createdAt: true, updatedAt: true });
export const insertUgcAvatarSchema = createInsertSchema(ugcAvatars).omit({ id: true, createdAt: true });

export type UgcCampaign = typeof ugcCampaigns.$inferSelect;
export type InsertUgcCampaign = z.infer<typeof insertUgcCampaignSchema>;
export type UgcScriptPlan = typeof ugcScriptPlans.$inferSelect;
export type InsertUgcScriptPlan = z.infer<typeof insertUgcScriptPlanSchema>;
export type UgcScript = typeof ugcScripts.$inferSelect;
export type InsertUgcScript = z.infer<typeof insertUgcScriptSchema>;
export type UgcAvatar = typeof ugcAvatars.$inferSelect;
export type InsertUgcAvatar = z.infer<typeof insertUgcAvatarSchema>;

// Per-character custom ElevenLabs voices (created once via Voice Design, cached
// and reused). The UGC voice-over path designs a UNIQUE voice per character and
// narrates by calling the ElevenLabs API directly — the KIE proxy only accepts
// its fixed catalog and cannot use a created voice id. Polymorphic owner so the
// same cache serves persona characters, ugc avatars, and the persona fallback.
export const ugcCharacterVoices = pgTable("ugc_character_voices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // "persona_character" | "ugc_avatar" | "persona"
  ownerType: varchar("owner_type", { length: 30 }).notNull(),
  ownerId: varchar("owner_id").notNull(),
  brandId: varchar("brand_id").notNull(),
  elevenlabsVoiceId: varchar("elevenlabs_voice_id", { length: 100 }),
  voiceName: varchar("voice_name", { length: 255 }),
  voiceDescription: text("voice_description"),
  sourceImageUrl: text("source_image_url"),
  // "generating" | "ready" | "failed"
  status: varchar("status", { length: 20 }).notNull().default("generating"),
  error: text("error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("ugc_character_voices_owner_uniq").on(table.ownerType, table.ownerId),
  index("ugc_character_voices_brand_idx").on(table.brandId),
]);

export const insertUgcCharacterVoiceSchema = createInsertSchema(ugcCharacterVoices).omit({ id: true, createdAt: true, updatedAt: true });
export type UgcCharacterVoice = typeof ugcCharacterVoices.$inferSelect;
export type InsertUgcCharacterVoice = z.infer<typeof insertUgcCharacterVoiceSchema>;

export const renders = pgTable("renders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ugcScriptId: varchar("ugc_script_id").notNull().references(() => ugcScripts.id, { onDelete: "cascade" }),
  ugcCampaignId: varchar("ugc_campaign_id").notNull().references(() => ugcCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("queued"),
  progress: integer("progress").default(0),
  outputUrl: text("output_url"),
  error: text("error"),
  durationSec: integer("duration_sec"),
  sceneCount: integer("scene_count"),
  // Captioned variant produced by fal-ai/veed/subtitles after the final
  // stitch completes. Auto-triggers on render completion; can be retried.
  captionedVideoUrl: text("captioned_video_url"),
  captionedVideoStatus: varchar("captioned_video_status", { length: 20 }).default("idle"),
  captionedVideoError: text("captioned_video_error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("renders_script_idx").on(table.ugcScriptId),
  index("renders_campaign_idx").on(table.ugcCampaignId),
]);

export const insertRenderSchema = createInsertSchema(renders).omit({ id: true, createdAt: true, updatedAt: true });
export type Render = typeof renders.$inferSelect;
export type InsertRender = z.infer<typeof insertRenderSchema>;

// ─── Claymation Video Campaigns ──────────────────────────────────────────────
// A full, independent parallel clone of the UGC Video Campaigns data model
// (campaigns → script plans → scripts → avatars → renders → character voices).
// Column structure is identical to UGC; only the table names differ so Claymation
// data is fully isolated from UGC data.
export const claymationCampaigns = pgTable("claymation_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  name: varchar("name", { length: 255 }).notNull(),
  personaId: varchar("persona_id").references(() => personas.id),
  productId: varchar("product_id").references(() => products.id),
  narrativeId: varchar("narrative_id").references(() => narratives.id),
  narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
  adUnitTypeId: varchar("ad_unit_type_id").references(() => adUnitTypes.id),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  concept: text("concept"),
  targetAudience: text("target_audience"),
  videoStyle: varchar("video_style", { length: 100 }),
  duration: varchar("duration", { length: 50 }),
  // Claymation scene clips render on kie.ai SeeDance 2.0. secondsPerScene scales
  // with the selected Ad Length (total ÷ 9 → 4/8/12 for 36/72/108s); the default
  // reflects the 36s default option.
  videoModel: varchar("video_model", { length: 50 }).default("seedance-2.0"),
  secondsPerScene: integer("seconds_per_scene").default(4),
  videoCount: integer("video_count").default(1),
  avatarCount: integer("avatar_count").default(1),
  campaignMode: varchar("campaign_mode", { length: 50 }).default("oneshot"),
  // Optional per-campaign ElevenLabs voice override. When set, ALL voiceovers in
  // this campaign use this voice id, taking precedence over the global
  // fixed/auto-cast voice. Scoped to this campaign only — every other campaign
  // keeps the default voice.
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  notes: jsonb("notes"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  index("claymation_campaigns_brand_idx").on(table.brandId),
]);

export const claymationScripts = pgTable("claymation_scripts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  claymationCampaignId: varchar("claymation_campaign_id").notNull().references(() => claymationCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  avatarId: varchar("avatar_id"),
  title: varchar("title", { length: 255 }),
  hook: text("hook"),
  body: text("body"),
  cta: text("cta"),
  fullScript: text("full_script"),
  talkingPoints: jsonb("talking_points"),
  visualDirections: jsonb("visual_directions"),
  thumbnailUrl: text("thumbnail_url"),
  videoUrl: text("video_url"),
  scenePrompts: jsonb("scene_prompts"),
  scenePromptStatus: varchar("scene_prompt_status", { length: 20 }).default("idle"),
  scenePromptError: text("scene_prompt_error"),
  sceneVideos: jsonb("scene_videos"),
  panelImages: jsonb("panel_images"),
  panelStatus: varchar("panel_status", { length: 20 }).default("idle"),
  panelError: text("panel_error"),
  storyboardPrompt: text("storyboard_prompt"),
  storyboardImageUrl: text("storyboard_image_url"),
  storyboardStatus: varchar("storyboard_status", { length: 20 }).default("idle"),
  storyboardError: text("storyboard_error"),
  storyboardPrompt2: text("storyboard_prompt_2"),
  storyboardImageUrl2: text("storyboard_image_url_2"),
  storyboardStatus2: varchar("storyboard_status_2", { length: 20 }).default("idle"),
  storyboardError2: text("storyboard_error_2"),
  storyboardPrompt3: text("storyboard_prompt_3"),
  storyboardImageUrl3: text("storyboard_image_url_3"),
  storyboardStatus3: varchar("storyboard_status_3", { length: 20 }).default("idle"),
  storyboardError3: text("storyboard_error_3"),
  // KIE task ids for in-flight storyboard grid renders (one per slot).
  // Persisted at createTask time so a server restart can resume polling the
  // existing task instead of failing the slot; cleared on terminal states.
  storyboardTaskId: text("storyboard_task_id"),
  storyboardTaskId2: text("storyboard_task_id_2"),
  storyboardTaskId3: text("storyboard_task_id_3"),
  voiceOverUrl: text("voice_over_url"),
  voiceOverStatus: varchar("voice_over_status", { length: 20 }).default("idle"),
  voiceOverError: text("voice_over_error"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("claymation_scripts_campaign_idx").on(table.claymationCampaignId),
  index("claymation_scripts_brand_idx").on(table.brandId),
]);

// Claymation concept plans — the per-concept brief produced by the Concept
// Generator, which is the FIRST production-pipeline step (before script
// generation). Mirrors ugc_script_plans exactly; only the FK table differs so
// Claymation data stays isolated from UGC. Re-added after being dropped when the
// flow briefly went script-first.
export const claymationScriptPlans = pgTable("claymation_script_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  claymationCampaignId: varchar("claymation_campaign_id").notNull().references(() => claymationCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  scriptNumber: integer("script_number").notNull(),
  title: text("title").notNull(),
  conceptDescription: text("concept_description"),
  hookStyle: text("hook_style"),
  storyType: text("story_type"),
  primaryEmotion: text("primary_emotion"),
  primaryObjectionOrDesire: text("primary_objection_or_desire"),
  setting: text("setting"),
  keyDistinction: text("key_distinction"),
  // Full Concept Generator JSON object: concept_title / concept_summary plus the
  // expanded planning fields (story_arc, opening_hook, storytelling_spine, etc.).
  // Stored verbatim and passed downstream to the Script Planner; the flat columns
  // above are kept for back-compat display.
  conceptJson: jsonb("concept_json"),
  avatarId: varchar("avatar_id"),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("claymation_script_plans_campaign_idx").on(table.claymationCampaignId),
]);

export const claymationAvatars = pgTable("claymation_avatars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  claymationCampaignId: varchar("claymation_campaign_id").notNull().references(() => claymationCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  imageUrl: varchar("image_url").notNull(),
  resolvedPrompt: text("resolved_prompt"),
  status: varchar("status", { length: 20 }).default("generated").notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("claymation_avatars_campaign_idx").on(table.claymationCampaignId),
]);

export const insertClaymationCampaignSchema = createInsertSchema(claymationCampaigns).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertClaymationScriptSchema = createInsertSchema(claymationScripts).omit({ id: true, createdAt: true, updatedAt: true });
export const insertClaymationScriptPlanSchema = createInsertSchema(claymationScriptPlans).omit({ id: true, createdAt: true });
export const insertClaymationAvatarSchema = createInsertSchema(claymationAvatars).omit({ id: true, createdAt: true });

export type ClaymationCampaign = typeof claymationCampaigns.$inferSelect;
export type InsertClaymationCampaign = z.infer<typeof insertClaymationCampaignSchema>;
export type ClaymationScript = typeof claymationScripts.$inferSelect;
export type InsertClaymationScript = z.infer<typeof insertClaymationScriptSchema>;
export type ClaymationScriptPlan = typeof claymationScriptPlans.$inferSelect;
export type InsertClaymationScriptPlan = z.infer<typeof insertClaymationScriptPlanSchema>;
export type ClaymationAvatar = typeof claymationAvatars.$inferSelect;
export type InsertClaymationAvatar = z.infer<typeof insertClaymationAvatarSchema>;

export const claymationRenders = pgTable("claymation_renders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  claymationScriptId: varchar("claymation_script_id").notNull().references(() => claymationScripts.id, { onDelete: "cascade" }),
  claymationCampaignId: varchar("claymation_campaign_id").notNull().references(() => claymationCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("queued"),
  progress: integer("progress").default(0),
  outputUrl: text("output_url"),
  error: text("error"),
  durationSec: integer("duration_sec"),
  sceneCount: integer("scene_count"),
  captionedVideoUrl: text("captioned_video_url"),
  captionedVideoStatus: varchar("captioned_video_status", { length: 20 }).default("idle"),
  captionedVideoError: text("captioned_video_error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("claymation_renders_script_idx").on(table.claymationScriptId),
  index("claymation_renders_campaign_idx").on(table.claymationCampaignId),
]);

export const insertClaymationRenderSchema = createInsertSchema(claymationRenders).omit({ id: true, createdAt: true, updatedAt: true });
export type ClaymationRender = typeof claymationRenders.$inferSelect;
export type InsertClaymationRender = z.infer<typeof insertClaymationRenderSchema>;

export const claymationCharacterVoices = pgTable("claymation_character_voices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // "persona_character" | "claymation_avatar" | "persona"
  ownerType: varchar("owner_type", { length: 30 }).notNull(),
  ownerId: varchar("owner_id").notNull(),
  brandId: varchar("brand_id").notNull(),
  elevenlabsVoiceId: varchar("elevenlabs_voice_id", { length: 100 }),
  voiceName: varchar("voice_name", { length: 255 }),
  voiceDescription: text("voice_description"),
  sourceImageUrl: text("source_image_url"),
  // "generating" | "ready" | "failed"
  status: varchar("status", { length: 20 }).notNull().default("generating"),
  error: text("error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("claymation_character_voices_owner_uniq").on(table.ownerType, table.ownerId),
  index("claymation_character_voices_brand_idx").on(table.brandId),
]);

export const insertClaymationCharacterVoiceSchema = createInsertSchema(claymationCharacterVoices).omit({ id: true, createdAt: true, updatedAt: true });
export type ClaymationCharacterVoice = typeof claymationCharacterVoices.$inferSelect;
export type InsertClaymationCharacterVoice = z.infer<typeof insertClaymationCharacterVoiceSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Video Express Max — a master-gated multi-style video pipeline that mirrors the
// Claymation family end-to-end. Two differences from Claymation: (1) campaigns
// carry a `videoStyle` selector (PersonaOS Visual Style Library, data-driven via
// shared/video-express-styles.ts), and (2) a NEW "Creative Strategist" step runs
// FIRST (before Concept) and
// persists its brief + status on the campaign (creativeStrategy / *Status /
// *Error). Tables intentionally isolated from claymation_* so the two pipelines
// never share rows. All Sim calls use dedicated /api/video-express-* URLs.
// ─────────────────────────────────────────────────────────────────────────────
export const videoExpressCampaigns = pgTable("video_express_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  name: varchar("name", { length: 255 }).notNull(),
  personaId: varchar("persona_id").references(() => personas.id, { onDelete: "set null" }),
  productId: varchar("product_id").references(() => products.id),
  // A Video Express campaign promotes EITHER a product OR an offer (mirrors the
  // narrative layer, where narrative_angles already carries offering_id).
  // `promotedItemType` discriminates; `offeringId` is set when it is "offering"
  // (in which case productId stays null). Legacy rows default to "product".
  promotedItemType: varchar("promoted_item_type", { length: 20 }).notNull().default("product"),
  offeringId: varchar("offering_id").references(() => offers.id),
  narrativeId: varchar("narrative_id").references(() => narratives.id),
  narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
  adUnitTypeId: varchar("ad_unit_type_id").references(() => adUnitTypes.id),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  concept: text("concept"),
  targetAudience: text("target_audience"),
  // Selected video style value (PersonaOS Visual Style Library). Resolved to its
  // full guidance via shared/video-express-styles.ts; defaults to "claymation".
  videoStyle: varchar("video_style", { length: 100 }).default("claymation"),
  // Selected story category (PersonaOS Story Category Library) — the narrative
  // storytelling method. Sent to the Creative Strategist step as story_category.
  storyCategory: varchar("story_category", { length: 120 }),
  // Creative Strategist brief (the NEW pipeline step that runs before Concept).
  // The full Sim brief object is stored verbatim and fed into the Concept step.
  creativeStrategy: jsonb("creative_strategy"),
  creativeStrategyStatus: varchar("creative_strategy_status", { length: 20 }).default("idle"),
  creativeStrategyError: text("creative_strategy_error"),
  // Concept Planner failures (the step between Creative Strategy and Scripts).
  // Concept plans have no per-row status — the plans either exist or they
  // don't — so this campaign-level field is where a failed/partial concept
  // generation records its visible error (cleared when generation starts).
  conceptError: text("concept_error"),
  duration: varchar("duration", { length: 50 }),
  videoModel: varchar("video_model", { length: 50 }).default("seedance-2.0"),
  secondsPerScene: integer("seconds_per_scene").default(4),
  videoCount: integer("video_count").default(1),
  avatarCount: integer("avatar_count").default(1),
  campaignMode: varchar("campaign_mode", { length: 50 }).default("oneshot"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  // Autopilot (Ads-Express-style end-to-end run kicked off from the create page).
  // A server-side orchestrator drives all 9 pipeline steps to completion; this
  // status survives navigation/refresh/server restart and a notification fires
  // on completion. A run left "running" across a server restart is marked
  // "interrupted" on boot (NEVER silently relaunched) — the user resumes it
  // explicitly via POST .../resume-autopilot. Values: queued | running |
  // completed | partial | failed | cancelled | interrupted (null = never
  // auto-run).
  autopilotStatus: varchar("autopilot_status", { length: 20 }),
  autopilotStartedAt: timestamp("autopilot_started_at"),
  autopilotCompletedAt: timestamp("autopilot_completed_at"),
  autopilotError: text("autopilot_error"),
  notes: jsonb("notes"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  index("video_express_campaigns_brand_idx").on(table.brandId),
]);

export const videoExpressScripts = pgTable("video_express_scripts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  videoExpressCampaignId: varchar("video_express_campaign_id").notNull().references(() => videoExpressCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  avatarId: varchar("avatar_id"),
  title: varchar("title", { length: 255 }),
  hook: text("hook"),
  body: text("body"),
  cta: text("cta"),
  fullScript: text("full_script"),
  talkingPoints: jsonb("talking_points"),
  visualDirections: jsonb("visual_directions"),
  thumbnailUrl: text("thumbnail_url"),
  videoUrl: text("video_url"),
  scenePrompts: jsonb("scene_prompts"),
  scenePromptStatus: varchar("scene_prompt_status", { length: 20 }).default("idle"),
  scenePromptError: text("scene_prompt_error"),
  sceneVideos: jsonb("scene_videos"),
  panelImages: jsonb("panel_images"),
  panelStatus: varchar("panel_status", { length: 20 }).default("idle"),
  panelError: text("panel_error"),
  storyboardPrompt: text("storyboard_prompt"),
  storyboardImageUrl: text("storyboard_image_url"),
  storyboardStatus: varchar("storyboard_status", { length: 20 }).default("idle"),
  storyboardError: text("storyboard_error"),
  storyboardPrompt2: text("storyboard_prompt_2"),
  storyboardImageUrl2: text("storyboard_image_url_2"),
  storyboardStatus2: varchar("storyboard_status_2", { length: 20 }).default("idle"),
  storyboardError2: text("storyboard_error_2"),
  storyboardPrompt3: text("storyboard_prompt_3"),
  storyboardImageUrl3: text("storyboard_image_url_3"),
  storyboardStatus3: varchar("storyboard_status_3", { length: 20 }).default("idle"),
  storyboardError3: text("storyboard_error_3"),
  voiceOverUrl: text("voice_over_url"),
  voiceOverStatus: varchar("voice_over_status", { length: 20 }).default("idle"),
  voiceOverError: text("voice_over_error"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("video_express_scripts_campaign_idx").on(table.videoExpressCampaignId),
  index("video_express_scripts_brand_idx").on(table.brandId),
]);

export const videoExpressScriptPlans = pgTable("video_express_script_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  videoExpressCampaignId: varchar("video_express_campaign_id").notNull().references(() => videoExpressCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  scriptNumber: integer("script_number").notNull(),
  title: text("title").notNull(),
  conceptDescription: text("concept_description"),
  hookStyle: text("hook_style"),
  storyType: text("story_type"),
  primaryEmotion: text("primary_emotion"),
  primaryObjectionOrDesire: text("primary_objection_or_desire"),
  setting: text("setting"),
  keyDistinction: text("key_distinction"),
  conceptJson: jsonb("concept_json"),
  avatarId: varchar("avatar_id"),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("video_express_script_plans_campaign_idx").on(table.videoExpressCampaignId),
]);

export const videoExpressAvatars = pgTable("video_express_avatars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  videoExpressCampaignId: varchar("video_express_campaign_id").notNull().references(() => videoExpressCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  imageUrl: varchar("image_url").notNull(),
  resolvedPrompt: text("resolved_prompt"),
  status: varchar("status", { length: 20 }).default("generated").notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("video_express_avatars_campaign_idx").on(table.videoExpressCampaignId),
]);

// Global, master-managed library of Video Express Max visual styles. Mirrors
// the shape of the hard-coded VIDEO_EXPRESS_STYLES seed array in
// shared/video-express-styles.ts (the styleDescription + visualStyle columns
// together form the verbatim "Visual Style" brief). `value` is the stable slug
// persisted on videoExpressCampaigns.videoStyle, so it is unique. Archivable
// like storyCategories so deleting a style never breaks older campaigns.
export const videoExpressStyles = pgTable("video_express_styles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  value: text("value").notNull().unique(),
  label: text("label").notNull(),
  description: text("description"),
  styleDescription: text("style_description"),
  visualStyle: text("visual_style"),
  scriptStyle: text("script_style"),
  videoPromptSuffix: text("video_prompt_suffix"),
  maleVoiceId: text("male_voice_id"),
  femaleVoiceId: text("female_voice_id"),
  // Optional 500×500 photo shown for this style in the create-page picker.
  imageUrl: text("image_url"),
  sortOrder: integer("sort_order").default(0),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by"),
  lastEditedBy: varchar("last_edited_by"),
  archivedAt: timestamp("archived_at"),
});

export const insertVideoExpressStyleSchema = createInsertSchema(videoExpressStyles).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export type InsertVideoExpressStyle = z.infer<typeof insertVideoExpressStyleSchema>;
export type VideoExpressStyleRecord = typeof videoExpressStyles.$inferSelect;

export const insertVideoExpressCampaignSchema = createInsertSchema(videoExpressCampaigns).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true, autopilotStatus: true, autopilotStartedAt: true, autopilotCompletedAt: true, autopilotError: true });
export const insertVideoExpressScriptSchema = createInsertSchema(videoExpressScripts).omit({ id: true, createdAt: true, updatedAt: true });
export const insertVideoExpressScriptPlanSchema = createInsertSchema(videoExpressScriptPlans).omit({ id: true, createdAt: true });
export const insertVideoExpressAvatarSchema = createInsertSchema(videoExpressAvatars).omit({ id: true, createdAt: true });

export type VideoExpressCampaign = typeof videoExpressCampaigns.$inferSelect;
export type InsertVideoExpressCampaign = z.infer<typeof insertVideoExpressCampaignSchema>;
export type VideoExpressScript = typeof videoExpressScripts.$inferSelect;
export type InsertVideoExpressScript = z.infer<typeof insertVideoExpressScriptSchema>;
export type VideoExpressScriptPlan = typeof videoExpressScriptPlans.$inferSelect;
export type InsertVideoExpressScriptPlan = z.infer<typeof insertVideoExpressScriptPlanSchema>;
export type VideoExpressAvatar = typeof videoExpressAvatars.$inferSelect;
export type InsertVideoExpressAvatar = z.infer<typeof insertVideoExpressAvatarSchema>;

export const videoExpressRenders = pgTable("video_express_renders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  videoExpressScriptId: varchar("video_express_script_id").notNull().references(() => videoExpressScripts.id, { onDelete: "cascade" }),
  videoExpressCampaignId: varchar("video_express_campaign_id").notNull().references(() => videoExpressCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("queued"),
  progress: integer("progress").default(0),
  outputUrl: text("output_url"),
  error: text("error"),
  durationSec: integer("duration_sec"),
  sceneCount: integer("scene_count"),
  captionedVideoUrl: text("captioned_video_url"),
  captionedVideoStatus: varchar("captioned_video_status", { length: 20 }).default("idle"),
  captionedVideoError: text("captioned_video_error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("video_express_renders_script_idx").on(table.videoExpressScriptId),
  index("video_express_renders_campaign_idx").on(table.videoExpressCampaignId),
]);

export const insertVideoExpressRenderSchema = createInsertSchema(videoExpressRenders).omit({ id: true, createdAt: true, updatedAt: true });
export type VideoExpressRender = typeof videoExpressRenders.$inferSelect;
export type InsertVideoExpressRender = z.infer<typeof insertVideoExpressRenderSchema>;

export const videoExpressCharacterVoices = pgTable("video_express_character_voices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ownerType: varchar("owner_type", { length: 30 }).notNull(),
  ownerId: varchar("owner_id").notNull(),
  brandId: varchar("brand_id").notNull(),
  elevenlabsVoiceId: varchar("elevenlabs_voice_id", { length: 100 }),
  voiceName: varchar("voice_name", { length: 255 }),
  voiceDescription: text("voice_description"),
  sourceImageUrl: text("source_image_url"),
  status: varchar("status", { length: 20 }).notNull().default("generating"),
  error: text("error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("video_express_character_voices_owner_uniq").on(table.ownerType, table.ownerId),
  index("video_express_character_voices_brand_idx").on(table.brandId),
]);

export const insertVideoExpressCharacterVoiceSchema = createInsertSchema(videoExpressCharacterVoices).omit({ id: true, createdAt: true, updatedAt: true });
export type VideoExpressCharacterVoice = typeof videoExpressCharacterVoices.$inferSelect;
export type InsertVideoExpressCharacterVoice = z.infer<typeof insertVideoExpressCharacterVoiceSchema>;

// ── UGC Express ─────────────────────────────────────────────────────────────
// A deliberately lean UGC video flow: pick ONE product, ADD MULTIPLE personas,
// and generate a separate 15s / 9:16 audio-on UGC review video per persona. One
// Sim workflow writes a Seedance script per persona, then a single KIE Seedance
// clip renders it — NO storyboard / beat-map / voiceover / caption pipeline.
// Global library of UGC Express video styles (mirrors video_express_styles'
// archivable pattern). A style is deliberately tiny: a name, a picker picture
// (display-only — never used as a generation reference), and a description
// that is passed to the Sim script workflow as `selected_video_style`.
// `value` is a stable internal slug (auto-derived from the name); the seeded
// default "hyperrealistic-ugc" style is the no-style-change baseline.
export const ugcExpressStyles = pgTable("ugc_express_styles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  value: text("value").notNull().unique(),
  name: text("name").notNull(),
  imageUrl: text("image_url"),
  // Short subtitle shown under the style name in the picker (display-only).
  // NULL on legacy rows — the picker falls back to the long description.
  shortDescription: text("short_description"),
  description: text("description"),
  sortOrder: integer("sort_order").default(0),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by"),
  lastEditedBy: varchar("last_edited_by"),
  archivedAt: timestamp("archived_at"),
});
export const insertUgcExpressStyleSchema = createInsertSchema(ugcExpressStyles).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export type InsertUgcExpressStyle = z.infer<typeof insertUgcExpressStyleSchema>;
export type UgcExpressStyleRecord = typeof ugcExpressStyles.$inferSelect;

// The seeded default style's stable slug. Runs generated with this style keep
// styleDescription NULL so the worker's Sim payload stays byte-identical to
// the pre-styles baseline.
export const UGC_EXPRESS_DEFAULT_STYLE_VALUE = "hyperrealistic-ugc";

export const ugcExpressRuns = pgTable("ugc_express_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  // Authoritative pipeline snapshot. Existing canonical runs are 15 seconds;
  // migrated UGC Express 2.0 runs are 30 seconds. Approval, retry, recovery,
  // billing, and history all resolve the pipeline from this persisted value.
  durationSeconds: integer("duration_seconds").notNull().default(15),
  // A run promotes EITHER a product OR an offer (mirrors the Ads Express
  // pattern). `promotedItemType` discriminates; exactly one of productId /
  // offerId is set. Legacy rows default to "product" so the product-only flow
  // keeps working unchanged. `productId` is now nullable (offer runs leave it
  // null).
  promotedItemType: varchar("promoted_item_type", { length: 20 }).notNull().default("product"),
  productId: varchar("product_id").references(() => products.id),
  offerId: varchar("offer_id").references(() => offers.id),
  name: varchar("name", { length: 255 }),
  // Selected video style, snapshotted at submit so mid-run style edits never
  // change resume/retry behavior. styleDescription is deliberately left NULL
  // when the default "Hyperrealistic UGC" style is picked — the worker only
  // sends `selected_video_style` (and the prompt's style section) when this is
  // a non-empty string, keeping the default path byte-identical to the
  // pre-styles baseline. Legacy rows (all NULL) behave as default.
  styleId: varchar("style_id").references(() => ugcExpressStyles.id),
  styleName: text("style_name"),
  styleDescription: text("style_description"),
  // Selected product variant, snapshotted (full variant object) at submit so
  // mid-run product/variant edits never change in-flight generations (mirrors
  // ugcCampaigns.selectedVariantSnapshot). NULL = "no specific variant" /
  // offer runs / legacy rows → product-level behavior unchanged.
  selectedVariantSnapshot: jsonb("selected_variant_snapshot"),
  // Onboarding-only intake reference images: the resolved AdReference[]
  // snapshot (durable R2 URLs) copied from the onboarding run's kickoff
  // snapshot at UGC target creation. Stamped EXCLUSIVELY by the onboarding
  // orchestrator — the normal UI create route never sets it. The worker
  // appends these URLs to the Seedance reference list AFTER the product
  // image. NULL/absent (all UI runs, onboarding runs without references,
  // legacy rows) keeps the render payload byte-identical to today.
  referenceImagesJson: jsonb("reference_images_json"),
  status: varchar("status", { length: 50 }).notNull().default("active"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("ugc_express_runs_brand_idx").on(table.brandId),
  check("ugc_express_runs_duration_seconds_check", sql`${table.durationSeconds} in (15, 30)`),
]);

// Per-brand "Pronunciation Bank": persistent, structured pronunciation fixes
// (word → spoken) that are AUTOMATICALLY applied to the spoken dialogue of every
// UGC Express generation for the brand. Replaces the old one-time freeform
// textarea — the user manages the bank once and never retypes per run.
export const ugcExpressPronunciations = pgTable("ugc_express_pronunciations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  word: varchar("word", { length: 255 }).notNull(),
  spoken: varchar("spoken", { length: 255 }).notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("ugc_express_pronunciations_brand_idx").on(table.brandId),
]);

// One row per (run × persona). status: pending (writing the Seedance script) →
// awaiting_approval (script approval gate — the user reviews/edits the spoken
// script before any KIE spend) → rendering (KIE Seedance clip) → ready | failed.
// kieTaskId is persisted BEFORE polling so a resumed worker re-polls the
// in-flight task instead of re-submitting (avoids a double charge). videoUrl is
// the durable R2-mirrored URL; the raw (expiring) provider URL is kept in
// providerVideoUrl as a fallback.
//
// Script approval gate fields:
//   fullPrompt      — the raw Sim output WITH the script formatting still in
//                     place ([SPOKEN SCRIPT START/END] markers, a trailing
//                     SCRIPT heading, or a tolerated variant — never shown to
//                     the user). On a script-format failure it holds the raw
//                     unparseable Sim output so the failure is diagnosable.
//   spokenScript    — the extracted (and possibly user-edited) spoken script;
//                     the ONLY prompt part surfaced in the UI.
//   scriptApprovedAt — set once, when the user approves. simScript remains the
//                     FINAL merged prompt actually sent to KIE (markers
//                     stripped, edited script spliced in), so retry/resume
//                     reuse the approved prompt without re-approval.
export const ugcExpressVideos = pgTable("ugc_express_videos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => ugcExpressRuns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  personaId: varchar("persona_id").notNull().references(() => personas.id),
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  simScript: text("sim_script"),
  fullPrompt: text("full_prompt"),
  spokenScript: text("spoken_script"),
  scriptApprovedAt: timestamp("script_approved_at"),
  kieTaskId: varchar("kie_task_id", { length: 120 }),
  providerVideoUrl: text("provider_video_url"),
  videoUrl: text("video_url"),
  error: text("error"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("ugc_express_videos_run_idx").on(table.runId),
  index("ugc_express_videos_brand_idx").on(table.brandId),
]);

export const insertUgcExpressRunSchema = createInsertSchema(ugcExpressRuns).omit({ id: true, createdAt: true, updatedAt: true });
export const insertUgcExpressVideoSchema = createInsertSchema(ugcExpressVideos).omit({ id: true, createdAt: true, updatedAt: true });
export const insertUgcExpressPronunciationSchema = createInsertSchema(ugcExpressPronunciations).omit({ id: true, createdAt: true });
export type UgcExpressRun = typeof ugcExpressRuns.$inferSelect;
export type InsertUgcExpressRun = z.infer<typeof insertUgcExpressRunSchema>;
export type UgcExpressVideo = typeof ugcExpressVideos.$inferSelect;
export type InsertUgcExpressVideo = z.infer<typeof insertUgcExpressVideoSchema>;
export type UgcExpressPronunciation = typeof ugcExpressPronunciations.$inferSelect;
export type InsertUgcExpressPronunciation = z.infer<typeof insertUgcExpressPronunciationSchema>;

export const brollExpressRuns = pgTable("broll_express_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  personaId: varchar("persona_id").notNull().references(() => personas.id),
  // A run promotes EITHER a product OR an offer (mirrors the ugcExpressRuns
  // pattern). `promotedItemType` discriminates; exactly one of productId /
  // offerId is set. Legacy rows default to "product" so the product-only flow
  // is unchanged.
  promotedItemType: varchar("promoted_item_type", { length: 20 }).notNull().default("product"),
  productId: varchar("product_id").references(() => products.id),
  offerId: varchar("offer_id").references(() => offers.id),
  // The narrative angle driving the script. A custom concept is transformed
  // into a saved angle at create time (Ads Express pattern), so this is always
  // set for new runs; the name/summary snapshots keep resume/retry stable even
  // if the angle is later edited or archived.
  narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
  angleName: varchar("angle_name", { length: 255 }),
  angleSummary: text("angle_summary"),
  name: varchar("name", { length: 255 }),
  status: varchar("status", { length: 50 }).notNull().default("active"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("broll_express_runs_brand_idx").on(table.brandId),
]);
export const slideshowExpressSettings = pgTable("slideshow_express_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 50 }).notNull().unique(),
  // Creative Strategist Agent (Task #1550) — runs automatically BEFORE the
  // Concept Agent, once per batch, with NO approval gate. Its creative-strategy
  // output is forwarded to the Concept Agent as an additional input. This slot
  // re-uses the column kept from the old removed Strategy + Format agent (the
  // Copy agent absorbed that role); master-editable, seeded on boot.
  strategyUrl: text("strategy_url"),
  // Concept Agent — the first pipeline agent. Writes ONE creative concept for
  // the run (each run in a 5-run batch gets its own distinct concept), which
  // the user reviews/edits/approves before the Copy Agent runs. Master-editable
  // like the other URLs; seeded on boot.
  conceptUrl: text("concept_url"),
  copyUrl: text("copy_url"),
  imagePromptUrl: text("image_prompt_url"),
  captionUrl: text("caption_url"),
  // One-off "Random concept" generator on the create form (not part of the run
  // pipeline). Master-editable like the other URLs; seeded on boot.
  randomConceptUrl: text("random_concept_url"),
  // Storyboard Agent (Task #1566) — runs AFTER the copy gate is approved,
  // writing a storyboard image prompt from the approved concept + the final
  // (possibly edited) slideshow text. The rendered storyboard parks the run at
  // its own approval gate before any slide image prompts/renders happen.
  // Master-editable like the other URLs; seeded on boot.
  storyboardUrl: text("storyboard_url"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// One row per slideshow run. Promotes EITHER a product OR an offer (mirrors the
// UGC/Ads Express pattern). Holds the raw JSON payloads returned by each of the
// four agents plus the final parsed caption / hashtags / pinned comment. The
// status column drives the step-by-step UI:
//   strategy → copy → image_prompts → rendering → packaging → ready | failed
export const slideshowExpressRuns = pgTable("slideshow_express_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  promotedItemType: varchar("promoted_item_type", { length: 20 }).notNull().default("product"),
  productId: varchar("product_id").references(() => products.id),
  offerId: varchar("offer_id").references(() => offers.id),
  // OPTIONAL target persona for the batch's concepts (Task #1479). Reused from
  // the legacy persona-character era (that render step stays removed — this is
  // concept-stage steering only, folded into the existing selected_video_style
  // channel). NULL = untargeted (byte-identical Sim payloads to today).
  personaId: varchar("persona_id").references(() => personas.id),
  // DEPRECATED (Task #1500) — the narrative-angle input was removed from
  // Slideshow Express. New runs never write these columns; old rows may still
  // carry an angleId/angleSnapshot, which the pipeline now ignores. Columns
  // retained per repo convention for removed features (no destructive
  // migration).
  angleId: varchar("angle_id").references(() => narrativeAngles.id),
  angleSnapshot: jsonb("angle_snapshot"),
  // Optional sale/discount attached to the batch (Task #1530). The full sale
  // row is SNAPSHOTTED at submit time (mirrors Ads Express) so mid-run edits
  // to the promotion never change in-flight generations; the snapshot drives
  // the last-slide CTA stanza. NULL = no sale (byte-identical Sim payloads).
  salesDiscountId: varchar("sales_discount_id").references(() => salesDiscounts.id),
  salesDiscountSnapshot: jsonb("sales_discount_snapshot"),
  // Selected narrative style id from the Slideshow Express style bank
  // (shared/slideshow-express-styles.ts). One run is fanned out per selected
  // style; its structured fields are threaded into all four Sim agents. Nullable
  // for legacy rows created before the style bank existed.
  styleId: varchar("style_id", { length: 60 }),
  // Optional user-provided creative concept. When set, the generated slideshow
  // follows this concept instead of a purely AI-invented angle. Snapshotted at
  // run creation so restart/resume renders consistently. Null = auto concept
  // (byte-identical Sim payloads to the pre-feature behavior).
  customConcept: text("custom_concept"),
  // Optional user-provided CTA text for the LAST slide (e.g. "Shop now — link
  // in bio"). When set, the final slide's on-screen text is guaranteed to use
  // it. Null = agent-chosen CTA (unchanged behavior).
  ctaText: text("cta_text"),
  name: varchar("name", { length: 255 }),
  status: varchar("status", { length: 50 }).notNull().default("strategy"),
  // Copy review gate: after the Copy agent writes the concept + slide text the
  // run pauses at status "awaiting_copy_review" until the user approves. This
  // marker records whether the gate has been cleared so a resumed/re-kicked
  // worker knows to skip the pause and continue on to the image-prompt step.
  // Default false = a fresh run always pauses for review before any image
  // credits are spent.
  copyApproved: boolean("copy_approved").notNull().default(false),
  // Immutable copy package approved for production. Every downstream slide,
  // package, save and export is fenced to this monotonically increasing
  // version and digest; copyJson remains the editable/source payload.
  approvedCopyVersion: integer("approved_copy_version").notNull().default(0),
  // Advances for every completed revision, including presentation-only edits
  // whose approved copy version intentionally stays unchanged.
  completedRevisionVersion: integer("completed_revision_version").notNull().default(0),
  approvedCopyDigest: varchar("approved_copy_digest", { length: 64 }),
  approvedCopyJson: jsonb("approved_copy_json"),
  approvedCopyApprovedAt: timestamp("approved_copy_approved_at"),
  approvedCopyInvalidatedAt: timestamp("approved_copy_invalidated_at"),
  copyValidationState: varchar("copy_validation_state", { length: 32 }).notNull().default("unapproved"),
  deliveryValidationState: varchar("delivery_validation_state", { length: 32 }).notNull().default("unverified"),
  deliveryVerifiedAt: timestamp("delivery_verified_at"),
  exportObjectUrl: text("export_object_url"),
  exportManifest: jsonb("export_manifest"),
  // Concept review gate (runs BEFORE the copy gate): after the Concept Agent
  // writes this run's creative concept the run pauses at status
  // "awaiting_concept_review" until the user approves (optionally editing the
  // concept text). Mirrors copyApproved semantics: default false = a fresh run
  // always pauses; a resumed/re-kicked worker never auto-advances an
  // unapproved gate. Existing rows are backfilled true by migration 0101 so
  // legacy runs never park at a gate that didn't exist when they were created.
  conceptApproved: boolean("concept_approved").notNull().default(false),
  // Raw Concept Agent payload (durable audit + resume reuse — a restart never
  // re-bills a concept that was already generated).
  conceptJson: jsonb("concept_json"),
  // The user-approved (possibly edited) concept text, stamped by the
  // approve-concept route. This — not conceptJson — is what the Copy Agent
  // receives. NULL for legacy runs (copy runs without a concept stanza).
  approvedConcept: text("approved_concept"),
  // Storyboard stage (Task #1566 — relocated from the Task #1560 concept-time
  // preview): after the COPY gate is approved, a dedicated Sim Storyboard
  // Agent writes `storyboardPrompt` from the approved concept + final edited
  // slideshow text, KIE renders it, and the run parks at
  // "awaiting_storyboard_review" until the user approves. The KIE task id is
  // persisted BEFORE polling so a restart resumes the existing task (never
  // re-submits / double-bills), and the finished image is mirrored to R2.
  // storyboardStatus: pending → generating → ready | failed. A failure parks
  // the run at the storyboard stage with a retry — never a silent skip.
  storyboardPrompt: text("storyboard_prompt"),
  storyboardStatus: varchar("storyboard_status", { length: 20 }),
  storyboardKieTaskId: varchar("storyboard_kie_task_id", { length: 120 }),
  storyboardImageUrl: text("storyboard_image_url"),
  storyboardError: text("storyboard_error"),
  // Storyboard review gate (Task #1566, runs AFTER the copy gate): default
  // false = a fresh run always pauses at "awaiting_storyboard_review" before
  // any slide image credits are spent. Existing rows past the copy gate are
  // backfilled true by migration 0109 so in-flight runs never park at a gate
  // that didn't exist when they were created.
  storyboardApproved: boolean("storyboard_approved").notNull().default(false),
  // 1-based position of this run within its 5-run batch. Threaded into the
  // Concept Agent as a "variation N of M" hint so parallel runs for the same
  // style produce DISTINCT concepts. NULL for legacy runs.
  variantIndex: integer("variant_index"),
  // Shared id stamped on every run created by ONE Generate click (Task #1633).
  // The page scopes its "current" view to the newest batch and lists older
  // batches in the past-runs history (prior runs are no longer deleted when a
  // new batch starts). NULL for legacy rows — they group as one legacy batch.
  batchId: varchar("batch_id", { length: 60 }),
  // Legacy: persona CHARACTER reference image from before the persona/character
  // step was removed. Kept only so old runs' R2 cleanup can find the file.
  characterImageUrl: text("character_image_url"),
  // Raw agent payloads (durable audit + resume reuse).
  strategyJson: jsonb("strategy_json"),
  copyJson: jsonb("copy_json"),
  // Multi-option copy: when the Copy Agent returns SEVERAL copy options
  // (concept + slides each), ALL parsed options are persisted here and the run
  // pauses at the review gate until the user picks exactly one. The chosen
  // option (index recorded below) is copied into copyJson as the canonical
  // package at approve time, so everything downstream (image prompts, overlay,
  // caption) reads only the selection. NULL for legacy/single-package runs.
  copyOptionsJson: jsonb("copy_options_json"),
  selectedCopyOption: integer("selected_copy_option"),
  imageGuidanceJson: jsonb("image_guidance_json"),
  packageJson: jsonb("package_json"),
  // Final parsed package (from agent 4) for easy read/export.
  caption: text("caption"),
  hashtags: text("hashtags").array(),
  pinnedComment: text("pinned_comment"),
  error: text("error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("slideshow_express_runs_brand_idx").on(table.brandId),
]);

// One row per slide within a run. Holds the slide's on-screen text (agent 2),
// the KIE image prompt + render guidance (agent 3), KIE task id (persisted
// BEFORE polling for restart-safe resume), the raw provider image URL and the
// durable R2-mirrored raw + composited (text-overlaid) image URLs.
// status: pending → rendering → overlaying → ready | failed
export const slideshowExpressSlides = pgTable("slideshow_express_slides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => slideshowExpressRuns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  sortOrder: integer("sort_order").default(0),
  // Stable within a run across retries/restarts (slide-01 .. slide-05).
  stableKey: varchar("stable_key", { length: 32 }),
  slideText: text("slide_text"),
  approvedCopyVersion: integer("approved_copy_version").notNull().default(0),
  approvedCopyDigest: varchar("approved_copy_digest", { length: 64 }),
  overlayValidationState: varchar("overlay_validation_state", { length: 32 }).notNull().default("unverified"),
  kiePrompt: text("kie_prompt"),
  renderGuidance: jsonb("render_guidance"),
  kieTaskId: varchar("kie_task_id", { length: 120 }),
  retryAttemptKey: varchar("retry_attempt_key", { length: 160 }),
  providerImageUrl: text("provider_image_url"),
  rawImageUrl: text("raw_image_url"),
  compositedImageUrl: text("composited_image_url"),
  // Per-slide user overrides for the text overlay (font, color, size,
  // alignment, vertical position) — see SlideOverlayOverrides in
  // shared/slideshow-overlay.ts. Null = pure visual-style defaults. Applied
  // whenever the slide is (re)composited, including worker retries.
  overlayJson: jsonb("overlay_json"),
  // Which image model produced the CURRENT raw image (Task #2807):
  // "kie-gpt-image-2" for the primary KIE path (all first-pass renders and
  // successful KIE retry legs) or "nano-banana-2" when a per-slide retry fell
  // back to the Fal-hosted alternate model. NULL for legacy rows rendered
  // before this column existed (all of those were KIE).
  renderModel: varchar("render_model", { length: 60 }),
  // Set when this slide has been saved into the My Ads library (points at the
  // generated_ads row created by the save). Used for idempotency (a slide is
  // saved at most once) and to render the "Saved" state in the UI. The saved
  // library ad owns its own R2 copy, so batch-clearing slideshow runs never
  // touches saved library images.
  savedGeneratedAdId: varchar("saved_generated_ad_id"),
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  error: text("error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("slideshow_express_slides_run_idx").on(table.runId),
  index("slideshow_express_slides_brand_idx").on(table.brandId),
]);

// Completed-run revisions are independent immutable attempts.  They stage
// outputs without touching the current run/slide pointers; only a transaction
// that sees every required outcome ready may promote the new revision.
export const slideshowExpressCompletedRevisions = pgTable("slideshow_express_completed_revisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => slideshowExpressRuns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  idempotencyKey: varchar("idempotency_key", { length: 160 }).notNull(),
  requestFingerprint: varchar("request_fingerprint", { length: 64 }).notNull(),
  baseCopyVersion: integer("base_copy_version").notNull(),
  baseRevisionVersion: integer("base_revision_version").notNull(),
  revisionVersion: integer("revision_version").notNull(),
  copyVersion: integer("copy_version").notNull(),
  copyDigest: varchar("copy_digest", { length: 64 }).notNull(),
  baseApprovedCopyJson: jsonb("base_approved_copy_json").notNull(),
  approvedCopyJson: jsonb("approved_copy_json").notNull(),
  targetKeys: jsonb("target_keys").notNull(),
  overrides: jsonb("overrides"),
  status: varchar("status", { length: 32 }).notNull().default("pending"),
  error: text("error"),
  promotedAt: timestamp("promoted_at"),
  savedCarouselSyncedAt: timestamp("saved_carousel_synced_at"),
  savedCarouselId: varchar("saved_carousel_id"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("slideshow_completed_revision_run_idempotency_unique").on(table.runId, table.idempotencyKey),
  index("slideshow_completed_revision_run_status_idx").on(table.runId, table.status),
]);

export const slideshowExpressCompletedRevisionSlides = pgTable("slideshow_express_completed_revision_slides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  revisionId: varchar("revision_id").notNull().references(() => slideshowExpressCompletedRevisions.id, { onDelete: "cascade" }),
  sourceSlideId: varchar("source_slide_id").notNull().references(() => slideshowExpressSlides.id, { onDelete: "restrict" }),
  stableKey: varchar("stable_key", { length: 32 }).notNull(),
  sortOrder: integer("sort_order").notNull(),
  priorText: text("prior_text").notNull(),
  priorOverlayJson: jsonb("prior_overlay_json"),
  text: text("text").notNull(),
  overlayJson: jsonb("overlay_json"),
  rawImageUrl: text("raw_image_url").notNull(),
  priorCompositedImageUrl: text("prior_composited_image_url").notNull(),
  compositedImageUrl: text("composited_image_url"),
  status: varchar("status", { length: 32 }).notNull().default("pending"),
  error: text("error"),
  claimToken: varchar("claim_token", { length: 80 }),
  claimExpiresAt: timestamp("claim_expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("slideshow_completed_revision_slide_unique").on(table.revisionId, table.sourceSlideId),
  index("slideshow_completed_revision_slide_status_idx").on(table.revisionId, table.status),
]);

export const slideshowExpressContentPlans = pgTable("slideshow_express_content_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  status: varchar("status", { length: 32 }).notNull().default("draft"),
  currentVersion: integer("current_version").notNull().default(1),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("slideshow_express_content_plans_brand_idx").on(table.brandId, table.updatedAt),
  index("slideshow_express_content_plans_status_idx").on(table.status, table.updatedAt),
]);
export const slideshowExpressStyles = pgTable("slideshow_express_styles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  value: text("value").notNull().unique(),
  label: text("label").notNull(),
  shortDescription: text("short_description"),
  definition: text("definition"),
  worksWithoutProduct: boolean("works_without_product").notNull().default(true),
  organicAngle: text("organic_angle"),
  productAngle: text("product_angle"),
  emotionalArc: text("emotional_arc"),
  bestDmBaits: text("best_dm_baits").array(),
  imageLogic: text("image_logic"),
  avoid: text("avoid").array(),
  // Single free-text style description — the SOURCE OF TRUTH for generation. One
  // message that explains the whole style; passed to all four Sim agents as the
  // style direction. The structured columns above are retained only to seed/
  // migrate this field (existing presets are combined into one readable message).
  styleDescription: text("style_description"),
  // Optional card preview photo shown for this style in the create-page picker.
  imageUrl: text("image_url"),
  // Per-style text-overlay settings (Manage Styles "Slideshow text" section):
  // font (bundled key or uploaded custom font), color, size, stroke, alignment
  // and vertical placement. Validated with slideshowTextSettingsSchema
  // (shared/slideshow-text-settings.ts). NULL = the compositor's hardcoded
  // defaults for this style, so untouched styles render byte-identical.
  textSettings: jsonb("text_settings"),
  sortOrder: integer("sort_order").default(0),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by"),
  lastEditedBy: varchar("last_edited_by"),
  archivedAt: timestamp("archived_at"),
});

export const slideshowExpressContentPlanGrants = pgTable("slideshow_express_content_plan_grants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  planVersionId: varchar("plan_version_id").notNull().references(() => slideshowExpressContentPlanVersions.id, { onDelete: "cascade" }),
  itemIds: jsonb("item_ids").notNull(),
  stages: jsonb("stages").notNull(),
  selectionCriteria: jsonb("selection_criteria"),
  illustrativeAlternative: boolean("illustrative_alternative").notNull().default(false),
  saveToLibrary: boolean("save_to_library").notNull().default(false),
  maxRetries: integer("max_retries").notNull().default(0),
  retriesUsed: integer("retries_used").notNull().default(0),
  maxRegenerations: integer("max_regenerations").notNull().default(0),
  regenerationsUsed: integer("regenerations_used").notNull().default(0),
  contentPolicyScope: jsonb("content_policy_scope").notNull().default([]),
  maxCredits: numeric("max_credits", { precision: 14, scale: 2 }),
  maxSpendUsd: numeric("max_spend_usd", { precision: 14, scale: 6 }),
  expiresAt: timestamp("expires_at").notNull(),
  status: varchar("status", { length: 24 }).notNull().default("active"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  revokedAt: timestamp("revoked_at"),
}, (table) => [
  index("slideshow_express_content_plan_grants_active_idx").on(table.planVersionId, table.status, table.expiresAt),
]);

export const slideshowExpressLifecycleEvents = pgTable("slideshow_express_lifecycle_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  runId: varchar("run_id").references(() => slideshowExpressRuns.id, { onDelete: "cascade" }),
  planVersionId: varchar("plan_version_id").references(() => slideshowExpressContentPlanVersions.id, { onDelete: "cascade" }),
  itemVersionId: varchar("item_version_id").references(() => slideshowExpressContentPlanItemVersions.id, { onDelete: "cascade" }),
  eventKey: varchar("event_key", { length: 240 }).notNull().unique(),
  type: varchar("type", { length: 80 }).notNull(),
  status: varchar("status", { length: 48 }).notNull(),
  nextAction: text("next_action"),
  workflowVersion: integer("workflow_version"),
  details: jsonb("details"),
  deliveryAttempts: integer("delivery_attempts").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("slideshow_express_lifecycle_events_brand_cursor_idx").on(table.brandId, table.createdAt, table.id),
  index("slideshow_express_lifecycle_events_run_idx").on(table.runId, table.createdAt),
  index("slideshow_express_lifecycle_events_item_idx").on(table.itemVersionId, table.createdAt),
]);
export const insertSlideshowExpressStyleSchema = createInsertSchema(slideshowExpressStyles).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export type InsertSlideshowExpressStyle = z.infer<typeof insertSlideshowExpressStyleSchema>;
export type SlideshowExpressStyleRecord = typeof slideshowExpressStyles.$inferSelect;

export const insertSlideshowExpressSettingsSchema = createInsertSchema(slideshowExpressSettings).omit({ id: true, updatedAt: true });
export const insertSlideshowExpressRunSchema = createInsertSchema(slideshowExpressRuns).omit({ id: true, createdAt: true, updatedAt: true });
export const insertSlideshowExpressSlideSchema = createInsertSchema(slideshowExpressSlides).omit({ id: true, createdAt: true, updatedAt: true });
export type SlideshowExpressSettings = typeof slideshowExpressSettings.$inferSelect;
export type InsertSlideshowExpressSettings = z.infer<typeof insertSlideshowExpressSettingsSchema>;
export type SlideshowExpressRun = typeof slideshowExpressRuns.$inferSelect;
export type InsertSlideshowExpressRun = z.infer<typeof insertSlideshowExpressRunSchema>;
export type SlideshowExpressSlide = typeof slideshowExpressSlides.$inferSelect;
export type InsertSlideshowExpressSlide = z.infer<typeof insertSlideshowExpressSlideSchema>;

// ----------------------------------------------------------------------------
// Meme Generator (Imgflip templates + single Sim workflow)
// ----------------------------------------------------------------------------

// Global settings row holding the SINGLE Sim workflow endpoint URL for the
// Meme Generator. Managed from the tool's in-app setup field (NOT an env var).
// A single row is kept, identified by a constant `key`. Generation is blocked
// until simUrl is saved.
export const memeGeneratorSettings = pgTable("meme_generator_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 50 }).notNull().unique(),
  simUrl: text("sim_url"),
  // ── Daily automation state (single 'global' row) ──────────────────────────
  // last_*_date columns are the atomic once-per-day claim markers (a NY-time
  // YYYY-MM-DD date key, claimed via one conditional UPDATE — same pattern as
  // the weekly digest, see .agents/memory/scheduled-digest-atomic-claim.md).
  // last_*_at / last_*_result are the master-visible "when did it last run and
  // how did it go" status (written by BOTH automatic and manual runs).
  lastRefreshDate: varchar("last_refresh_date", { length: 10 }),
  lastRefreshAt: timestamp("last_refresh_at"),
  lastRefreshResult: jsonb("last_refresh_result"),
  lastEnrichDate: varchar("last_enrich_date", { length: 10 }),
  lastEnrichAt: timestamp("last_enrich_at"),
  lastEnrichResult: jsonb("last_enrich_result"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// LLM-generated "how this meme works" context for one template. Generated
// ONCE (GPT-4o vision seeding), stored in meme_template_library, snapshotted
// onto runs, and attached to every Sim call — never regenerated per run.
export interface MemeTemplateContext {
  visual: string;        // what's happening visually in the template
  textPlacement: string; // where the text goes
  boxMeaning: string;    // what each text box means
  whyFunny: string;      // why the meme is funny / its comedic mechanic
  generality: string;    // generic vs. specific usage notes
}

// Persistent Imgflip template library ("Meme Settings"). One row per Imgflip
// template from the official get_memes list; enrichment columns hold the
// one-time GPT-4o vision answers. enriched = approved (selectable in the
// generator). Seeding is idempotent: enriched rows are skipped.
export const memeTemplateLibrary = pgTable("meme_template_library", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: varchar("template_id", { length: 60 }).notNull().unique(),
  name: varchar("name", { length: 255 }).notNull(),
  url: text("url").notNull(),
  width: integer("width"),
  height: integer("height"),
  boxCount: integer("box_count"),
  // Enrichment (the five questions). All five set = enriched/approved.
  visual: text("visual"),
  textPlacement: text("text_placement"),
  boxMeaning: text("box_meaning"),
  whyFunny: text("why_funny"),
  generality: text("generality"),
  enrichedAt: timestamp("enriched_at"),
  // ── Enrichment failure state (per template) ───────────────────────────────
  // Last enrichment failure for this row (cleared on success). imageDeadAt is
  // set when the template's Imgflip image is confirmed gone upstream (HTTP
  // 4xx on the pre-check) — dead rows are skipped by auto passes until a
  // re-check window elapses; manual passes always re-check.
  lastEnrichError: text("last_enrich_error"),
  lastEnrichErrorAt: timestamp("last_enrich_error_at"),
  imageDeadAt: timestamp("image_dead_at"),
  // ── Imgflip sort membership (daily import) ────────────────────────────────
  // 1-based rank within each Imgflip sort, null = not currently in that sort.
  // A template can rank in several sorts at once (one library row, shown under
  // each relevant tab). The daily refresh sets ranks for templates currently
  // in a sort and NULLs the rank for templates that dropped out — the row (and
  // its enrichment) is never deleted, so past runs stay intact.
  rankTopNew: integer("rank_top_new"),
  rankTop30: integer("rank_top_30_days"),
  rankTopAll: integer("rank_top_all_time"),
  lastSeenAt: timestamp("last_seen_at"),
  // ── Soft delete (master blocklist) ────────────────────────────────────────
  // Set when a master deletes the template from Meme Settings. The row is
  // KEPT (past runs snapshot their context, history stays intact) but the
  // template is hidden from the picker/settings lists, excluded from run
  // creation, skipped by the daily Imgflip import (blocklist) and by the
  // enrichment passes. An explicit manual import via "Search Imgflip"
  // restores it (clears the marker).
  deletedAt: timestamp("deleted_at"),
  // ── Curated "Recommended" list ────────────────────────────────────────────
  // Set when the designated curator adds this template to the shared
  // "Recommended" list surfaced (globally, same for everyone) in the Meme
  // Generator picker and Meme Settings. NULL = not recommended. Independent of
  // enrichment/soft-delete: clearing it removes the template from Recommended
  // WITHOUT deleting the underlying library row. Doubles as the ordering field
  // (curated order = ascending recommended_at). Only the single curator account
  // may set/clear it (server-enforced — see auth-predicates.ts).
  recommendedAt: timestamp("recommended_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// One Imgflip template snapshotted at generation time (from
// the enriched meme_template_library, falling back to the live catalog).
// `context` carries the stored enrichment so mid-run library edits never
// affect an in-flight run (and the worker never re-fetches it).
export interface MemeTemplateSnapshot {
  id: string;
  name: string;
  url: string;
  width: number;
  height: number;
  boxCount: number;
  context?: MemeTemplateContext | null;
}

// One row per generation run: Product/Offer × N selected Imgflip templates.
// ONE Sim call per run (billed once) adapts the product/offer into every
// selected template; the per-template outputs live in meme_run_results.
// status: generating → compositing → ready | failed
export const memeRuns = pgTable("meme_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  promotedItemType: varchar("promoted_item_type", { length: 20 }).notNull().default("product"),
  productId: varchar("product_id").references(() => products.id),
  offerId: varchar("offer_id").references(() => offers.id),
  name: varchar("name", { length: 255 }),
  status: varchar("status", { length: 50 }).notNull().default("generating"),
  // Snapshot of the selected Imgflip templates (MemeTemplateSnapshot[]) so
  // mid-run Imgflip list changes never affect an in-flight run.
  templatesJson: jsonb("templates_json"),
  // Vestigial run-level Sim audit columns. The current per-template worker
  // persists the Sim image prompt on each result row (memeRunResults.imagePrompt)
  // and resumes from that, so these are no longer written (retry nulls
  // simResponseJson). Kept nullable to avoid a destructive migration.
  simPayloadJson: jsonb("sim_payload_json"),
  simResponseJson: jsonb("sim_response_json"),
  error: text("error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // Composite covers the runs-list query (WHERE brand_id = ? ORDER BY
  // created_at DESC) and fully replaces the old single-column brand_id index.
  index("meme_runs_brand_created_idx").on(table.brandId, table.createdAt.desc()),
  index("meme_runs_status_idx").on(table.status),
  index("meme_runs_product_idx").on(table.productId),
  index("meme_runs_offer_idx").on(table.offerId),
]);

// One row per (run × selected template). The Sim workflow returns an
// image-generation prompt (imagePrompt); the Imgflip template is mirrored to R2
// (rawImageUrl) and used as the reference image for a KIE nano-banana-2 render,
// and the finished meme is mirrored to R2 (compositedImageUrl). Both R2 copies
// are ours (Imgflip URLs are third-party; KIE URLs expire).
// The overlay_text/overlay_position/caption columns are vestigial from the old
// text-overlay pipeline (kept nullable to avoid a destructive migration).
// status: pending → ready | failed
export const memeRunResults = pgTable("meme_run_results", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => memeRuns.id, { onDelete: "cascade" }),
  templateId: varchar("template_id", { length: 60 }).notNull(),
  templateName: varchar("template_name", { length: 255 }).notNull(),
  templateUrl: text("template_url").notNull(),
  templateWidth: integer("template_width"),
  templateHeight: integer("template_height"),
  boxCount: integer("box_count"),
  // User-chosen output aspect ratio (shared AD_ASPECT_RATIOS list), snapshotted
  // at run creation — one result row per (template × selected ratio). NULL on
  // legacy rows, where the worker falls back to the template-derived nearest
  // supported ratio (pickAspectRatio). Retry/edit re-renders read this back so
  // a result keeps its ratio for life.
  aspectRatio: varchar("aspect_ratio", { length: 10 }),
  // Durable R2 URL of the PRE-padding base render shared by all sibling ratio
  // rows of the same template (the meme is generated once per template; each
  // row only differs in post-generation aspect fitting). Never confuse with
  // compositedImageUrl, which is post-padding and unusable as a base. Cleared
  // on retry/regenerate.
  baseImageUrl: text("base_image_url"),
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  overlayText: text("overlay_text"),
  overlayPosition: varchar("overlay_position", { length: 20 }).default("top"),
  caption: text("caption"),
  // The Sim-generated image-generation prompt for this template. Also the
  // "already generated" marker on resume — reused (no re-bill) unless the run
  // is regenerated.
  imagePrompt: text("image_prompt"),
  // The reference image (R2 mirror of the Imgflip template) and the final KIE
  // meme image (stored in composited_image_url). kie_task_id persists the
  // in-flight render's provider id so a restart resumes it instead of
  // re-charging — a Fal request id when render_engine is NULL, a KIE task id
  // when render_engine = 'grok' (the automatic grok-imagine/image-to-image
  // fallback after a failed fresh nano-banana-2 render). While the Grok
  // fallback is in flight, `error` temporarily holds the nano-banana-2 error
  // so a restart can still compose the combined two-model failure message.
  kieTaskId: text("kie_task_id"),
  renderEngine: varchar("render_engine", { length: 20 }),
  rawImageUrl: text("raw_image_url"),
  compositedImageUrl: text("composited_image_url"),
  // Normally the generated_ads.id after this meme is saved into My Ads. During
  // the save operation it briefly holds a "meme-save:<timestamp>:<nonce>"
  // ownership claim so a concurrent result deletion preserves the shared R2
  // object. Drives persistent "Saved" state and endpoint idempotency. The saved
  // ad REUSES this result's R2 object (no re-copy), so run/result deletion must
  // keep the composited image while this is set. Cleared on run retry (the
  // regenerated image is a new creative, saveable again).
  savedGeneratedAdId: varchar("saved_generated_ad_id"),
  error: text("error"),
  // ── User-requested edit of a READY meme (Task: meme edit feature) ─────────
  // edit_status: NULL (no edit) → "editing" (in flight) → NULL (success) or
  // "failed" (edit failed; the previous image + prompt stay intact). While an
  // edit is in flight the row's `status` stays "ready" and compositedImageUrl
  // is NEVER cleared, so the run worker ignores it and the tile keeps showing
  // the current meme. The in-flight render reuses kie_task_id/render_engine
  // (safe: the run worker skips ready rows), while the render-chain's error
  // bookkeeping is redirected into edit_error so `error` is untouched.
  // edit_prompt holds the Sim-rewritten prompt BEFORE the render (the
  // "rewrite already done" resume marker — a restart never re-calls/re-bills
  // the rewriter); on success it is copied into image_prompt (so the next
  // edit builds on it) and cleared.
  editStatus: varchar("edit_status", { length: 20 }),
  editInstructions: text("edit_instructions"),
  editPrompt: text("edit_prompt"),
  editError: text("edit_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("meme_run_results_run_idx").on(table.runId),
]);

export const insertMemeTemplateLibrarySchema = createInsertSchema(memeTemplateLibrary).omit({ id: true, createdAt: true, updatedAt: true });
export type MemeTemplateLibraryEntry = typeof memeTemplateLibrary.$inferSelect;
export type InsertMemeTemplateLibraryEntry = z.infer<typeof insertMemeTemplateLibrarySchema>;

export const insertMemeGeneratorSettingsSchema = createInsertSchema(memeGeneratorSettings).omit({ id: true, updatedAt: true });
export const insertMemeRunSchema = createInsertSchema(memeRuns).omit({ id: true, createdAt: true, updatedAt: true });
export const insertMemeRunResultSchema = createInsertSchema(memeRunResults).omit({ id: true, createdAt: true, updatedAt: true });
export type MemeGeneratorSettings = typeof memeGeneratorSettings.$inferSelect;
export type InsertMemeGeneratorSettings = z.infer<typeof insertMemeGeneratorSettingsSchema>;
export type MemeRun = typeof memeRuns.$inferSelect;
export type InsertMemeRun = z.infer<typeof insertMemeRunSchema>;
export type MemeRunResult = typeof memeRunResults.$inferSelect;
export type InsertMemeRunResult = z.infer<typeof insertMemeRunResultSchema>;

export const landingPages = pgTable("landing_pages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  campaignId: varchar("campaign_id").references(() => campaigns.id),
  title: varchar("title", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 255 }).notNull(),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  productId: varchar("product_id").references(() => products.id),
  personaId: varchar("persona_id").references(() => personas.id),
  narrativeAngle: text("narrative_angle"),
  narrativeOpportunity: text("narrative_opportunity"),
  offerData: jsonb("offer_data"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by"),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  index("landing_pages_brand_idx").on(table.brandId),
  index("landing_pages_campaign_idx").on(table.campaignId),
]);

export const landingPageSections = pgTable("landing_page_sections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  landingPageId: varchar("landing_page_id").notNull().references(() => landingPages.id, { onDelete: "cascade" }),
  sectionType: varchar("section_type", { length: 100 }).notNull(),
  enabled: boolean("enabled").notNull().default(true),
  order: integer("order").notNull().default(0),
  variant: varchar("variant", { length: 100 }),
  overrides: jsonb("overrides"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("landing_page_sections_page_idx").on(table.landingPageId),
]);

export const landingPageTemplates = pgTable("landing_page_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Stable LP Express catalog identity. NULL is retained for legacy templates
  // authored directly in the older landing-page template editor.
  catalogKey: varchar("catalog_key", { length: 64 }).unique(),
  // Exact Magic Patterns editor that originated this Studio publication.
  // This is intentionally distinct from templateBrandVariants.mpEditorId,
  // which identifies a brand-specific fork/job.  Legacy publications remain
  // nullable and fail closed when an LP2 brand fork is requested.
  sourceMpEditorId: varchar("source_mp_editor_id", { length: 255 }),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  sections: jsonb("sections").notNull(),
  sectionFormulas: jsonb("section_formulas"),
  sectionHtml: jsonb("section_html"),
  isDefault: boolean("is_default").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const appNotifications = pgTable("app_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  type: varchar("type", { length: 50 }).notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  message: text("message"),
  link: text("link"),
  read: boolean("read").notNull().default(false),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Remote MCP credentials are deliberately opaque, user-scoped bearer tokens.
// Only a SHA-256 digest is persisted; the one-time plaintext token is never
// recoverable by Persona OS, support staff, or an MCP discovery response.
export const mcpConnections = pgTable("mcp_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  label: varchar("label", { length: 120 }).notNull().default("ChatGPT"),
  tokenHash: varchar("token_hash", { length: 64 }).notNull().unique(),
  scopes: varchar("scopes", { length: 120 }).notNull().default("mcp.read"),
  expiresAt: timestamp("expires_at").notNull(),
  revokedAt: timestamp("revoked_at"),
  lastUsedAt: timestamp("last_used_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("mcp_connections_user_idx").on(table.userId, table.createdAt),
  index("mcp_connections_active_token_idx").on(table.tokenHash, table.revokedAt),
]);

// These are shared durable contracts for future MCP mutations. The initial
// public catalog is read-only, but recording them now prevents future tools
// from inventing incompatible operation/approval/audit semantics.
export const mcpOperations = pgTable("mcp_operations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  connectionId: varchar("connection_id").notNull().references(() => mcpConnections.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  toolName: varchar("tool_name", { length: 120 }).notNull(),
  status: varchar("status", { length: 32 }).notNull().default("queued"),
  contextSnapshot: jsonb("context_snapshot").notNull(),
  inputSummary: jsonb("input_summary"),
  result: jsonb("result"),
  errorCode: varchar("error_code", { length: 80 }),
  errorMessage: text("error_message"),
  idempotencyKey: varchar("idempotency_key", { length: 160 }),
  attemptCount: integer("attempt_count").notNull().default(0),
  cancelRequestedAt: timestamp("cancel_requested_at"),
  retryOfOperationId: varchar("retry_of_operation_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  completedAt: timestamp("completed_at"),
}, (table) => [
  index("mcp_operations_brand_idx").on(table.brandId, table.createdAt),
  index("mcp_operations_status_created_idx").on(table.status, table.createdAt),
  uniqueIndex("mcp_operations_connection_idempotency_unique")
    .on(table.connectionId, table.idempotencyKey)
    .where(sql`${table.idempotencyKey} IS NOT NULL`),
]);

export const mcpApprovals = pgTable("mcp_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  operationId: varchar("operation_id").notNull().references(() => mcpOperations.id, { onDelete: "cascade" }),
  version: varchar("version", { length: 128 }).notNull(),
  requestedByUserId: varchar("requested_by_user_id").notNull().references(() => users.id),
  approvedByUserId: varchar("approved_by_user_id").references(() => users.id),
  status: varchar("status", { length: 24 }).notNull().default("pending"),
  expiresAt: timestamp("expires_at").notNull(),
  diff: jsonb("diff").notNull(),
  estimate: jsonb("estimate"),
  requiredApproverRole: varchar("required_approver_role", { length: 50 }),
  separationOfDuties: boolean("separation_of_duties").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  decidedAt: timestamp("decided_at"),
}, (table) => [index("mcp_approvals_operation_idx").on(table.operationId)]);

export const mcpAuditEvents = pgTable("mcp_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  connectionId: varchar("connection_id").references(() => mcpConnections.id, { onDelete: "set null" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  brandId: varchar("brand_id").references(() => brands.id, { onDelete: "set null" }),
  method: varchar("method", { length: 120 }).notNull(),
  outcome: varchar("outcome", { length: 32 }).notNull(),
  inputSummary: jsonb("input_summary"),
  outputRefs: jsonb("output_refs"),
  errorCode: varchar("error_code", { length: 80 }),
  operationId: varchar("operation_id").references(() => mcpOperations.id, { onDelete: "set null" }),
  approvalId: varchar("approval_id").references(() => mcpApprovals.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("mcp_audit_events_connection_idx").on(table.connectionId, table.createdAt)]);

export const mcpOauthClients = pgTable("mcp_oauth_clients", {
  clientId: varchar("client_id", { length: 100 }).primaryKey(),
  clientName: varchar("client_name", { length: 200 }).notNull(),
  redirectUris: jsonb("redirect_uris").$type<string[]>().notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});
export const mcpOauthCodes = pgTable("mcp_oauth_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  codeHash: varchar("code_hash", { length: 64 }).notNull().unique(),
  clientId: varchar("client_id", { length: 100 }).notNull().references(() => mcpOauthClients.clientId, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  redirectUri: text("redirect_uri").notNull(),
  codeChallenge: varchar("code_challenge", { length: 128 }).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("mcp_oauth_codes_client_expiry_idx").on(table.clientId, table.expiresAt)]);
export const mcpOauthTokens = pgTable("mcp_oauth_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  connectionId: varchar("connection_id").notNull().references(() => mcpConnections.id, { onDelete: "cascade" }),
  clientId: varchar("client_id", { length: 100 }).notNull().references(() => mcpOauthClients.clientId, { onDelete: "cascade" }),
  // Refresh-token rotations share this durable family identifier. A replay or
  // explicit revoke invalidates every access/refresh credential in the family.
  familyId: varchar("family_id", { length: 100 }).notNull(),
  accessTokenHash: varchar("access_token_hash", { length: 64 }).notNull().unique(),
  refreshTokenHash: varchar("refresh_token_hash", { length: 64 }).notNull().unique(),
  accessExpiresAt: timestamp("access_expires_at").notNull(),
  refreshExpiresAt: timestamp("refresh_expires_at").notNull(),
  revokedAt: timestamp("revoked_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("mcp_oauth_tokens_access_idx").on(table.accessTokenHash, table.revokedAt), index("mcp_oauth_tokens_family_idx").on(table.familyId, table.revokedAt)]);
export const mcpOauthAuthorizationTransactions = pgTable("mcp_oauth_authorization_transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  transactionHash: varchar("transaction_hash", { length: 64 }).notNull().unique(),
  clientId: varchar("client_id", { length: 100 }).notNull().references(() => mcpOauthClients.clientId, { onDelete: "cascade" }),
  redirectUri: text("redirect_uri").notNull(),
  state: text("state").notNull(),
  codeChallenge: varchar("code_challenge", { length: 128 }).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  consumedAt: timestamp("consumed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});
export const mcpRateLimitBuckets = pgTable("mcp_rate_limit_buckets", {
  connectionId: varchar("connection_id").notNull().references(() => mcpConnections.id, { onDelete: "cascade" }),
  bucketStart: timestamp("bucket_start").notNull(),
  requests: integer("requests").notNull().default(0),
}, (table) => [uniqueIndex("mcp_rate_limit_bucket_unique").on(table.connectionId, table.bucketStart)]);

export const insertAppNotificationSchema = createInsertSchema(appNotifications).omit({ id: true, createdAt: true });
export type AppNotification = typeof appNotifications.$inferSelect;
export type InsertAppNotification = z.infer<typeof insertAppNotificationSchema>;

export const insertLandingPageSchema = createInsertSchema(landingPages).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertLandingPageSectionSchema = createInsertSchema(landingPageSections).omit({ id: true, createdAt: true, updatedAt: true });
export const insertLandingPageTemplateSchema = createInsertSchema(landingPageTemplates).omit({ id: true, createdAt: true, updatedAt: true });

export type LandingPage = typeof landingPages.$inferSelect;
export type InsertLandingPage = z.infer<typeof insertLandingPageSchema>;
export type LandingPageSection = typeof landingPageSections.$inferSelect;
export type InsertLandingPageSection = z.infer<typeof insertLandingPageSectionSchema>;
export type LandingPageTemplate = typeof landingPageTemplates.$inferSelect;
export type InsertLandingPageTemplate = z.infer<typeof insertLandingPageTemplateSchema>;

// Per-brand connection to the Meta Ads CLI (the `meta` command line tool
// from PyPI package `meta-ads`). We store the access token encrypted at
// rest. One row per brand for now: a brand maps to a single Meta ad
// account in this phase.
export const metaAdsConnections = pgTable("meta_ads_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  // Meta ad account ID, typically formatted as "act_123456789".
  adAccountId: varchar("ad_account_id", { length: 255 }).notNull(),
  // Optional Business Manager ID for catalog/dataset operations.
  businessId: varchar("business_id", { length: 255 }),
  // AES-256-GCM encrypted system user access token. Only ever decrypted
  // in memory when shelling out to the meta CLI; never returned to the
  // client.
  encryptedAccessToken: text("encrypted_access_token").notNull(),
  // "pending" before first test, "connected" after a successful read,
  // "error" if the last validation failed.
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  lastCheckedAt: timestamp("last_checked_at"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("meta_ads_connections_brand_unique").on(table.brandId),
]);

export const insertMetaAdsConnectionSchema = createInsertSchema(metaAdsConnections)
  .omit({ id: true, encryptedAccessToken: true, createdAt: true, updatedAt: true, lastCheckedAt: true, lastError: true, status: true })
  .extend({
    accessToken: z.string().min(1, "Access token is required"),
    adAccountId: z.string().min(1, "Ad account ID is required"),
    businessId: z.string().optional().nullable(),
  });
export type MetaAdsConnection = typeof metaAdsConnections.$inferSelect;
export type InsertMetaAdsConnection = z.infer<typeof insertMetaAdsConnectionSchema>;

// Per-brand Klaviyo Private API Key connection (Task #1776). Mirrors
// metaAdsConnections: one row per brand, AES-256-GCM encrypted key (its own
// encryption purpose string — never the Meta Ads one), status tracked by the
// inline connect/test UI on Email Express. Brands without a row fall back to
// the global KLAVIYO_API_KEY env var.
export const klaviyoConnections = pgTable("klaviyo_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  // AES-256-GCM encrypted Klaviyo Private API Key. Only ever decrypted in
  // memory when calling the Klaviyo API; never returned to the client.
  encryptedApiKey: text("encrypted_api_key").notNull(),
  // "pending" before first test, "connected" after a successful read,
  // "error" if the last validation failed.
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  lastCheckedAt: timestamp("last_checked_at"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("klaviyo_connections_brand_unique").on(table.brandId),
]);

export const insertKlaviyoConnectionSchema = createInsertSchema(klaviyoConnections)
  .omit({ id: true, encryptedApiKey: true, createdAt: true, updatedAt: true, lastCheckedAt: true, lastError: true, status: true })
  .extend({
    apiKey: z.string().min(1, "API key is required"),
  });
export type KlaviyoConnection = typeof klaviyoConnections.$inferSelect;
export type InsertKlaviyoConnection = z.infer<typeof insertKlaviyoConnectionSchema>;

// ----------------------------------------------------------------------------
// Saved Ads / Ad Library (Projects → Generations → Ads)
// ----------------------------------------------------------------------------
//
// A `Project` is an optional, lightweight container for grouping generation
// runs — it is intentionally separate from the existing `campaigns` table to
// avoid coupling with the Static Campaigns wizard.
//
// An `AdGeneration` is one Generate-click batch from Ads Express. It records
// the inputs (persona/product/angle, selected ad unit IDs) and snapshots
// their display names so renames don't muddle old runs.
//
// A `GeneratedAd` is a single image. The Fal URL is stored in
// `imageUrlOriginal`. When a user clicks Save, the bytes are copied to R2 and
// `imageObjectKey` (+ contentType / sizeBytes) are populated; the public
// durable URL is derived at read time from `R2_PUBLIC_BASE_URL + objectKey`.
// Tags are a simple text[] for V1; we'll normalize later if needed.

export const projects = pgTable(
  "projects",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    slug: varchar("slug", { length: 255 }).notNull(),
    description: text("description"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
    // Task #2185 — project visibility. "everyone" (default) = visible to all
    // brand members; "master_only" = hidden from non-master project lists and
    // rejected as a listing filter for non-masters.
    visibility: varchar("visibility", { length: 32 }).notNull().default("everyone"),
  },
  (table) => [index("projects_brand_idx").on(table.brandId)],
);

// Task #1307 — Ads Express feature gate. Single "global" row (same pattern as
// memeGeneratorSettings / slideshowExpressSettings). `enhancedPipeline` gates
// ALL post-July-10 Ads Express output behavior (location plates, product/logo
// vision QA gates, template-reference leak gate, band text QA, inline-edit QA
// extras). OFF (default) = the July 10 baseline pipeline, byte-for-byte.
export const adsExpressSettings = pgTable("ads_express_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 50 }).notNull().unique(),
  nanoBananaVersion: varchar("nano_banana_version", { length: 3 }).$type<"2.0" | "2.1">(),
  nanoBananaChangedBy: varchar("nano_banana_changed_by"),
  nanoBananaChangedAt: timestamp("nano_banana_changed_at", { withTimezone: true }),
  enhancedPipeline: boolean("enhanced_pipeline").notNull().default(false),
  // Task #2046 follow-up — GLOBAL switch for the master-only
  // enriched "Recent generations" table on Ads Express. Default ON; OFF =
  // masters see the standard Previous Sessions panel like everyone else.
  recentGenerationsPanel: boolean("recent_generations_panel").notNull().default(true),
  // Task #2117 (follow-up) — GLOBAL, master-only Ads Express run queue. Not a
  // beta grant: when ON, MASTER ADMINS keep a usable form during a
  // run and can queue extra submissions (FIFO per user+brand+variant); all
  // other users always get the legacy single-active-run behavior. Default OFF.
  runQueue: boolean("run_queue").notNull().default(false),
  portraitReframeEnabled: boolean("portrait_reframe_enabled").notNull().default(true),
  // Retain the existing SQL column/migration; active contents are native
  // first-pass instruction prose only, not placement profiles.
  placementConfiguration: jsonb("placement_configuration").$type<Record<string, unknown>>(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const falNanoBananaRequests = pgTable("fal_nano_banana_requests", {
  requestId: text("request_id").primaryKey(),
  endpoint: text("endpoint").notNull(),
  version: text("version").notNull(),
  emergencyOverride: boolean("emergency_override").notNull(),
  submittedAt: timestamp("submitted_at", { withTimezone: true }).notNull().defaultNow(),
});
export const insertAdsExpressSettingsSchema = createInsertSchema(adsExpressSettings, {
  nanoBananaVersion: z.enum(["2.0", "2.1"]).nullable().optional(),
}).omit({ id: true, updatedAt: true });
export type AdsExpressSettings = typeof adsExpressSettings.$inferSelect;
export type InsertAdsExpressSettings = z.infer<typeof insertAdsExpressSettingsSchema>;

// Retained only for schema compatibility with historical homepage rollout data.
// Homepage selection no longer reads or writes this table.
export const homepageSettings = pgTable("homepage_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 50 }).notNull().unique(),
  newHomepageEnabled: boolean("new_homepage_enabled").notNull().default(false),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertHomepageSettingsSchema = createInsertSchema(homepageSettings).omit({ id: true, updatedAt: true });
export type HomepageSettings = typeof homepageSettings.$inferSelect;
export type InsertHomepageSettings = z.infer<typeof insertHomepageSettingsSchema>;

// Beta feature flags are scoped per user. A row's
// existence = the flag is granted (revoke = delete the row), so the default
// is OFF for everyone. Managed on the master-only
// "Beta Features" admin page; generic (flagKey) so future flags reuse the
// same table/endpoints without schema changes.
export const betaFeatureGrants = pgTable(
  "beta_feature_grants",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    // Kept nullable as a compatibility boundary for legacy production rows.
    // New user-scoped grants always leave this null; old brand-scoped rows
    // remain distinguishable while Publish applies schema-only diffs.
    brandId: varchar("brand_id").references(() => brands.id, { onDelete: "cascade" }),
    flagKey: varchar("flag_key", { length: 100 }).notNull(),
    createdBy: varchar("created_by"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("beta_feature_grants_user_flag_uniq")
      .on(table.userId, table.flagKey)
      .where(sql`${table.brandId} IS NULL`),
    index("beta_feature_grants_flag_idx").on(table.flagKey),
  ],
);

export const insertBetaFeatureGrantSchema = createInsertSchema(betaFeatureGrants).omit({ id: true, createdAt: true });
export type BetaFeatureGrant = typeof betaFeatureGrants.$inferSelect;
export type InsertBetaFeatureGrant = z.infer<typeof insertBetaFeatureGrantSchema>;

// Historical Ads Manager analytics table retained unchanged for schema
// compatibility; the retired diagnostics feature no longer reads or writes it.
export const adsManagerShadowDiagnostics = pgTable("ads_manager_shadow_diagnostics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull(),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  userId: varchar("user_id").notNull(),
  kind: varchar("kind", { length: 32 }).notNull(),
  details: jsonb("details").$type<Record<string, unknown>>().notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => [index("ads_manager_shadow_diagnostics_created_idx").on(t.createdAt)]);

// Historical Ads Manager analytics table retained unchanged for schema
// compatibility; the retired run feature no longer reads or writes it.
export const adsManagerShadowReadRuns = pgTable("ads_manager_shadow_read_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  brandId: varchar("brand_id").notNull(),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  since: date("since").notNull(),
  until: date("until").notNull(),
  status: varchar("status", { length: 24 }).notNull(),
  reason: varchar("reason", { length: 80 }),
  progress: jsonb("progress").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
  result: jsonb("result").$type<Record<string, unknown>>(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  completedAt: timestamp("completed_at"),
}, (t) => [
  uniqueIndex("ads_manager_shadow_read_active_scope").on(t.userId, t.brandId, t.accountId, t.since, t.until)
    .where(sql`${t.status} IN ('waiting', 'reading')`),
  index("ads_manager_shadow_read_recent").on(t.brandId, t.accountId, t.createdAt),
  index("ads_manager_shadow_read_expiry").on(t.status, t.expiresAt),
]);

// Historical Ads Manager analytics table retained unchanged for schema
// compatibility; the retired comparison feature no longer reads or writes it.
export const adsManagerShadowSettings = pgTable("ads_manager_shadow_settings", {
  id: integer("id").primaryKey(),
  requestTimeoutSeconds: integer("request_timeout_seconds").notNull(),
  readDeadlineSeconds: integer("read_deadline_seconds").notNull(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const LP_EXPRESS_2_BETA_FLAG = "lp_express_2";

// Retained only as a schema-compatibility boundary for legacy production
// records. The user-scoped beta resolver does not read or write this table,
// but Publish must not destroy rows it cannot translate through a schema diff.
export const legacyBetaBrandOptOuts = pgTable(
  "beta_brand_opt_outs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    flagKey: varchar("flag_key", { length: 100 }).notNull(),
    createdBy: varchar("created_by"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("beta_brand_opt_outs_brand_flag_uniq").on(table.brandId, table.flagKey),
    index("beta_brand_opt_outs_flag_idx").on(table.flagKey),
  ],
);

// Ads Express rollout/operational settings are deliberately independent from
// beta_feature_grants. The latter stays global per user; this table answers the
// separate question "is this capability enabled for this brand?".
export const adsExpressBrandSettings = pgTable(
  "ads_express_brand_settings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    settingKey: varchar("setting_key", { length: 100 }).notNull(),
    enabled: boolean("enabled").notNull(),
    updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("ads_express_brand_settings_brand_key_uniq").on(table.brandId, table.settingKey),
    index("ads_express_brand_settings_key_idx").on(table.settingKey),
  ],
);

export const insertAdsExpressBrandSettingSchema = createInsertSchema(adsExpressBrandSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type AdsExpressBrandSetting = typeof adsExpressBrandSettings.$inferSelect;
export type InsertAdsExpressBrandSetting = z.infer<typeof insertAdsExpressBrandSettingSchema>;

// Task #3103 — optional per-brand Ads Express prompt-generator workflow.
// A missing row, NULL, or an empty URL all mean "use the standard in-code
// workflow routing".  Keeping this separate from the rollout switches makes
// changing a prompt workflow an operational setting rather than a feature
// grant.
export const adsExpressWorkflowConfigs = pgTable("ads_express_workflow_configs", {
  brandId: varchar("brand_id").primaryKey().references(() => brands.id, { onDelete: "cascade" }),
  promptGeneratorSimUrl: text("prompt_generator_sim_url"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const personaWorkflowConfigs = pgTable("persona_workflow_configs", {
  brandId: varchar("brand_id").primaryKey().references(() => brands.id, { onDelete: "cascade" }),
  profileSimUrl: text("profile_sim_url"),
  starterSimUrl: text("starter_sim_url"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const insertAdsExpressWorkflowConfigSchema = createInsertSchema(
  adsExpressWorkflowConfigs,
).omit({ createdAt: true, updatedAt: true });
export type AdsExpressWorkflowConfig = typeof adsExpressWorkflowConfigs.$inferSelect;
export type InsertAdsExpressWorkflowConfig = z.infer<typeof insertAdsExpressWorkflowConfigSchema>;

// Registry of known beta flags (drives the admin page's user-grant flag list
// and effective-flags endpoint). Add future flags here.
export const BETA_FEATURE_FLAGS = [
  {
    key: VIDEO_BLUEPRINT_SYSTEM_FLAG,
    name: "Video Blueprint + Production System",
    description: "Unlocks both video workflows for granted users within authorized brands and the explicit development pilot scope. Production is disabled.",
  },
  {
    key: "ads_express_image_engine_selector",
    name: "Ads Express Image Engine Selector",
    description: "Lets a granted user select the image engine for new standard Ads Express runs without changing the brand default.",
  },
  {
    key: LP_EXPRESS_2_BETA_FLAG,
    name: "Landing Pages Express 2",
    description:
      "Unlocks the Template Studio-powered Landing Pages Express 2 surface for the granted user on the granted brand.",
  },
  {
    key: "reference_images_enabled",
    name: "Ads Express Reference Images",
    description:
      "Unlocks the Add Reference Image controls (visual reference images + per-image notes) in standard Ads Express for the granted user across their authorized brands.",
  },
  {
    key: "ads_person_identity_references",
    name: "Ads Express Person / Identity References",
    description:
      "Allows the granted user to mark an uploaded person reference for exact identity preservation in non-meme Ads Express ads. Requires likeness consent and is available wherever Ads Express Reference Images is enabled.",
  },
  {
    key: "variant_aware_ads_enabled",
    name: "Ads Express Variant-Aware Prompts",
    description:
      "Locks in-ad color/variant callouts to the product's real variants and features the selected variant as the hero product for the granted user in standard Ads Express (beta Sim workflow pair only).",
  },
  {
    // When granted, the 9:16 safe-zone band outpainting pass
    // (fal-ai/bria/expand) sends SAFE_ZONE_EXTENSION_NEGATIVE_PROMPT to
    // harden the top/bottom bands. Not granted (the default) = the submit
    // payload stays byte-for-byte identical to today (no negative_prompt
    // key). Independent of the global enhanced_pipeline toggle.
    key: "safe_zone_enhanced_render",
    name: "Safe Zone Enhanced Render",
    description:
      "Hardens 9:16 ad band outpainting with a negative prompt (against gibberish text, logos, and duplicated content in the top/bottom bands) for the granted user across their authorized brands. Applies to Ads Express, Creative Tinder, and Creative Tinder Sets renders.",
  },
  {
    // Task #2448 — when granted, each
    // "Fix bands" repair starts one escalation tier higher than a fresh
    // render (attempts land on tiers 2–3, so the strict prompt suffix is
    // reachable) and carries a per-click random Bria seed, so repeated
    // clicks submit different payloads instead of re-billing an identical
    // near-deterministic replay. Without the grant, the legacy repair payload
    // is used (byte-identical to a fresh enhanced render). Never
    // affects fresh renders on either arm.
    key: "safe_zone_repair_variation",
    name: "Ads Express Band Repair Variation",
    description:
      "Makes each 'Fix bands' click on a 9:16 ad actually try something new: repairs start at a stricter prompt tier and use a fresh random seed per click, instead of replaying the original render's near-identical payload.",
  },
  {
    // KC-style product image selection for standard Ads Express, fully
    // separate from the upload-references (Advanced settings) flow: an
    // inline click-order picker over the product's own photo library
    // (adgenImageRefs + mediaJson) whose selection is snapshotted on the
    // run (product_image_urls_snapshot) and overrides the product photos
    // the render attaches — #1 becomes the primary positional product
    // slot image. No selection = null snapshot = legacy product-photo
    // resolution, byte-for-byte.
    key: "product_reference_library_enabled",
    name: "Ads Express Product Reference Library",
    description:
      "Shows a KC-style inline product-image picker in standard Ads Express (under the product selection), letting the granted user choose which of the product's own library photos anchor the run — click in order, #1 is the main shot. Separate from the upload-reference beta.",
  },
  {
    // Task #2005 — when granted for the brand, standard Ads Express runs
    // append the persona primary character's appearance (name, age,
    // heritage, physical look, wardrobe, image-prompt summary) as a
    // DATA-ONLY "PERSONA CHARACTER" stanza to the Sim payload's personaInfo
    // field, so rendered people match the persona's character instead of
    // the reference template's person. Snapshotted on the run at submit
    // (persona_character_enabled). Not granted (the default) = personaInfo
    // byte-for-byte identical to today. TEXT ONLY — the character headshot
    // is never attached to image_urls (deferred by user decision).
    key: "ads_persona_character_appearance",
    name: "Ads Express Persona Character Appearance",
    description:
      "Describes the persona's primary character (age, heritage, build, styling) in standard Ads Express prompts for the granted user across authorized brands, so rendered people match the character instead of copying the person in the reference template.",
  },
  {
    key: "beta_ad_prompts_enabled",
    name: "Beta Ad Prompts",
    description:
      "Routes standard Ads Express prompt generation to the beta Sim workflow pair (New NB Prompt Maker w/ refs, standard + no-logo) even without reference images or variant-aware mode — no other behavior change.",
  },
  {
    // Gates the region-targeted editing tools in the shared Edit Image
    // modal (brush + click-to-select region masks, per-selection comments,
    // FLUX Fill inpainting / masked paste-back). Not granted (the default)
    // = the modal shows only the legacy whole-image describe-your-edit
    // flow, and the server rejects masked submits + SAM 2 segment calls.
    key: "region_masked_image_edit",
    name: "Region-Targeted Image Editing",
    description:
      "Unlocks the Select Area tools in the Edit Image modal (paint or click to select regions, per-selection comments, region-only edits) for the granted user across their authorized brands.",
  },
  {
    // Task #1399 — speed up describe-your-edit image edits. Gates render
    // right-sizing (no 4K escalation when re-editing a prior edit result),
    // skipping/localizing marginal Topaz upscales, and the single-buffer
    // finishing pipeline. Not granted (the default) = today's edit pipeline,
    // byte-identical (same resolution choices, Topaz triggers, and billing).
    key: "fast_image_edit_pipeline",
    name: "Fast Image Edit Pipeline",
    description:
      "Speeds up Edit Image jobs for the granted user across their authorized brands: right-sized render resolution (re-edits no longer escalate to 4K), fewer unnecessary upscale round-trips, and a single-pass finishing step. Edits still never persist below the source resolution.",
  },
  {
    // Task #1473 — speed up image edits by cutting redundant render passes.
    // Gates three behaviors: (1) a looser band-alignment re-roll trigger for
    // region text edits (borderline ~2–3% drift keeps the first render;
    // clear misalignment still retries), (2) collapsing multi-region mask
    // chains whose prompts are normalized-identical (and same per-selection
    // refs) into ONE unioned-mask pass (one render, one bill), and (3) a
    // stronger first-attempt CTA/text-edit prompt (the locate step also
    // captures the current text + styling, so the render prompt states the
    // exact before → after change). Not granted (the default) = today's
    // pipeline, byte-identical (same thresholds, passes, prompts, billing).
    key: "image_edit_speed_boost",
    name: "Image Edit Speed Boost",
    description:
      "Speeds up Edit Image jobs for the granted user across their authorized brands: fewer unnecessary alignment re-rolls on text edits, identical multi-region comments merged into a single render pass, and a more precise first-attempt prompt for CTA/text style changes.",
  },
  {
    // Task #1476 — group same-model region edits into one pass. Gates:
    // (1) upfront PARALLEL intent classification for all regions of a
    // multi-region chain (instead of one serial classifier call before each
    // pass), (2) merging ADJACENT regions that route to the same model
    // (removal→FLUX Erase, attribute→nano-banana-2, text→gpt-image-2) into
    // ONE model call — union mask for removals, a numbered instruction list
    // (with mask-position location hints) for attribute/text groups — and
    // (3) skipping intermediate output re-fetches within a group. A failed
    // grouped pass falls back to today's sequential per-region passes.
    // Independent of image_edit_speed_boost (Task #1473's identical-prompt
    // collapse). Not granted (the default) = today's sequential chain,
    // byte-identical (same classifier timing, passes, prompts, billing).
    key: "region_edit_grouping",
    name: "Region Edit Grouping",
    description:
      "Speeds up multi-region Edit Image jobs for the granted user across their authorized brands: regions needing the same kind of change (adjacent removals, attribute tweaks, or text edits) render in one merged model pass instead of one pass per region, and intent classification runs upfront in parallel.",
  },
  {
    // Task #1522 — element-aware paste bands for region-targeted text edits.
    // Production failure (Naadam, July 21): editing text inside a drawn
    // container (orange CTA pill) computed paste geometry from the text
    // glyphs only; the quiet-row seam test treated the pill's flat interior
    // as a safe seam, so a sliver of the original pill survived above the
    // re-rendered band. When granted, the text-band locator ALSO reports the
    // container's bounds (pill/button/box/banner), the paste band grows
    // (grow-only, fail-open) to fully cover the container, and container-
    // interior rows are treated as unsafe for seam placement so seams can
    // only settle on rows genuinely outside the element. No QA gate or
    // re-roll is added — results still ship as rendered. Not granted (the
    // default) = today's glyph-only geometry, byte-identical (locate prompt,
    // parsing, band math, billing all unchanged).
    key: "element_aware_text_edit",
    name: "Element-Aware Text Edit Bands",
    description:
      "When editing text that sits inside a drawn container (pill button, box, banner), the edit replaces the whole element cleanly instead of leaving slivers of the old container around the re-rendered text. Applies to region-targeted text edits for the granted user across their authorized brands.",
  },
  {
    // Task #1432 — user-uploaded reference images for Edit Image jobs.
    // Gates the "Add reference image" control in the Edit Image modal, the
    // durable ref-upload endpoint, and the `userReferenceImageUrls` submit
    // field. Refs are appended to the edit models' image inputs only — no
    // prompt text is added for them (unlike the parent-supplied product
    // refs and their fidelity directive, which stay ungated and unchanged).
    // Not granted (the default) = no upload, user refs on submit rejected
    // fail-closed, and the edit pipeline stays byte-identical.
    key: "edit_reference_images",
    name: "Edit Image Reference Images",
    description:
      "Lets the granted user attach up to 3 of their own reference images in the Edit Image modal across their authorized brands. The refs are passed to the edit model as extra image inputs for whole-frame edits and instruction-style region edits (pure-removal region passes take no references).",
  },
  {
    // Task #1495 — steer Ads Express renders toward the brand's design-system
    // color palette, VIA SIM (user decision — the render-prompt directive
    // variant was replaced). When granted, run submit resolves the brand's
    // primary/secondary/accent colors (brandDesignSystem.primaryColors/
    // secondaryColors/accentColors) into a snapshot on the ad_generations
    // row; at prompt generation the palette rides the Sim payload as a
    // DATA-ONLY "BRAND COLOR PALETTE — …" stanza on brandInfo AND the run
    // routes to the beta prompt-workflow pair, where the USER's
    // color-adherence rules live (stanza + rules travel together, VARIANT
    // MODE precedent). Data + routing only — no QA gates, no re-rolls, no
    // extra billed calls. Not granted (the default), or a brand with no
    // valid colors = null snapshot = byte-identical payloads and routing.
    // STANDARD Ads Express only (the beta pair is a standard-run concept):
    // KC, meme ad units, Creative Tinder, Instant Ad, and image edits are
    // untouched.
    key: "brand_color_adherence",
    name: "Ads Express Brand Color Adherence",
    description:
      "Passes each authorized brand's design-system color palette to the beta prompt workflows for the granted user's standard Ads Express runs, and routes those runs to that workflow pair. The color-adherence rules themselves live in the beta Sim workflow prompts.",
  },
  {
    // Task #1529 — gate the Brand Design System's CTA/button style behind a
    // user grant for an authorized brand, VIA SIM (mirrors brand_color_adherence — the
    // old render-prompt directive stays disconnected by user decision). When
    // granted, standard Ads Express submit resolves + snapshots the brand's
    // ctaStyle onto the ad_generations row (`cta_style_snapshot`); at prompt
    // generation the style rides the Sim payload as a DATA-ONLY
    // "BRAND BUTTON STYLE — …" stanza on brandInfo AND the run routes to the
    // beta prompt-workflow pair, where the USER's button-styling rules live
    // (stanza + rules travel together, VARIANT MODE precedent). The grant
    // also gates the CTA style editor section in the Brand Design System
    // tab. Not granted (the default) = null snapshot, no editor section, and
    // byte-identical payloads/routing — even for a brand that previously
    // configured and enabled a CTA style (fail-closed, user decision). KC
    // stamps the gated snapshot for uniformity but stays inert (own Sim
    // branch); meme ad units, Creative Tinder, Instant Ad, LP Express, and
    // Email Express are untouched.
    key: "brand_cta_style",
    name: "Brand CTA Button Style",
    description:
      "Unlocks the Brand Design System's CTA/button style editor for the granted user across their authorized brands, and passes the configured button style to the beta prompt workflows for their standard Ads Express runs (routing those runs to that workflow pair). The button-styling rules themselves live in the beta Sim workflow prompts.",
  },
  {
    // Task #1517 — safe-zone-aware reframes. When granted, reframing a saved
    // ad to 9:16 routes through the mechanical safe-zone pipeline: get a 4:5
    // creative first (a source that is already 4:5 skips the nano re-render),
    // then band-outpaint + paste-back via the existing extender — honoring
    // the independent `safe_zone_enhanced_render` grant (negative prompt +
    // band QA) exactly like fresh renders. Granted saves also normalize the
    // persisted image to the exact advertised dimensions and stamp the
    // aspect ratio on the library row. Not granted (the default) = today's
    // reframe behavior, billing, payloads, and saved bytes, byte-identical.
    key: "safe_zone_reframe",
    name: "Safe Zone Reframe",
    description:
      "Makes 9:16 reframes respect the safe zone for the granted user across their authorized brands: the reframed creative is confined to the centered 4:5 region with background-only outpainted bands (same guarantee as fresh 9:16 renders), and saved reframes are normalized to exact advertised dimensions with the aspect ratio recorded.",
  },
  {
    // Task #1536 — Google Font picker for standard Ads Express. The
    // beta-features endpoint and submit route resolve the requesting user's
    // global grant. The font reaches the
    // render through two DATA-ONLY channels — a font specimen PNG attached
    // at Image 2 of the Fal image_urls array (directly after the reference
    // ad, shifting logo/product/refs one slot down), and the verbatim label
    // stanza ("AD FONT MODE: ON. …", shared/ad-font.ts) appended to the
    // Sim payload's brandInfo — never a server-side prompt injection. The
    // typeface RULES live in the user's dedicated custom-font Sim workflow
    // pair prompts, keyed off the stanza labels, so font runs route to that
    // pair. No grant / no font picked / meme ad units / KC = byte-for-byte
    // legacy.
    key: "google_font_ads",
    name: "Ads Express Google Font",
    description:
      "Adds a Google Font picker to standard Ads Express for the granted user across authorized brands. The chosen font rides the run as a data-only brandInfo stanza plus a font specimen image attached directly after the reference ad; the typography rules live in the dedicated custom-font Sim workflow prompts.",
  },
  {
    // Task #1602 — pixel-exact brand logo overlay for standard Ads Express.
    // When granted, an includeLogo run renders through the EXISTING no-logo
    // pipeline (no logo reference, no-logo Sim workflow + a reserveLogoZone
    // payload hint) and the brand's REAL logo file (transparent-background
    // version prepped once per brand, cached in R2) is composited onto the
    // finished ad deterministically with sharp — actual pixels, never
    // redrawn. Corner picked by local pixel analysis; composite happens
    // before the 9:16 band extension and before normalize/persist. Decision
    // + prepped asset are snapshotted on the run at submit (retries replay
    // the snapshot); overlay failures are fail-open (the ad ships un-stamped
    // and the miss is logged). Not granted (the default) = byte-identical
    // baked-logo behavior. KC, memes, Creative Tinder, Instant Ad, and image
    // edits are untouched.
    key: "exact_logo_overlay",
    name: "Ads Express Exact Logo Overlay",
    description:
      "Composites each authorized brand's real logo file onto finished standard Ads Express renders (exact pixels, never redrawn by the model) for the granted user. The ad renders logo-free and the logo is stamped in the cleanest corner; 9:16 renders keep it inside the safe zone. Prep or overlay failures fall back to today's baked-logo behavior.",
  },
  {
    // Task #1625 — assignable plain-text notes on saved ads. User-grant
    // semantics are user-scoped and fail closed: no grant for the requesting
    // account = no notes UI and the notes API 403s.
    key: "ad_notes",
    name: "Ad Notes",
    description:
      "Lets the granted user leave short plain-text notes on saved ads (Ad Library + Ads Express lightbox), assign each note to a teammate, and browse notes in a Notes view with an \"Assigned to me\" filter. No threads, editing, or notifications.",
  },
  {
    // Task #1654 — master-only cross-brand template browser in standard
    // Ads Express. The flag alone never exposes
    // anything: the "All brands" tab AND the server-side generation
    // exception BOTH also require user.isMaster, so non-master users see
    // zero change regardless of this flag.
    key: "cross_brand_ad_templates_enabled",
    name: "Ads Express — Cross-Brand Template Browser",
    description:
      "Lets master admins browse and generate from OTHER brands' own ad templates inside standard Ads Express, via a second 'All brands' tab. No effect for non-master users regardless of this flag.",
  },
  {
    // Task #1665 — restore the post-render quality guards that the
    // 2026-07-18 "ship exactly what the model returns" experiment hardcoded
    // off for every edit job (REGION_MASKED_SHIP_MODEL_OUTPUT /
    // SKIP_POST_RENDER_SIZING in image-edit-worker.ts). Resolved ONCE at job
    // creation, stamped on the job, retry copies the snapshot. When ON:
    // (1) EVERY masked pass — the full-frame renderers (nano-banana-2 /
    // gpt-image-2, which re-render the whole frame and never receive the
    // mask) AND FLUX Erase/Fill (fal's erase can return a downscaled full
    // re-render that garbles off-mask text) — composites only the masked
    // pixels back onto the source, so off-mask drift is mechanically
    // discarded; (2) every edit job re-runs exact-aspect normalization
    // (Task #1228) and the never-persist-below-source Topaz/local-resize
    // guard (Task #917), so ~1.3MP outputs stop replacing 2MP/4MP saved ads.
    // Task #1692 — when granted, quality guards are active; without the grant,
    // the experiment behavior remains byte-identical (raw model output, no
    // sizing).
    key: "image_edit_quality_guards",
    name: "Image Edit Quality Guards",
    description:
      "Brush edits change only the brushed area (everything outside the selection ships pixel-identical from the source), and edited images never persist below the source resolution or drift off its exact aspect. Applies to every masked pass, including FLUX erase/fill. Without the grant, the ship-raw experiment behavior remains unchanged.",
  },
  {
    // Task #1692 — stricter reposition-intent detector for MASKED edits.
    // The Task #1637 detector over-matches: broad movement verbs paired with
    // any placement word (put/place/bring/push/slide + top/bottom/corner/…)
    // silently convert masked edits that are not real move requests into
    // full-frame re-renders that re-draw ALL text. When granted (user-level
    // toggle via the Ads Express admin Features tab; snapshot stamped on the
    // job at creation, retry copies it), masked edits reroute to the
    // full-frame relocation path ONLY for genuine move/relocate requests:
    // explicit reposition/relocate verbs, unambiguous movement verbs
    // (move/shift/nudge/scoot) with a placement cue, ambiguous verbs
    // (put/place/bring/push/slide) only when aimed at a definite existing
    // nameable element, or raise/lower applied to a nameable element. Not
    // granted (the default) = the Task #1637 detector, byte-identical.
    key: "strict_reposition_reroute",
    name: "Strict Reposition Reroute",
    description:
      "Tightens when masked (brushed/region) edits are treated as \"move this element\" requests and rerouted to a full-frame re-render. With this on, only genuine move/relocate phrasings reroute; prompts that merely mention a position (e.g. \"put a badge in the top corner\") keep the confined masked edit path. Off = current routing, unchanged.",
  },
  {
    // Task #1901 — confined text-edit paste. User-grant flag (like
    // strict_reposition_reroute: enabled only for the requesting user's grant,
    // toggled via the Ads Express admin Features tab). Snapshot stamped on
    // the image_edit_jobs row at creation; retry copies it. When ON, the
    // locate-band text-edit path confines the paste-back to the edited text
    // element: the render's color-grade drift is normalized against the
    // source over unchanged pixels, and only pixels inside the text
    // element/container (grown as needed) or genuinely changed near it are
    // pasted, with a feathered alpha edge so no tonal step or hard seam
    // survives on busy photographic ads. Any confinement failure fails open
    // to the current full-band paste. OFF (the default) = byte-identical
    // current behavior.
    key: "confined_text_edit_paste",
    name: "Confined Text-Edit Paste",
    description:
      "Stops headline/CTA text edits from visibly slicing photos: the re-rendered band is pasted back only where the text actually changed (element/container plus genuinely-changed pixels), color-matched to the source with a feathered edge so no horizontal seam or tonal band shows on busy photographic ads. Off = current full-band paste behavior, unchanged.",
  },
  {
    // Task #2122 — reflow-safe text paste. User-grant flag (same pattern as
    // confined_text_edit_paste: enabled only for the requesting user's grant,
    // toggled via the Ads Express admin Features tab). Snapshot stamped on
    // the image_edit_jobs row at creation; retry copies it. When ON (and the
    // confined-paste arm is active), the confined text-edit paste
    // deterministically compares line positions between the source band and
    // the render: if lines OUTSIDE the targeted text moved vertically (the
    // model re-laid-out the whole block because the replacement was
    // taller/longer), the shipped region expands to the union of the old and
    // new text-block extents so the original lines are fully overwritten —
    // never a mix of old and new line positions (the double-printed
    // subheadline failure). Any detection failure fails open to the plain
    // confined paste. OFF (the default) = byte-identical current behavior.
    key: "reflow_safe_text_paste",
    name: "Reflow-Safe Text Paste",
    description:
      "Stops text edits from double-printing neighboring lines when the replacement copy is longer/taller and the model reflows the whole text block: on a detected reflow, the pasted region grows to cover both the old and new line positions so exactly one coherent layout ships. Only active alongside Confined Text-Edit Paste. Off = current confined-paste behavior, unchanged.",
  },
  {
    // Task #1669 follow-on — widens the "Restore original" action in the ad
    // library beyond the 2026-07-18..28 degradation window. When granted,
    // the restorable index includes ANY image ad with a surviving superseded
    // original in the quarantine ledger (i.e. any edited ad whose prior
    // image hasn't been purged yet), and restore reverts to the EARLIEST
    // surviving version. Not granted (the default) = bug-window-only
    // eligibility, byte-identical to the original Task #1669 behavior.
    // Restore stays strictly user-driven either way.
    key: "restore_original_any_edit",
    name: "Restore Original on Any Edited Ad",
    description:
      "Shows the \"Restore original\" action in the ad library on ANY edited image ad whose previous image still survives in storage (normally superseded images are kept 30 days), not just ads degraded by the July 2026 edit bug. Restoring reverts to the earliest surviving version and is always a per-ad user action. Off = the action appears only on July-bug-affected ads.",
  },
  {
    // Manual image/video uploads into Ads Express are default-on per brand.
    // An explicit admin opt-out gates both the client controls and server
    // upload/edit endpoints; no user grant is required. Uploaded media
    // becomes normal generated_ads rows (status ready, no template,
    // source "manual_upload") stored durably in R2 at upload time.
    key: "ads_express_manual_upload",
    name: "Ads Express Manual Uploads",
    description:
      "Default on for every brand, with no user grant required. Authorized users can upload images, videos, or mixed batches as ready ads marked \"Manual upload\" and edit upload tags, replace uploaded images, save to the library, or download. An admin can disable uploads and manual-upload editing per brand.",
  },
  {
    // Task #1806 — re-enable Email Express AI polish. Gates BOTH the editor's
    // auto-polish trigger /
    // polish POST endpoint (403 without a grant) AND the AI
    // art-direction step (design-spec LLM + creative direction + post-render
    // critique). Without a grant = byte-identical legacy behavior: captured-PNG live
    // preview is canonical and no polish runs ever start.
    key: "email_polish",
    name: "Email Express AI Polish",
    description:
      "Re-enables the Email Express AI polish pass for the granted user across authorized brands: each section renders as a premium AI-designed image with the AI art-direction step active, and the AI picks its own world-class palette/typography (brand design is inspiration only; logo and copy fidelity stay strict). Without a grant, the live captured preview remains canonical and no polish runs.",
  },
  {
    // Task #1872 — Ads Express copy diversification. User-scoped grant,
    // resolved fail-closed for the requesting account.
    // When ON, each child ad in a standard (non-KC, non-meme) run gets a
    // deterministic hook-style directive from a fixed rotation (snapshotted
    // on the generated_ads row at submit — resumes keep the same directive)
    // plus a best-effort avoid-list of headlines already produced by sibling
    // ads, both sent as ONE new declared Sim Start input (`copy_diversity`,
    // JSON string — shared/copy-diversity.ts). The arm is stamped on the
    // ad_generations row at submit (enhanced_pipeline replay rule). OFF (the
    // default) = the key is never added and the Sim payload stays
    // byte-for-byte identical. KC and meme paths are untouched.
    key: "ads_copy_diversification",
    name: "Ads Express Copy Diversification",
    description:
      "Diversifies headlines and CTAs across a standard Ads Express run for the granted user on authorized brands. Each ad gets its own hook-style directive from a fixed rotation plus a list of headlines sibling ads already produced to avoid repeating, sent to the prompt workflow as a single copy_diversity input. Without the user grant, today's payload remains byte-for-byte unchanged.",
  },
  {
    // Task #2231 — Ads Express diversify mode. User-scoped grant resolved
    // fail-closed for the requesting account, but a fully INDEPENDENT toggle —
    // the two flags never gate
    // each other. When ON, each standard (non-KC, non-meme) ad's Sim
    // ctPayload gains ONE extra declared Start input, `diversify: "true"`,
    // AND the run routes to the beta copyWriter pair (which declares/
    // consumes it via its DIVERSITY MODE prompt section —
    // brand_color_adherence routing precedent). The arm is stamped on the
    // ad_generations row at submit
    // (enhanced_pipeline replay rule — resumes never re-check the live
    // grant). OFF (the default) = the key is never added and the Sim
    // payload stays byte-for-byte identical. KC and meme paths untouched;
    // custom-font copyWriter workflows not wired yet.
    key: "diversify_mode",
    name: "Ads Express Diversify Mode",
    description:
      "Sends diversify: \"true\" to the beta copyWriter prompt workflows for the granted user's standard Ads Express runs on authorized brands, routing runs there and activating the Sim-side DIVERSITY MODE prompt section. Independent of Copy Diversification — either, both, or neither may be on. Without the user grant, today's payload and routing remain byte-for-byte unchanged.",
  },
  {
    // Task #1971 — explicit quick actions for masked region edits. USER-level
    // grant (like edit_reference_images), fail-closed, checked through the
    // same brand-resolution/anti-spoof gate as region_masked_image_edit. When
    // granted, the masked-edit UI offers per-region "Remove" and "Edit text"
    // quick actions that send an explicit `region_intent` override; the
    // server composes the prompt from structured fields and the worker skips
    // the intent classifier entirely (the override lands directly on
    // REGION_ROUTE_TABLE — removal → FLUX Erase, text_edit → gpt-image-2).
    // No grant = no quick-action UI and any submitted override is rejected
    // 403; freeform regions stay byte-identical either way.
    key: "region_edit_quick_actions",
    name: "Masked-Edit Quick Actions",
    description:
      "Adds per-region \"Remove\" and \"Edit text\" quick actions to the masked (select-area) Edit Image flow for the granted user across their authorized brands. Remove always routes to the eraser (no prompt needed); Edit text takes the current/new text and always routes to the text-edit renderer with an unambiguous replace prompt. Freeform region comments are unaffected. Off = no quick actions and overrides are rejected.",
  },
  {
    // Task #1934 — brush-removal smudge guards. User-grant flag (Ads
    // Express admin Features tab): enabled only for the requesting user's grant,
    // snapshotted on image_edit_jobs.erase_smudge_guards at creation (retry
    // copies it). When ON (and the job is on the composite quality-guards
    // arm), the FLUX Erase pass (1) rejects/re-requests a downscaled full
    // re-render, falling back to FLUX Fill instead of upscaling blur into
    // the paste band, and (2) protects neighboring high-contrast source ink
    // (text glyphs) in the outer paste ring so the erase's blend zone can't
    // garble an adjacent text line. Both guards fail open. OFF (the
    // default) = byte-identical current erase paste-back behavior.
    key: "erase_smudge_guards",
    name: "Brush-Removal Smudge Guards",
    description:
      "Stops brush-tool removals from leaving a blurry smudge or garbling nearby text: rejects degraded (downscaled) erase renders instead of pasting upscaled blur, and keeps neighboring text pixels from being overwritten by the erase's blend zone. Off = current erase paste-back behavior, unchanged.",
  },
  {
    // Task #2326 — 9:16 safe-zone seam blend. User-scoped grant (Ads Express
    // admin Features tab), resolved fail-closed at submit and
    // snapshotted onto the render/repair/edit job (retry copies it). When
    // ON, AI-outpainted 9:16 bands composite with an explicit outpainted
    // seam mode: the creative pastes opaque through the seam and the
    // band-side transition is a short deterministic blend built from Bria's
    // own adjacent band rows, attenuating the legacy stretched-edge-row ramp
    // wherever the band's first rows diverge from the creative edge — the
    // ramp is what shipped a dark ~32px "shadow" stripe on dark/busy edge
    // rows (NAADAM denim). Also gates the seam-stripe bulk-repair
    // classification and the deterministic seam-region erase repair.
    // Flat-wash fallback bands keep the current behavior unchanged. OFF
    // (the default) = byte-identical current compositor/repair/erase
    // behavior.
    key: "safe_zone_seam_blend",
    name: "Safe Zone Seam Blend",
    description:
      "Fixes the dark streaky ~32px 'shadow' stripe at 9:16 band seams: with the flag on, outpainted bands blend at the seam using the band's own adjacent rows instead of stretching the creative's edge row into the band. Also lets Fix bands and bulk repair heal already-striped ads, and makes brush-erases over the seam rebuild the blend instead of restoring the stripe. Off = current behavior, unchanged.",
  },
  {
    key: "safe_zone_portrait_continuation",
    name: "9:16 Portrait Photo Continuation",
    description:
      "On by default for new standard Ads Express runs across all brands. For 4:5 templates requested at 9:16, permits verified nonessential lower-body/scenery continuation with independent boundary checks and safe background fallbacks. Native 9:16 templates render directly without expansion. Turn off per brand to use the previous extension path for new jobs. Existing jobs do not change; live provider validation remains separate.",
  },
  {
    key: "image_edit_916_text_safe_zone",
    name: "9:16 Text Edit Safe-Zone Enforcement",
    description:
      "Keeps eligible 9:16 Ads Express text edits inside the centered 4:5 safe area with deterministic source-text planning, bounded rendering, and output verification. Turn off temporarily to use the prior image-edit path for newly submitted edits.",
  },
  {
    key: "image_edit_verified_masked_text",
    name: "Verified Brushed Text Replacement",
    description:
      "On by default for new Ads Express image edits across aspect ratios when you brush text and provide exact replacement wording. Checks complete lettering, old-copy removal and nearby content before saving; unsafe results fail with a retryable message. Turn off for a brand to use the previous edit path for new jobs. Existing jobs do not change.",
  },
  {
    // Task #2022 — erase target-ink attribution. User-grant flag (Ads
    // Express admin Features tab, same semantics as erase_smudge_guards):
    // enabled only for the requesting user's grant, snapshotted on
    // image_edit_jobs.erase_target_attribution at creation (retry copies
    // it). When ON (and the smudge guards themselves are active), the FLUX
    // Erase paste-band ink protection attributes source ink to the removal
    // TARGET vs a NEIGHBOR by connected component: ink whose component lies
    // mostly inside the painted brush is the thing being removed and is
    // never stamped back over the erased output (the geometric outer-ring
    // rule alone re-stamped the target's ascenders/descenders, leaving a
    // garbled ghost on tight brush bands over a text line). Components
    // anchored outside the painted mask keep today's neighbor protection.
    // Fails open with the guard. OFF (the default) = byte-identical
    // current ring-only protection.
    key: "erase_target_attribution",
    name: "Brush-Removal Target Attribution",
    description:
      "Stops the brush-removal smudge guard from stamping the removed text itself back over the erased area: ink that belongs to the brushed target is recognized as the target (not a neighbor) and always ships the erased output, while genuinely neighboring text keeps its protection. Off = current ring-only protection, unchanged.",
  },
  {
    // Preserve the deep interior of explicit brush-removal selections.
    // User-grant, resolved
    // once at image-edit job creation and copied by retries. When ON, target
    // attribution may still carve grazed neighboring ink from a shallow
    // brush-edge strip, but an eroded core of the normalized painted mask is
    // immutable through model-mask growth and final paste-back.
    key: "erase_mask_interior_core",
    name: "Brush-Removal Interior Preservation",
    description:
      "Prevents selected text from surviving as chopped or overlapping fragments when photo edges are grouped with the letters. Neighbor protection can still preserve text only grazed at the brush edge, but cannot punch holes through the deep interior of the user's painted removal. Without the grant, the previous mask carving behavior remains unchanged.",
  },
  {
    // Task #2022 (user-directed follow-on) — erase invented-text check.
    // User-grant flag (Ads Express admin Features tab, same semantics as
    // erase_smudge_guards): enabled only for the requesting user's grant,
    // snapshotted on image_edit_jobs.erase_hallucination_check at creation
    // (retry copies it). When ON (and the job is on the composite
    // quality-guards arm), after a brush-removal erase is composited the
    // worker crops the erased region (painted-mask bbox, before vs after)
    // and asks one low-detail gpt-4o-mini vision question: did the erase
    // INVENT new readable text that wasn't in the source? If yes, the erase
    // is re-rolled ONCE and the re-roll ships regardless (no check loop).
    // Fail-open everywhere: any check error ships the render as today.
    // OFF (the default) = byte-identical current erase behavior.
    key: "erase_hallucination_check",
    name: "Brush-Removal Invented-Text Check",
    description:
      "After a brush removal, checks the erased area for text the AI invented out of thin air (hallucinated words in the erased spot) and automatically re-rolls the erase once when it finds any. Adds ~1-3s to each removal; a caught hallucination re-runs the erase. Off = current erase behavior, unchanged.",
  },
  {
    // Task #2250 — erase band local repair. User-grant flag (Ads Express
    // admin Features tab, same semantics as erase_smudge_guards): ON when
    // enabled only for the requesting user's grant, snapshotted on
    // image_edit_jobs.erase_band_local_repair at creation (retry copies
    // it). When ON (and the smudge guards themselves are active), the ONE
    // remaining ship-blur path — compositing a downscale-rejected FLUX
    // Erase after the FLUX Fill fallback hallucinated text — repairs the
    // paste band PURELY LOCALLY before compositing: quiet/gradient
    // surrounds are rebuilt deterministically from surrounding source
    // pixels (the flat-background fill machinery), and busy surrounds get
    // a high-quality local resample+sharpen of the downscaled erase
    // instead of the soft default upscale. ZERO extra model/provider
    // calls, no re-rolls, no vision checks — outputs already in hand only.
    // Clean full-res erases are untouched regardless of the flag. Fails
    // open to today's soft composite. OFF (the default) = byte-identical
    // current behavior.
    key: "erase_band_local_repair",
    name: "Brush-Removal Band Repair",
    description:
      "When a brush removal is forced to ship a degraded (downscaled) erase render, repairs the erased band locally before compositing: flat or gradient backgrounds are rebuilt pixel-perfect from the surrounding image, and detailed backgrounds get a sharper local re-enlargement instead of the soft default. Pure local image processing — no extra AI calls, no added wait. Off = current composite behavior, unchanged.",
  },
  {
    key: "adaptive_masked_removal",
    name: "Adaptive Masked Removal Repair",
    description:
      "Repairs simple brushed removals locally at native resolution, keeps contextual removals on FLUX Erase, and rejects visibly blurred or mismatched composites. Enabled by default; turn it off to restore the prior removal path.",
  },
  {
    // Task #3093 — full-resolution brush-removal tonal correction. Brand-only
    // opt-in resolved at submit and snapshotted on image_edit_jobs so a retry
    // cannot change an in-flight job's image-processing contract. It is
    // available without a separate user grant and defaults off.
    key: "erase_tone_correction",
    name: "Brush-Removal Tone Correction",
    description:
      "For full-resolution brush removals, repairs a captured tonal patch in the final output using a bounded source-context tone-surface model. Busy scenes and legacy/null jobs fail closed; no extra AI/provider calls. Off = current composite behavior, unchanged.",
  },
  {
    // Task #1972 — goal picker for masked edits. USER-level grant (like
    // region_edit_quick_actions), fail-closed, checked through the same
    // brand-resolution/anti-spoof gate. When granted, the masked-edit flow
    // opens with a goal picker (Remove something / Edit text / Change color
    // or style / Describe it) that SUGGESTS a default selection tool
    // (Remove → click-to-select, Edit text & Change color/style → brush)
    // with a short inline hint — the brush/click toggle stays fully usable
    // in every goal. Goals pre-set the per-region intent bypass: Remove and
    // Edit text ride the Task #1971 quick-action overrides, and Change
    // color or style extends the same mechanism to the attribute_edit
    // intent. "Describe it" (and skipping the picker) stays byte-identical
    // to the freeform flow. No grant = no goal picker and any
    // attribute_edit override is rejected 403.
    key: "masked_edit_goal_picker",
    name: "Masked-Edit Goal Picker",
    description:
      "Opens the masked (select-area) Edit Image flow with a goal picker — Remove something, Edit text, Change color or style, Describe it — for the granted user across their authorized brands. Each goal suggests the best selection tool (click-to-select for distinct objects, brush for text and soft-edge areas) with a short hint, never hiding or locking the other tool, and routes the edit straight to the matching renderer without intent guessing. Off = no goal picker; the masked-edit flow is unchanged.",
  },
  {
    // Task #2388 — standalone layered Creative Editor. Access is determined
    // by the granted user's beta access; server routes fail closed without it.
    key: "creative_editor",
    name: "Creative Editor",
    description:
      "Shows the 'Edit Creative' action on image ads in My Ads, which copies the ad into a standalone layered Creative Editor project (Konva canvas: text/image layers, undo/redo, autosave, PNG/JPG export). One-time import — the source ad is never modified. Off = no entry point and every /api/creative-editor route is 403.",
  },
  {
    key: "creative_iterations_video",
    name: "Creative Iterations Video",
    description:
      "Allows the granted user to start Creative Iterations from saved, ready video ads and generate video variations. Without the user grant, video sources are hidden, the My Ads entry point is unavailable, and new video runs are rejected.",
  },
  {
    key: "email_express_2",
    name: "Email Express 2.0",
    description:
      "Shows Email Express 2.0 in Marketing Channels for the granted user across their authorized brands, with access to create, reopen, edit, and revise plain-HTML email designs. Off = no navigation, no page access, and every Email Express 2.0 API route is denied.",
  },
  {
    key: "weekly_deliverables",
    name: "Deliverables",
    description:
      "Shows the brand-facing Deliverables approval page and the internal delivery assembly and scoreboard in My Ads for the granted user. Without the user grant, there is no navigation or entry point and every Deliverables API route is denied.",
  },
  {
    key: SMB_AUTOMATION_FLAG_KEY,
    name: "SMB Automation",
    description: "Development-only SMB automation for explicitly granted masters. No grant means no SMB behavior.",
  },
  // Task #2117 note — the Ads Express run queue is NOT a beta flag: it's the
  // GLOBAL master-only `adsExpressSettings.runQueue` switch (see above),
  // toggled from the Global switches section of the admin Ads Express
  // Features tab. Only master admins get the queue behavior when it's on.
] as const;
export type BetaFeatureFlagKey = (typeof BETA_FEATURE_FLAGS)[number]["key"];
export const BETA_FEATURE_FLAG_KEYS: readonly string[] = BETA_FEATURE_FLAGS.map((f) => f.key);

// Separate brand-setting registry. Keys intentionally remain identical to the
// historical runtime keys so persisted run snapshots and clients do not need a
// translation layer. Defaults generally preserve prior rollout behavior;
// deliberate default-on changes are called out beside their keys.
// `requiresUserGrant` is true only for capabilities that were genuinely
// experimental per-user; historically brand-wide/default-on rollout controls
// are resolved from the brand setting alone.
export const ADS_EXPRESS_DEFAULT_ON_SETTING_KEYS = new Set([
  "ad_delayer_layered_edit",
  "ads_express_image_engine_selector",
  "safe_zone_enhanced_render",
  "safe_zone_reframe",
  "region_masked_image_edit",
  "fast_image_edit_pipeline",
  "image_edit_speed_boost",
  "region_edit_grouping",
  "element_aware_text_edit",
  "edit_reference_images",
  "cross_brand_ad_templates_enabled",
  "image_edit_quality_guards",
  "reference_images_enabled",
  "ads_person_identity_references",
  "ads_persona_character_selector",
  "ads_persona_character_reference_sheet",
  "product_reference_library_enabled",
  "variant_aware_ads_enabled",
  "email_polish",
  // Missing brand rows enable manual uploads; saved admin opt-outs win.
  "ads_express_manual_upload",
  "strict_reposition_reroute",
  "confined_text_edit_paste",
  "reflow_safe_text_paste",
  // The verified selected-copy route is default-on for new jobs across
  // brands; an explicit brand row remains the opt-out.
  "image_edit_verified_masked_text",
  "image_edit_selected_text_editing",
  "region_edit_quick_actions",
  "erase_smudge_guards",
  "erase_target_attribution",
  "erase_mask_interior_core",
  "erase_hallucination_check",
  "erase_band_local_repair",
  "adaptive_masked_removal",
  "masked_edit_goal_picker",
  "diversify_mode",
  "safe_zone_seam_blend",
  "safe_zone_repair_variation",
  // Task #3326 — adaptive source-measured replacement layout. This is
  // deliberately default-on; an explicit brand row is the opt-out.
  "adaptive_text_edit_layout",
  // Task #3093 — the Ads Express master switch for all text-removal
  // repairs. It is default-on for every brand; an explicit brand row can
  // disable the whole arm.
  "text_removal_repairs",
  "safe_zone_person_seam_guard",
  "safe_zone_direct_916_guard",
  "ads_express_916_via_45_reframe",
  // New standard renders use guarded photo continuation for every brand;
  // saved brand opt-outs win and historical job snapshots remain immutable.
  "safe_zone_portrait_continuation",
  "weekly_deliverables",
]);
const ADS_EXPRESS_BRAND_ONLY_SETTING_KEYS = new Set([
  "ad_delayer_layered_edit",
  ...ADS_EXPRESS_DEFAULT_ON_SETTING_KEYS,
  // The separate deterministic 9:16 text setting remains opt-in.
  "image_edit_916_text_safe_zone",
  // Copy-delivery repair is an explicit brand opt-in, independent of the
  // default-on verified brushed-text route. Keep it brand-only when off.
  "image_edit_copy_delivery_repair",
  "ideogram_text_edit",
  "erase_tone_correction",
  "ads_persona_character_appearance",
  "customer_evidence_import",
  "ads_persona_character_selector",
  "ads_persona_character_reference_sheet",
  "ads_express_product_flatlay",
  "brand_cta_style",
  "brand_color_adherence",
  "google_font_ads",
  "ad_notes",
  "restore_original_any_edit",
  "ads_copy_diversification",
  "creative_editor",
  "creative_iterations_video",
  // This is an Ads Express brand rollout control, not a per-user beta grant.
  "text_removal_repairs",
]);
export const ADS_EXPRESS_BRAND_SETTINGS = [
  {
    key: "ads_express_916_via_45_reframe",
    name: "Two-Pass 9:16 Portraits",
    description: "Generate standard static Ads Express portraits at 4:5, then use the same Reframe to 9:16 operation and safe-zone settings as the requesting user. Default on for every brand; the global switch overrides this setting. New jobs and fresh regenerations only.",
  },
  ...BETA_FEATURE_FLAGS.filter(
    (flag) => flag.key !== "email_express_2" &&
      flag.key !== "lp_express_2" &&
      flag.key !== VIDEO_BLUEPRINT_SYSTEM_FLAG &&
      flag.key !== SMB_AUTOMATION_FLAG_KEY,
  ),
  {
    key: "customer_evidence_import",
    name: "Customer Evidence Import",
    description: "Show Knowledge Base > Customer Evidence for master admins on this brand. Aggregate buyer evidence informs personas and research. Off by default.",
  },
  {
    key: "text_removal_repairs",
    name: "Text Removal Repairs",
    description:
      "Enables bounded text-removal isolation, selected-text removal routing, and the safe tonal/pattern repairs for Ads Express image edits.",
  },
  {
    key: "image_edit_selected_text_editing",
    name: "Selected Text Editing",
    description:
      "Default on for every brand. Uses selection-aware text replacement, color and alignment editing while preserving the original CTA shape and unrelated pixels. No output quality checks or extra renders. New edits only; retries and resumes keep their saved behavior. Off uses the legacy editing settings.",
  },
  {
    key: "image_edit_copy_delivery_repair",
    name: "Text Copy Delivery Repair",
    description:
      "Off by default; enable per brand to fit verified provider lettering within measured safe space and check the final delivered copy for explicit text replacements. Applies to new Ads Express jobs only; retries and resumes keep their saved behavior.",
  },
  {
    key: "ideogram_text_edit",
    name: "Ideogram v4.5 Text Edits",
    description: "Off by default; enable per brand to render Ads Express text edits with Ideogram v4.5 Edit instead of gpt-image-2. Falls back to the existing engines on any failure. Applies to new edit jobs only; retries and resumes keep their saved behavior.",
  },
  {
    key: "ad_delayer_layered_edit",
    name: "Layered Edit (Bria Ad Delayer)",
    description: "Default on for every brand. Shows Layered edit in Edit Image to split an ad into editable layers with Bria Ad Delayer and apply local edits without regenerating the image. Existing saved layers are reused. Provider charges apply to new decompositions.",
  },
  {
    key: "adaptive_text_edit_layout",
    name: "Adaptive Text Edit Layout",
    description:
      "Lets source-measured text replacements reflow into verified empty space without moving products, faces, logos, CTAs, or unrelated copy.",
  },
  {
    key: "safe_zone_person_seam_guard",
    name: "Safe Zone Cropped-Head Guard",
    description:
      "Keeps 9:16 band extension from inventing heads or faces that a template crops off. Off means previous behavior.",
  },
  {
    key: "safe_zone_direct_916_guard",
    name: "Native 9:16 Platform Text Guard",
    description: "Keeps text, logos and CTAs out of the Instagram and Facebook Reels and Stories UI zones on native 9:16 renders. Adds a safe-zone instruction to the render prompt, checks the result, retries once, then falls back to the 4:5 plus extended-bands path.",
  },
  {
    key: "ads_persona_character_selector",
    name: "Ads Express Persona Character Selector",
    description:
      "Lets users choose which ready character from the selected persona is used by new standard Ads Express runs. The resolved choice is snapshotted for retries and resumes.",
  },
  {
    key: "ads_persona_character_reference_sheet",
    name: "Ads Express Persona Character Reference Sheets",
    description:
      "Attaches the primary persona character's durable reference sheet to standard Ads Express renders for exact identity preservation.",
  },
  {
    key: "ads_express_product_flatlay",
    name: "Reuse Product Flatlays",
    description:
      "Reuses or prepares a product/variant flatlay as a product reference in standard Ads Express template renders.",
  },
]
  .map((flag) => ({
    ...flag,
    defaultEnabled: ADS_EXPRESS_DEFAULT_ON_SETTING_KEYS.has(flag.key),
    requiresUserGrant: flag.key === "ads_express_image_engine_selector" ||
      !ADS_EXPRESS_BRAND_ONLY_SETTING_KEYS.has(flag.key),
  }));
export const ADS_EXPRESS_BRAND_SETTING_KEYS: readonly string[] =
  ADS_EXPRESS_BRAND_SETTINGS.map((setting) => setting.key);
export const ADS_EXPRESS_BRAND_SETTING_BY_KEY: ReadonlyMap<
  string,
  (typeof ADS_EXPRESS_BRAND_SETTINGS)[number]
> = new Map(
  ADS_EXPRESS_BRAND_SETTINGS.map((setting) => [setting.key, setting] as const),
);

export function composeAdsExpressFeatureAccess(
  setting: Pick<(typeof ADS_EXPRESS_BRAND_SETTINGS)[number], "requiresUserGrant">,
  brandEnabled: boolean,
  userGranted: boolean,
): boolean {
  return brandEnabled && (!setting.requiresUserGrant || userGranted);
}

export const adGenerations = pgTable(
  "ad_generations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    projectId: varchar("project_id").references(() => projects.id),
    personaId: varchar("persona_id").references(() => personas.id),
    productId: varchar("product_id").references(() => products.id),
    // Phase 1 prep for Offer-based ad generation. Existing rows default
    // to "product" so the legacy product-only flow keeps working.
    // promoted_item_type: "product" | "offer"
    promotedItemType: varchar("promoted_item_type", { length: 20 }).default("product"),
    offerId: varchar("offer_id").references(() => offers.id),
    narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
    // Kill Crew (KC) variant: replaces persona+narrativeAngle with a
    // product-derived "creative angle". Nullable so legacy/standard
    // Ads Express runs (which use personaId+narrativeAngleId) keep
    // working unchanged.
    creativeAngleId: varchar("creative_angle_id"),
    // KC single-step variant: optional free-form steer the user typed
    // alongside their template selection. Empty string allowed; null
    // means "not a KC single-step run" (legacy/standard path).
    creativeDirection: text("creative_direction"),
    // KC single-step variant: explicit subset of the product's reference
    // images the user picked for this run. Null = legacy behaviour
    // (worker falls back to the product's adgenImageRefs/mediaJson).
    productImageUrlsSnapshot: text("product_image_urls_snapshot").array(),
    // Snapshots so renames or deletes don't break the library view.
    personaNameSnapshot: varchar("persona_name_snapshot", { length: 255 }),
    productNameSnapshot: varchar("product_name_snapshot", { length: 255 }),
    angleNameSnapshot: varchar("angle_name_snapshot", { length: 255 }),
    angleFamilySnapshot: varchar("angle_family_snapshot", { length: 100 }),
    creativeAngleNameSnapshot: varchar("creative_angle_name_snapshot", { length: 255 }),
    creativeAngleCategorySnapshot: varchar("creative_angle_category_snapshot", { length: 100 }),
    // Optional Sales & Discount attached to this run. The full sale row
    // is also snapshotted as JSON so the worker / Sim payload reflects
    // what the user selected even if the sale is later edited or deleted.
    salesDiscountId: varchar("sales_discount_id"),
    salesDiscountSnapshot: jsonb("sales_discount_snapshot"),
    // Ordered list of ad-unit IDs the user selected for this run.
    selectedAdUnitIds: text("selected_ad_unit_ids").array(),
    // Aspect ratio applied to every ad in this run. One of "1:1" | "4:5"
    // | "9:16". Default null = legacy 4:5 (the original Ads Express
    // hardcoded ratio); the worker resolves null to "4:5" so older runs
    // resume identically.
    aspectRatio: varchar("aspect_ratio", { length: 10 }),
    // Creative Style mode applied to every ad in this run. One of
    // "precise" | "balanced" | "creative_freedom". Null on legacy rows;
    // the worker resolves null to "balanced" so older runs resume
    // identically (Balanced = no prepend, template kept — byte-for-byte
    // identical Sim payload to the original Ads Express behaviour).
    creativeStyleMode: varchar("creative_style_mode", { length: 50 }),
    // Whether the brand logo is baked into every ad in this run. Default
    // true = legacy behaviour (logo always included). When false the
    // worker omits the logo reference image from Fal and uses the no-logo
    // Sim prompt workflow. Snapshotted at queue time so resumes/restarts
    // honour the original choice.
    includeLogo: boolean("include_logo").default(true).notNull(),
    // Up to 3 visual reference images attached to this run. They steer the
    // generated ad's VISUAL ENVIRONMENT (background, lighting, color
    // temperature, texture, mood) while the Sim-generated prompt continues to
    // drive product, copy, and layout. Each entry is { url, note } where note
    // is an optional "what to pull from this" hint. Snapshotted at submit time
    // so editing/removing references mid-run doesn't change in-flight renders
    // (mirrors salesDiscountSnapshot). Null/empty = legacy behaviour: the
    // render is byte-for-byte identical to a run with no references.
    referenceImagesSnapshot: jsonb("reference_images_snapshot"),
    // Freeform "additional context" the user typed alongside the visual
    // references; appended to the prompt tail. Null/empty = legacy behaviour.
    additionalContextSnapshot: text("additional_context_snapshot"),
    // Task #2665 — user-gated Person / identity reference arm, snapshotted
    // at submit time. Null/false keeps legacy reference behavior; resumes
    // never re-check a live brand setting.
    personIdentityEnabled: boolean("person_identity_enabled"),
    // Task #744 — the selected product variant for this run, snapshotted as the
    // full variant object ({ name, price?, imageUrl?, description? }) at submit
    // time so mid-run edits to the product don't change
    // in-flight renders and restart/resume re-reads the same variant. Null =
    // "no specific variant" → byte-for-byte identical to the legacy path.
    selectedVariantSnapshot: jsonb("selected_variant_snapshot"),
    // Brand-only decision frozen at admission. Legacy/null rows stay on catalog photos.
    productFlatlayEnabled: boolean("product_flatlay_enabled"),
    // Admission decision, independent of the saved-angle picker filter.
    // NULL arm = legacy unstamped; false = explicitly disabled; true with
    // NULL stage = enabled but unclassified. No live lookup on replay.
    narrativeFunnelStageEnabled: boolean("narrative_funnel_stage_enabled"),
    funnelStageSnapshot: varchar("funnel_stage_snapshot", { length: 10 }),
    // Task #1365 — whether the `variant_aware_ads_enabled` beta flag was
    // granted (user+brand) at SUBMIT time. Snapshotted like pipelineVariant /
    // includeLogo so restarts/resumes replay the stamped decision and never
    // re-check the live grant mid-run. Null/false on legacy rows = the
    // variant-aware Sim fields are never sent (byte-for-byte legacy payload).
    variantAwareModeEnabled: boolean("variant_aware_mode_enabled"),
    // Task #1382 — whether the `beta_ad_prompts_enabled` beta flag was
    // granted (user+brand) at SUBMIT time. Same snapshot rule as
    // variantAwareModeEnabled above: restarts/resumes replay the stamped
    // decision and never re-check the live grant mid-run. Null/false on
    // legacy rows = original workflow pair, byte-for-byte.
    betaAdPromptsEnabled: boolean("beta_ad_prompts_enabled"),
    // DEPRECATED (Task #1115 removal) — the brand-font-in-ads feature was
    // removed entirely; nothing writes or reads this snapshot anymore. The
    // column is kept nullable (this repo's deprecated-column convention) so
    // old rows keep parsing; new runs always leave it null.
    adFontSnapshot: jsonb("ad_font_snapshot"),
    // Task #1233 — resolved BrandCtaStyleSelection (shared/brand-cta-style.ts)
    // snapshotted at run submit so mid-run design-system edits / restart-resume
    // never change what an in-flight render steers toward. HARD-GATED on the
    // brand's `ctaStyle.enabled` setting: null = no prompt injection,
    // byte-for-byte identical to the legacy render.
    ctaStyleSnapshot: jsonb("cta_style_snapshot"),
    // Task #1536 — AdFontSnapshot (shared/ad-font.ts) snapshotted at run
    // submit, gated on the user's `google_font_ads` beta grant. A NEW
    // column — the deprecated ad_font_snapshot above (removed Task #1115
    // feature) is never reused. Includes the per-family specimen PNG URL
    // resolved at submit so restarts/resumes replay the identical stanza +
    // reference attachment and never re-render the specimen. Null (no
    // grant, or no font picked) = byte-for-byte legacy render.
    googleFontSnapshot: jsonb("google_font_snapshot"),
    // Task #1495 — resolved BrandColorPaletteSnapshot
    // (shared/brand-color-adherence.ts) snapshotted at run submit, gated on
    // the `brand_color_adherence` beta grant (user+brand) at SUBMIT time.
    // Same replay rule as ctaStyleSnapshot: mid-run design-system edits /
    // restart-resume never change what an in-flight render steers toward.
    // Null (no grant, or no valid colors) = no prompt injection, byte-for-
    // byte identical to the legacy render.
    brandColorsSnapshot: jsonb("brand_colors_snapshot"),
    // Brand-level Style Rules resolved and trimmed at submit time. Null means
    // disabled, empty, or legacy; workers only replay this immutable snapshot.
    styleRulesSnapshot: text("style_rules_snapshot"),
    // Typography preferences paired with styleRulesSnapshot. This is nullable
    // so legacy generations remain byte-for-byte unchanged and retries replay
    // the exact submit-time value rather than reading the live brand blob.
    styleRulesFontSnapshot: text("style_rules_font_snapshot"),
    // Task #1307 — the Ads Express feature gate (ads_express_settings.
    // enhanced_pipeline) SNAPSHOTTED at run submit, so flipping the flag
    // mid-run / restart-resume never changes an in-flight render. Null on
    // legacy rows; the worker resolves null/false to the July-10 baseline
    // pipeline (byte-for-byte identical prompts + QA behavior).
    enhancedPipeline: boolean("enhanced_pipeline"),
    portraitContinuation: boolean("portrait_continuation"),
    // Retired. Columns kept so the schema matches migration 0458. Not read or written.
    nativeSafeZonePolicy: jsonb("native_safe_zone_policy"),
    // Task #1602 — exact-logo-overlay arm, snapshotted at run submit (gated
    // on the `exact_logo_overlay` beta grant + includeLogo there). Carries
    // the PREPPED transparent-background logo URL ({ logoUrl }) produced by
    // ensureOverlayLogoAsset so retries/resumes replay the identical asset —
    // never a live grant re-check or re-prep. Null (no grant, logo opt-out,
    // or prep failure — fail-open) = today's baked-logo behavior,
    // byte-for-byte.
    exactLogoOverlaySnapshot: jsonb("exact_logo_overlay_snapshot"),
    // Task #1777 — image-engine override (brand_image_engine_
    // overrides), SNAPSHOTTED at run submit like enhancedPipeline above so
    // flipping the admin dropdown mid-run / restart-resume never changes an
    // in-flight render. "gpt-image-2" | "flux-2-pro"; null = Default (today's
    // GPT-Image-2 on-grid / nano-banana-2 off-grid split), byte-for-byte.
    // Stamped ONLY by the standard Ads Express submit route — KC and legacy
    // rows stay null and the generator ignores it off the CT pipeline anyway.
    imageEngineSnapshot: varchar("image_engine_snapshot", { length: 30 }),
    // Explicit user choice, independent of the admin's 9:16-only snapshot.
    // Null on default, legacy, KC and meme runs. Workers never re-read grants.
    imageEngineChoiceSnapshot: varchar("image_engine_choice_snapshot", { length: 30 }),
    // Task #1872 — copy-diversification arm, snapshotted at run submit
    // (gated on the user's `ads_copy_diversification` beta grant
    // there — same replay rule as enhancedPipeline: resumes/restarts replay
    // the stamped decision, never a live grant re-check). Null/false =
    // byte-for-byte legacy Sim payload. Stamped ONLY by the standard Ads
    // Express submit route — KC and legacy rows stay null.
    copyDiversificationEnabled: boolean("copy_diversification_enabled"),
    // Task #2231 — diversify-mode arm, snapshotted at run submit (gated on
    // the user's `diversify_mode` beta grant there — same replay rule
    // as copyDiversificationEnabled above, but a fully INDEPENDENT flag).
    // Null/false = the `diversify` key never rides the Sim payload —
    // byte-for-byte legacy. Stamped ONLY by the standard Ads Express submit
    // route — KC and legacy rows stay null.
    diversifyModeEnabled: boolean("diversify_mode_enabled"),
    // Task #2005 — persona primary-character appearance stanza arm,
    // snapshotted at run submit (gated on the user's
    // `ads_persona_character_appearance` beta grant there — same replay rule
    // as copyDiversificationEnabled above). Null/false = personaInfo stays
    // byte-for-byte legacy. Stamped ONLY by the standard Ads Express submit
    // route — KC and legacy rows stay null.
    personaCharacterEnabled: boolean("persona_character_enabled"),
    // Distinguishes selector-enabled admissions (including a deterministic
    // "no ready character" null result) from legacy rows that predate the
    // selector and must retain live-primary compatibility.
    personaCharacterSelectorEnabled: boolean("persona_character_selector_enabled"),
    // Ready persona character resolved at standard-run admission. A non-null
    // value is authoritative on retries/resumes; null legacy rows retain the
    // historical live-primary fallback.
    selectedPersonaCharacterIdSnapshot: varchar("selected_persona_character_id_snapshot"),
    // Admission-time display label for selector-enabled standard runs. Kept
    // separately from the character row so history remains auditable after a
    // rename or deletion. Null on legacy, KC, meme, and no-ready-character runs.
    selectedPersonaCharacterNameSnapshot: varchar("selected_persona_character_name_snapshot", { length: 255 }),
    // Task #3377 — brand-only arm for attaching the primary character's
    // durable reference sheet. Snapshotted at submit and replayed on retry.
    personaCharacterReferenceSheetEnabled: boolean("persona_character_reference_sheet_enabled"),
    // Task #1705 — "manual_upload" marks the brand's lightweight container
    // generation for manually uploaded images (created completed, never
    // touched by the render worker / billing). Null on all real runs.
    source: varchar("source", { length: 30 }),
    // queued | running | completed | partial | failed | cancelled
    status: varchar("status", { length: 50 }).notNull().default("queued"),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
  },
  (table) => [
    index("ad_generations_brand_idx").on(table.brandId),
    index("ad_generations_project_idx").on(table.projectId),
    // At most ONE manual-upload container generation per brand — concurrent
    // first uploads must converge on the same row (see
    // server/services/manual-upload-generation.ts).
    uniqueIndex("ad_generations_manual_upload_brand_uniq")
      .on(table.brandId)
      .where(sql`source = 'manual_upload'`),
  ],
);

// Browser-to-R2 manual uploads. The row is the authorization and ownership
// fence for the randomly generated object key; no request may supply its own
// key. Terminal rows are retained for idempotent finalize/cancel responses.
export const pendingManualUploads = pgTable(
  "pending_manual_uploads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    ownershipToken: varchar("ownership_token", { length: 100 }).notNull(),
    objectKey: text("object_key").notNull(),
    originalFilename: varchar("original_filename", { length: 500 }).notNull(),
    contentType: varchar("content_type", { length: 100 }).notNull(),
    sizeBytes: bigint("size_bytes", { mode: "number" }).notNull(),
    mediaType: varchar("media_type", { length: 20 }).notNull(),
    transferKind: varchar("transfer_kind", { length: 20 }).notNull(),
    multipartUploadId: text("multipart_upload_id"),
    status: varchar("status", { length: 30 }).notNull().default("pending"),
    personaId: varchar("persona_id"),
    personaNameSnapshot: varchar("persona_name_snapshot", { length: 255 }),
    productId: varchar("product_id"),
    productNameSnapshot: varchar("product_name_snapshot", { length: 255 }),
    offerId: varchar("offer_id"),
    offerNameSnapshot: varchar("offer_name_snapshot", { length: 255 }),
    narrativeAngleId: varchar("narrative_angle_id"),
    narrativeAngleNameSnapshot: varchar("narrative_angle_name_snapshot", { length: 255 }),
    // Kept as an idempotency pointer. No FK avoids a schema-level cycle with
    // generated_ads while application finalization writes both atomically.
    generatedAdId: varchar("generated_ad_id"),
    errorStage: varchar("error_stage", { length: 30 }),
    errorMessage: text("error_message"),
    expiresAt: timestamp("expires_at").notNull(),
    finalizedAt: timestamp("finalized_at"),
    cancelledAt: timestamp("cancelled_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("pending_manual_uploads_object_key_uq").on(table.objectKey),
    uniqueIndex("pending_manual_uploads_generated_ad_uq").on(table.generatedAdId),
    index("pending_manual_uploads_owner_idx").on(table.brandId, table.userId),
    index("pending_manual_uploads_expiry_idx").on(table.status, table.expiresAt),
  ],
);

export type PendingManualUpload = typeof pendingManualUploads.$inferSelect;
export type InsertPendingManualUpload = typeof pendingManualUploads.$inferInsert;

export const generatedAds = pgTable(
  "generated_ads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    generationId: varchar("generation_id").notNull().references(() => adGenerations.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    // Denormalized for fast filtering in the My Ads grid.
    projectId: varchar("project_id").references(() => projects.id),
    adUnitTypeId: varchar("ad_unit_type_id").references(() => adUnitTypes.id),
    adUnitNameSnapshot: varchar("ad_unit_name_snapshot", { length: 500 }),
    // User-set display name. When present it overrides the auto-generated
    // template-name title everywhere (library, Ads Express tiles, download
    // filenames). Null = fall back to adUnitNameSnapshot.
    customName: varchar("custom_name", { length: 200 }),
    // Task #1572/#1798 — auto-generated media-buyer naming convention,
    // computed at run submit for standard Ads Express runs:
    //   {Persona}_{Product}_{Angle}_Static_PersonaOS_{YYMMDD}{Letter}
    // Letter = render order within the run (A, B, … Z, AA, AB, …), shared
    // across aspect-ratio variants of the same ad unit. Null on legacy rows
    // and on tools without a persona/angle pair (KC, memes, slideshows…) —
    // display falls back to adUnitNameSnapshot. customName (user rename)
    // still overrides this everywhere.
    defaultNameSnapshot: varchar("default_name_snapshot", { length: 500 }),
    // Task #2181 — sale/discount applied to this ad's run, snapshotted per
    // ad at creation from the parent generation's salesDiscountId/
    // salesDiscountSnapshot so the library, previous sessions, and download
    // filenames can show "this ad was made under a sale" without joining
    // back to the run. Name feeds the optional {Sale Name} segment of
    // defaultNameSnapshot. Null on no-sale runs, legacy rows, manual
    // uploads, and non-Ads-Express tools.
    salesDiscountIdSnapshot: varchar("sales_discount_id_snapshot"),
    salesDiscountNameSnapshot: varchar("sales_discount_name_snapshot", { length: 255 }),
    // Task #1872 — deterministic per-ad hook-style directive key
    // (shared/copy-diversity.ts COPY_HOOK_STYLES), stamped at run submit on
    // copy-diversification runs only (cycled by child index). Resumes and
    // restarts replay the stamped key so an ad never switches style
    // mid-flight. Null on flag-off runs, KC rows, and legacy rows.
    copyHookStyleSnapshot: varchar("copy_hook_style_snapshot", { length: 60 }),
    // Kill Crew (KC) variant: which creative angle this ad was rendered
    // for. Null on standard Ads Express runs.
    creativeAngleId: varchar("creative_angle_id"),
    creativeAngleNameSnapshot: varchar("creative_angle_name_snapshot", { length: 255 }),
    // Full snapshot of the creative-angle fields used to render this ad,
    // so the library can show "what this ad was actually built on" even
    // if the angle is later edited or archived.
    creativeAngleSnapshot: jsonb("creative_angle_snapshot"),
    // KC single-step variant snapshots:
    //   - kcDesignPromptSnapshot: the per-template KC design prompt that
    //     was active when the run was queued, captured so admin edits
    //     after-the-fact don't change what the worker renders.
    //   - creativeDirectionSnapshot: the user's free-form steer text for
    //     this run (mirrored from the parent generation for fast filtering).
    kcDesignPromptSnapshot: text("kc_design_prompt_snapshot"),
    creativeDirectionSnapshot: text("creative_direction_snapshot"),
    // KC: snapshot of the ad-unit's `adgenExampleImageUrl` at queue
    // time when the template's KC prompt has `useTemplateImage=true`.
    // Empty/null means "do not send the template image to Fal" — the
    // worker won't include it in `image_urls`. Snapshotted so admin
    // toggles or template image swaps mid-run don't change what an
    // in-flight or replay render uses.
    kcTemplateImageUrlSnapshot: text("kc_template_image_url_snapshot"),
    // For edits made via the Inpainting modal: the new ad rows back-link
    // to the original ad they were derived from.
    parentAdId: varchar("parent_ad_id"),
    // Per-ad aspect ratio. Ads Express fans out each (ad unit × ratio) into
    // its own row, so the ratio lives here rather than only on the parent
    // generation. Null = fall back to the parent generation's aspectRatio
    // (KC + legacy single-ratio runs).
    // Restore/undo/redo stamp the current object's measured geometry here;
    // the parent generation's ratio remains historical and is not rewritten.
    aspectRatio: varchar("aspect_ratio", { length: 10 }),
    // Whether this library entry is a still image (default, the historical
    // case) or a rendered video (e.g. a Video Express Max final cut saved
    // into My Ads). Video rows store their durable URL in `videoUrl` and
    // leave the image columns null.
    mediaType: varchar("media_type", { length: 20 }).notNull().default("image"),
    // Durable (already-R2-hosted) video URL for `mediaType="video"` rows.
    // Null for image rows.
    videoUrl: text("video_url"),
    // For `mediaType="slideshow"` rows (a whole Slideshow Express run saved
    // as ONE library entry): the ordered slide set. Each entry stores the
    // source slide id (for idempotent re-saves), the durable R2 public URL,
    // and the R2 object key (for storage housekeeping). The row's
    // imageObjectKey / imageUrlOriginal always mirror the FIRST slide so
    // every image-only consumer keeps a usable cover image. Null for
    // image/video rows.
    slideImages: jsonb("slide_images").$type<Array<{ slideId: string; url: string; objectKey: string }>>(),
    // The Fal URL we got back from /api/generate-campaign-ad-image. May
    // expire over time. Always populated when status="ready".
    imageUrlOriginal: text("image_url_original"),
    // R2 object key, populated on Save. When this is set, displayUrl()
    // returns the durable R2 URL; otherwise it falls back to imageUrlOriginal.
    imageObjectKey: text("image_object_key"),
    imageContentType: varchar("image_content_type", { length: 100 }),
    imageSizeBytes: integer("image_size_bytes"),
    prompt: text("prompt"),
    // Fal queue request id, persisted as soon as fal.queue.submit() returns.
    // On worker resume after a crash, a row in `rendering` with this set
    // means we polled-not-resubmitted (no double Fal charge).
    falRequestId: varchar("fal_request_id", { length: 200 }),
    falEndpoint: text("fal_endpoint"),
    providerJournal: jsonb("provider_journal").$type<import("./campaign-gpt-image").CampaignImageJournal>(),
    // Task #1705 — "manual_upload" marks an ad the user uploaded themselves
    // (no template, no generation run, no billing; imageUrlOriginal is a
    // durable R2 URL written at upload time). Null on all generated rows.
    source: varchar("source", { length: 30 }),
    // Task #1705 follow-on — optional per-ad tags for MANUAL UPLOADS only
    // (real runs carry persona/product on the generation, and angle via
    // creativeAngleId on KC rows). Ids + name snapshots so tiles keep
    // their labels if the persona/product/angle is later renamed or
    // deleted. Always null on generated (non-manual) image rows.
    personaId: varchar("persona_id"),
    personaNameSnapshot: varchar("persona_name_snapshot", { length: 255 }),
    productId: varchar("product_id"),
    productNameSnapshot: varchar("product_name_snapshot", { length: 255 }),
    // Manual uploads share a synthetic parent generation, so their promoted
    // offer must live on the child ad just like their per-ad product tag.
    offerId: varchar("offer_id"),
    offerNameSnapshot: varchar("offer_name_snapshot", { length: 255 }),
    // Task #3098 — the user who actually uploaded this manual ad. Manual
    // uploads share a per-brand synthetic parent generation, so the parent's
    // createdBy is not an attribution source for an individual ad. Keep this
    // nullable for legacy rows and set it to NULL when the user is deleted;
    // readers must never fall back to the synthetic parent for manual rows.
    uploadedBy: varchar("uploaded_by").references(() => users.id, { onDelete: "set null" }),
    // pending | rendering | ready | failed | cancelled
    //   pending   -> not yet claimed by a worker (cancellable)
    //   rendering -> a worker has claimed and is talking to Sim/Fal
    //                (cancel leaves these alone; in-flight Fal completes)
    //   ready/failed/cancelled -> terminal
    // Task #1777 — which engine ACTUALLY rendered this ad's base creative
    // ("gpt-image-2" | "nano-banana-2" | "seedream-4" | "flux-2-pro"),
    // recorded at the ready transition so engine-override test
    // output can be compared side-by-side against default output (usage_logs
    // alone can't join back per ad). For 9:16 deliverables this is the engine
    // of the 4:5 BASE render (the safe-zone extender's bria/expand outpaint
    // pass is mechanical and engine-independent). Null on legacy rows,
    // videos, manual uploads, and failed renders.
    renderedEngine: varchar("rendered_engine", { length: 30 }),
    safeZonePlan: jsonb("safe_zone_plan").$type<import("./ad-safe-zone-plan").StoredAdSafeZonePlan>(),
    // Retired. Columns kept so the schema matches migration 0458. Not read or written.
    nativeSafeZoneState: jsonb("native_safe_zone_state"),
    nativeCompositionState: jsonb("native_composition_state"),
    // Task #1858 — 9:16 safe-zone band outcome, recorded at the ready
    // transition so prod frequency per cause is measurable per saved ad:
    //   "outpainted"        — bands are AI-outpainted (bandAttempts = which
    //                         expand pass shipped)
    //   "qa_downgraded"     — every attempt was QA-flagged; flat-wash seed
    //                         bands shipped
    //   "failure_fallback"  — every attempt failed (Bria error/timeout);
    //                         flat-wash seed bands shipped
    //   "person_seam_fallback": cropped head at outer edge; no Bria attempt,
    //                            flat-wash seed bands shipped
    // Operational provenance for the current image, not proof derived from
    // dimensions. Restore/undo/redo set bandSource="restored_unknown" and clear
    // bandAttempts when the selected object's band history is unknown, preventing
    // dimensions-only extraction. Historical render plans/outcomes are retained.
    // Otherwise null on non-9:16 rows, legacy rows, videos, and manual uploads.
    bandSource: varchar("band_source", { length: 24 }),
    bandAttempts: integer("band_attempts"),
    portraitOutcome: jsonb("portrait_outcome").$type<import("./safe-zone-portrait").SafeZonePortraitOutcome>(),
    status: varchar("status", { length: 50 }).notNull().default("pending"),
    errorMessage: text("error_message"),
    isSaved: boolean("is_saved").notNull().default(false),
    isWinner: boolean("is_winner").notNull().default(false),
    tags: text("tags").array(),
    savedAt: timestamp("saved_at"),
    archivedAt: timestamp("archived_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("generated_ads_brand_idx").on(table.brandId),
    index("generated_ads_generation_idx").on(table.generationId),
    index("generated_ads_project_idx").on(table.projectId),
    index("generated_ads_ad_unit_idx").on(table.adUnitTypeId),
    index("generated_ads_uploaded_by_idx").on(table.uploadedBy),
    index("generated_ads_status_idx").on(table.status),
  ],
);

export const creativeAdMatches = pgTable(
  "creative_ad_matches",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    externalAdId: text("external_ad_id").notNull(),
    generatedAdId: varchar("generated_ad_id").notNull().references(() => generatedAds.id, { onDelete: "cascade" }),
    confirmedBy: varchar("confirmed_by").references(() => users.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("creative_ad_matches_brand_external_uniq").on(table.brandId, table.externalAdId),
    index("creative_ad_matches_generated_ad_idx").on(table.generatedAdId),
  ],
);

export const creativePerformanceIterationSources = pgTable(
  "creative_performance_iteration_sources",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    externalAdId: text("external_ad_id").notNull(),
    mediaType: varchar("media_type", { length: 20 }).notNull(),
    sourceName: text("source_name").notNull(),
    sourceUrl: text("source_url").notNull(),
    // Version 2 image sources are selected by decoding every trustworthy
    // candidate and keeping the largest pixel area. The persisted version lets
    // an existing v1 preview snapshot upgrade exactly once.
    sourceQualityVersion: integer("source_quality_version").notNull().default(1),
    objectKey: text("object_key"),
    durableUrl: text("durable_url"),
    contentType: varchar("content_type", { length: 128 }),
    sizeBytes: integer("size_bytes"),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    errorMessage: text("error_message"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("creative_perf_iteration_sources_brand_external_uq").on(table.brandId, table.externalAdId),
    index("creative_perf_iteration_sources_brand_status_idx").on(table.brandId, table.status),
  ],
);

export const creativePerformanceIterationSourceOrphans = pgTable(
  "creative_performance_iteration_source_orphans",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    objectKey: text("object_key").notNull().unique(),
    durableUrl: text("durable_url").notNull(),
    reason: varchar("reason", { length: 32 }).notNull(),
    status: varchar("status", { length: 20 }).notNull().default("quarantined"),
    firstSeenAt: timestamp("first_seen_at").defaultNow(),
    purgedAt: timestamp("purged_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_perf_source_orphans_status_idx").on(table.status, table.firstSeenAt),
  ],
);

// Durable, brand-scoped creative-family graph. A node is deliberately distinct
// from generated_ads: it can retain an immutable historical relationship even
// when the ad is archived, and it never uses names, URLs, or timestamps as
// evidence of ancestry.
export const creativeLineageNodes = pgTable(
  "creative_lineage_nodes",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    generatedAdId: varchar("generated_ad_id").notNull().references(() => generatedAds.id, { onDelete: "restrict" }),
    rootNodeId: varchar("root_node_id"),
    immediateParentNodeId: varchar("immediate_parent_node_id"),
    generationDepth: integer("generation_depth"),
    generationBatchId: varchar("generation_batch_id"),
    iterationDirection: text("iteration_direction"),
    // native | imported_verified | backfilled_explicit | unknown
    provenance: varchar("provenance", { length: 32 }).notNull().default("unknown"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("creative_lineage_nodes_generated_ad_uq").on(table.generatedAdId),
    index("creative_lineage_nodes_brand_root_idx").on(table.brandId, table.rootNodeId),
    index("creative_lineage_nodes_brand_parent_idx").on(table.brandId, table.immediateParentNodeId),
    index("creative_lineage_nodes_batch_idx").on(table.generationBatchId),
  ],
);

// Every explicitly known contributing source is retained. immediate_parent is
// meaningful only for a single direct parent; multi-source generations carry
// all source edges without inventing a primary source.
export const creativeLineageEdges = pgTable(
  "creative_lineage_edges",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    childNodeId: varchar("child_node_id").notNull().references(() => creativeLineageNodes.id, { onDelete: "cascade" }),
    sourceNodeId: varchar("source_node_id").notNull().references(() => creativeLineageNodes.id, { onDelete: "restrict" }),
    // immediate_parent | contributing_source | imported_source | backfilled_explicit
    relation: varchar("relation", { length: 32 }).notNull(),
    evidence: jsonb("evidence").notNull().default(sql`'{}'::jsonb`),
    provenance: varchar("provenance", { length: 32 }).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("creative_lineage_edges_unique_relation_uq").on(table.childNodeId, table.sourceNodeId, table.relation),
    index("creative_lineage_edges_brand_child_idx").on(table.brandId, table.childNodeId),
    index("creative_lineage_edges_brand_source_idx").on(table.brandId, table.sourceNodeId),
  ],
);

// A verified external identity may be linked explicitly to a known creative.
// Launches remain in platform_ad_launch_lineage; this table never replaces or
// coalesces append-only launch records.
export const creativeExternalLinks = pgTable(
  "creative_external_links",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    creativeNodeId: varchar("creative_node_id").notNull().references(() => creativeLineageNodes.id, { onDelete: "cascade" }),
    externalPlatformAdId: varchar("external_platform_ad_id"),
    platform: varchar("platform", { length: 32 }).notNull(),
    accountId: varchar("account_id", { length: 64 }).notNull(),
    campaignId: varchar("campaign_id", { length: 64 }),
    adsetId: varchar("adset_id", { length: 64 }),
    adId: varchar("ad_id", { length: 64 }).notNull(),
    creativeId: varchar("creative_id", { length: 64 }),
    status: varchar("status", { length: 20 }).notNull().default("linked"),
    evidence: jsonb("evidence").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("creative_external_links_exact_identity_uq")
      .on(table.brandId, table.creativeNodeId, table.platform, table.accountId, table.adId, sql`coalesce(${table.creativeId}, '')`),
    index("creative_external_links_node_idx").on(table.brandId, table.creativeNodeId),
  ],
);
export const weeklyDeliveries = pgTable(
  "weekly_deliveries",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    title: varchar("title", { length: 255 }).notNull(),
    note: text("note"),
    status: varchar("status", { length: 20 }).notNull().default("draft"),
    dueAt: timestamp("due_at"),
    publishedAt: timestamp("published_at"),
    closedAt: timestamp("closed_at"),
    revokedAt: timestamp("revoked_at"),
    revokedBy: varchar("revoked_by").references(() => users.id, { onDelete: "set null" }),
    createdBy: varchar("created_by").notNull().references(() => users.id),
    // Idempotency markers for the standalone day-3/day-5 reminder job.
    reminderDaysSent: jsonb("reminder_days_sent").$type<number[]>().notNull().default(sql`'[]'::jsonb`),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("weekly_deliveries_brand_idx").on(table.brandId),
    check("weekly_deliveries_status_check", sql`${table.status} IN ('draft', 'delivered', 'closed', 'revoked')`),
  ],
);
export const brandImageEngineOverrides = pgTable("brand_image_engine_overrides", {
  brandId: varchar("brand_id")
    .primaryKey()
    .references(() => brands.id, { onDelete: "cascade" }),
  engine: varchar("engine", { length: 30 }).notNull(),
  updatedBy: varchar("updated_by"),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const adNotes = pgTable(
  "ad_notes",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    adId: varchar("ad_id").notNull().references(() => generatedAds.id, { onDelete: "cascade" }),
    // Denormalized from the ad for fast brand-wide listing + access checks.
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    authorId: varchar("author_id").notNull(),
    assigneeId: varchar("assignee_id"),
    text: text("text").notNull(),
    // Snapshot of the ad's display image URL when the note was written, so the
    // UI can flag notes that refer to an earlier version after image edits.
    imageUrlSnapshot: text("image_url_snapshot"),
    // Optional pin: normalized 0..1 coordinates on the ad image. Null = an
    // overall note (not anchored to a spot).
    pinX: real("pin_x"),
    pinY: real("pin_y"),
    // Task #1730 — resolve/reopen lifecycle. Null resolvedAt = open (all
    // pre-existing notes are open). Any brand member can resolve or reopen.
    resolvedAt: timestamp("resolved_at"),
    resolvedBy: varchar("resolved_by"),
    // Shared master review evidence; independent of resolution and notification reads.
    // No historical evidence exists: legacy and newly created comments start unreviewed.
    reviewedAt: timestamp("reviewed_at"),
    reviewedBy: varchar("reviewed_by"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("ad_notes_ad_idx").on(table.adId),
    index("ad_notes_brand_idx").on(table.brandId),
    index("ad_notes_assignee_idx").on(table.assigneeId),
    index("ad_notes_brand_activity_idx").on(table.brandId, table.createdAt, table.id),
  ],
);

export const insertAdNoteSchema = createInsertSchema(adNotes).omit({
  id: true, createdAt: true, reviewedAt: true, reviewedBy: true,
});
export type AdNote = typeof adNotes.$inferSelect;
export type InsertAdNote = z.infer<typeof insertAdNoteSchema>;

// -----------------------------------------------------------------------------
// LANDING PAGES EXPRESS
// Standalone batch generator for landing pages. Independent from the existing
// landing_pages / landing_page_sections tables — fan-out lives in
// lp_express_pages and follows the adGenerations / generatedAds pattern.
// -----------------------------------------------------------------------------
export const lpExpressRuns = pgTable(
  "lp_express_runs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    userId: varchar("user_id").notNull(),
    personaId: varchar("persona_id").references(() => personas.id),
    productId: varchar("product_id").references(() => products.id),
    narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
    // Optional Sale & Discount. Snapshot the full row at submit time so
    // admin edits mid-run don't change the payload the worker sends to Sim.
    saleId: varchar("sale_id").references(() => salesDiscounts.id),
    salesDiscountSnapshot: jsonb("sales_discount_snapshot"),
    // Snapshots so the library stays readable after renames/archives.
    personaNameSnapshot: varchar("persona_name_snapshot", { length: 255 }),
    productNameSnapshot: varchar("product_name_snapshot", { length: 255 }),
    angleNameSnapshot: varchar("angle_name_snapshot", { length: 255 }),
    // queued | running | completed | partial | failed | cancelled
    status: varchar("status", { length: 50 }).notNull().default("queued"),
    errorMessage: text("error_message"),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("lp_express_runs_brand_idx").on(table.brandId),
    index("lp_express_runs_user_idx").on(table.userId),
  ],
);

export const lpExpressPages = pgTable(
  "lp_express_pages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    runId: varchar("run_id").notNull().references(() => lpExpressRuns.id, { onDelete: "cascade" }),
    // pending | rendering | ready | failed | cancelled
    status: varchar("status", { length: 50 }).notNull().default("pending"),
    // Sim workflow's structured JSON output (sections, copy, etc.).
    // Schema-less so the workflow can evolve without migrations.
    generatedContent: jsonb("generated_content"),
    errorMessage: text("error_message"),
    // User-configured buy-button destination for every CTA on the page.
    // NULL = not configured (default resolves to product PDP URL → brand URL);
    // "" = explicitly inert (buttons render with no href); a value = used verbatim.
    // Frozen into the published snapshot at publish time.
    checkoutUrl: varchar("checkout_url", { length: 1000 }),
    // Hero image carousel — three parallel variants generated via
    // Sim (prompt) → Fal (openai/gpt-image-2/edit) → R2 (durable).
    // idle | pending | queued | rendering | ready | failed
    heroImageStatus: text("hero_image_status"),
    heroImageError: text("hero_image_error"),
    // string[] — durable R2 URLs of the completed variants, written as
    // each one finishes (UI carousel pads empty slots with skeleton).
    heroImageUrls: jsonb("hero_image_urls"),
    // Array<{variantHint, prompt?, falRequestId?, url?, error?}> — used
    // by the worker for idempotent resume on restart/crash so we never
    // re-call Sim for a slot whose prompt is already stored, and never
    // re-submit to Fal for a slot whose falRequestId is already stored.
    heroImagePrompts: jsonb("hero_image_prompts"),
    // Version-history pointer: the `seq` of the snapshot that currently
    // represents the live page state. NULL = no snapshots recorded yet.
    // Undo/redo move this pointer; restore sets it to a chosen snapshot.
    currentSnapshotSeq: integer("current_snapshot_seq"),
    // Per-page opt-in: install the brand's Meta Pixel (brands.meta_pixel_id)
    // on this page's PUBLISHED output. Default OFF. The resolved pixel state is
    // frozen onto the published row at publish time, so flipping this only
    // affects the live page on the next republish.
    metaPixelEnabled: boolean("meta_pixel_enabled").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("lp_express_pages_run_idx").on(table.runId),
  ],
);

// -----------------------------------------------------------------------------
// lp_express_page_snapshots — version history for the LP Express builder editor.
// Every significant editor action records a full snapshot of the page's
// generatedContent + a small set of out-of-band page fields (checkoutUrl,
// heroImageUrls). The snapshots form a per-page linear stack ordered by `seq`
// (monotonic, never reused). `lpExpressPages.currentSnapshotSeq` points at the
// snapshot representing the live state; undo/redo move that pointer and restore
// rewrites the live page from a chosen snapshot. Capped at 50 per page (oldest
// pruned). A new edit after an undo truncates the "redo" branch (seq > pointer).
// -----------------------------------------------------------------------------
export const lpExpressPageSnapshots = pgTable(
  "lp_express_page_snapshots",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    pageId: varchar("page_id").notNull().references(() => lpExpressPages.id, { onDelete: "cascade" }),
    // Who triggered the action (best-effort; null for system/baseline).
    userId: varchar("user_id"),
    // Monotonic per-page ordering key (max(seq)+1). Survives pruning so order
    // and the pointer stay stable even after the oldest rows are dropped.
    seq: integer("seq").notNull(),
    // Friendly, server-derived label e.g. "Hero text edited", "Section deleted".
    actionLabel: varchar("action_label", { length: 255 }).notNull(),
    // Deep copy of the page's generatedContent at the time of the snapshot.
    content: jsonb("content").notNull(),
    // Out-of-band page fields not stored inside generatedContent:
    // { checkoutUrl, heroImageUrls }. Restored alongside content.
    pageFields: jsonb("page_fields"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("lp_express_page_snapshots_page_seq_idx").on(table.pageId, table.seq),
  ],
);

export const insertLpExpressPageSnapshotSchema = createInsertSchema(lpExpressPageSnapshots).omit({
  id: true,
  createdAt: true,
});
export type InsertLpExpressPageSnapshot = z.infer<typeof insertLpExpressPageSnapshotSchema>;
export type LpExpressPageSnapshot = typeof lpExpressPageSnapshots.$inferSelect;

export const insertLpExpressRunSchema = createInsertSchema(lpExpressRuns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  startedAt: true,
  completedAt: true,
});
export type InsertLpExpressRun = z.infer<typeof insertLpExpressRunSchema>;
export type LpExpressRun = typeof lpExpressRuns.$inferSelect;

export const insertLpExpressPageSchema = createInsertSchema(lpExpressPages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertLpExpressPage = z.infer<typeof insertLpExpressPageSchema>;
export type LpExpressPage = typeof lpExpressPages.$inferSelect;

// -----------------------------------------------------------------------------
// email_express_emails — Email Express generator output records.
// Mirrors the Express-tool conventions (persona × product × angle/flow). Each
// row is one drafted marketing email for a brand. Name snapshots keep the
// library readable after the source persona/product/angle is renamed/archived.
// Generated copy is stored inline (subject lines, preview, headline, body, CTA)
// plus a schema-less `blockCopy` JSON for the optional-block payloads so the
// copy generator can evolve without migrations. Klaviyo sending is out of
// scope: `status` is a stored value only ("draft" | "sent_to_klaviyo").
// -----------------------------------------------------------------------------
export const emailExpressEmails = pgTable(
  "email_express_emails",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    userId: varchar("user_id"),
    // Auto-generated from product + angle (campaign) or flow type (flow).
    name: varchar("name", { length: 500 }).notNull(),
    // campaign | flow
    type: varchar("type", { length: 20 }).notNull().default("campaign"),
    // draft | sent_to_klaviyo
    status: varchar("status", { length: 30 }).notNull().default("draft"),
    personaId: varchar("persona_id").references(() => personas.id),
    productId: varchar("product_id").references(() => products.id),
    personaNameSnapshot: varchar("persona_name_snapshot", { length: 255 }),
    productNameSnapshot: varchar("product_name_snapshot", { length: 255 }),
    // Campaign-only narrative angle (nullable; flows derive tone from flowType).
    narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
    narrativeAngleLabel: varchar("narrative_angle_label", { length: 255 }),
    // Flow-only: welcome | cart_abandonment | browse_abandonment | retention.
    flowType: varchar("flow_type", { length: 50 }),
    // Position within a multi-email flow sequence (1-based). Campaign emails
    // leave this null; flow children always carry an ordinal.
    flowSequence: integer("flow_sequence"),
    // Multi-email flows: the parent flow container this email belongs to. Null
    // for campaign emails. Legacy single-email flows are backfilled to a
    // one-email container (see migration 0041).
    flowId: varchar("flow_id").references((): any => emailExpressFlows.id),
    // Optional sale/discount. Stored as the chosen label + optional custom code
    // and percentage (no FK — Email Express uses a simple preset/custom toggle).
    saleDiscountLabel: varchar("sale_discount_label", { length: 255 }),
    saleCode: varchar("sale_code", { length: 100 }),
    salePercentage: integer("sale_percentage"),
    // Ordered list of selected block keys (always-included + optional).
    blocks: text("blocks").array(),
    // Per-email hidden (temporarily non-visible) block keys. Kept separate from
    // `blocks` so a hidden block stays in the list but is excluded from the
    // preview and export. Optional keys only (locked blocks can't be hidden).
    hiddenBlocks: text("hidden_blocks").array(),
    // Generated subject-line options (count = requested variants).
    subjectLineVariants: text("subject_line_variants").array(),
    previewText: text("preview_text"),
    headline: text("headline"),
    bodyCopy: text("body_copy"),
    ctaText: varchar("cta_text", { length: 255 }),
    // Per-optional-block generated copy. Schema-less so the generator can grow:
    //   { sellingPoints: string[], reviews: {quote,author}[],
    //     usVsThem: {us,them}[], didYouKnow: string,
    //     logoBannerTagline: string, productListIntro: string }
    blockCopy: jsonb("block_copy"),
    // Progressive copy generation (Email Express loads like LP Express): an email
    // is created immediately with templated copy + all blocks marked pending,
    // then a background copy worker fills each block (W1 brief → restyle → W3
    // hero → W2 subjects → W5 selling points → W7 us-vs-them) and flips per-block
    // status. The editor shows a skeleton per pending block and the final copy is
    // IDENTICAL to the old synchronous flow — only the timing/UX changes.
    //   pending | ready | failed   (null = legacy/synchronously-generated = ready)
    copyStatus: text("copy_status"),
    // Actionable message when copy generation failed (e.g. the W1 Brand Voice
    // Brief workflow blocked on missing brand inputs). Surfaced inline in the
    // editor with a Retry control. Null while pending/ready.
    copyError: text("copy_error"),
    // Per-block copy generation status, keyed by block KEY (hero/body/footer/…).
    // Written only by the copy worker + the generate route (which seeds every
    // block `pending`). The editor renders a skeleton for `pending`, an inline
    // failed state for `failed`, and the real copy for `ready`/absent.
    //   { [blockKey]: "pending" | "ready" | "failed" }
    blockCopyStatus: jsonb("block_copy_status"),
    // Snapshot of any Universal-library assets attached to this email (keyed by
    // asset type). Snapshotted at generate time so editing a library asset later
    // never changes an already-drafted email. See `EmailUniversalAssets`.
    universalAssets: jsonb("universal_assets"),
    // Editor link configuration. Controls how the email's CTAs are linked:
    //   { mode: "single" | "cta" | "multi"(legacy), sitewideUrl?: string,
    //     blockLinks?: { [blockKey]: url } }
    // "single" uses one email-wide URL for every section; "cta" gives each CTA
    // button (hero/body) its own URL with non-CTA sections falling back to the
    // email-wide URL. Legacy "multi" configs are normalized to "cta" at read
    // time. See `EmailLinkConfig` / `normalizeEmailLinkConfig`.
    linkConfig: jsonb("link_config"),
    // Per-section preview captures — the canonical send input for NON-polished
    // emails. The client renders the shared preview off-screen, captures ONE
    // retina PNG per visible section (pixel-identical to the editor preview),
    // uploads each to R2, and persists the ordered list here BEFORE the send is
    // claimed, so the async send worker + flow sends + a restart-resume retry
    // can compile the stacked-image email without a browser. Array of
    // `EmailCapturedBlock` ({ blockKey, url, linkUrl? }); refreshed on every
    // capture-backed send. Polished emails ignore this (polishBlocks win).
    capturedBlocks: jsonb("captured_blocks"),
    // Per-email footer edits, merged OVER the snapshotted Universal footer asset
    // at render time so editing the library asset / regenerating copy never
    // collides with them. Holds an optional effective social-links list (present
    // = fully replaces the snapshot list — label edits, URL edits, and per-item
    // deletion are all represented by replacing the list), an optional disclaimer
    // text override + a disclaimer-deleted flag, and an optional unsubscribe URL
    // override. The single source of footer edits; NEVER written by the generate
    // route, the regenerate route, the copy worker, or the snapshot logic. See
    // `EmailFooterOverrides` / `resolveEffectiveEmailFooter`.
    footerOverrides: jsonb("footer_overrides"),
    // Hero banner image — a brand-true lifestyle photo generated via Fal
    // (nano-banana-2) and stored durably on R2. Generated once and persisted
    // here so the editor preview shows the SAME image the send embeds (Fal is
    // non-deterministic, so a regen would never match). The send worker reuses
    // this when ready instead of regenerating.
    //   idle | pending | ready | failed
    heroImageStatus: text("hero_image_status"),
    heroImageUrl: text("hero_image_url"),
    heroImageError: text("hero_image_error"),
    // When the current generation attempt began — lets a startup resume tell a
    // genuinely in-flight render from one orphaned by a crash/restart.
    heroImageStartedAt: timestamp("hero_image_started_at"),
    // Task #2329 — a hero image swap (choose/upload, or an AI regen that
    // finished) landed while a POLISHED render exists, and the polished hero
    // still shows the OLD image. Swaps never auto-regenerate (user decision):
    // the editor surfaces an explicit "Apply new image" affordance driven by
    // this flag, which survives reloads. Set by /hero/set and the hero worker
    // (when polish is ready); cleared by the polish worker only after a run
    // that re-rendered the hero completes successfully.
    heroSwapPending: boolean("hero_swap_pending").notNull().default(false),
    // Per-instance selling-points block images. The W5 Selling Points workflow
    // produces both the block copy AND a Fal image prompt; the supporting image
    // is rendered via Fal (nano-banana-2) and stored durably on R2 — generated
    // once and persisted so the editor preview matches the sent email. Stored as
    // an ARRAY of objects keyed by block instance id so the same block can appear
    // more than once, each with its own image; the image prompt is persisted so
    // the worker / startup resume can re-render WITHOUT re-calling the
    // non-deterministic W5 copy workflow. See `EmailSellingPointsImage`. Written
    // only by the selling-points worker + the generate/regenerate routes (which
    // seed `pending` entries) — never through a generic create/update payload.
    sellingPointsImages: jsonb("selling_points_images"),
    // "Us vs. them" comparison image — a brand-true visual generated via Fal
    // (nano-banana-2) and stored durably on R2, mirroring the hero exactly. The
    // W7 (Us-vs-Them) sim.ai workflow drives BOTH the block copy (punchline +
    // us/them rows, persisted into blockCopy) AND the image prompt persisted
    // here. Generated once and reused in preview + send (Fal is non-deterministic
    // so a regen would never match). One image per email — us_vs_them is singular
    // at creation; rare duplicate instances share this single image.
    //   idle | pending | ready | failed
    usVsThemImageStatus: text("us_vs_them_image_status"),
    usVsThemImageUrl: text("us_vs_them_image_url"),
    usVsThemImageError: text("us_vs_them_image_error"),
    // The W7-produced image prompt (and "avoid" clause) persisted so the
    // render-only worker can produce the image WITHOUT re-calling Sim, and so a
    // startup resume can re-render an orphaned attempt from the same prompt. On
    // a W7 failure these hold a templated comparison prompt (graceful fallback).
    usVsThemImagePrompt: text("us_vs_them_image_prompt"),
    usVsThemNegativePrompt: text("us_vs_them_negative_prompt"),
    usVsThemImageStartedAt: timestamp("us_vs_them_image_started_at"),
    // "Comparison table" supporting image — a brand-true visual generated via Fal
    // (nano-banana-2) and stored durably on R2, mirroring the us-vs-them image
    // exactly. The W9 (Comparison Table) sim.ai workflow drives BOTH the block
    // copy (punchline + columns + feature rows, persisted into blockCopy) AND the
    // image prompt persisted here. Generated once and reused in preview + send
    // (Fal is non-deterministic so a regen would never match). One image per
    // email — comparison_table is singular at creation; rare duplicate instances
    // share this single image.
    //   idle | pending | ready | failed
    comparisonTableImageStatus: text("comparison_table_image_status"),
    comparisonTableImageUrl: text("comparison_table_image_url"),
    comparisonTableImageError: text("comparison_table_image_error"),
    // The W9-produced image prompt (and "avoid" clause) persisted so the
    // render-only worker can produce the image WITHOUT re-calling Sim, and so a
    // startup resume can re-render an orphaned attempt from the same prompt. On
    // a W9 failure these hold a templated comparison prompt (graceful fallback).
    comparisonTableImagePrompt: text("comparison_table_image_prompt"),
    comparisonTableNegativePrompt: text("comparison_table_negative_prompt"),
    comparisonTableImageStartedAt: timestamp("comparison_table_image_started_at"),
    // Per-instance did-you-know block images. The W8 Did You Know workflow
    // produces both the block copy (a fact) AND a Fal image prompt; the
    // supporting image is rendered via Fal (nano-banana-2) and stored durably on
    // R2 — generated once and persisted so the editor preview matches the sent
    // email. Stored as an ARRAY of objects keyed by block instance id so the
    // same block can appear more than once, each with its own image; the image
    // prompt is persisted so the worker / startup resume can re-render WITHOUT
    // re-calling the non-deterministic W8 copy workflow. See
    // `EmailDidYouKnowImage`. Written only by the did-you-know worker + the
    // generate/regenerate routes (which seed `pending` entries) — never through a
    // generic create/update payload.
    didYouKnowImages: jsonb("did_you_know_images"),
    // ---- Preset templates (Task #975) ---------------------------------------
    // When set, this email renders through the deterministic preset-template
    // path (shared/email-presets.ts): fixed section structure, brand-resolved
    // colors/fonts, no AI polish. Null = today's archetype-driven behavior,
    // byte-for-byte.
    presetTemplateKey: varchar("preset_template_key", { length: 50 }),
    // Per-preset user inputs captured at create time (myth to bust, villain,
    // awareness facts, second product snapshot, ...). Pass-through only for
    // factual/numeric content. See `EmailPresetInputs`.
    presetData: jsonb("preset_data"),
    // AI image slots for the preset's sections (hero lifestyle photo,
    // ingredient macro, ...). ARRAY of `EmailPresetImage` keyed by section
    // instance id, mirroring the selling-points/did-you-know image pattern
    // (prompt persisted for restart resume; URL R2-durable). Written only by
    // the preset image worker + generate/regenerate routes.
    presetImages: jsonb("preset_images"),
    // The structured Brand Voice Brief (W1 sim.ai workflow) generated at
    // email-creation time and stored as the JSON-stringified `voice_brief`
    // object. This is the single source of voice/tone: it's threaded into every
    // downstream workflow (W2 subject lines, W3 hero copy, W13 hero image) so the
    // send-time hero worker reuses the IDENTICAL brief (parity), and it's fed
    // back in as `currentCopy` when the user restyles the whole email's tone.
    voiceBrief: text("voice_brief"),
    // ---- Polish pipeline (AI-enhanced image-block email) --------------------
    // The "Polish" feature runs the fully-composed email through KIE
    // gpt-image-2 image-to-image to make it look like a world-class DTC
    // marketing email, then hard-overrides every logo/product region with the
    // original pixels and slices the result into horizontal image blocks. Those
    // blocks become the canonical preview/download/send for a polished email
    // (non-polished emails keep the MJML send). Server-managed: the polish
    // worker owns every field below; never set through a create/update payload.
    // The user never sees an intermediate image — the editor reveals the blocks
    // only when polishStatus flips to "ready".
    //   idle | pending | ready | failed   (null = never polished)
    polishStatus: text("polish_status"),
    // Fine-grained internal stage for resume/diagnostics (capture happens client
    // side before the claim): enhancing | restoring | slicing | validating.
    // Never surfaced to the user; null unless polishStatus = pending.
    polishStage: text("polish_stage"),
    polishError: text("polish_error"),
    // When the current polish attempt began — lets a startup resume tell a
    // genuinely in-flight job from one orphaned by a crash/restart.
    polishStartedAt: timestamp("polish_started_at"),
    // Durable R2 URL of the captured full-length original render (the AI input
    // and the source of truth for the hard pixel override). Required for resume.
    polishOriginalUrl: text("polish_original_url"),
    // Original render pixel dimensions — the enhanced output is resized back to
    // these so the protected-region coordinates line up for the override.
    polishOriginalWidth: integer("polish_original_width"),
    polishOriginalHeight: integer("polish_original_height"),
    // Protected regions in original PNG pixel space: an array of
    // { x, y, w, h, kind: "logo" | "product" | "text" }, derived from the rendered
    // DOM (logos/product images tagged; dense small-text blocks like reviews /
    // ingredient checklists resolved via emailBlockType). See `EmailPolishRegion`.
    polishProtectedRegions: jsonb("polish_protected_regions"),
    // Per-section boundary Y-positions (original PNG pixel space) sent by the
    // client to seed the block cuts (snapped to whitespace by the worker).
    polishSectionBoundaries: jsonb("polish_section_boundaries"),
    // KIE image-to-image task id, persisted so an in-flight enhancement can be
    // re-polled after a restart instead of restarting from scratch.
    polishKieTaskId: text("polish_kie_task_id"),
    // Durable R2 URL of the raw enhanced (pre-restoration) output, persisted so
    // a resume that finds a finished KIE task can continue from restoration.
    // Legacy (v11 stacked-image) column — no longer written by the v12 per-block
    // pipeline; kept for back-compat with rows polished before the rewrite.
    polishEnhancedUrl: text("polish_enhanced_url"),
    // Stage 1 of the v12 pipeline: the concrete text DESIGN SPEC (exact hex
    // palette, typography direction, visual motif, single background treatment)
    // generated ONCE per email and copied verbatim into every per-block render
    // prompt so all blocks share one cohesive look. Persisted and NEVER
    // re-derived (a re-polish reuses it), so the palette stays stable across
    // re-polishes. See `EmailPolishDesignSpec`. Server-managed.
    polishDesignSpec: jsonb("polish_design_spec"),
    // Per-email Sim-authored section-treatment device (block-driven emails
    // only): { key, name, promptTemplate, rationale? }. Generated ONCE by the
    // design-author Sim workflow at first polish and reused verbatim on every
    // re-polish so the email's look stays stable. Null = fall back to the
    // curated email_section_devices library (deterministic archetype pick).
    // Prompt text is still composed with the mandatory seam/margin constraints
    // in code. Server-managed.
    polishSectionDevice: jsonb("polish_section_device"),
    // The validated block manifest: an ordered array of
    // { index, y, height, url, hasProtectedRegion, alt }. Only persisted once
    // validation passes; the canonical polished output for preview/download/send.
    // See `EmailPolishBlock`.
    polishBlocks: jsonb("polish_blocks"),
    // Per-chunk enhancement plan + progress (resumable). Array of
    // { index, y0, y1, aspectRatio, taskId, url }. The worker redesigns each
    // near-2:3 chunk independently so the AI redesign never squishes a tall
    // email; persisted before/after each KIE call so a restart never rebills a
    // finished chunk. See `EmailPolishChunk`.
    polishChunks: jsonb("polish_chunks"),
    // Resolved per-section link targets in original PNG pixel space: an array of
    // { y, url } sent by the client so each sliced block carries its own CTA
    // link at send time. See `EmailPolishSectionLink`.
    polishSectionLinks: jsonb("polish_section_links"),
    // Content signature of the email at the moment it was polished — lets the
    // editor flag "edits since last polish" (re-polish to refresh) without
    // false positives from server-managed status churn. See
    // `computeEmailPolishSourceHash`.
    polishSourceHash: text("polish_source_hash"),
    // When the polish last reached "ready".
    polishReadyAt: timestamp("polish_ready_at"),
    // ---- Creative Designer / Art Director phase -----------------------------
    // The persisted CreativeDirection object produced BEFORE all copy/image
    // generation (stage 0 of the copy worker). Drives layout-variant selection,
    // hero art direction, supporting-image prompt steering, the restyle pass,
    // and the polish design spec. Null = legacy email generated before the
    // Creative Designer phase existed — every consumer must guard on presence
    // and behave exactly as before when absent. See `EmailCreativeDirection`.
    // Server-managed: written only by the creative-director service.
    creativeDirection: jsonb("creative_direction"),
    // pending | ready | failed (null = legacy / never attempted). Diagnostic —
    // a "failed" direction NEVER blocks generation (the pipeline proceeds
    // exactly like a legacy email).
    creativeDirectionStatus: text("creative_direction_status"),
    // Post-render critique: after the copy pipeline (and again after a polish)
    // completes, a lightweight art-director critique compares the composed
    // email against the CreativeDirection and records issues. Advisory only —
    // persisted for diagnostics/iteration, never blocks preview or send. See
    // `EmailPostRenderCritique`. Server-managed.
    postRenderCritique: jsonb("post_render_critique"),
    // ---- Brand Styling layer ------------------------------------------------
    // The persisted BrandStyleDirection object produced BEFORE CreativeDirection
    // (stage 0-a of the copy worker). Dresses the email in the brand's visual
    // identity (color roles, typography, CTA family, icon/motif style, photo
    // treatment, dividers/surfaces, footer) WITHOUT choosing layout/archetype.
    // Null = legacy email generated before the Brand Styling layer existed —
    // every consumer guards on presence and renders byte-identically when
    // absent. See `EmailBrandStyleDirection`. Server-managed.
    brandStyleDirection: jsonb("brand_style_direction"),
    // pending | ready | failed (null = legacy / never attempted). Diagnostic —
    // a "failed" brand style NEVER blocks generation (the pipeline renders with
    // the deterministic brand defaults exactly like a legacy email).
    brandStyleDirectionStatus: text("brand_style_direction_status"),
    // ---- Scoped regeneration ------------------------------------------------
    // Top-level copy fields + block instances the user has HAND-EDITED. Every
    // regeneration mode preserves these EXCEPT an explicit, confirmed "Start
    // over" (full). Keys: "headline" | "bodyCopy" | "ctaText" | "previewText"
    // | "block:<instanceId>" | "punchline:<instanceId>". Server-managed.
    userEditedFields: text("user_edited_fields")
      .array()
      .default(sql`ARRAY[]::text[]`),
    // Block INSTANCE ids the user has LOCKED — never regenerated except by a
    // confirmed "Start over". Server-managed (lock/unlock endpoints).
    lockedBlocks: text("locked_blocks").array().default(sql`ARRAY[]::text[]`),
    // AI image surface keys the user has LOCKED (never re-rendered except by a
    // confirmed "Start over"): "hero" | "us_vs_them" | "comparison_table" |
    // "selling_points:<instanceId>" | "did_you_know:<instanceId>". Real product
    // photos / logos are brand assets and are ALWAYS preserved regardless.
    lockedImages: text("locked_images").array().default(sql`ARRAY[]::text[]`),
    // Structured log of recent regeneration runs (mode, what it touched /
    // preserved), capped to the most recent entries. See EmailRegenLogEntry.
    regenLog: jsonb("regen_log"),
    // Mode-specific regeneration status while a scoped re-roll is in flight:
    // one of EMAIL_REGEN_STATUSES ("content_regenerating" | ...), or null when
    // idle. Set when a mode starts and lazily cleared once all the async work it
    // kicked (copy / image / polish) has settled. Drives the editor's per-mode
    // loading label and survives a refresh mid-regeneration.
    regenStatus: varchar("regen_status", { length: 30 }),
    projectId: varchar("project_id").references(() => projects.id),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    sentToKlaviyoAt: timestamp("sent_to_klaviyo_at"),
  },
  (table) => [
    index("email_express_emails_brand_idx").on(table.brandId),
    index("email_express_emails_persona_idx").on(table.personaId),
    index("email_express_emails_flow_idx").on(table.flowId),
  ],
);

// Multi-email flow container. A flow is an ordered sequence of emails
// (Trigger -> Email #1 -> #2 -> End). Ordering only lives here — delays are set
// by the user in Klaviyo. The flow owns the Klaviyo send status; its child
// emails stay `draft` and are sent together as one chained draft flow.
export const emailExpressFlows = pgTable(
  "email_express_flows",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    userId: varchar("user_id"),
    name: varchar("name", { length: 500 }).notNull(),
    // welcome | cart_abandonment | browse_abandonment | retention — drives the
    // Klaviyo flow trigger at send time.
    flowType: varchar("flow_type", { length: 50 }).notNull(),
    // Flow-level Klaviyo send status: draft | sending | sent_to_klaviyo |
    // send_failed.
    status: varchar("status", { length: 30 }).notNull().default("draft"),
    projectId: varchar("project_id").references(() => projects.id),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    sentToKlaviyoAt: timestamp("sent_to_klaviyo_at"),
  },
  (table) => [index("email_express_flows_brand_idx").on(table.brandId)],
);

export const insertEmailExpressFlowSchema = createInsertSchema(emailExpressFlows).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  sentToKlaviyoAt: true,
});
export type InsertEmailExpressFlow = z.infer<typeof insertEmailExpressFlowSchema>;
export type EmailExpressFlow = typeof emailExpressFlows.$inferSelect;

export const insertEmailExpressEmailSchema = createInsertSchema(emailExpressEmails).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  sentToKlaviyoAt: true,
  // Hero image is server-managed (generated by the hero worker), never set
  // through a create/update payload.
  heroImageStatus: true,
  heroImageUrl: true,
  heroImageError: true,
  heroImageStartedAt: true,
  heroSwapPending: true,
  // Selling-points images are server-managed (W5 prompt + selling-points worker),
  // never set through a create/update payload.
  sellingPointsImages: true,
  // Us-vs-them comparison image is server-managed (W7 + the us-vs-them image
  // worker), never set through a create/update payload.
  usVsThemImageStatus: true,
  usVsThemImageUrl: true,
  usVsThemImageError: true,
  usVsThemImagePrompt: true,
  usVsThemNegativePrompt: true,
  usVsThemImageStartedAt: true,
  // Comparison-table supporting image is server-managed (W9 + the comparison
  // table image worker), never set through a create/update payload.
  comparisonTableImageStatus: true,
  comparisonTableImageUrl: true,
  comparisonTableImageError: true,
  comparisonTableImagePrompt: true,
  comparisonTableNegativePrompt: true,
  comparisonTableImageStartedAt: true,
  // Did-you-know images are server-managed (W8 prompt + did-you-know worker),
  // never set through a create/update payload.
  didYouKnowImages: true,
  // Preset-template AI image slots are server-managed (the preset image
  // worker + generate route own them), never set through a create/update
  // payload. (presetTemplateKey/presetData ARE creatable inputs.)
  presetImages: true,
  // Progressive copy generation status is server-managed (the copy worker + the
  // generate route seed/flip these), never set through a create/update payload.
  copyStatus: true,
  copyError: true,
  blockCopyStatus: true,
  // Polish pipeline fields are server-managed (the polish worker owns them),
  // never set through a create/update payload.
  polishStatus: true,
  polishStage: true,
  polishError: true,
  polishStartedAt: true,
  polishOriginalUrl: true,
  polishOriginalWidth: true,
  polishOriginalHeight: true,
  polishProtectedRegions: true,
  polishSectionBoundaries: true,
  polishKieTaskId: true,
  polishEnhancedUrl: true,
  polishBlocks: true,
  polishChunks: true,
  polishSectionLinks: true,
  polishSourceHash: true,
  polishReadyAt: true,
  // Creative Designer / Art Director fields are server-managed (the
  // creative-director service owns them), never set through a create/update
  // payload.
  creativeDirection: true,
  creativeDirectionStatus: true,
  postRenderCritique: true,
  // Brand Styling fields are server-managed (the brand-style-director service
  // owns them), never set through a create/update payload.
  brandStyleDirection: true,
  brandStyleDirectionStatus: true,
  // Scoped-regeneration bookkeeping is server-managed (dedicated lock /
  // mark-edited endpoints + the regenerate route own these), never set through
  // a create/update payload.
  userEditedFields: true,
  lockedBlocks: true,
  lockedImages: true,
  regenLog: true,
  regenStatus: true,
});
export type InsertEmailExpressEmail = z.infer<typeof insertEmailExpressEmailSchema>;
export type EmailExpressEmail = typeof emailExpressEmails.$inferSelect;

// ---- Scoped regeneration (Task #863) ---------------------------------------
// The single ambiguous "Regenerate" is replaced by SIX clearly-scoped modes.
// Each mode preserves what it should not touch and invalidates only the render
// artifacts it affects. Shared by the client menu and the /regenerate route.
export const EMAIL_REGEN_MODES = [
  "content", // new copy; keep layout, design and images; preserve edited/locked text
  "layout", // keep copy; lay out in a materially different design
  "images", // re-generate AI visuals; keep copy, layout, real product photos/logos
  "hero", // re-compose just the hero (headline, offer, banner image)
  "section", // regenerate one selected section (see EmailSectionRegenScope)
  "full", // start over — everything fresh; keeps brand/product/persona/inputs
] as const;
export type EmailRegenMode = (typeof EMAIL_REGEN_MODES)[number];
export function isEmailRegenMode(x: unknown): x is EmailRegenMode {
  return typeof x === "string" && (EMAIL_REGEN_MODES as readonly string[]).includes(x);
}

// User-facing labels for each regeneration mode.
export const EMAIL_REGEN_LABELS: Record<EmailRegenMode, string> = {
  content: "Regenerate all content",
  layout: "Regenerate layout & design",
  images: "Regenerate images",
  hero: "Regenerate hero",
  section: "Regenerate this section",
  full: "Rewrite everything",
};

// One-line explanation shown as a tooltip/subtitle for each mode.
export const EMAIL_REGEN_DESCRIPTIONS: Record<EmailRegenMode, string> = {
  content:
    "Rewrite all the copy across the whole email, keeping the current layout, design and images. Your hand-edited and locked text is preserved.",
  layout:
    "Keep all the copy, but lay the email out in a materially different layout and design.",
  images:
    "Re-generate the AI visuals only — copy, layout, real product photos and logos stay the same.",
  hero: "Re-compose just the hero section (headline, offer and banner image).",
  section: "Regenerate only the section you have selected.",
  full: "Rewrite everything — new copy, design and images. Keeps the brand, product, persona and inputs, but replaces everything else, including your edits.",
};

// Sub-scope for the section ("Regenerate this section") mode.
export const EMAIL_SECTION_REGEN_SCOPES = [
  "copy",
  "layout",
  "image",
  "full",
] as const;
export type EmailSectionRegenScope = (typeof EMAIL_SECTION_REGEN_SCOPES)[number];
export function isEmailSectionRegenScope(x: unknown): x is EmailSectionRegenScope {
  return (
    typeof x === "string" &&
    (EMAIL_SECTION_REGEN_SCOPES as readonly string[]).includes(x)
  );
}

// One structured entry in `regenLog` — what a single regeneration run did.
// Captures not just the mode but the concrete telemetry the spec calls for:
// what was preserved vs. regenerated, which render artifacts/hashes were
// invalidated, and which providers/pipeline stages ran.
export interface EmailRegenLogEntry {
  mode: EmailRegenMode;
  at: string; // ISO timestamp
  block?: string | null; // section mode: the instance regenerated
  scope?: EmailSectionRegenScope | null; // section mode sub-scope
  slant?: EmailHeroSlant | null; // hero mode: chosen emphasis, if any
  detail?: string; // human-readable summary of what was touched/preserved
  // Structured telemetry (all optional; omitted when empty):
  preserved?: string[]; // what was intentionally kept (copy fields, blocks, images, locks)
  regenerated?: string[]; // what was re-rolled (copy, layout, images, direction, ...)
  invalidated?: string[]; // render artifacts dropped/re-kicked (polish, image surfaces, ...)
  // Concrete content-hash deltas dropped by this run — e.g. the polished
  // composite's source hash — so the log records WHICH artifact changed, not
  // just that it was invalidated. `before` is the pre-run hash (the "after" is
  // recomputed asynchronously by the render worker, so it is not known here).
  invalidatedHashes?: { artifact: string; before: string | null }[];
  providers?: string[]; // Sim | Creative Designer | Fal | KIE | Satori | MJML | ...
}
export const EMAIL_REGEN_LOG_CAP = 20;

// Canonical per-mode regeneration status labels persisted on `regenStatus`
// while a scoped re-roll is in flight (spec Step 1). Cleared to null once the
// async work the mode kicked has settled.
export const EMAIL_REGEN_STATUSES = [
  "content_regenerating",
  "layout_regenerating",
  "images_regenerating",
  "hero_regenerating",
  "section_regenerating",
  "full_regenerating",
] as const;
export type EmailRegenStatus = (typeof EMAIL_REGEN_STATUSES)[number];
export const EMAIL_REGEN_STATUS_LABELS: Record<EmailRegenStatus, string> = {
  content_regenerating: "Regenerating content…",
  layout_regenerating: "Trying a new design…",
  images_regenerating: "Regenerating images…",
  hero_regenerating: "Regenerating hero…",
  section_regenerating: "Regenerating section…",
  full_regenerating: "Starting over…",
};
export function emailRegenStatusFor(mode: EmailRegenMode): EmailRegenStatus {
  return `${mode}_regenerating` as EmailRegenStatus;
}
export function isEmailRegenStatus(x: unknown): x is EmailRegenStatus {
  return (
    typeof x === "string" &&
    (EMAIL_REGEN_STATUSES as readonly string[]).includes(x)
  );
}

// Optional hero emphasis for "Regenerate hero" (spec Step 4). Each maps to a
// steering phrase fed to the hero art-director / hero copy Sim as `feedback`.
export const EMAIL_HERO_SLANTS = [
  "product-led",
  "lifestyle",
  "graphic-poster",
  "editorial",
  "offer-focused",
] as const;
export type EmailHeroSlant = (typeof EMAIL_HERO_SLANTS)[number];
export function isEmailHeroSlant(x: unknown): x is EmailHeroSlant {
  return (
    typeof x === "string" && (EMAIL_HERO_SLANTS as readonly string[]).includes(x)
  );
}
export const EMAIL_HERO_SLANT_LABELS: Record<EmailHeroSlant, string> = {
  "product-led": "Product-led",
  lifestyle: "Lifestyle",
  "graphic-poster": "Graphic poster",
  editorial: "Editorial",
  "offer-focused": "Offer-focused",
};
export const EMAIL_HERO_SLANT_STEER: Record<EmailHeroSlant, string> = {
  "product-led":
    "Make the hero product-led: the product is the clear focal subject, staged prominently in a real environment with room for a bold headline.",
  lifestyle:
    "Make the hero lifestyle-led: a real person or real-life moment using the product, warm natural light, aspirational yet authentic.",
  "graphic-poster":
    "Make the hero a bold graphic poster: strong graphic/typographic treatment, high contrast, campaign-poster energy, the product integrated into the composition.",
  editorial:
    "Make the hero editorial: magazine-style restraint, elegant composition, generous negative space, refined type.",
  "offer-focused":
    "Make the hero offer-focused: lead with the promotion/deal, prominent offer framing, clear value and a sense of urgency.",
};

// Field-key helpers for the userEditedFields / locked* arrays so the client and
// server always agree on the key shape.
export function emailEditedBlockKey(instanceId: string): string {
  return `block:${instanceId}`;
}
export function emailEditedPunchlineKey(instanceId: string): string {
  return `punchline:${instanceId}`;
}
export function emailLockedImageKey(surface: string, instanceId?: string): string {
  return instanceId && instanceId !== surface
    ? `${surface}:${instanceId}`
    : surface;
}

// ---- Creative Designer / Art Director phase ---------------------------------

// One entry of the CreativeDirection section system: how a single block of the
// email should be art-directed. `blockKey` matches an entry of `email.blocks`
// (instance id). `layoutVariant`, when present, pins the block's layout variant
// (applied to blockCopy.instances at direction time, BEFORE any copy/image
// generation, so the variant-gated image kicks see the final variant).
export interface EmailCreativeSection {
  blockKey: string;
  // The block's role in the persuasion arc (e.g. "hook", "proof", "offer",
  // "education", "closing").
  role: string;
  // Short art direction for the block (module treatment, emphasis, rhythm).
  direction: string;
  // Optional pinned layout variant for the block (must be a valid variant for
  // the block type — validated before applying).
  layoutVariant?: string;
  // ── Chapter design plan: image/graphic placement (all optional — older
  // directions predate these; consumers guard on presence). ──
  // Whether this section's design depends on a photographic image.
  requiresImage?: boolean;
  // Whether this section's design depends on a designed graphic module
  // (icon row, comparison graphic, stat band, CTA band...).
  requiresGraphic?: boolean;
  // What the image IS for this section (e.g. "hero product image",
  // "lifestyle/product-in-use", "ingredient/proof visual", "category grid").
  imageRole?: string;
  // What the graphic module IS (e.g. "proof icon row", "comparison graphic",
  // "benefit chips", "CTA band", "icon footer row").
  graphicRole?: string;
  // Where the visual sits in the module (e.g. "full-width above copy",
  // "image + copy split", "band behind headline").
  placement?: string;
  // How the image and copy relate (e.g. "image anchors the claim beside it",
  // "UGC-style image paired with the review quote").
  imageToTextRelationship?: string;
}

// Motif intensity for decorative brand-motif usage — chosen by campaign
// archetype, never by brand style alone. High intensity is reserved for
// deliberately poster/graphic campaigns.
export type EmailMotifIntensity = "none" | "subtle" | "moderate" | "expressive";
// How image-driven the email design is (photographic imagery).
export type EmailImageDensity = "minimal" | "balanced" | "image_rich" | "image_led";
// How graphic-module-driven the design is (icon rows, comparison graphics,
// stat bands, designed CTA/proof modules).
export type EmailGraphicDensity = "minimal" | "balanced" | "graphic_rich" | "poster_led";
// Overall vertical pacing character, refining the spacing scale.
export type EmailSpacingRhythm =
  | "tight_energetic"
  | "balanced"
  | "editorial_airy"
  | "dense_offer"
  | "poster_led";

// Planned supporting visual for one of the EXISTING image surfaces. The plan
// steers the surface's Fal/Sim prompt — it never invents a new render surface,
// so preview/send parity is preserved.
export type EmailAssetSurface =
  | "hero"
  | "selling_points"
  | "did_you_know"
  | "us_vs_them"
  | "comparison_table";

export interface EmailAssetPlanItem {
  surface: EmailAssetSurface;
  // What the visual must accomplish (e.g. "campaign-poster hero with product
  // held in-scene, golden-hour kitchen").
  purpose: string;
  // Concrete steering appended to the surface's image prompt.
  promptSteer: string;
  // Steering for what must NOT appear.
  avoid?: string;
  // Whether this supporting visual is REQUIRED for the direction to be
  // fulfilled. Required surfaces are verified against the persisted image
  // columns by the post-render critique; a missing required visual is flagged
  // as a deterministic issue (advisory — never blocks delivery).
  required?: boolean;
}

export const EMAIL_HERO_COMPOSITION_ARCHETYPES = [
  "centered_product",
  "rule_of_thirds_scene",
  "flat_lay_top_down",
  "in_hand_human_moment",
  "macro_texture_detail",
  "environmental_wide",
  "graphic_color_block",
  "ingredient_deconstructed",
] as const;

// Canonical hero LOCKUP arrangements for block-driven (fixed-layout) template
// emails: how the composited brand-name/headline/CTA text lockup sits over the
// clean full-bleed hero photo. Rotated by the creative director (LRU over the
// brand's recent picks) so consecutive emails don't all use the same
// top-centered band. Legacy directions without a `lockupArrangement` render
// the original top_center arrangement byte-identically.
export const EMAIL_HERO_LOCKUP_ARRANGEMENTS = [
  "top_center", // legacy: centered lockup near the top, top scrim
  "top_left", // left-aligned lockup near the top, top scrim
  "bottom_left", // left-aligned lockup anchored at the bottom, bottom scrim
  "bottom_center", // centered lockup anchored at the bottom, bottom scrim
] as const;
export type EmailHeroLockupArrangement =
  (typeof EMAIL_HERO_LOCKUP_ARRANGEMENTS)[number];
export type EmailReferenceMode =
  | "none"
  | "inspiration_only"
  | "starter_layout_archetype"
  | "uploaded_reference_layout";

// The persisted output of the Creative Designer / Art Director phase. Produced
// once per email BEFORE all copy/image generation and consumed by: layout
// variant assignment, the restyle pass, the W13 hero art director, the
// supporting-image prompt seeds, and the polish design spec.
export interface EmailCreativeDirection {
  // Classified campaign intent this direction was designed for (education |
  // sale | product_highlight | review_led | announcement | welcome |
  // cart_abandonment | browse_abandonment | retention | ...).
  campaignType?: string;
  // One-paragraph creative thesis: the single idea the email is built around.
  creativeThesis: string;
  // How the reference layers were used for THIS email (see EmailReferenceMode).
  referenceMode?: EmailReferenceMode;
  // Why the reference mode / starter layout was (or was not) used — the
  // designer's persisted decision rationale.
  starterLayoutDecision?: string | null;
  // The 1-3 reference design mechanics deliberately borrowed (generic
  // descriptions, never brand artwork/copy/colors).
  referenceMechanicsToBorrow?: string[];
  // What must NOT be copied from the references (literal layouts, exact copy,
  // reference brand palettes, artwork).
  referenceMechanicsToAvoid?: string[];
  // Key of the starter layout archetype this direction is grounded in (from
  // email_starter_layouts), or null when no archetype fit.
  archetypeKey: string | null;
  // Human-readable archetype name (snapshot — archetype rows can evolve).
  archetypeName: string | null;
  // Design-DNA mechanic keys the email should exhibit (from the reference
  // Design DNA catalog).
  designMechanics: string[];
  // Per-block art direction, in display order.
  sectionSystem: EmailCreativeSection[];
  // Hero art direction (threaded to the W13 hero art director as feedback).
  // The optional treatment fields (rounds after the original concept/
  // environment/mood/avoid quartet) make the creative director the real owner
  // of the hero's LOOK: when compositionArchetype is present the hero render
  // prompt is built FROM this plan instead of the fixed editorial style block
  // (see email-graphics buildHeroPromptFromPlan). Legacy plans without the
  // treatment fields keep the exact pre-treatment behavior.
  heroPlan: {
    concept: string;
    environment: string;
    mood: string;
    avoid: string;
    // Canonical composition treatment (one of EMAIL_HERO_COMPOSITION_ARCHETYPES),
    // rotated away from the brand's recent heroes so consecutive emails get
    // visibly different hero treatments.
    compositionArchetype?: EmailHeroCompositionArchetype;
    // Concrete scene/setting for THIS treatment (richer than `environment`).
    setting?: string;
    // Lighting treatment (e.g. "hard directional sunlight, crisp shadows").
    lighting?: string;
    // How the brand palette is handled in-scene (e.g. "monochrome wash of the
    // brand's deep green", "neutral scene with one saturated accent").
    colorTreatment?: string;
    // Props / texture / styling ingredients for the scene.
    props?: string;
    // Block-driven (fixed-layout) template emails only: how the composited
    // text lockup (brand name / headline / CTA) is arranged over the hero
    // photo. Absent = legacy top_center arrangement, byte-identical.
    lockupArrangement?: EmailHeroLockupArrangement;
  } | null;
  // Planned supporting visuals mapped onto existing image surfaces.
  assetPlan: EmailAssetPlanItem[];
  // CTA rhythm instruction (e.g. "bold rectangular CTA after every major
  // section, identical label").
  ctaRhythm: string;
  // Color/band rhythm instruction for the polish design spec.
  colorRhythm: string;
  // Voice/copy steering folded into the restyle pass.
  copyDirection: string;
  // Hard "never do" list (enforced by the pre-render critique gate).
  mustAvoid: string[];
  // ── Brand/persona-as-ingredients decoupling (all optional — older
  // directions predate these fields and every consumer guards on presence).
  // Brand and persona provide CONSTRAINTS and INGREDIENTS (palette,
  // typography, tone, appropriate imagery, proof emphasis) — they must NOT
  // dictate the layout structure / module system / overall visual style.
  // What the brand contributes to this design (colors, fonts, logo usage,
  // tone limits, category context).
  brandConstraints?: string[];
  // What the persona contributes (what proof matters, claims to emphasize,
  // what to avoid saying).
  personaConstraints?: string[];
  // Why THIS campaign design archetype fits this campaign type / conversion
  // job — the designer's persisted system-selection rationale.
  designSystemReasoning?: string;
  // Explicit influence boundaries (anti-overfit — the critique gate rejects
  // directions where brand/persona dictated the whole design).
  whatBrandInfluences?: string[];
  whatPersonaInfluences?: string[];
  whatBrandShouldNotDictate?: string[];
  whatPersonaShouldNotDictate?: string[];
  // Campaign-job-driven image/graphic strategy: why each planned visual
  // exists for THIS conversion job (never brand/persona decoration).
  imageGraphicStrategy?: string;
  // Spacing system consumed by the polish render prompts. `scale` picks a
  // fixed per-module rule set (qualitative prompt language — the renderer is
  // an image model); `rhythm` refines the pacing character; `notes` is
  // optional per-email nuance.
  spacing?: {
    scale: "compact" | "balanced" | "airy";
    rhythm?: EmailSpacingRhythm;
    notes?: string;
  };
  // ── Motif / density controls (round 4 — all optional; older directions
  // predate these and every consumer guards on presence). ──
  // Decorative motif intensity, chosen by campaign archetype (not brand).
  motifIntensity?: EmailMotifIntensity;
  // How motifs may be used (accents supporting the archetype's hierarchy —
  // never the layout system, never competing with product imagery or copy).
  motifUsageRules?: string;
  // Image/graphic density decisions driven by campaign type + conversion job.
  imageDensity?: EmailImageDensity;
  graphicDensity?: EmailGraphicDensity;
  // How many supporting visuals (beyond the hero) the plan commits to.
  // Clamped to the number of image surfaces that actually exist for this
  // email — the plan never invents render surfaces.
  supportingImageCountTarget?: number;
  // How many designed graphic modules the sections commit to.
  supportingGraphicCountTarget?: number;
  // The photographic roles this email uses (e.g. "hero product image",
  // "lifestyle/product-in-use", "ingredient/proof visual").
  imageRoles?: string[];
  // The graphic-module roles (e.g. "proof icon rows", "comparison graphic",
  // "CTA band", "icon footer row").
  graphicRoles?: string[];
  // REQUIRED when imageDensity is "minimal" on a content-dense email: why
  // this campaign intentionally uses almost no supporting imagery.
  noAdditionalImagesReason?: string;
  // Pre-render critique gate result: "approved" passed as-is, "revised" means
  // the gate repaired/filled structural fields from the deterministic
  // baseline, "rejected_rewritten" means the semantic gate rejected the LLM's
  // direction (brand-restatement thesis, gray-card tendency, literal reference
  // copying, missing CTA rhythm/hero plan) and the offending fields were
  // rewritten (one LLM retry, then deterministic baseline). `notes` persists
  // every repair/rejection reason for diagnostics.
  critique: {
    status: "approved" | "revised" | "rejected_rewritten";
    notes: string[];
  };
  // "llm" = grok-enriched, "deterministic" = rule-based fallback (no key /
  // LLM failure). Diagnostic only.
  source: "llm" | "deterministic";
  createdAt: string;
}

// Advisory post-render critique persisted on the email after the copy pipeline
// (phase "copy") or a polish (phase "polish") completes. Never blocks.
export interface EmailPostRenderCritique {
  phase: "copy" | "polish";
  // 1-10 overall adherence to the CreativeDirection.
  score: number | null;
  issues: string[];
  notes: string;
  createdAt: string;
}

// ---- Brand Styling layer ----------------------------------------------------
// A persisted, brand-sourced BrandStyleDirection that dresses each email in the
// brand's visual identity WITHOUT choosing layout/archetype/density/order. Only
// the BOUNDED, validated token fields (hex role assignments + the enum unions
// below) are applied to rendered HTML by the ONE shared style resolver both
// render twins consume; the free-text descriptor fields feed image/graphic
// prompts and logging only (never emitted as CSS). Null = legacy email that
// renders byte-identically with the deterministic brand defaults.

// How strongly the brand styling appears — independent of the layout archetype.
export type EmailBrandStyleIntensity = "quiet" | "balanced" | "expressive";
// CTA button family tokens (bounded — email-client safe).
export type EmailCtaShape = "rectangle" | "rounded" | "pill";
export type EmailCtaTextCase = "uppercase" | "title" | "sentence";
export type EmailCtaSize = "compact" | "standard" | "large";
// Which resolved brand color plays a given role in rendering.
export type EmailColorRole = "accent" | "primary" | "dark" | "secondary";
export type EmailHeaderColorRole = "primary" | "dark" | "text" | "accent";
export type EmailDividerColorRole = "accent" | "primary" | "muted";
export type EmailFooterSurfaceRole = "dark" | "primary" | "light";
// Divider / rule line language (bounded).
export type EmailDividerStyle =
  | "none"
  | "thin_rule"
  | "thick_rule"
  | "dotted"
  | "double_rule"
  | "ornament";
// Surface / background treatment tokens (bounded — mapped to existing surfaces).
export type EmailSurfaceTreatment =
  | "flat"
  | "soft_tint"
  | "paper"
  | "panelled"
  | "bordered";
export type EmailShadowDepth = "none" | "subtle" | "soft" | "pronounced";
// Icon / graphic language (bounded — steers KIE/graphic prompts, never CSS).
export type EmailIconStyle =
  | "line"
  | "solid"
  | "duotone"
  | "hand_drawn"
  | "geometric"
  | "none";

export interface EmailBrandStyleDirection {
  // ── Bounded token layer applied to rendered HTML (validated at normalize). ──
  colorSystem: {
    // Role → hex (validated hex; defaulted from resolveEmailBrandStyle).
    primary: string;
    accent: string;
    background: string;
    dark: string;
    secondary?: string;
    light?: string;
    // Which resolved color fills each render role (bounded).
    ctaRole: EmailColorRole;
    headerRole: EmailHeaderColorRole;
    dividerRole: EmailDividerColorRole;
    // Free-text usage guidance for prompts/logging (never rendered as CSS).
    usageRules?: string;
  };
  typographySystem: {
    // Descriptive hints (mapped to loadable fonts by the resolver, same as the
    // brand's own typography direction — never raw CSS).
    headlineFont?: string;
    bodyFont?: string;
    accentFont?: string;
    headlineTreatment?: string;
    sectionHeaderTreatment?: string;
    bodyTreatment?: string;
    ctaTextTreatment?: string;
  };
  ctaSystem: {
    shape: EmailCtaShape;
    textCase: EmailCtaTextCase;
    size: EmailCtaSize;
    // Which colorSystem color fills the CTA.
    colorRole: EmailColorRole;
    primaryCtaStyle?: string;
    secondaryCtaStyle?: string;
    spacing?: string;
    usageRules?: string;
  };
  imageStyle: {
    photoMood?: string;
    lighting?: string;
    backgrounds?: string;
    colorTemperature?: string;
    productTreatment?: string;
    lifestyleTreatment?: string;
    supportingImageTreatment?: string;
    compositionPolish?: string;
    avoid?: string[];
  };
  graphicStyle: {
    iconStyle: EmailIconStyle;
    dividerStyle: EmailDividerStyle;
    motifIntensity: EmailMotifIntensity;
    lineStyle?: string;
    badgeStyle?: string;
    illustrationStyle?: string;
    // Bounded motif control (spec MOTIF CONTROL): which motifs are allowed,
    // where they may appear, and what they must not do.
    allowedMotifs?: string[];
    motifPlacementRules?: string;
    usageRules?: string;
  };
  surfaceSystem: {
    backgroundTreatment: EmailSurfaceTreatment;
    panelTreatment: EmailSurfaceTreatment;
    shadowDepth?: EmailShadowDepth;
    rulesAndBorders?: string;
    textureUse?: string;
    avoid?: string[];
  };
  footerStyle: {
    // Which surface the footer paints from.
    footerRole: EmailFooterSurfaceRole;
    logoTreatment?: string;
    iconRowTreatment?: string;
    linkTreatment?: string;
    campaignCloseTreatment?: string;
  };
  // The flexible-brand-system strategy (stable vs flexible vs variation vs
  // sameness-risk) — keeps brand styling a design system with multiple valid
  // expressions, never a fixed template.
  brandConsistencyStrategy: {
    stableElements: string[];
    flexibleElements: string[];
    variationRules: string[];
    samenessRisks: string[];
  };
  brandStyleIntensity: EmailBrandStyleIntensity;
  brandDo: string[];
  brandDont: string[];
  // Advisory brand-style critique (never blocks preview or send). Populated by
  // the post-render critique pass.
  critique?: {
    status: "approved" | "revised" | "flagged";
    notes: string[];
  };
  // "llm" = grok-enriched, "deterministic" = rule-based fallback. Diagnostic.
  source: "llm" | "deterministic";
  createdAt: string;
}

// The campaign design system an archetype defines — layout/style decisions
// driven by campaign type + conversion job (brand assets are applied INSIDE
// this system, never the reason for it). Stored on email_starter_layouts as
// one jsonb unit; all fields are short qualitative descriptors threaded into
// the Creative Designer prompt and the polish render prompts.
export interface EmailArchetypeDesignSystem {
  heroStyle: string;
  sectionRhythm: string;
  ctaRhythm: string;
  imageStrategy: string;
  graphicStrategy: string;
  headerStyle: string;
  footerStyle: string;
  // Which spacing scale this archetype defaults to + its pacing character.
  spacingRhythm: string;
  // Default spacing scale for the direction's spacing system.
  spacingScale: "compact" | "balanced" | "airy";
  moduleTypes: string[];
  // ── Round-4 defaults (optional so pre-existing designSystem blobs keep
  // working until the seed refreshes them). ──
  // Default vertical pacing character for the direction's spacing.rhythm.
  defaultSpacingRhythm?: EmailSpacingRhythm;
  // Default decorative-motif intensity for this archetype.
  motifIntensity?: EmailMotifIntensity;
  // Default image/graphic density for this campaign design system.
  imageDensity?: EmailImageDensity;
  graphicDensity?: EmailGraphicDensity;
  // Default supporting-visual count targets (pre-clamp; the direction
  // builder clamps to the surfaces that exist for the email).
  supportingImageTarget?: number;
  supportingGraphicTarget?: number;
}

// Brand-abstracted starter layout archetypes curated from high-performing DTC
// reference emails. Read-only reference layer for the Creative Designer phase
// (no user-facing UI). Seeded idempotently on startup.
export const emailStarterLayouts = pgTable("email_starter_layouts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Stable identifier (e.g. "education_deep_dive").
  key: varchar("key", { length: 100 }).notNull().unique(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description").notNull(),
  // Campaign intents this archetype fits: education | sale | product_highlight
  // | bundle | review_led | announcement | welcome | cart_abandonment |
  // browse_abandonment | retention.
  campaignTypes: text("campaign_types").array().notNull(),
  // Ordered section roles: [{ role, blockTypes: string[], direction,
  // preferredVariants?: Record<blockType, variant> }]
  sectionSequence: jsonb("section_sequence").notNull(),
  // Design-DNA mechanic keys this archetype exhibits.
  designMechanics: text("design_mechanics").array().notNull(),
  // Default hero plan + asset steering hints for the archetype.
  heroHint: text("hero_hint"),
  ctaRhythm: text("cta_rhythm"),
  colorRhythm: text("color_rhythm"),
  // Full campaign design system (EmailArchetypeDesignSystem) — nullable so
  // pre-existing rows keep working until the seed refreshes them.
  designSystem: jsonb("design_system").$type<EmailArchetypeDesignSystem | null>(),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertEmailStarterLayoutSchema = createInsertSchema(
  emailStarterLayouts,
).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  // drizzle-zod types jsonb generically — pin the design system shape so the
  // seed catalog stays type-checked against EmailArchetypeDesignSystem.
  designSystem: z
    .custom<EmailArchetypeDesignSystem>()
    .nullable()
    .optional(),
});
export type InsertEmailStarterLayout = z.infer<
  typeof insertEmailStarterLayoutSchema
>;
export type EmailStarterLayout = typeof emailStarterLayouts.$inferSelect;

export const emailSectionDevices = pgTable("email_section_devices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Stable identifier (e.g. "editorial_keylines"). "legacy_rustic_print" is
  // special: it always renders the pinned in-code legacy rustic string.
  key: varchar("key", { length: 100 }).notNull().unique(),
  name: varchar("name", { length: 255 }).notNull(),
  // Device-specific core prompt text (no header/tail — those are code-side).
  promptTemplate: text("prompt_template").notNull(),
  // Only enabled rows participate in render-time selection. Sim drafts start
  // disabled until an admin reviews and enables them.
  enabled: boolean("enabled").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  // "seed" | "sim" | "manual" — provenance for the admin surface.
  source: varchar("source", { length: 20 }).notNull().default("manual"),
  // Optional authoring rationale (e.g. the Sim workflow's description of the
  // visual language and why it's distinct from existing devices).
  rationale: text("rationale"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const emailPolishPromptTemplates = pgTable("email_polish_prompt_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Stable fragment key, e.g. "stage2.type_scale_lock".
  key: varchar("key", { length: 120 }).notNull().unique(),
  name: varchar("name", { length: 255 }).notNull(),
  // The fragment's prose with {{placeholder}} tokens (substituted in code).
  template: text("template").notNull(),
  // Disabled rows are ignored at render time (that key falls back to the
  // in-code default text), so an edit can be reverted without deleting it.
  enabled: boolean("enabled").notNull().default(true),
  // Bumped on every template edit — cheap provenance for "which wording
  // rendered this email" debugging.
  version: integer("version").notNull().default(1),
  // "seed" | "manual" — provenance for the admin surface.
  source: varchar("source", { length: 20 }).notNull().default("manual"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const emailPresetTemplates = pgTable("email_preset_templates", {
  // Stable template key ("bundle_pair", ...).
  key: varchar("key", { length: 50 }).primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description").notNull(),
  useCase: text("use_case"),
  // Snapshot of the template's section schema (EmailPresetSectionDef[]) for
  // reference/inspection — render code always uses the shared module.
  sectionSchema: jsonb("section_schema"),
  // Durable R2 URLs: the mirrored reference screenshot + a derived picker
  // thumbnail (never attached_assets / expiring URLs).
  referenceImageUrl: text("reference_image_url"),
  thumbnailUrl: text("thumbnail_url"),
  // Shared visual assets, e.g. { grainUrl } — the brand-tintable paper-grain
  // texture generated at seed time and stored on R2.
  assets: jsonb("assets"),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertEmailPresetTemplateSchema = createInsertSchema(
  emailPresetTemplates,
).omit({ createdAt: true, updatedAt: true });
export type InsertEmailPresetTemplate = z.infer<
  typeof insertEmailPresetTemplateSchema
>;
export type EmailPresetTemplate = typeof emailPresetTemplates.$inferSelect;

// Per-optional-block generated/edited copy stored in `blockCopy` (jsonb). The
// generator produces this and the editor edits it in place. Schema-less in the
// DB; this is the shared shape both ends agree on.
// One entry per visible selling-points block INSTANCE, stored in the email's
// `sellingPointsImages` jsonb array. The W5 workflow returns the image prompt
// alongside the copy; we persist the prompt so the worker (and a startup resume)
// can re-render the Fal image without re-running the non-deterministic W5 copy
// step. `url` holds the durable R2 image once `status` is "ready".
export interface EmailSellingPointsImage {
  instanceId: string;
  status: "pending" | "ready" | "failed";
  url: string | null;
  error: string | null;
  imagePrompt: string | null;
  negativePrompt: string | null;
  // ISO timestamp of when this render attempt began — lets a startup resume tell
  // a genuinely in-flight render from one orphaned by a crash/restart.
  startedAt: string | null;
}

// One entry per visible did-you-know block INSTANCE, stored in the email's
// `didYouKnowImages` jsonb array. The W8 workflow returns the image prompt
// alongside the copy; we persist the prompt so the worker (and a startup resume)
// can re-render the Fal image without re-running the non-deterministic W8 copy
// step. `url` holds the durable R2 image once `status` is "ready". Mirrors
// `EmailSellingPointsImage`.
export interface EmailDidYouKnowImage {
  instanceId: string;
  status: "pending" | "ready" | "failed";
  url: string | null;
  error: string | null;
  imagePrompt: string | null;
  negativePrompt: string | null;
  // ISO timestamp of when this render attempt began — lets a startup resume tell
  // a genuinely in-flight render from one orphaned by a crash/restart.
  startedAt: string | null;
}

// A protected region in the captured original render's PIXEL space (already
// scaled by the capture pixelRatio). Logos and product images are preserved
// pixel-for-pixel from the original after AI enhancement, so the worker crops
// these rectangles from the original and overwrites the enhanced output. Stored
// in `polishProtectedRegions` (jsonb array).
export interface EmailPolishRegion {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: "logo" | "product" | "text";
}

export interface EmailPolishImageSlot {
  // Normalized coordinates in the block raster's 0..1000 space. Once a slot has
  // passed strict localization/validation, later user-selected images reuse
  // this geometry instead of trying to infer it again from the replacement.
  left: number;
  top: number;
  right: number;
  bottom: number;
}
export interface EmailPolishBlock {
  index: number;
  y: number;
  height: number;
  url: string;
  hasProtectedRegion: boolean;
  alt?: string;
  // Per-block CTA link resolved from the section it covers. Falls back to the
  // email's default link at send time when absent.
  href?: string;
  // Stable instance key of the source block (hero / body / selling_points /
  // "<type>__<suffix>" / footer). Lets the editor map a manifest image back to
  // its block for per-block edit/regenerate. Absent on legacy manifests.
  blockKey?: string;
  // computeEmailPolishBlockHash of the content this image was rendered from.
  // The editor compares it to the live per-block hash to flag a block as needing
  // a re-render ("Pending"). Absent on legacy manifests → treated as up to date.
  contentHash?: string;
  // Non-text visual signature copied from the source chunk. The editor uses it
  // to distinguish a staged Body Copy image change from an ordinary copy edit,
  // so the image picker can show its deliberate Apply state precisely.
  visualHash?: string;
  // Whether the rendered Body Copy section contains an image that can be
  // replaced. Present only on Body Copy entries. Missing legacy metadata is
  // treated conservatively as unknown/no replacement affordance.
  hasImagery?: boolean;
  // Verified replaceable-image geometry tied to this manifest raster. This is
  // intentionally absent on fresh/legacy renders until a safe apply succeeds.
  imageSlots?: EmailPolishImageSlot[];
  // Set ONLY when a per-block (partial) regenerate of this block failed: the
  // prior image is kept so the polished email stays visible, and the editor shows
  // a localized "couldn't regenerate — retry" state for just this block instead
  // of collapsing the whole email into the global failed card. Cleared on the
  // next successful render of this block.
  renderError?: string;
  // POLISH_STITCH_VERSION baked in by the stitch stage that produced this
  // manifest. The editor compares it to the current version and, when older or
  // absent, silently triggers a FREE partial re-stitch (no re-render) so a
  // stitch improvement (e.g. tighter edge-band trimming) reaches already-polished
  // emails the next time they're opened.
  stitchVersion?: string;
}

export function resolveEmailBodyPolishBlock<
  T extends { blockKey?: string | null },
>(
  manifest: readonly T[],
  visibleKeys: readonly string[],
): T | undefined {
  // A mixed manifest is ambiguous: once any entry is keyed, require the Body
  // key rather than guessing positionally around a partial migration.
  if (manifest.some((block) => !!block.blockKey)) {
    return manifest.find((block) => block.blockKey === "body");
  }
  const bodyIndex = visibleKeys.findIndex(
    (key) => emailBlockType(key) === "body",
  );
  return bodyIndex >= 0 ? manifest[bodyIndex] : undefined;
}

// One per-BLOCK render record of a polished email, stored in `polishChunks`
// (jsonb). As of v12 the pipeline renders ONE image per visible block (not
// 1–3 grouped "chapters"), so each chunk corresponds 1:1 to a block: `index` is
// the block's display order, `taskId`/`url` persist KIE progress so a restart
// re-polls or skips a finished block instead of re-billing, and `y0`/`y1` are
// the block's top/bottom position in the final zero-gap stacked column (common
// width pixel space).
export interface EmailPolishChunk {
  index: number;
  y0: number;
  y1: number;
  aspectRatio: string;
  taskId: string | null;
  url: string | null;
  // True when the product-photo render was dropped — a content rejection OR a
  // persistent upstream outage — and the block fell back to a copy-only render
  // (no product) or, at worst, a neutral background scaffold, so one bad block
  // never fails the whole email.
  fallback?: boolean;
  // True only when EVEN the copy-only render kept failing and the block fell all
  // the way back to a BLANK background scaffold (no copy). The worker counts
  // these for its systemic-outage safety valve.
  blank?: boolean;
  // Stable instance key of the block this chunk renders (see EmailPolishBlock).
  // Required for partial (per-block) re-renders: the worker remaps carried urls
  // by blockKey across an edit/reorder. Absent on legacy chunks → the partial
  // route falls back to a full re-polish (which re-stamps every chunk's key).
  blockKey?: string;
  // computeEmailPolishBlockHash of the content rendered into `url`. Carried onto
  // the manifest entry; lets a partial regen reuse an unchanged block's image.
  contentHash?: string;
  // computeEmailPolishBlockVisualHash of the block's NON-TEXT render inputs
  // (block type + product photo URLs + pipeline version) at render time. On a
  // partial regen the worker compares it to the live visual hash: unchanged →
  // the edit was copy-only, so the block re-renders as a text-preserving EDIT of
  // its previous image (design kept identical) instead of a fresh redesign.
  // Absent on legacy chunks → the worker still DEFAULTS to the design-
  // preserving edit path (the prior image IS the design to keep) and backfills
  // visualHash on carried chunks so later product-image swaps render fresh.
  visualHash?: string;
  // Same normalized verified geometry as the visible manifest. Persisted on
  // the resumable chunk as well so partial-run crash recovery keeps it.
  imageSlots?: EmailPolishImageSlot[];
  // Partial runs clear a targeted chunk's `url` before rendering. These fields
  // retain the exact visible source and its degradation provenance so a process
  // restart can safely resume a design-preserving edit. They are removed after
  // the replacement succeeds. Missing provenance must fail closed rather than
  // recovering a potentially blank/degraded manifest raster.
  retrySourceUrl?: string;
  retrySourceFallback?: boolean;
  retrySourceBlank?: boolean;
}

// Stage 1 of the v12 polish pipeline: the concrete text DESIGN SPEC generated
// once per email, persisted in `polishDesignSpec`, and copied VERBATIM into
// every per-block render prompt so all blocks share one cohesive look. Exact
// hex palette + typography direction + a short visual motif + a single explicit
// background treatment. See `email-polish-prompt.ts`.
export interface EmailPolishDesignSpec {
  // The ONE named aesthetic point of view the design committed to (2-5 words,
  // e.g. "warm artisanal craft"). Used for the per-brand anti-repeat rotation:
  // recent specs' labels are fed back into the next spec generation so
  // consecutive emails land on genuinely different design systems. Optional
  // (older persisted specs predate it); the no-LLM defaults path fills
  // "brand-inspired classic".
  aestheticLabel?: string;
  // Exact 6-digit hex (with leading "#").
  backgroundHex: string;
  primaryTextHex: string;
  accentCtaHex: string;
  secondaryAccentHex: string;
  // Free-text typography direction for each text role.
  headlineTypography: string;
  bodyTypography: string;
  labelTypography: string;
  // 1–2 sentence visual motif unique to this email.
  motif: string;
  // The single, explicit background treatment used identically behind every
  // block (e.g. "a flat solid #faf7f0 field, edge to edge, no texture").
  // NOTE (Task #2287): when `sectionBackgrounds` below is present this is the
  // BASE tone's treatment — an INTENTIONAL relaxation of the historical
  // "identical behind EVERY block" contract: sections assigned "band" render
  // on `bandHex` instead, per the planned light/dark alternation.
  backgroundTreatment: string;
  // ── Task #2287 — master style frame + planned backgrounds ────────────────
  // All optional: older persisted specs (and specs from the not-yet-updated
  // Sim Design Spec workflow) simply lack them → legacy behavior (fail-open).
  // A FAL image prompt for the email's MASTER STYLE FRAME: a compact visual
  // comp (typography specimen in the actual headline face, palette swatches
  // in context, background band rhythm, one GENERIC placeholder module in the
  // chosen register). Emitted by the Sim Design Spec workflow.
  styleFramePrompt?: string;
  // Durable R2 URL of the rendered style frame. Rendered ONCE (on FAL) from
  // `styleFramePrompt` and persisted here so re-polish and per-block partial
  // regen reuse the same frame. Cleared with the whole spec on
  // `freshDesignSpec: true` ("Try a new design"), so it can never go stale
  // relative to its own spec.
  styleFrameUrl?: string;
  // Deep band tone for sections assigned "band" (exact 6-digit hex). Emitted
  // by the Sim Design Spec workflow alongside `sectionBackgrounds`.
  bandHex?: string;
  // Planned per-section background assignment, keyed by BLOCK TYPE (e.g.
  // "hero", "cta", "reviews"): "base" → backgroundHex, "band" → bandHex.
  // Unlisted types default to "base". Absent → every block on backgroundHex
  // (legacy behavior, byte-identical prompts).
  sectionBackgrounds?: Record<string, "base" | "band">;
}

// A resolved per-section CTA link in original PNG pixel space, stored in
// `polishSectionLinks` (jsonb). `y` is the section's top; `url` is its resolved
// link target. The worker assigns each sliced block the link of the topmost
// section it covers.
export interface EmailPolishSectionLink {
  y: number;
  url: string;
}

// Deterministic, key-order-independent JSON used to fingerprint email content.
function stablePolishStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((v) => stablePolishStringify(v)).join(",")}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stablePolishStringify(obj[k])}`)
    .join(",")}}`;
}

// Content signature of an email's user-visible, render-affecting fields. The
// polish route stores this on the email when a polish is claimed; the editor
// recomputes it from the live email row to flag "edits since last polish". It
// deliberately EXCLUDES server-managed status churn (copyStatus, polish*,
// hero*Status, …) so a status flip never reads as a content edit. Both ends call
// the SAME function over the SAME row shape, so the hash can't diverge.
// Bump when the polish PIPELINE itself changes (the KIE prompt, the output
// resolution tier, the chunking/slicing) rather than the email's content. It is
// folded into the source hash below so existing polished emails register as
// stale and re-polish ONCE on their next send — picking up the new output —
// without needing a manual re-polish control. v2: full-bleed prompt (kills the
// navy border) + 2K render (was a blurry 1K upscale). v3: stop protecting the
// reviews block so it polishes like every other block (2K keeps its small text
// sharp) + prompt now guards small testimonial text. v4: prompt anchored each
// chunk to the input background + forbade inventing motifs — REGRESSED quality
// (suppressed the elevation, blander output). v5: reset to a rich, high-contrast,
// world-class direction. v6: reframe the WHOLE email as ONE continuous
// full-length editorial photograph. v7: REVERSE v6 to a flat editorial-print
// look — one consistent flat cream / off-white background everywhere, photography
// only inside contained image blocks (never behind text), transitions via
// typography + spacing only, a single accent color used sparingly; per-chunk
// seam-safe full-bleed guards + hard-preserve logos/product images kept. v8: add
// an exact-text-reproduction prompt clause AND restore the hard pixel-override of
// dense small-text blocks (reviews + selling-points/ingredient checklists) so
// their crisp original pixels replace the AI render. v9: taller full-width hero
// (significant min-height + larger headline + more prominent product) in the
// captured source AND a footer-fully-in-frame capture guard (explicit full-height
// canvas + fonts.ready settle + post-capture footer-bottom assertion) so the
// unsubscribe/preferences/contact links are never cut off before the KIE send.
// v11: FROM-SCRATCH GENERATION. The polish no longer screenshots the rendered
// email and runs image-to-image over it (no client capture, no pixel-restore
// pass). Instead the worker reads the email's structured blocks + brand/product
// data server-side, assembles a detailed DTC art-direction prompt per "chapter"
// (1–3 grouped sections, each near a renderable ~2:3 ratio), and renders each
// chapter with KIE gpt-image-2-image-to-image using the brand's PRODUCT photos
// as reference (or a neutral cream scaffold when none exist). Chapters are
// stacked, parchment-textured, and auto-sliced on whitespace + chapter seams.
// v12: TWO-STAGE SEAMLESS, ONE IMAGE PER BLOCK. Chapter grouping, whitespace
// slicing and the parchment composite are GONE. Stage 1 generates a concrete
// text design spec (exact hex palette + typography + a single background
// treatment + a short visual motif) ONCE per email (persisted in
// `polishDesignSpec`, never re-derived). Stage 2 renders EVERY visible block as
// its OWN image against that spec copied verbatim into each prompt plus a
// repeated seamlessness instruction (no borders/frames/shadows/cards/vignettes/
// rounded corners/bands; clean matching-background edge bands), the block's
// exact copy, and the product reference where the block type carries a product
// shot. Blocks stack with zero gap; an automated seam check samples pixel strips
// above/below every boundary and regenerates the offending block on a mismatch;
// the reveal is all-or-nothing. Each image maps 1:1 to a source block, so each
// manifest entry carries that block's own CTA link.
// Baseline prompt history is in email-express-polish.md.
// NOTE (v16 kept): the logo-reference / no-invented-theming prompt changes and
// the universal full-width + large-typography rules deliberately ship WITHOUT
// a version bump — cached legacy renders are not mis-served by the old
// prompts, and a bump would flag every legacy polished email stale
// (re-polish/re-bill churn). New/edited blocks pick the rules up naturally.
// v18 (Task #2287): MASTER STYLE FRAME + PLANNED BACKGROUNDS. The design spec
// gains an optional style-frame prompt (rendered ONCE on FAL to a compact
// visual comp, persisted as styleFrameUrl) that every KIE block render then
// receives as an extra reference image so all blocks follow ONE concrete
// design instead of each reinterpreting the text spec. The spec may also
// emit per-section background assignments (base vs deep band) injected into
// each block's prompt; the seam check for assignment-carrying emails compares
// each block's edges against its OWN planned background instead of its
// neighbor. Old-shape specs (no new fields) keep the v17 behavior verbatim.
// v19 (Task #2496): section identity de-labeled from the per-block prompt —
// the interpolated human heading ("Render it as a single Body copy block…" /
// 'This block is the "Body copy" section') sometimes rendered as a visible
// section-name badge. The role now rides an explicitly non-renderable SECTION
// ROLE line, the anti-transcription guard bans section names/type labels, and
// QA treats the heading as disallowed. Bumped so existing polished emails
// re-polish with the de-labeled prompts on their next polish trigger.
export const EMAIL_POLISH_PIPELINE_VERSION = "19";

// Version of the STITCH stage ONLY — the deterministic edge-band trim + zero-gap
// stacking applied to already-rendered blocks. Kept SEPARATE from
// EMAIL_POLISH_PIPELINE_VERSION (and deliberately NOT folded into the polish
// source/block hashes) so a stitch-logic change can re-stack the EXISTING block
// images for FREE — via an on-view partial re-stitch — without invalidating any
// per-block render hash or triggering a paid KIE re-render. Bump when the
// trim/stacking changes a polished email's visible vertical spacing or its
// delivery-asset compatibility (for example, the 2×/1200px inbox-safe cap).
export const POLISH_STITCH_VERSION = "7";

export function computeEmailPolishSourceHash(e: {
  headline?: string | null;
  bodyCopy?: string | null;
  ctaText?: string | null;
  blockCopy?: unknown;
  blocks?: unknown;
  hiddenBlocks?: unknown;
  linkConfig?: unknown;
  footerOverrides?: unknown;
  heroImageUrl?: string | null;
  usVsThemImageUrl?: string | null;
  comparisonTableImageUrl?: string | null;
  productImageUrls?: unknown;
  productListImages?: unknown;
  sellingPointsImages?: unknown;
  didYouKnowImages?: unknown;
}, opts?: { legacyLinkFold?: boolean }): string {
  const urlsOf = (arr: unknown, key: string): string[] =>
    Array.isArray(arr)
      ? arr.map((x) => (x && typeof x === "object" ? String((x as any)[key] ?? "") : ""))
      : [];
  const norm = {
    pipeline: EMAIL_POLISH_PIPELINE_VERSION,
    headline: e.headline ?? "",
    bodyCopy: e.bodyCopy ?? "",
    ctaText: e.ctaText ?? "",
    blockCopy: e.blockCopy ?? null,
    blocks: e.blocks ?? null,
    hiddenBlocks: e.hiddenBlocks ?? null,
    // Task #2057: linkConfig is EXCLUDED — CTA link URLs are metadata. The
    // rendered pixels never depend on them (the send worker re-resolves the
    // live linkConfig at send time), so a link edit must never read as a
    // content change and trigger a re-polish/re-capture. `legacyLinkFold`
    // recomputes the PRE-#2057 formula (linkConfig folded in) so stored
    // hashes stamped under the old formula can be recognized and freely
    // re-stamped without any regeneration.
    ...(opts?.legacyLinkFold ? { linkConfig: e.linkConfig ?? null } : {}),
    footerOverrides: e.footerOverrides ?? null,
    heroImageUrl: e.heroImageUrl ?? "",
    usVsThemImageUrl: e.usVsThemImageUrl ?? "",
    comparisonTableImageUrl: e.comparisonTableImageUrl ?? "",
    productImageUrls: Array.isArray(e.productImageUrls) ? e.productImageUrls : [],
    productListImages: urlsOf(e.productListImages, "imageUrl"),
    sellingPointsImages: urlsOf(e.sellingPointsImages, "url"),
    didYouKnowImages: urlsOf(e.didYouKnowImages, "url"),
  };
  const json = stablePolishStringify(norm);
  let h = 5381;
  for (let i = 0; i < json.length; i++) {
    h = (((h << 5) + h) + json.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

// Content signature of a SINGLE block instance — only the fields that actually
// render into that block's polished image. The worker stamps it on the chunk +
// manifest entry when it renders the block; the editor recomputes it from live
// (locally-edited) state and compares to flag JUST that block as "needs a
// re-render" (Pending). Both ends call this over the SAME enriched email shape
// (the GET response is enriched with the product's resolved photos, which the
// editor overlays with its local copy edits), so the hashes can't diverge.
// Folds EMAIL_POLISH_PIPELINE_VERSION so a pipeline bump invalidates every
// block at once, exactly like the whole-email hash.
export function computeEmailPolishBlockHash(
  e: {
    headline?: string | null;
    bodyCopy?: string | null;
    ctaText?: string | null;
    blockCopy?: unknown;
    linkConfig?: unknown;
    footerOverrides?: unknown;
    saleDiscountLabel?: string | null;
    salePercentage?: number | null;
    saleCode?: string | null;
    productImageUrls?: unknown;
    productListImages?: unknown;
  },
  blockKey: string,
  opts?: { legacyLinkFold?: boolean },
): string {
  const urlsOf = (arr: unknown, key: string): string[] =>
    Array.isArray(arr)
      ? arr.map((x) => (x && typeof x === "object" ? String((x as any)[key] ?? "") : ""))
      : [];
  const type = emailBlockType(blockKey);
  const copy = resolveEmailInstanceCopy(e.blockCopy as any, blockKey);
  const bc = (e.blockCopy as any) ?? {};
  // Product blocks render the product photos; fold them so swapping a product
  // image re-flags only those blocks.
  const usesProduct = type === "hero" || type === "product_list";
  const bodyImage = resolveEmailBodyImagePreference(copy);
  // Strip fields that are handled separately to avoid double-counting in the hash:
  // - `bodyImage` for the body block (handled explicitly below with automatic→no-key rule)
  // - `imageTreatments` for ALL block types (eligible blocks handle it explicitly below;
  //   non-eligible blocks must not be affected by imageTreatments at all so their hashes
  //   stay invariant regardless of what imageTreatments might be stored on the instance).
  const copyForHash = Object.fromEntries(
    Object.entries(copy).filter(([k]) => {
      if (k === "imageTreatments") return false;
      if (k === "bodyImage" && type === "body") return false;
      return true;
    }),
  );
  const norm: Record<string, unknown> = {
    pipeline: EMAIL_POLISH_PIPELINE_VERSION,
    type,
    copy: copyForHash,
    punchline: bc.punchlines?.[blockKey] ?? "",
  };
  // Task #2057: link URLs are EXCLUDED — they never render into the block's
  // image (send-time href resolution reads the live linkConfig), so a link
  // edit must not flag the block "needs a re-render". `legacyLinkFold`
  // recomputes the PRE-#2057 formula for the free re-stamp heal.
  if (opts?.legacyLinkFold) {
    const lc = (e.linkConfig as any) ?? {};
    norm.link = {
      mode: lc.mode ?? "",
      own: lc.blockLinks?.[blockKey] ?? lc.blockLinks?.[type] ?? "",
      sitewide: lc.sitewideUrl ?? "",
    };
  }
  if (type === "hero") {
    norm.headline = e.headline ?? "";
    norm.ctaText = e.ctaText ?? "";
    norm.sale = {
      label: e.saleDiscountLabel ?? "",
      pct: e.salePercentage ?? null,
      code: e.saleCode ?? "",
    };
  }
  // The standalone CTA band renders the shared email-level button label
  // (email.ctaText) into its image, so its fingerprint must fold it in too —
  // otherwise editing the label flags only the hero and the band never
  // re-renders.
  if (type === "cta") norm.ctaText = e.ctaText ?? "";
  if (type === "body") {
    norm.bodyCopy = e.bodyCopy ?? "";
    // Missing and explicit "automatic" are intentionally byte-identical so
    // legacy polished emails do not become stale merely by opening/saving.
    if (bodyImage.mode !== "automatic") norm.bodyImage = bodyImage;
  }
  if (type === "footer") norm.footerOverrides = e.footerOverrides ?? null;
  if (usesProduct) {
    norm.productImageUrls = Array.isArray(e.productImageUrls) ? e.productImageUrls : [];
    norm.productListImages = urlsOf(e.productListImages, "imageUrl");
  }
  // Task #2556: fold non-automatic image treatments into the content hash so
  // selecting/clearing a treatment triggers a block re-render. Automatic/absent
  // add no key (byte-identical to the pre-#2556 hash for legacy emails).
  if (isEmailImageTreatmentEligible(type) && copy.imageTreatments) {
    const nonAuto: Record<string, unknown> = {};
    const productListTileCount =
      type === "product_list"
        ? (copy.productListType ??
            (urlsOf(e.productListImages, "imageUrl").length
              ? "bundle"
              : "range")) === "bundle"
          ? urlsOf(e.productListImages, "imageUrl").length
          : (Array.isArray(e.productImageUrls)
              ? e.productImageUrls.length
              : 0)
        : undefined;
    for (const [slot, t] of Object.entries(copy.imageTreatments)) {
      if (
        t &&
        t.mode !== "automatic" &&
        isEmailImageTreatmentAllowedForBlock(
          type,
          slot,
          t,
          productListTileCount,
        )
      ) {
        nonAuto[slot] = t;
      }
    }
    if (Object.keys(nonAuto).length > 0) norm.imageTreatments = nonAuto;
  }
  const json = stablePolishStringify(norm);
  let h = 5381;
  for (let i = 0; i < json.length; i++) {
    h = (((h << 5) + h) + json.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

// Density-aware render canvas for the two list blocks whose height genuinely
// depends on how many rows they carry. A sparse list (e.g. 2 selling points)
// on the default tall 3:4 canvas makes the model fill the height with GIANT
// text, which then gets upscaled to the stitch width and reads as blurry /
// low-res next to its neighbors. Short lists get a shorter, wider canvas so
// the copy renders at a normal scale. Returns null for every other block type
// (their static ratios in blockAspectRatio stay authoritative).
// Shared so the polish PLAN (server/services/email-polish-prompt.ts) and the
// visual hash below can never disagree about the intended canvas.
export function emailListBlockCanvasRatio(
  type: string,
  itemCount: number,
): string | null {
  if (type === "selling_points") {
    if (itemCount <= 2) return "3:2";
    if (itemCount === 3) return "4:3";
    return "3:4";
  }
  if (type === "comparison_table") {
    if (itemCount <= 1) return "3:2";
    if (itemCount === 2) return "4:3";
    return "3:4";
  }
  return null;
}

// Signature of a single block's NON-TEXT render inputs — the inputs whose
// change requires a fresh full redesign of the block's polished image (block
// type, the product photos rendered into product blocks, the STRUCTURE of
// list-style blocks, and the pipeline version). Deliberately EXCLUDES all
// copy/link/sale WORDING: when only wording changes, the partial polish can
// re-render the block as a text-preserving EDIT of its previous image
// (image-to-image "replace the text, keep everything else identical") so the
// block's design never changes on a wording edit. List-style blocks
// (selling_points / reviews / us_vs_them / comparison_table) additionally fold
// their ITEM COUNTS: removing or adding an item changes the block's layout
// structure (rows, checkboxes), which an image-to-image text edit cannot
// reliably add/remove — the model tends to leave the old row's chrome behind
// (e.g. an orphaned empty checkbox). A count change therefore forces the fresh
// redesign path. Stamped on each chunk at render time (see
// EmailPolishChunk.visualHash).
export function computeEmailPolishBlockVisualHash(
  e: {
    productImageUrls?: unknown;
    productListImages?: unknown;
    blockCopy?: unknown;
  },
  blockKey: string,
): string {
  const urlsOf = (arr: unknown, key: string): string[] =>
    Array.isArray(arr)
      ? arr.map((x) => (x && typeof x === "object" ? String((x as any)[key] ?? "") : ""))
      : [];
  const type = emailBlockType(blockKey);
  // Product-using blocks fold the real product photo URLs: a photo swap must
  // force a fresh redesign (the i2i text edit would keep drawing the old
  // product). "reviews" briefly joined this set when it carried product refs,
  // but refs + "reproduce EXACTLY" language still redrew the product at
  // thumbnail size, so reviews is now pinned strictly TEXT-ONLY and left the
  // set again — the resulting one-time stamp mismatch on reviews chunks
  // polished under the old fold is deliberate: it heals the product-thumbnail
  // renders into text-only on their next regen. Keep this in lockstep with
  // PRODUCT_BLOCK_TYPES (server/services/email-polish-prompt.ts).
  const usesProduct = type === "hero" || type === "product_list";
  const norm: Record<string, unknown> = {
    pipeline: EMAIL_POLISH_PIPELINE_VERSION,
    type,
  };
  if (usesProduct) {
    norm.productImageUrls = Array.isArray(e.productImageUrls) ? e.productImageUrls : [];
    norm.productListImages = urlsOf(e.productListImages, "imageUrl");
  }
  // Item counts for list-structured blocks (structure only — never the text).
  // Non-list block types keep their input object byte-identical so their
  // stamped hashes stay valid across this addition.
  const lenOf = (arr: unknown): number => (Array.isArray(arr) ? arr.length : 0);
  const copy = resolveEmailInstanceCopy(e.blockCopy as any, blockKey);
  // Per-instance selected image (image_only / image_discount, Task #2286): the
  // rendered photo is a VISUAL input — after a swap, an explicit regen must do
  // a fresh render (an i2i text edit would keep drawing the old photo). Key is
  // added only when a selection exists so legacy stamps stay byte-identical.
  if ((type === "image_only" || type === "image_discount") && copy.imageUrl) {
    norm.selectedImageUrl = copy.imageUrl;
  }
  if (type === "body") {
    const bodyImage = resolveEmailBodyImagePreference(copy);
    // Same compatibility rule as the content hash: automatic adds no key.
    if (bodyImage.mode !== "automatic") norm.bodyImage = bodyImage;
  }
  if (type === "selling_points") norm.itemCount = lenOf(copy.sellingPoints);
  if (type === "reviews") norm.itemCount = lenOf(copy.reviews);
  if (type === "us_vs_them") norm.itemCount = lenOf(copy.usVsThem);
  if (type === "comparison_table") {
    norm.itemCount = {
      columns: lenOf(copy.comparisonTable?.columns),
      rows: lenOf(copy.comparisonTable?.rows),
    };
  }
  // Fold the density-aware CANVAS ratio too (selling_points/comparison_table
  // only): the intended canvas shape is part of the block's visual design, so
  // a stamp rendered on the old always-tall canvas mismatches once and takes a
  // fresh redesign at the correct scale (heals the giant-text/blurry render).
  const canvas = emailListBlockCanvasRatio(
    type,
    type === "comparison_table"
      ? lenOf(copy.comparisonTable?.rows)
      : lenOf(copy.sellingPoints),
  );
  if (canvas) norm.canvas = canvas;
  // Task #2556: non-automatic image treatments are visual inputs — a selected
  // URL or "none" changes the rendered pixels, so a treatment change must force
  // a fresh redesign of the block. Same byte-compatibility rule: automatic/absent
  // adds no key so legacy stamps stay valid.
  if (isEmailImageTreatmentEligible(type) && copy.imageTreatments) {
    const nonAuto: Record<string, unknown> = {};
    const productListTileCount =
      type === "product_list"
        ? (copy.productListType ??
            (urlsOf(e.productListImages, "imageUrl").length
              ? "bundle"
              : "range")) === "bundle"
          ? urlsOf(e.productListImages, "imageUrl").length
          : (Array.isArray(e.productImageUrls)
              ? e.productImageUrls.length
              : 0)
        : undefined;
    for (const [slot, t] of Object.entries(copy.imageTreatments)) {
      if (
        t &&
        t.mode !== "automatic" &&
        isEmailImageTreatmentAllowedForBlock(
          type,
          slot,
          t,
          productListTileCount,
        )
      ) {
        nonAuto[slot] = t;
      }
    }
    if (Object.keys(nonAuto).length > 0) norm.imageTreatments = nonAuto;
  }
  const json = stablePolishStringify(norm);
  let h = 5381;
  for (let i = 0; i < json.length; i++) {
    h = (((h << 5) + h) + json.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

export function emailBodyImageNeedsApply(args: {
  hasManifest: boolean;
  manifestVisualHash?: string | null;
  currentVisualHash: string;
  preference: EmailBodyImagePreference;
}): boolean {
  if (!args.hasManifest) return false;
  return args.manifestVisualHash
    ? args.manifestVisualHash !== args.currentVisualHash
    : args.preference.mode !== "automatic";
}

export function emailBodyHasReplaceableImage(args: {
  editMode: boolean;
  polished: boolean;
  preference: EmailBodyImagePreference;
  manifestHasImagery?: boolean;
  capturedHasImagery?: boolean;
  legacyDetectedHasImagery?: boolean;
}): boolean {
  // The live draft renderer deterministically emits an <img> only for a
  // selected treatment. Canonical polished/captured views trust metadata stamped
  // from the pixels actually being shown. A polished legacy manifest may be
  // inspected on demand; that result is used ONLY when the persisted marker is
  // missing, never to override an explicit false.
  if (args.editMode) return args.preference.mode === "selected";
  return args.polished
    ? (args.manifestHasImagery ?? args.legacyDetectedHasImagery) === true
    : args.capturedHasImagery === true;
}

export type EmailCopyBlockStatus = "pending" | "ready" | "failed";
export type EmailBlockCopyStatusMap = Record<string, EmailCopyBlockStatus>;

// Per-instance copy for a single optional block. Each optional block type maps
// to exactly one of these fields, so an instance only ever populates the field
// its type uses. This is the unit stored under `EmailBlockCopy.instances`,
// keyed by the block's instance id (so the same type can appear more than once).
export interface EmailInstanceCopy {
  sellingPoints?: string[];
  reviews?: { quote: string; author: string }[];
  usVsThem?: { us: string; them: string }[];
  didYouKnow?: string;
  logoBannerTagline?: string;
  productListIntro?: string;
  // "range" (a selection of the brand's products) vs "bundle" (the items in a
  // bundle/pair). Drives the W11 intro framing + the default layout variant.
  productListType?: "range" | "bundle";
  comparisonTable?: {
    columns: string[];
    rows: { feature: string; values: string[] }[];
  };
  // Small all-caps kicker shown above the block's headline/copy — but ONLY when
  // the assigned layout variant calls for it (see emailVariantShowsEyebrow);
  // otherwise stored-but-hidden. Decorative: always captured on generation when
  // the workflow returns it. Applies to hero, selling_points, us_vs_them,
  // did_you_know, comparison_table, product_list.
  eyebrow?: string;
  // Short, visually distinct italic line shown ONLY when the assigned variant
  // calls for it (see emailVariantShowsPullQuote). For Body Copy it is
  // highlighted verbatim from within the existing body copy (never a separate
  // invented element). Decorative. Applies to hero and body.
  pullQuote?: string;
  // Functional offer callout (HERO only) — rendered whenever non-null REGARDLESS
  // of the hero variant: a label, a prominently displayed code, a description,
  // and a brand-colored CTA button. Absent / all-empty → not rendered.
  offerBox?: { label: string; code: string; description: string; cta: string };
  // Standalone CTA button band (cta block): short headline + supporting line +
  // button label. Empty fields fall back to the email-level headline/ctaText.
  ctaHeadline?: string;
  ctaBody?: string;
  ctaLabel?: string;
  // Image + discount band (image_discount block): short note + the code shown
  // in a dashed pill. Empty fields fall back to the email's sale settings.
  discountNote?: string;
  discountCode?: string;
  // Selected image for the image_only / image_discount blocks (Task #2286):
  // either one of the product's existing photo URLs or a custom uploaded
  // durable R2 URL. Absent → the first product photo (the historical default),
  // so legacy emails render byte-identical. Setting it is a plain instance-copy
  // save — it never enqueues polish/regeneration by itself.
  imageUrl?: string;
  // Body Copy visual treatment. Absent is the legacy "automatic" treatment.
  // Keeping this as one discriminated value makes a selected URL atomic with
  // its mode, so autosave can never persist "selected" without its source.
  bodyImage?: EmailBodyImagePreference;
  // Layout VARIANT for this block instance — controls only the visual expression
  // (the copy fields above are identical across variants). Assigned on generation
  // (see assignEmailLayoutVariants), cycled by the user in the editor, and
  // preserved through copy regeneration. See EMAIL_LAYOUT_VARIANTS for the catalog
  // per block type. Absent on legacy emails → the type's default variant.
  layoutVariant?: string;
  // Preset-template section copy (Task #975). Only present on preset emails,
  // keyed by section instance id like every other field here. The shape is
  // `EmailPresetSectionCopy` (shared/email-presets.ts) — kept loose here so
  // the schema module doesn't import the preset module.
  preset?: Record<string, unknown>;
  // Task #2556 — per-slot image-treatment preferences for eligible blocks
  // (selling_points, did_you_know, us_vs_them, comparison_table, product_list).
  // Keyed by stable slot name ("main" for singular blocks; "tile_N" for
  // product_list). Absent or { mode: "automatic" } preserves legacy behavior
  // byte-for-byte (no existing hash is affected). Only "selected" (url) and
  // "none" require a re-render of that block's polish image. Product List is
  // image-required and therefore accepts only automatic/selected tile modes.
  imageTreatments?: Record<string, EmailImageTreatment>;
}

export type EmailBodyImagePreference =
  | { mode: "automatic" }
  | { mode: "selected"; url: string }
  | { mode: "none" };

export type EmailImageTreatment =
  | { mode: "automatic" }
  | { mode: "selected"; url: string }
  | { mode: "none" };
export interface EmailBlockCopy {
  // ---- Legacy single-instance fields (kept for backward compatibility) ------
  // Pre-existing emails store one set of fields per block TYPE here. New/edited
  // emails store per-instance copy under `instances` instead; the legacy fields
  // remain readable as the first (bare type-keyed) instance via
  // `resolveEmailInstanceCopy`.
  sellingPoints?: string[];
  reviews?: { quote: string; author: string }[];
  usVsThem?: { us: string; them: string }[];
  didYouKnow?: string;
  logoBannerTagline?: string;
  productListIntro?: string;
  productListType?: "range" | "bundle";
  // Feature-by-feature comparison matrix. `columns` are the comparison columns
  // (first column = the brand); each row pairs a `feature` label with one value
  // per column (e.g. "✓" / "✗" / short text).
  comparisonTable?: {
    columns: string[];
    rows: { feature: string; values: string[] }[];
  };
  // Legacy top-level mirrors of the new decorative/functional fields (see
  // EmailInstanceCopy). New/edited emails store these per-instance; these remain
  // readable as the first (bare type-keyed) instance via resolveEmailInstanceCopy.
  eyebrow?: string;
  pullQuote?: string;
  offerBox?: { label: string; code: string; description: string; cta: string };
  // Optional one-line section headline ("punchline") keyed by block INSTANCE id
  // (legacy: by block type, which equals a bare instance id), shown atop the
  // section. Sparse — only sections the user/generator filled appear.
  punchlines?: Record<string, string>;
  // Per-instance block copy keyed by instance id. Lets the same optional block
  // type appear more than once, each with its own copy. Absent on legacy emails.
  instances?: Record<string, EmailInstanceCopy>;
}

// An optional block can appear more than once. Each occurrence in `blocks`
// carries a unique instance id: the bare block type for the first/legacy
// instance (e.g. "selling_points") or "<type>__<suffix>" for additional ones.
// `emailBlockType` recovers the type for render/copy-shape decisions.
export function emailBlockType(instanceId: string): string {
  const i = instanceId.indexOf("__");
  return i === -1 ? instanceId : instanceId.slice(0, i);
}

// The two "us vs. the alternative" comparison blocks. They do the same
// persuasion job, so rendering them back-to-back reads as repetitive. Both
// renderers route their visible optional list through
// `separateEmailComparisonBlocks` so the two never sit adjacent when any other
// optional block can fall between them.
export const EMAIL_COMPARISON_FAMILY: ReadonlySet<string> = new Set([
  "comparison_table",
  "us_vs_them",
]);

// Reorder a VISIBLE optional-block list (hero/body/footer already excluded) so
// two comparison-family blocks never render adjacently. Walks the list in the
// user's order; when the previously placed block was comparison-family and the
// next one is too, it pulls the nearest following NON-family block forward to
// separate them. Relative order within each group is preserved, and when no
// separator exists (the list is only comparison blocks) the order is returned
// unchanged. Shared by the preview (email-render) and the MJML send so the two
// can never drift.
export function separateEmailComparisonBlocks(optional: string[]): string[] {
  const isFamily = (k: string) => EMAIL_COMPARISON_FAMILY.has(emailBlockType(k));
  const items = optional.slice();
  // Nothing to do without ≥2 family blocks AND at least one non-family separator.
  if (items.filter(isFamily).length < 2 || items.every(isFamily)) return items;
  const result: string[] = [];
  let prevFamily = false;
  while (items.length) {
    let idx = 0;
    if (prevFamily && isFamily(items[0])) {
      const sep = items.findIndex((k) => !isFamily(k));
      if (sep !== -1) idx = sep;
    }
    const [picked] = items.splice(idx, 1);
    result.push(picked);
    prevFamily = isFamily(picked);
  }
  return result;
}

// The order a list of VISIBLE block keys actually renders in: hero and body
// first, footer last, and the optional middle routed through
// `separateEmailComparisonBlocks`. This is the single source of truth for
// render order — the polish plan (server), the MJML send, and the editor's
// "block order changed" staleness check must all derive from it, otherwise a
// stored order the renderer legitimately re-derives (e.g. separating adjacent
// comparison blocks) false-flags as a user reorder forever.
export function orderedEmailRenderKeys(visible: string[]): string[] {
  const locked = ["hero", "body", "footer"];
  const optional = separateEmailComparisonBlocks(
    visible.filter((k) => !locked.includes(k)),
  );
  const out = ["hero", "body", ...optional, "footer"];
  return out.filter((k, i) => out.indexOf(k) === i);
}

// Resolve the copy for a single block instance. Prefers the per-instance entry
// in `blockCopy.instances`; falls back to the legacy top-level fields for a bare
// (type-keyed) id so pre-existing emails open/render/edit unchanged.
export function resolveEmailInstanceCopy(
  blockCopy: EmailBlockCopy | null | undefined,
  instanceId: string,
): EmailInstanceCopy {
  const bc = blockCopy ?? {};
  const fromInstances = bc.instances?.[instanceId];
  // Bare instances (id === type) fall back to the legacy top-level copy fields.
  // We MERGE that fallback UNDER the per-instance entry so a sparse instance
  // (e.g. one carrying only `layoutVariant`) still inherits the top-level copy
  // the copy worker writes, instead of shadowing it to empty.
  const topLevel: EmailInstanceCopy =
    instanceId === emailBlockType(instanceId)
      ? {
          sellingPoints: bc.sellingPoints,
          reviews: bc.reviews,
          usVsThem: bc.usVsThem,
          didYouKnow: bc.didYouKnow,
          logoBannerTagline: bc.logoBannerTagline,
          productListIntro: bc.productListIntro,
          productListType: bc.productListType,
          comparisonTable: bc.comparisonTable,
          eyebrow: bc.eyebrow,
          pullQuote: bc.pullQuote,
          offerBox: bc.offerBox,
        }
      : {};
  if (fromInstances) return { ...topLevel, ...fromInstances };
  return topLevel;
}

// Product-list block layout. `productListType` records the user's choice when
// they add the block: "range" shows a selection of the brand's products,
// "bundle" shows the items in a bundle/pair. The layout VARIANT is derived from
// that type (and the photo count) so the default layout always reflects the
// chosen type — bundles favor an editorial hero-plus-companions layout (or a
// horizontal strip once there are many items); ranges favor an even grid.
export type ProductListType = "range" | "bundle";
export type ProductListVariant =
  | "grid-2col"
  | "grid-3col"
  | "editorial-feature"
  | "horizontal-scroll-style";

export function resolveProductListVariant(
  type: ProductListType,
  count: number,
): ProductListVariant {
  if (type === "bundle") {
    return count > 4 ? "horizontal-scroll-style" : "editorial-feature";
  }
  return count <= 2 ? "grid-2col" : "grid-3col";
}

// -----------------------------------------------------------------------------
// Email Express — per-block LAYOUT VARIANTS.
// Every block except Footer is assigned a layout variant on generation. A
// variant controls ONLY the visual expression of a block — the copy fields are
// identical across variants. The generator mixes variants for visual rhythm; the
// editor lets the user cycle a block's variant with live preview; regeneration
// keeps the current variant. Variants flagged type-only / graphic-only render
// WITHOUT a generated photo, so the (paid) Fal image call is skipped for them.
//
// The FIRST entry for each type is its default. `product_list` keeps its
// historical type+count default via resolveProductListVariant when nothing is
// stored (see the renderers); the catalog here drives the cycle UI. Footer is
// intentionally absent (fixed layout, no variants).
// -----------------------------------------------------------------------------
export const EMAIL_LAYOUT_VARIANTS: Record<string, string[]> = {
  // The four standard hero art directions: image-overlay (headline band over the
  // photo), split (photo above a solid brand band), text-dominant (image-free,
  // typographic), full-bleed-minimal (full photo, minimal headline/CTA at the
  // bottom). text-dominant is the image-free one (see EMAIL_VARIANT_NO_IMAGE).
  hero: ["image-overlay", "split", "text-dominant", "full-bleed-minimal"],
  // The four standard body directions: standard (lead-emphasized left column),
  // pull-quote-feature (a large italic centered pull quote), color-band (the copy
  // on a dark brand band — also a guaranteed dark contrast section), and
  // editorial-margins (decorative brand-derived margin rules around the copy).
  body: ["standard", "pull-quote-feature", "color-band", "editorial-margins"],
  selling_points: [
    "annotated-lifestyle",
    "callout-overlay",
    "icon-grid",
    "checklist-product",
    "type-hero",
  ],
  reviews: ["single-hero", "two-column", "ugc-stack"],
  us_vs_them: ["table-minimal", "table-bold", "split-visual", "badge-wall"],
  comparison_table: ["grid-classic", "prose-table", "annotated-product", "graphic-card"],
  did_you_know: ["image-editorial", "split-contrast", "color-band", "illustrated-callout"],
  logo_banner: ["centered-minimal", "color-band", "editorial-type"],
  product_list: ["grid-2col", "grid-3col", "editorial-feature", "horizontal-feature"],
  // V2-parity blocks (single fixed layout each — no cycle UI). They render with
  // REAL product photos (image_only / image_discount) or no image at all (cta),
  // so none of them enter the paid Fal image pipeline.
  image_only: ["standard"],
  image_discount: ["standard"],
  cta: ["standard"],
};

export function emailLayoutVariantsFor(type: string): string[] {
  return EMAIL_LAYOUT_VARIANTS[type] ?? [];
}

export function emailDefaultLayoutVariant(type: string): string {
  return EMAIL_LAYOUT_VARIANTS[type]?.[0] ?? "";
}

// Legacy → canonical variant names. The hero/body catalogs were renamed to the
// four standard art directions; emails generated before the rename store the old
// names on their instances. These aliases map them onto the current catalog so a
// stored choice keeps rendering (and cycling) correctly instead of silently
// resetting to the default. Keyed by block type.
const EMAIL_VARIANT_ALIASES: Record<string, Record<string, string>> = {
  hero: {
    "overlay-band": "image-overlay",
    "split-feature": "split",
    "type-statement": "text-dominant",
    "editorial-frame": "full-bleed-minimal",
  },
  body: {
    "editorial-leadin": "editorial-margins",
    "accent-band": "color-band",
    "centered-statement": "pull-quote-feature",
  },
};

// Map a possibly-legacy stored variant onto the current catalog name.
export function canonicalEmailVariant(type: string, variant: string): string {
  return EMAIL_VARIANT_ALIASES[type]?.[variant] ?? variant;
}

// Content-section variants (non-hero/non-footer) that render on a DARK brand
// background with light text. assignEmailLayoutVariants guarantees at least one
// of these appears per email so every email has a dark contrast section for
// depth; both renderers paint these from the shared dark surface (surfaces.dark),
// which is driven WCAG-dark regardless of the brand palette. The hero and footer
// are always dark too, so baseline depth survives any later cycling.
// NOTE: logo_banner:color-band is intentionally NOT a member — it renders as a
// brand-PRIMARY band (colors.primary), which can be light for a pale brand, so it
// can't be counted toward the dark-contrast guarantee.
const EMAIL_VARIANT_DARK = new Set<string>([
  "body:color-band",
  "did_you_know:split-contrast",
  "did_you_know:color-band",
]);

export function emailVariantIsDark(type: string, variant: string): boolean {
  return EMAIL_VARIANT_DARK.has(`${type}:${canonicalEmailVariant(type, variant)}`);
}

// The brand surface a content section paints from. Both renderers (MJML send +
// React preview) resolve their section background/text from style.surfaces[key]
// via this single map, so a section is never flat white and the two renderers
// stay in lockstep. Dark variants resolve to the dark surface (WCAG-safe light
// text); other sections rotate page/light/tinted for editorial rhythm. Hero and
// footer are special-cased in the renderers (always brand-dark) and don't use
// this map.
export type EmailSurfaceKey = "page" | "light" | "tinted" | "dark" | "accent";

export function emailSectionSurfaceKey(
  type: string,
  rawVariant: string | null | undefined,
): EmailSurfaceKey {
  const variant = canonicalEmailVariant(type, rawVariant ?? "");
  if (EMAIL_VARIANT_DARK.has(`${type}:${variant}`)) return "dark";
  switch (type) {
    case "body":
      // pull-quote-feature reads as a quiet tinted band; the rest on soft paper.
      return variant === "pull-quote-feature" ? "tinted" : "page";
    case "selling_points":
      return "tinted";
    case "us_vs_them":
    case "comparison_table":
      return "light";
    case "logo_banner":
      return variant === "editorial-type" ? "light" : "page";
    case "reviews":
      // Social proof rides the bold accent (pop-color) band — the email's one
      // saturated brand moment, breaking up the lighter editorial sections.
      // Both renderers paint the testimonial cards on the light card surface so
      // the quotes stay legible over the accent.
      return "accent";
    case "did_you_know":
    case "product_list":
    default:
      return "page";
  }
}

// Variants whose visual expression is type-only or graphic-only — they render
// WITHOUT a generated photo, so the (paid) Fal image call is skipped for that
// block instance. Keyed "<type>:<variant>". Only image-bearing block types need
// entries here; types that never use a generated image (reviews, logo_banner,
// product_list — which uses real product photos) don't gate on this set.
const EMAIL_VARIANT_NO_IMAGE = new Set<string>([
  "hero:text-dominant",
  "selling_points:icon-grid",
  "selling_points:type-hero",
  "us_vs_them:table-minimal",
  "us_vs_them:table-bold",
  "us_vs_them:badge-wall",
  "comparison_table:grid-classic",
  "comparison_table:prose-table",
  "comparison_table:graphic-card",
  "did_you_know:color-band",
  "did_you_know:illustrated-callout",
  // Standalone CTA band is purely typographic; image_only / image_discount use
  // REAL product photos (never generated), mirroring product_list.
  "cta:standard",
  "image_only:standard",
  "image_discount:standard",
]);

// Graphic-energy variants (a subset of the no-image variants) — used only to
// spread visual rhythm during assignment (image / type / graphic alternation).
const EMAIL_VARIANT_GRAPHIC = new Set<string>([
  "selling_points:icon-grid",
  "us_vs_them:table-bold",
  "us_vs_them:badge-wall",
  "comparison_table:graphic-card",
  "did_you_know:illustrated-callout",
  "logo_banner:color-band",
]);

export type EmailVariantEnergy = "image" | "type" | "graphic";

export function emailVariantEnergy(type: string, variant: string): EmailVariantEnergy {
  const k = `${type}:${variant}`;
  if (!EMAIL_VARIANT_NO_IMAGE.has(k)) return "image";
  return EMAIL_VARIANT_GRAPHIC.has(k) ? "graphic" : "type";
}

// Whether a block instance's assigned variant needs a generated image. Unknown
// types/variants default to TRUE so a new variant never silently drops an image.
export function emailVariantNeedsImage(
  type: string,
  variant: string | null | undefined,
): boolean {
  const raw = variant && variant.trim() ? variant : emailDefaultLayoutVariant(type);
  if (!raw) return true;
  const v = canonicalEmailVariant(type, raw);
  return !EMAIL_VARIANT_NO_IMAGE.has(`${type}:${v}`);
}

// Resolve the layout variant for a block instance: the explicitly stored variant
// when valid for the type, else the type's default. (`product_list` callers fall
// back to resolveProductListVariant when nothing is stored — see the renderers.)
export function resolveEmailLayoutVariant(
  type: string,
  instance: EmailInstanceCopy | null | undefined,
): string {
  const stored = instance?.layoutVariant;
  if (stored) {
    const canon = canonicalEmailVariant(type, stored);
    if (EMAIL_LAYOUT_VARIANTS[type]?.includes(canon)) return canon;
  }
  return emailDefaultLayoutVariant(type);
}

// Cycle to the next variant for a block type (wraps). Used by the editor's
// "Change layout" control. Unknown variant → starts at the type's default.
export function nextEmailLayoutVariant(type: string, current: string | null | undefined): string {
  const variants = EMAIL_LAYOUT_VARIANTS[type];
  if (!variants || variants.length === 0) return "";
  const canon = current ? canonicalEmailVariant(type, current) : null;
  const idx = canon ? variants.indexOf(canon) : -1;
  return variants[(idx + 1) % variants.length];
}

// ---------------------------------------------------------------------------
// Decorative-field variant gating
//
// `eyebrow` (small all-caps kicker) and `pullQuote` (short italic line) are
// ALWAYS captured/stored on generation, but only SHOWN when the block's
// assigned layout variant calls for it. These sets enumerate the (type:variant)
// pairs that surface each field. Keyed by CANONICAL variant; unknown/legacy
// variants → not shown (decorative fields default OFF — the opposite of
// `emailVariantNeedsImage`, so legacy emails render unchanged).
const EMAIL_VARIANT_EYEBROW: ReadonlySet<string> = new Set([
  "hero:image-overlay",
  "hero:split",
  "hero:text-dominant",
  "selling_points:type-hero",
  "selling_points:callout-overlay",
  "us_vs_them:table-bold",
  "us_vs_them:badge-wall",
  "did_you_know:image-editorial",
  "did_you_know:split-contrast",
  "comparison_table:grid-classic",
  "comparison_table:annotated-product",
  "product_list:editorial-feature",
  "product_list:grid-3col",
]);

const EMAIL_VARIANT_PULL_QUOTE: ReadonlySet<string> = new Set([
  "hero:full-bleed-minimal",
  "hero:image-overlay",
  "body:pull-quote-feature",
]);

function emailVariantInSet(
  set: ReadonlySet<string>,
  type: string,
  variant?: string | null,
): boolean {
  const raw = variant && variant.trim() ? variant : emailDefaultLayoutVariant(type);
  if (!raw) return false;
  return set.has(`${type}:${canonicalEmailVariant(type, raw)}`);
}

// True when the given block type + assigned variant should DISPLAY the eyebrow.
export function emailVariantShowsEyebrow(
  type: string,
  variant?: string | null,
): boolean {
  return emailVariantInSet(EMAIL_VARIANT_EYEBROW, type, variant);
}

// True when the given block type + assigned variant should DISPLAY the pull quote.
export function emailVariantShowsPullQuote(
  type: string,
  variant?: string | null,
): boolean {
  return emailVariantInSet(EMAIL_VARIANT_PULL_QUOTE, type, variant);
}

// A comparison-table cell value is "checkmark-style" (glyph/short token) rather
// than prose. Used to lay prose rows out with more vertical room than ✓/✗ rows.
const EMAIL_COMPARISON_CHECKMARK_VALUES: ReadonlySet<string> = new Set([
  "✓", "✗", "✔", "✘", "×", "✕", "•", "—", "–", "-", "", "n/a",
  "yes", "no", "y", "n",
]);

export function emailComparisonValueIsCheckmark(value: string): boolean {
  const s = (value ?? "").trim().toLowerCase();
  if (EMAIL_COMPARISON_CHECKMARK_VALUES.has(s)) return true;
  // A very short token (a single glyph or two-letter marker) is treated as a
  // checkmark; anything longer is prose.
  return s.length <= 2;
}

// True when ANY value in the row is prose (not a checkmark/short token).
export function emailComparisonRowIsProse(values: string[]): boolean {
  return (values ?? []).some((v) => !emailComparisonValueIsCheckmark(v));
}

// Assign a layout variant to every block instance at generation, mixing variants
// for visual rhythm: never repeat a variant on two adjacent same-type blocks, and
// avoid placing two same-energy (image/type/graphic) variants back to back.
// Footer (and any type without a catalog) is skipped.
export function assignEmailLayoutVariants(
  blockKeys: string[],
  seed = 0,
): Record<string, string> {
  const out: Record<string, string> = {};
  let prevEnergy: EmailVariantEnergy | null = null;
  const lastByType: Record<string, string> = {};
  // `seed` rotates the starting offset so the FIRST block of each type (notably
  // the hero) doesn't always land on the catalog default — emails across a brand
  // get visual variety instead of an identical hero every time. Deterministic per
  // caller-supplied seed (a stable hash of the email identity).
  let rot = ((seed % 4) + 4) % 4;
  for (const key of blockKeys) {
    const type = emailBlockType(key);
    const catalog = EMAIL_LAYOUT_VARIANTS[type];
    if (!catalog || catalog.length === 0) continue;
    // The hero ALWAYS leads with a generated image by default: exclude the
    // image-free `text-dominant` direction from generation so the hero image
    // worker always kicks and the banner renders on every email. text-dominant
    // stays in the catalog (and manually selectable via the editor's "Change
    // layout" control) for anyone who explicitly wants a text-only hero.
    const variants =
      type === "hero"
        ? catalog.filter((v) => emailVariantNeedsImage(type, v))
        : catalog;
    if (variants.length === 0) continue;
    const ordered = variants.map((_, idx) => variants[(idx + rot) % variants.length]);
    const notRepeat =
      variants.length > 1 ? ordered.filter((v) => v !== lastByType[type]) : ordered;
    const pool = notRepeat.length ? notRepeat : ordered;
    let pick = pool.find((v) => emailVariantEnergy(type, v) !== prevEnergy);
    if (!pick) pick = pool[0];
    out[key] = pick;
    lastByType[type] = pick;
    prevEnergy = emailVariantEnergy(type, pick);
    rot++;
  }

  // Guarantee at least one DARK content section (non-hero/non-footer) for depth.
  // If the rhythmic assignment above produced none, force one — preferring the
  // optional did_you_know block (its color-band renders from surfaces.dark) so the
  // body keeps its readable default, and falling back to the body's color-band only
  // when no optional candidate exists. Both choices paint from the WCAG-dark
  // surfaces.dark, so the guarantee is a TRUE dark band regardless of brand palette.
  // (logo_banner:color-band is deliberately excluded — it's a brand-primary band
  // that can be light, so it can't satisfy the dark-contrast guarantee.)
  // Hero + footer are always dark, so this purely adds a dark CONTENT band.
  const hasDarkContent = Object.entries(out).some(([k, v]) =>
    emailVariantIsDark(emailBlockType(k), v),
  );
  if (!hasDarkContent) {
    const darkChoice: Record<string, string> = {
      did_you_know: "color-band",
      body: "color-band",
    };
    const pickDark = (preferOptional: boolean): boolean => {
      for (const key of blockKeys) {
        const type = emailBlockType(key);
        if (preferOptional && type === "body") continue;
        const choice = darkChoice[type];
        if (choice && EMAIL_LAYOUT_VARIANTS[type]?.includes(choice)) {
          out[key] = choice;
          return true;
        }
      }
      return false;
    };
    if (!pickDark(true)) pickDark(false);
  }

  return out;
}

// Re-apply each instance's existing layoutVariant onto a freshly-built blockCopy
// so a copy regeneration KEEPS the user's chosen layout (the variant is never
// part of the regenerated copy payload — only the editor's "Change layout"
// control sets it). Mutates `next` in place; only fills a variant that the
// regenerated instance is missing, so it never clobbers an explicit new one.
export function preserveEmailLayoutVariants(
  next: { instances?: Record<string, EmailInstanceCopy> } | null | undefined,
  prev: { instances?: Record<string, EmailInstanceCopy> } | null | undefined,
): void {
  const prevInst = prev?.instances;
  const nextInst = next?.instances;
  if (!prevInst || !nextInst) return;
  for (const id of Object.keys(nextInst)) {
    const prevVar = prevInst[id]?.layoutVariant;
    if (prevVar && nextInst[id] && nextInst[id].layoutVariant == null) {
      nextInst[id].layoutVariant = prevVar;
    }
  }
}

// Editor link configuration stored in `linkConfig` (jsonb).
//   "single" — ONE email-wide URL (`sitewideUrl`) applied to every section.
//   "cta"    — each CTA BUTTON (hero/body block) carries its own URL in
//              `blockLinks` (keyed by block key); every non-CTA section falls
//              back to the email-wide `sitewideUrl`.
//   "multi"  — LEGACY per-section mode; normalized to "cta" at read time
//              (existing per-section URLs are kept so old emails still send
//              with their configured links).
export interface EmailLinkConfig {
  mode: "single" | "multi" | "cta";
  sitewideUrl?: string;
  blockLinks?: Record<string, string>;
}

// Block TYPES that render an actual CTA button in the email (the hero's main
// button, the body copy's closing button, and the standalone CTA band block).
// These are the blocks that can carry a per-block link override; everything
// else uses the email-wide URL.
export const EMAIL_CTA_BLOCK_TYPES = ["hero", "body", "cta"] as const;

export function emailIsCtaBlock(blockKey: string): boolean {
  return (EMAIL_CTA_BLOCK_TYPES as readonly string[]).includes(
    emailBlockType(blockKey),
  );
}

const normalizeEmailUrlValue = (u: unknown): string =>
  typeof u === "string" ? u.trim() : "";

// Read-time normalization of a stored link config. Legacy "multi" configs map
// to the new per-CTA mode, KEEPING all stored per-block URLs (so a legacy
// per-section URL keeps working via the resolver's legacy fallback). Never
// mutates stored data — callers normalize on read.
export function normalizeEmailLinkConfig(
  lc: EmailLinkConfig | null | undefined,
): { mode: "single" | "cta"; sitewideUrl: string; blockLinks: Record<string, string> } {
  const mode = lc?.mode === "multi" || lc?.mode === "cta" ? "cta" : "single";
  const blockLinks: Record<string, string> = {};
  if (lc?.blockLinks && typeof lc.blockLinks === "object") {
    for (const [k, v] of Object.entries(lc.blockLinks)) {
      const url = normalizeEmailUrlValue(v);
      if (url) blockLinks[k] = url;
    }
  }
  return { mode, sitewideUrl: normalizeEmailUrlValue(lc?.sitewideUrl), blockLinks };
}

// The email-wide fallback link: the sitewide URL, else the first configured
// per-CTA/per-block URL (so a legacy multi config with only block URLs still
// yields a usable email-wide link). "" when nothing usable is configured.
export function emailLinkFallbackHref(
  lc: EmailLinkConfig | null | undefined,
): string {
  const cfg = normalizeEmailLinkConfig(lc);
  if (cfg.sitewideUrl) return cfg.sitewideUrl;
  for (const v of Object.values(cfg.blockLinks)) if (v) return v;
  return "";
}

// Resolve the link a given SECTION image should carry in the sent email:
//   single mode — every section uses the email-wide URL.
//   cta mode    — a CTA section (hero/body) uses its own configured URL first;
//                 non-CTA sections use the email-wide URL, honoring a legacy
//                 per-section URL (old "multi" configs) before the last-resort
//                 fallback so existing emails keep their configured links.
// Returns "" when nothing usable is configured.
export function resolveEmailBlockHref(
  lc: EmailLinkConfig | null | undefined,
  blockKey: string,
): string {
  const cfg = normalizeEmailLinkConfig(lc);
  if (cfg.mode === "single") return cfg.sitewideUrl;
  const own =
    cfg.blockLinks[blockKey] || cfg.blockLinks[emailBlockType(blockKey)] || "";
  if (emailIsCtaBlock(blockKey)) {
    return own || cfg.sitewideUrl || emailLinkFallbackHref(lc);
  }
  return cfg.sitewideUrl || own || emailLinkFallbackHref(lc);
}

// Send-time guard: does the config contain at least one usable link?
export function emailLinkConfigHasUsableLink(
  lc: EmailLinkConfig | null | undefined,
): boolean {
  return !!emailLinkFallbackHref(lc);
}

// One per-section preview capture persisted on `capturedBlocks` (jsonb). The
// ordered array mirrors the editor preview's visible sections top-to-bottom.
export interface EmailCapturedBlock {
  // Block instance key ("hero", "body", "selling_points__a1b2", "footer", …).
  blockKey: string;
  // Durable (R2-mirrored) URL of the captured retina PNG.
  url: string;
  // Link resolved client-side at capture time (informational — the send worker
  // re-resolves every href from the CURRENT linkConfig at build time).
  linkUrl?: string;
  // Content hash (computeEmailPolishSourceHash) of the email at capture time,
  // stamped on the FIRST entry only — lets the editor show "queued changes"
  // for edits made before a reload. Legacy captures lack it (treated as fresh).
  sourceHash?: string;
  // Whether this captured Body Copy section contains an image that can be
  // replaced. Present only on Body Copy entries.
  hasImagery?: boolean;
}

// Validation for the captured-blocks payload (send / captured-blocks routes).
export const emailCapturedBlocksSchema = z
  .array(
    z
      .object({
        blockKey: z.string().min(1).max(120),
        url: z.string().min(1).max(2048),
        linkUrl: z.string().max(2048).optional(),
        sourceHash: z.string().max(256).optional(),
        hasImagery: z.boolean().optional(),
      })
      .strip(),
  )
  .max(40);

export const emailBodyImagePreferenceSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("automatic") }).strict(),
  z
    .object({
      mode: z.literal("selected"),
      url: z
        .string()
        .trim()
        .url()
        .max(2048)
        .refine(
          (url) => /^https?:\/\//i.test(url),
          "Body image URL must use HTTP or HTTPS",
        ),
    })
    .strict(),
  z.object({ mode: z.literal("none") }).strict(),
]);
const emailInstanceCopyShape = {
  sellingPoints: z.array(z.string().max(500)).max(12).optional(),
  reviews: z
    .array(
      z.object({
        quote: z.string().max(2000),
        author: z.string().max(200),
      }),
    )
    .max(12)
    .optional(),
  usVsThem: z
    .array(z.object({ us: z.string().max(500), them: z.string().max(500) }))
    .max(12)
    .optional(),
  didYouKnow: z.string().max(2000).optional(),
  logoBannerTagline: z.string().max(500).optional(),
  productListIntro: z.string().max(2000).optional(),
  productListType: z.enum(["range", "bundle"]).optional(),
  comparisonTable: z
    .object({
      columns: z.array(z.string().max(120)).max(6),
      rows: z
        .array(
          z.object({
            feature: z.string().max(200),
            values: z.array(z.string().max(120)).max(6),
          }),
        )
        .max(20),
    })
    .optional(),
  eyebrow: z.string().max(200).optional(),
  pullQuote: z.string().max(500).optional(),
  offerBox: z
    .object({
      label: z.string().max(120),
      code: z.string().max(80),
      description: z.string().max(600),
      cta: z.string().max(120),
    })
    .optional(),
  layoutVariant: z.string().max(60).optional(),
  // Standalone CTA band + image/discount band per-instance copy. These are
  // seeded by the generator (and restyled by the copy worker) — the PATCH
  // schema must round-trip them, otherwise the editor's first save silently
  // STRIPS them (`.strip()` drops unknown keys), the cta block's live content
  // no longer matches the hash the polish worker stamped over the pre-strip
  // row, and the section shows a false "ungenerated changes" flag forever.
  ctaHeadline: z.string().max(300).optional(),
  ctaBody: z.string().max(1000).optional(),
  ctaLabel: z.string().max(120).optional(),
  discountNote: z.string().max(500).optional(),
  discountCode: z.string().max(80).optional(),
  // Selected image for image_only / image_discount blocks (Task #2286) — a
  // product photo URL or a custom uploaded R2 URL. Must round-trip or the
  // editor's first autosave silently drops the user's selection.
  imageUrl: z.string().max(2048).optional(),
  bodyImage: emailBodyImagePreferenceSchema.optional(),
  // Preset-template section copy (Task #975) — opaque here, but must survive
  // the same round-trip for preset emails.
  preset: z.record(z.unknown()).optional(),
  // Task #2556 — per-slot image-treatment map (see EmailInstanceCopy.imageTreatments).
  // Must round-trip through the PATCH schema or autosave silently drops user selections.
  imageTreatments: z.record(
    z.discriminatedUnion("mode", [
      z.object({ mode: z.literal("automatic") }).strict(),
      z
        .object({
          mode: z.literal("selected"),
          url: z
            .string()
            .trim()
            .url()
            .max(2048)
            .refine((u) => /^https?:\/\//i.test(u), "Image treatment URL must use HTTP or HTTPS"),
        })
        .strict(),
      z.object({ mode: z.literal("none") }).strict(),
    ]),
  ).optional(),
};

export const emailInstanceCopySchema = z.object(emailInstanceCopyShape).strip();

export const emailBlockCopySchema = z
  .object({
    ...emailInstanceCopyShape,
    punchlines: z.record(z.string().max(300)).optional(),
    // Per-instance copy keyed by instance id (≤ 40 instances is plenty).
    instances: z.record(emailInstanceCopySchema).optional(),
  })
  .strip();

// Validation for the editable `linkConfig` payload (PATCH). "multi" is the
// legacy per-section mode, still accepted so old clients/rows round-trip; it
// behaves as "cta" (see `normalizeEmailLinkConfig`).
export const emailLinkConfigSchema = z
  .object({
    mode: z.enum(["single", "multi", "cta"]),
    sitewideUrl: z.string().max(2048).optional(),
    blockLinks: z.record(z.string(), z.string().max(2048)).optional(),
  })
  .strip();

export function parseEmailCtaLink(
  raw: unknown,
):
  | { ok: true; linkConfig: { mode: "single"; sitewideUrl: string } | null }
  | { ok: false; message: string } {
  if (raw === undefined || raw === null) return { ok: true, linkConfig: null };
  if (typeof raw !== "string") {
    return { ok: false, message: "CTA link must be text." };
  }
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, linkConfig: null };
  if (!/^https?:\/\/\S+$/i.test(trimmed)) {
    return {
      ok: false,
      message: "CTA link must be a full URL starting with http:// or https://",
    };
  }
  const parsed = emailLinkConfigSchema.safeParse({
    mode: "single",
    sitewideUrl: trimmed,
  });
  if (!parsed.success) {
    return {
      ok: false,
      message: "CTA link is too long — keep it under 2048 characters.",
    };
  }
  return { ok: true, linkConfig: { mode: "single", sitewideUrl: trimmed } };
}
export interface EmailFooterOverrides {
  // When present, this list FULLY replaces the snapshot's social links (a label
  // edit, a URL edit, and a per-item deletion are all expressed by replacing the
  // list). Absent = use the snapshot's list unchanged.
  socialLinks?: UniversalSocialLink[];
  // Disclaimer (legal text) override. Absent = use the snapshot's `legalText`.
  disclaimerText?: string;
  // When true, the disclaimer is hidden in BOTH the preview and the sent email,
  // regardless of `disclaimerText` / the snapshot value.
  disclaimerDeleted?: boolean;
  // Unsubscribe URL override. Absent = use the snapshot's `unsubscribeUrl`; when
  // blank/absent the sent email falls back to the platform unsubscribe behavior.
  // The unsubscribe link can never be deleted/hidden (compliance).
  unsubscribeUrl?: string;
  // Preferences nav link: label/URL edits + a deletion flag. Absent label/url =
  // use the snapshot value; `preferencesDeleted` hides it on BOTH surfaces.
  preferencesLabel?: string;
  preferencesUrl?: string;
  preferencesDeleted?: boolean;
  // Contact nav link: label/URL edits + a deletion flag. Contact has no snapshot
  // field today, so these overrides are its only source; `contactDeleted` hides it.
  contactLabel?: string;
  contactUrl?: string;
  contactDeleted?: boolean;
}

// Validation for the editable `footerOverrides` payload (PATCH). All optional;
// unknown keys are stripped. URLs/labels are length-bounded to keep payloads sane.
export const emailFooterOverridesSchema = z
  .object({
    socialLinks: z
      .array(
        z.object({
          platform: z.string().max(120),
          url: z.string().max(2048),
        }),
      )
      .max(24)
      .optional(),
    disclaimerText: z.string().max(4000).optional(),
    disclaimerDeleted: z.boolean().optional(),
    unsubscribeUrl: z.string().max(2048).optional(),
    preferencesLabel: z.string().max(120).optional(),
    preferencesUrl: z.string().max(2048).optional(),
    preferencesDeleted: z.boolean().optional(),
    contactLabel: z.string().max(120).optional(),
    contactUrl: z.string().max(2048).optional(),
    contactDeleted: z.boolean().optional(),
  })
  .strip();

export interface EmailBrandFooterSettings {
  physicalAddress?: string | null;
  unsubscribeUrl?: string | null;
}
export function resolveEffectiveEmailFooter(
  snapshot: UniversalFooterContent | null | undefined,
  overrides: EmailFooterOverrides | null | undefined,
  brandSettings?: EmailBrandFooterSettings | null,
): UniversalFooterContent | null {
  const brandAddress = brandSettings?.physicalAddress?.trim() || "";
  const brandUnsub = brandSettings?.unsubscribeUrl?.trim() || "";
  if (!snapshot && !overrides) {
    if (!brandAddress && !brandUnsub) return null;
    return {
      address: brandAddress || undefined,
      unsubscribeUrl: brandUnsub || undefined,
    };
  }
  const snap = snapshot ?? {};
  const ov = overrides ?? {};
  const socialLinks =
    ov.socialLinks !== undefined ? ov.socialLinks : snap.socialLinks;
  const legalText = ov.disclaimerDeleted
    ? ""
    : ov.disclaimerText !== undefined
      ? ov.disclaimerText
      : snap.legalText;
  const unsubscribeUrl =
    ov.unsubscribeUrl !== undefined ? ov.unsubscribeUrl : snap.unsubscribeUrl;
  const preferencesLabel =
    ov.preferencesLabel !== undefined ? ov.preferencesLabel : snap.preferencesLabel;
  const preferencesUrl =
    ov.preferencesUrl !== undefined ? ov.preferencesUrl : snap.preferencesUrl;
  const contactLabel =
    ov.contactLabel !== undefined ? ov.contactLabel : snap.contactLabel;
  const contactUrl =
    ov.contactUrl !== undefined ? ov.contactUrl : snap.contactUrl;
  return {
    ...snap,
    socialLinks,
    legalText,
    address: brandAddress || snap.address,
    unsubscribeUrl: brandUnsub || unsubscribeUrl,
    preferencesLabel,
    preferencesUrl,
    contactLabel,
    contactUrl,
    preferencesDeleted: !!ov.preferencesDeleted,
    contactDeleted: !!ov.contactDeleted,
  };
}

// -----------------------------------------------------------------------------
// email_express_universal_assets — Klaviyo-style "Universal" reusable content.
// A brand-scoped library of email building blocks (footer, promo banner,
// disclaimer, social links, announcement bar) saved once — with their links and
// copy — and dropped into any email. The per-type payload lives in `content`
// (jsonb, schema-less) so each type can evolve without migrations. When an asset
// is attached to an email it is SNAPSHOTTED into email.universalAssets, so
// editing the library asset later never changes an already-drafted email.
// -----------------------------------------------------------------------------
export const UNIVERSAL_ASSET_TYPES = [
  "footer",
  "promo_banner",
  "disclaimer",
  "social_links",
  "announcement_bar",
] as const;
export type UniversalAssetType = (typeof UNIVERSAL_ASSET_TYPES)[number];

export interface UniversalSocialLink {
  platform: string;
  url: string;
}
export interface UniversalFooterContent {
  logoUrl?: string;
  companyName?: string;
  address?: string;
  legalText?: string;
  unsubscribeLabel?: string;
  unsubscribeUrl?: string;
  preferencesLabel?: string;
  preferencesUrl?: string;
  contactLabel?: string;
  contactUrl?: string;
  socialLinks?: UniversalSocialLink[];
  // Computed by resolveEffectiveEmailFooter from the per-email overrides (the
  // library snapshot never sets these): when true the renderers hide that nav
  // link on BOTH the preview and the sent email.
  preferencesDeleted?: boolean;
  contactDeleted?: boolean;
}
export interface UniversalPromoBannerContent {
  imageUrl?: string;
  headline?: string;
  subtext?: string;
  ctaLabel?: string;
  linkUrl?: string;
}
export interface UniversalDisclaimerContent {
  text?: string;
}
export interface UniversalSocialLinksContent {
  links?: UniversalSocialLink[];
}
export interface UniversalAnnouncementBarContent {
  text?: string;
  linkLabel?: string;
  linkUrl?: string;
}
export type UniversalAssetContent =
  | UniversalFooterContent
  | UniversalPromoBannerContent
  | UniversalDisclaimerContent
  | UniversalSocialLinksContent
  | UniversalAnnouncementBarContent;

// One asset's content, snapshotted onto an email when attached. `sourceAssetId`
// links back to the library row it came from (null when generated fresh).
export interface EmailUniversalAssetSnapshot {
  sourceAssetId?: string | null;
  name?: string | null;
  generated?: boolean;
  content: UniversalAssetContent;
}
export type EmailUniversalAssets = Partial<
  Record<UniversalAssetType, EmailUniversalAssetSnapshot>
>;

export const emailExpressUniversalAssets = pgTable(
  "email_express_universal_assets",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    userId: varchar("user_id"),
    // One of UNIVERSAL_ASSET_TYPES.
    type: varchar("type", { length: 40 }).notNull(),
    name: varchar("name", { length: 255 }).notNull(),
    // Per-type payload (links, copy, image URLs). Schema-less; the shape is one
    // of the Universal*Content interfaces above keyed by `type`.
    content: jsonb("content").notNull(),
    // When true, this asset is pre-selected as the brand's default of its type
    // in the email builder. At most one default per (brand, type).
    isDefault: boolean("is_default").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("email_express_universal_assets_brand_idx").on(table.brandId),
    index("email_express_universal_assets_type_idx").on(table.brandId, table.type),
  ],
);

export const emailExpressBrandSettings = pgTable(
  "email_express_brand_settings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    // Physical mailing address shown in every footer (CAN-SPAM). Blank = the
    // snapshot's address (if any) keeps rendering.
    physicalAddress: text("physical_address").notNull().default(""),
    // Unsubscribe link URL. Blank = the per-email/snapshot chain, ultimately
    // falling back to Klaviyo's {% unsubscribe %} merge tag at send time.
    unsubscribeUrl: text("unsubscribe_url").notNull().default(""),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("email_express_brand_settings_brand_unique").on(table.brandId),
  ],
);
const universalSocialLinkSchema = z.object({
  platform: z.string().trim().max(60),
  url: z.string().trim().max(2048),
});
// One permissive bag covering every type's optional fields; unknown keys are
// stripped. Stronger per-type validation is unnecessary because each field is an
// optional, length-bounded string (or a small array of links).
export const universalAssetContentSchema = z
  .object({
    logoUrl: z.string().trim().max(2048).optional(),
    companyName: z.string().trim().max(255).optional(),
    address: z.string().trim().max(1000).optional(),
    legalText: z.string().trim().max(4000).optional(),
    unsubscribeLabel: z.string().trim().max(120).optional(),
    unsubscribeUrl: z.string().trim().max(2048).optional(),
    preferencesLabel: z.string().trim().max(120).optional(),
    preferencesUrl: z.string().trim().max(2048).optional(),
    socialLinks: z.array(universalSocialLinkSchema).max(12).optional(),
    imageUrl: z.string().trim().max(2048).optional(),
    headline: z.string().trim().max(255).optional(),
    subtext: z.string().trim().max(1000).optional(),
    ctaLabel: z.string().trim().max(120).optional(),
    linkUrl: z.string().trim().max(2048).optional(),
    text: z.string().trim().max(4000).optional(),
    links: z.array(universalSocialLinkSchema).max(12).optional(),
    linkLabel: z.string().trim().max(120).optional(),
  })
  .strip();

export const insertEmailExpressUniversalAssetSchema = createInsertSchema(
  emailExpressUniversalAssets,
)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    type: z.enum(UNIVERSAL_ASSET_TYPES),
    name: z.string().trim().min(1).max(255),
    content: universalAssetContentSchema,
  });
export type InsertEmailExpressUniversalAsset = z.infer<
  typeof insertEmailExpressUniversalAssetSchema
>;
export type EmailExpressUniversalAsset =
  typeof emailExpressUniversalAssets.$inferSelect;

// Single source of truth for "what URL should every CTA on an LP Express page
// point to". Prefer the user-configured value (including an explicit "" which
// means inert buttons); otherwise fall back to the primary product's PDP URL,
// then the brand URL, then "". Used by both the builder preview and the publish
// snapshot so the two never drift.
export function resolveLpCheckoutUrl(
  configured: string | null | undefined,
  productPdpUrl: string | null | undefined,
  brandUrl: string | null | undefined,
): string {
  if (configured !== null && configured !== undefined) {
    return configured.trim();
  }
  const pdp = (productPdpUrl ?? "").trim();
  if (pdp) return normalizeCheckoutFallbackUrl(pdp);
  return normalizeCheckoutFallbackUrl((brandUrl ?? "").trim());
}

// Brand/product URLs are often stored as bare domains ("yourstore.com") which
// aren't valid `new URL()` inputs and would otherwise fail checkout validation
// and block publishing. For the AUTO-DERIVED fallback only (never a value the
// user explicitly typed), prepend https:// when there's no scheme so the
// resolved default is a usable, valid URL.
function normalizeCheckoutFallbackUrl(value: string): string {
  const v = value.trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  // Only upgrade things that look like a domain (contain a dot, no whitespace);
  // leave anything else untouched so validation can still surface real problems.
  if (/^[^\s]+\.[^\s]+$/.test(v)) return `https://${v}`;
  return v;
}

// Validate a checkout URL: empty is allowed (=> inert buttons), otherwise it
// must be a well-formed http(s) URL. Returns null when valid, or an error
// message string when invalid.
export function validateCheckoutUrl(value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  if (!v) return null;
  let parsed: URL;
  try {
    parsed = new URL(v);
  } catch {
    return "Enter a valid http(s) URL (e.g. https://yourstore.com/checkout).";
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return "Checkout URL must start with http:// or https://.";
  }
  return null;
}

// Same-page anchor ids must start with a letter and contain only letters,
// digits, hyphens or underscores (matches the ids produced by
// `lpSectionAnchorId`). Kept here so client and server share one rule.
const LP_ANCHOR_HREF_RE = /^#[A-Za-z][\w-]*$/;

// Validate an editable CONTENT link href (footer/proof/origin links, etc.).
// Empty is allowed (=> inert/non-clickable). A leading "#" is treated as a
// same-page section anchor and validated against the anchor-id shape. Anything
// else must be a well-formed http(s) URL (reuses the checkout validator).
// Returns null when valid, or an error message string when invalid.
//
// Used for ALL editable LP Express link surfaces, including the checkout/CTA URL
// (so a buy button can jump to an on-page section). `validateCheckoutUrl` remains
// the http(s)-only variant for cases that must never accept an anchor.
export function validateLinkHref(value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  if (!v) return null;
  if (v.startsWith("#")) {
    return LP_ANCHOR_HREF_RE.test(v)
      ? null
      : "Enter a valid section anchor (e.g. #lp-faq).";
  }
  return validateCheckoutUrl(v);
}

// Validate an editable email FOOTER link URL (unsubscribe / preferences /
// contact / social). Footer links may use http(s) plus `mailto:`/`tel:` — a
// Contact link is commonly a `mailto:`. Empty is allowed (=> inert/non-clickable).
// Unsafe protocols (e.g. `javascript:`, `data:`) are rejected. Returns null when
// valid, or an error message string when invalid.
const FOOTER_LINK_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];
export function validateFooterLinkUrl(value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  if (!v) return null;
  let parsed: URL;
  try {
    parsed = new URL(v);
  } catch {
    return "Enter a valid URL (https://…, mailto:…, or tel:…).";
  }
  if (!FOOTER_LINK_PROTOCOLS.includes(parsed.protocol)) {
    return "Link must start with http://, https://, mailto:, or tel:.";
  }
  return null;
}

// Sanitize a stored footer link URL for rendering an `href` in the React
// preview/export surfaces (the MJML path normalizes separately). Returns the
// URL only when it parses to a safe protocol, otherwise "#" so a malicious or
// malformed value (e.g. `javascript:`) can never become a live href. A blank
// value also collapses to "#".
export function safeFooterHref(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  if (!v) return "#";
  try {
    const parsed = new URL(v);
    if (FOOTER_LINK_PROTOCOLS.includes(parsed.protocol)) return v;
  } catch {
    /* fall through to inert href */
  }
  return "#";
}

// -----------------------------------------------------------------------------
// lp_express_published_pages — a frozen, publicly-viewable snapshot of an
// LP Express page. Publishing copies the page's generated content + durable
// hero R2 image URLs (plus the resolved brand/product/persona/angle/sale and
// reviews) into `snapshot` so the public page never changes if the source run
// is later edited or deleted. Served logged-out via `/p/:slug`.
// -----------------------------------------------------------------------------
export const lpExpressPublishedPages = pgTable(
  "lp_express_published_pages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    slug: varchar("slug", { length: 255 }).notNull(),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    // Snapshot of the source brand's slug at publish time. The public route is
    // `/p/:brandSlug/:slug`; resolving by this stored pair means the public page
    // never needs a live brand lookup (no auth/workspace context) and stays
    // stable even if the brand's slug later changes. Brand slugs are notNull but
    // not globally unique, so resolution keys off this stored brandSlug+slug
    // pair, not a live brand record.
    brandSlug: varchar("brand_slug", { length: 255 }).notNull(),
    // Source page/run. The published snapshot is self-contained, so null these
    // out on delete rather than cascading the published row away.
    pageId: varchar("page_id").references(() => lpExpressPages.id, { onDelete: "set null" }),
    runId: varchar("run_id").references(() => lpExpressRuns.id, { onDelete: "set null" }),
    // Frozen render bundle: { brand, products, sale, persona, angle,
    // reviewsByProduct, generatedContent } — everything LpExpressRenderer needs.
    snapshot: jsonb("snapshot").notNull(),
    // Resolved buy-button target (products.pdpUrl). Empty/null => inert button.
    ctaUrl: varchar("cta_url", { length: 1000 }),
    // SEO / Open Graph.
    seoTitle: varchar("seo_title", { length: 255 }),
    seoDescription: text("seo_description"),
    ogImageUrl: varchar("og_image_url", { length: 1000 }),
    // published | unpublished
    status: varchar("status", { length: 50 }).notNull().default("published"),
    // Whether this page is also served on the brand's connected custom domain
    // (at `https://<custom-domain>/<slug>`). The /p/:brandSlug/:slug URL always
    // works regardless; this toggle only controls custom-domain exposure.
    customDomainEnabled: boolean("custom_domain_enabled").notNull().default(false),
    // Publish-time snapshot of the resolved Meta Pixel state (brand pixel ID +
    // the page's opt-in toggle). The public serving path injects the pixel from
    // THESE columns only — never a live brand/page lookup — so the live page
    // updates on republish, exactly like the content snapshot.
    metaPixelId: varchar("meta_pixel_id", { length: 32 }),
    metaPixelEnabled: boolean("meta_pixel_enabled").notNull().default(false),
    publishedByUserId: varchar("published_by_user_id"),
    publishedAt: timestamp("published_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("lp_express_published_pages_brand_idx").on(table.brandId),
    index("lp_express_published_pages_page_idx").on(table.pageId),
    // Slugs are unique PER BRAND, not globally — two brands can both own
    // "summer-sale". Collision checks (publish/rename) key off (brandId, slug).
    uniqueIndex("lp_express_published_pages_brand_slug_unique").on(
      table.brandId,
      table.slug,
    ),
    // Public resolution path: GET /p/:brandSlug/:slug resolves by this pair.
    index("lp_express_published_pages_brandslug_slug_idx").on(
      table.brandSlug,
      table.slug,
    ),
  ],
);

export const insertLpExpressPublishedPageSchema = createInsertSchema(lpExpressPublishedPages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  publishedAt: true,
});
export type InsertLpExpressPublishedPage = z.infer<typeof insertLpExpressPublishedPageSchema>;
export type LpExpressPublishedPage = typeof lpExpressPublishedPages.$inferSelect;

// -----------------------------------------------------------------------------
// lp_express_leads — native lead storage for the "Coffee Shop Lead Capture"
// LP Express template. Each row is one visitor sign-up captured on a PUBLISHED
// page (public unauthenticated POST on /p/:brandSlug/:slug), scoped to the
// brand and the published page it came from. The coupon code shown in the
// page's reveal card is generated server-side at insert time and stored here
// so a repeat submission (same phone on the same page) returns the SAME code
// instead of minting a new one ("one coupon per phone number").
// -----------------------------------------------------------------------------
export const lpExpressLeads = pgTable(
  "lp_express_leads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    // The published page the lead came from. Kept (set null) if the published
    // page is later deleted — the lead itself is still brand data worth keeping.
    publishedPageId: varchar("published_page_id").references(
      () => lpExpressPublishedPages.id,
      { onDelete: "set null" },
    ),
    firstName: varchar("first_name", { length: 255 }).notNull(),
    email: varchar("email", { length: 320 }).notNull(),
    phone: varchar("phone", { length: 50 }).notNull(),
    // Digits-only form of `phone`, used for per-page dedupe.
    phoneNormalized: varchar("phone_normalized", { length: 50 }).notNull(),
    couponCode: varchar("coupon_code", { length: 64 }).notNull(),
    // Template type of the page the lead was captured on (e.g. "coffee-shop"),
    // SNAPSHOTTED at insert time so the lead keeps its template even if the
    // published page is later deleted (published_page_id goes null). Drives the
    // template column/filter in the master-only /admin/leads view.
    templateType: varchar("template_type", { length: 64 }).notNull().default("coffee-shop"),
    // Repeat-signup tracking: bumped on the dedupe path when the same phone
    // re-submits on the same page (the form returns the SAME coupon). A lead
    // with resubmissionCount > 0 shows a "repeat" badge in the admin view.
    resubmissionCount: integer("resubmission_count").notNull().default(0),
    lastSubmittedAt: timestamp("last_submitted_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("lp_express_leads_brand_idx").on(table.brandId),
    index("lp_express_leads_page_idx").on(table.publishedPageId),
    // Admin view filters/sorts.
    index("lp_express_leads_template_idx").on(table.templateType),
    index("lp_express_leads_created_idx").on(table.createdAt),
    // Dedupe path: one lead per phone per published page. Unique so that
    // concurrent submissions can't produce duplicate rows/coupon codes.
    uniqueIndex("lp_express_leads_page_phone_idx").on(table.publishedPageId, table.phoneNormalized),
  ],
);

export const insertLpExpressLeadSchema = createInsertSchema(lpExpressLeads).omit({
  id: true,
  createdAt: true,
});
export type InsertLpExpressLead = z.infer<typeof insertLpExpressLeadSchema>;
export type LpExpressLead = typeof lpExpressLeads.$inferSelect;

export const demoRequests = pgTable(
  "demo_requests",
  {
    id: varchar("id", { length: 36 }).primaryKey(),
    name: varchar("name", { length: 100 }).notNull(),
    email: varchar("email", { length: 254 }).notNull(),
    business: varchar("business", { length: 150 }).notNull(),
    plan: varchar("plan", { length: 30 }).notNull(),
    preferredAt: timestamp("preferred_at", { withTimezone: true }).notNull(),
    timezone: varchar("timezone", { length: 100 }).notNull(),
    goals: text("goals").notNull(),
    status: varchar("status", { length: 30 }).notNull().default("requested"),
    ipHash: varchar("ip_hash", { length: 64 }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    index("demo_requests_ip_created_idx").on(table.ipHash, table.createdAt),
  ],
);

export type DemoRequest = typeof demoRequests.$inferSelect;

// -----------------------------------------------------------------------------
// lp_express_custom_domains — maps a user-owned hostname (e.g.
// landing.theirbrand.com) to a BRAND. Once DNS verification confirms the
// hostname CNAMEs at the app's target host, requests arriving on that hostname
// serve the brand's published LP Express pages at `/<slug>` paths (each page
// opted in via lpExpressPublishedPages.customDomainEnabled), with the same
// render + SEO/OG meta as the canonical `/p/:brandSlug/:slug` path. One domain
// per brand (brandId unique); a hostname is globally unique. SSL/TLS, apex/
// A-records, and admin tooling are out of scope (CNAME subdomains only).
// -----------------------------------------------------------------------------
export const lpExpressCustomDomains = pgTable(
  "lp_express_custom_domains",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // Fully-qualified hostname, normalized lowercase. Globally unique — a given
    // hostname can only ever point at one brand.
    hostname: varchar("hostname", { length: 255 }).notNull(),
    // User who connected the domain.
    userId: varchar("user_id").notNull(),
    // Brand that owns the domain. One custom domain per brand (unique).
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    // pending | verified
    status: varchar("status", { length: 50 }).notNull().default("pending"),
    // Cloudflare for SaaS: the id of the custom hostname registered in the
    // `personaos-lps.com` zone. Cloudflare provisions + renews the certificate
    // and proxies verified traffic to this app's origin.
    cloudflareHostnameId: varchar("cloudflare_hostname_id", { length: 255 }),
    // Raw Cloudflare hostname status (e.g. "pending", "active") — kept for
    // display/debug and to drive the UI's setup lifecycle messaging.
    cloudflareStatus: varchar("cloudflare_status", { length: 50 }),
    // Last verification error (shown to the user while pending).
    lastError: text("last_error"),
    verifiedAt: timestamp("verified_at"),
    lastCheckedAt: timestamp("last_checked_at"),
    // SSL/TLS state for the hostname. TLS is terminated by the hosting
    // platform's edge (the app can't bind :443 itself), which provisions and
    // renews the certificate automatically once the domain is attached to the
    // deployment. We don't issue certs here — instead we actively probe the
    // live HTTPS endpoint and record what we observe so the UI can show whether
    // a domain is actually secured and when its certificate expires.
    // pending (DNS verified, cert not yet observed) | secured | error
    sslStatus: varchar("ssl_status", { length: 50 }).notNull().default("pending"),
    // notAfter of the certificate currently served on the hostname.
    sslExpiresAt: timestamp("ssl_expires_at"),
    // Last time we probed the hostname's TLS endpoint.
    sslCheckedAt: timestamp("ssl_checked_at"),
    // Last SSL probe error (shown while the cert isn't yet valid/served).
    sslError: text("ssl_error"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("lp_express_custom_domains_hostname_unique").on(table.hostname),
    uniqueIndex("lp_express_custom_domains_brand_unique").on(table.brandId),
    index("lp_express_custom_domains_status_idx").on(table.status),
  ],
);

export const insertLpExpressCustomDomainSchema = createInsertSchema(
  lpExpressCustomDomains,
).omit({
  id: true,
  status: true,
  cloudflareHostnameId: true,
  cloudflareStatus: true,
  lastError: true,
  verifiedAt: true,
  lastCheckedAt: true,
  sslStatus: true,
  sslExpiresAt: true,
  sslCheckedAt: true,
  sslError: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertLpExpressCustomDomain = z.infer<
  typeof insertLpExpressCustomDomainSchema
>;
export type LpExpressCustomDomain = typeof lpExpressCustomDomains.$inferSelect;

// -----------------------------------------------------------------------------
// lp_express_storage_orphans — soft-delete / quarantine ledger for LP Express
// durable media stored in R2 (user uploads under `lp-express/`, AI hero images
// under `lp-express-hero/`). When the storage sweeper finds an object no longer
// referenced by ANY live page, version-history snapshot, or published page, it
// records the object key here (status "quarantined") INSTEAD of deleting it
// right away. A later sweep re-confirms the object is STILL unreferenced and,
// once it has been quarantined longer than the retention window, hard-deletes
// the R2 object and flips the row to "purged". If a quarantined object becomes
// referenced again (e.g. a snapshot is restored), its row is removed so it is
// never purged. This gives an auditable recovery window before any irreversible
// delete — the DB record is the soft-delete; the file delete is deferred.
// -----------------------------------------------------------------------------
export const lpExpressStorageOrphans = pgTable(
  "lp_express_storage_orphans",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // R2 object key, e.g. "lp-express/<userId>/<uuid>.jpg". One row per object.
    objectKey: varchar("object_key", { length: 1024 }).notNull().unique(),
    // quarantined | purged
    status: varchar("status", { length: 50 }).notNull().default("quarantined"),
    // First sweep that saw this object unreferenced — starts the retention clock.
    firstSeenAt: timestamp("first_seen_at").defaultNow(),
    // Most recent sweep that still saw it unreferenced.
    lastSeenAt: timestamp("last_seen_at").defaultNow(),
    // When the R2 object was actually deleted (null until purged).
    purgedAt: timestamp("purged_at"),
    // Best-effort object size for reclaimed-bytes reporting.
    sizeBytes: integer("size_bytes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("lp_express_storage_orphans_status_idx").on(table.status),
  ],
);

export const insertLpExpressStorageOrphanSchema = createInsertSchema(lpExpressStorageOrphans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertLpExpressStorageOrphan = z.infer<typeof insertLpExpressStorageOrphanSchema>;
export type LpExpressStorageOrphan = typeof lpExpressStorageOrphans.$inferSelect;

// -----------------------------------------------------------------------------
// generated_ad_storage_orphans — soft-delete / quarantine ledger for saved-ad
// durable images stored in R2 under `generated-ads/`. Editing a saved ad in
// place uploads a NEW R2 object and repoints the ad's columns at it, leaving
// the previous object behind. Instead of deleting that superseded object
// eagerly inside the request path, the edit endpoint records its key here
// (status "quarantined"). A background sweep re-confirms the key is still
// referenced by NO live generated ad and, once it has been quarantined longer
// than the retention window, hard-deletes the R2 object and flips the row to
// "purged". If a quarantined key becomes referenced by a live ad again, its
// row is removed so it is never purged. Mirrors lp_express_storage_orphans.
// -----------------------------------------------------------------------------
export const generatedAdStorageOrphans = pgTable(
  "generated_ad_storage_orphans",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // R2 object key, e.g. "generated-ads/<brandId>/<adId>/<uuid>.png".
    objectKey: varchar("object_key", { length: 1024 }).notNull().unique(),
    // quarantined | purged
    status: varchar("status", { length: 50 }).notNull().default("quarantined"),
    // When the object was first recorded as superseded — starts the retention clock.
    firstSeenAt: timestamp("first_seen_at").defaultNow(),
    // Most recent sweep / record that still saw it unreferenced.
    lastSeenAt: timestamp("last_seen_at").defaultNow(),
    // When the R2 object was actually deleted (null until purged).
    purgedAt: timestamp("purged_at"),
    // Best-effort object size for reclaimed-bytes reporting.
    sizeBytes: integer("size_bytes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("generated_ad_storage_orphans_status_idx").on(table.status),
  ],
);

export const insertGeneratedAdStorageOrphanSchema = createInsertSchema(generatedAdStorageOrphans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertGeneratedAdStorageOrphan = z.infer<typeof insertGeneratedAdStorageOrphanSchema>;
export type GeneratedAdStorageOrphan = typeof generatedAdStorageOrphans.$inferSelect;

// -----------------------------------------------------------------------------
// brand_font_storage_orphans — soft-delete / quarantine ledger for brand-font
// files uploaded to R2 under `brand-fonts/<userId>/`. Removing a font from a
// brand's Design System deletes its R2 object eagerly, and deleting a whole
// brand does too, but archived brands and abandoned uploads (a font uploaded
// but never saved to a brand) leave objects behind. A background sweep records
// any `brand-fonts/` object no longer referenced by ANY non-archived brand's
// brandDesignSystem.fonts here (status "quarantined") instead of deleting it
// right away. A later sweep re-confirms it is STILL unreferenced and, once it
// has been quarantined longer than the retention window, hard-deletes the R2
// object and flips the row to "purged". If a quarantined object becomes
// referenced again (e.g. a brand is un-archived), its row is removed so it is
// never purged. Mirrors lp_express_storage_orphans / generated_ad_storage_orphans.
// -----------------------------------------------------------------------------
export const brandFontStorageOrphans = pgTable(
  "brand_font_storage_orphans",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // R2 object key, e.g. "brand-fonts/<userId>/<uuid>.woff2". One row per object.
    objectKey: varchar("object_key", { length: 1024 }).notNull().unique(),
    // quarantined | purged
    status: varchar("status", { length: 50 }).notNull().default("quarantined"),
    // First sweep that saw this object unreferenced — starts the retention clock.
    firstSeenAt: timestamp("first_seen_at").defaultNow(),
    // Most recent sweep that still saw it unreferenced.
    lastSeenAt: timestamp("last_seen_at").defaultNow(),
    // When the R2 object was actually deleted (null until purged).
    purgedAt: timestamp("purged_at"),
    // Best-effort object size for reclaimed-bytes reporting.
    sizeBytes: integer("size_bytes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("brand_font_storage_orphans_status_idx").on(table.status),
  ],
);

export const insertBrandFontStorageOrphanSchema = createInsertSchema(brandFontStorageOrphans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertBrandFontStorageOrphan = z.infer<typeof insertBrandFontStorageOrphanSchema>;
export type BrandFontStorageOrphan = typeof brandFontStorageOrphans.$inferSelect;

// -----------------------------------------------------------------------------
// Landing-page template TYPES — global catalog of page shapes the LP Express
// generator can produce (e.g. "long-form", "quiz-funnel", "listicle").
// Seeded idempotently at startup.
// -----------------------------------------------------------------------------
export const landingPageTemplateTypes = pgTable(
  "landing_page_template_types",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    key: varchar("key", { length: 64 }).notNull().unique(),
    source: varchar("source", { length: 32 }).notNull().default("hand-coded"),
    label: varchar("label", { length: 128 }).notNull(),
    description: text("description"),
    sortOrder: integer("sort_order").notNull().default(0),
    isActive: boolean("is_active").notNull().default(true),
    // NULL/empty is global only when brandAccessRestricted is false (legacy).
    // A populated list of normalized brand identifiers (slugs) limits
    // the template to those brands only. Matched case-insensitively against the
    // active brand's slug/name.
    allowedBrandSlugs: text("allowed_brand_slugs").array(),
    brandAccessRestricted: boolean("brand_access_restricted").notNull().default(false),
    availableForSmb: boolean("available_for_smb").notNull().default(false),
    smbOnly: boolean("smb_only").notNull().default(false),
    // Set when a master user renames the template from Admin → LP Templates.
    // The startup seed only syncs `label` from the catalog when this is false,
    // so admin-edited names survive restarts.
    labelCustomized: boolean("label_customized").notNull().default(false),
    // Template Studio registrations point at the existing reusable template
    // table; this is deliberately not a parallel catalog or page table.
    registeredTemplateId: varchar("registered_template_id").references(
      () => landingPageTemplates.id,
      { onDelete: "set null" },
    ),
    // LP Express renderer/copy contract for a Studio publication. The seed is
    // the JSON object supplied to the strict-JSON copy workflow/fallback.
    rendererTemplateKey: varchar("renderer_template_key", { length: 64 }),
    contentShape: jsonb("content_shape"),
    contentSeed: jsonb("content_seed"),
    copySchema: jsonb("copy_schema"),
    copyGuidance: text("copy_guidance"),
    strictJsonSimWorkflowUrl: text("strict_json_sim_workflow_url"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    check(
      "landing_page_template_types_source_check",
      sql`${table.source} IN ('hand-coded', 'template-studio')`,
    ),
  ],
);
export type LandingPageTemplateType = typeof landingPageTemplateTypes.$inferSelect;

export const templateBrandVariants = pgTable(
  "template_brand_variants",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    templateKey: varchar("template_key", { length: 64 }).notNull().references(
      () => landingPageTemplateTypes.key,
      { onDelete: "cascade" },
    ),
    brandId: varchar("brand_id").notNull().references(() => brands.id, {
      onDelete: "cascade",
    }),
    status: varchar("status", { length: 16 }).notNull().default("pending"),
    // The prepared, static artifact and its section metadata are persisted
    // together. This never contains executable provider TSX.
    html: text("html"),
    sections: jsonb("sections"),
    baselineHtml: text("baseline_html").notNull(),
    baselineFingerprint: varchar("baseline_fingerprint", { length: 64 }).notNull(),
    // Hash of the authored publication before CSS/font materialization. This
    // lets status/Generate reuse a ready artifact without re-fetching external
    // resources; NULL is retained only for legacy rows created before this
    // provenance was recorded.
    baselineSourceFingerprint: varchar("baseline_source_fingerprint", { length: 64 }),
    contentSeed: jsonb("content_seed").notNull(),
    copySchema: jsonb("copy_schema").notNull(),
    registeredTemplateId: varchar("registered_template_id").notNull().references(
      () => landingPageTemplates.id,
      { onDelete: "cascade" },
    ),
    designSystemId: text("design_system_id").notNull(),
    mpEditorId: text("mp_editor_id"),
    mpArtifactId: text("mp_artifact_id"),
    failureReason: text("failure_reason"),
    // Internal-only provider/error detail. Route serializers must not expose it.
    failureDiagnostics: text("failure_diagnostics"),
    attemptId: varchar("attempt_id", { length: 64 }).notNull(),
    leaseOwner: varchar("lease_owner", { length: 64 }),
    leaseExpiresAt: timestamp("lease_expires_at"),
    // reserved -> submitting -> submitted. Ambiguous is terminal until a
    // human resolves it; never turn it back into a paid create automatically.
    submissionState: varchar("submission_state", { length: 16 }).notNull().default("reserved"),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("template_brand_variants_pair_unique").on(table.templateKey, table.brandId),
    index("template_brand_variants_pending_idx").on(table.status, table.leaseExpiresAt),
    check(
      "template_brand_variants_status_check",
      sql`${table.status} IN ('pending', 'ready', 'failed')`,
    ),
    check(
      "template_brand_variants_submission_state_check",
      sql`${table.submissionState} IN ('reserved', 'submitting', 'submitted', 'failed', 'ambiguous')`,
    ),
  ],
);
export const productTruths = pgTable(
  "product_truths",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    productId: varchar("product_id").notNull().unique().references(() => products.id),
    // Free-shape JSON the SIM_PRODUCT_TRUTH_URL workflow returns. Kept as
    // jsonb so the workflow can evolve without schema churn. The KC ad
    // prompt workflow gets this object verbatim.
    extractedTruths: jsonb("extracted_truths").notNull(),
    // Snapshot of the product fields fed into the truth-extraction call.
    sourceProductSnapshot: jsonb("source_product_snapshot"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
  },
  (table) => [
    index("product_truths_product_idx").on(table.productId),
    index("product_truths_brand_idx").on(table.brandId),
  ],
);

export const creativeAngles = pgTable(
  "creative_angles",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    productId: varchar("product_id").notNull().references(() => products.id),
    name: varchar("name", { length: 255 }).notNull(),
    // Apparel-friendly category buckets: "product", "ugc", "fit-fabric",
    // "lifestyle", "bold-graphic", "other".
    category: varchar("category", { length: 50 }).notNull().default("other"),
    coreClaim: text("core_claim"),
    bodyFeelLanguage: text("body_feel_language"),
    fitLanguage: text("fit_language"),
    visualDirection: text("visual_direction"),
    copyTone: text("copy_tone"),
    rationale: text("rationale"),
    // "generated" | "manual"
    source: varchar("source", { length: 20 }).notNull().default("generated"),
    active: boolean("active").notNull().default(true),
    archivedAt: timestamp("archived_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
  },
  (table) => [
    index("creative_angles_product_idx").on(table.productId),
    index("creative_angles_brand_idx").on(table.brandId),
  ],
);

export const insertProductTruthSchema = createInsertSchema(productTruths).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCreativeAngleSchema = createInsertSchema(creativeAngles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
});
export type ProductTruth = typeof productTruths.$inferSelect;
export type InsertProductTruth = z.infer<typeof insertProductTruthSchema>;
export type CreativeAngle = typeof creativeAngles.$inferSelect;
export type InsertCreativeAngle = z.infer<typeof insertCreativeAngleSchema>;

// -----------------------------------------------------------------------------
// KILL CREW (KC) — per-template "design prompt" store.
// One row per ad-unit template, holding the KC-specific design prompt
// the SIM_AD_PROMPT_URL workflow consumes alongside the product +
// creativeDirection. Distinct from `ad_unit_types.image_prompt_template`
// (which the standard Ads Express + Static Ads Unit module use) so KC
// can iterate on its own per-template prompts without disrupting the
// non-KC pipeline. Managed from the admin Tabs > "KC Prompts" page.
// -----------------------------------------------------------------------------
export const adUnitKcPrompts = pgTable(
  "ad_unit_kc_prompts",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    adUnitTypeId: varchar("ad_unit_type_id")
      .notNull()
      .unique()
      .references(() => adUnitTypes.id, { onDelete: "cascade" }),
    designPrompt: text("design_prompt").notNull(),
    // When true, the template's `adgenExampleImageUrl` is sent to Fal
    // as the FIRST `image_urls` entry on KC runs (visual style anchor).
    // When false, only the brand logo + product photos are sent. Default
    // true so newly-imported templates use their visual reference unless
    // an admin explicitly turns it off.
    useTemplateImage: boolean("use_template_image").notNull().default(true),
    updatedAt: timestamp("updated_at").defaultNow(),
    updatedBy: varchar("updated_by"),
  },
);
export const insertAdUnitKcPromptSchema = createInsertSchema(adUnitKcPrompts).omit({
  id: true,
  updatedAt: true,
});
export type AdUnitKcPrompt = typeof adUnitKcPrompts.$inferSelect;
export type InsertAdUnitKcPrompt = z.infer<typeof insertAdUnitKcPromptSchema>;

// -----------------------------------------------------------------------------
// PIXAR CAMPAIGNS — full pipeline clone of the Claymation video-campaign system.
// concept -> script -> beat map -> storyboards -> scene prompts -> scene videos
// -> final render -> captions. Tables mirror claymation_* exactly; only the
// names differ so Pixar data stays isolated. Replaces the OLD simple Pixar
// wizard feature (single pixar_campaigns table), which was deleted.
// -----------------------------------------------------------------------------
export const pixarCampaigns = pgTable("pixar_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  name: varchar("name", { length: 255 }).notNull(),
  personaId: varchar("persona_id").references(() => personas.id),
  productId: varchar("product_id").references(() => products.id),
  narrativeId: varchar("narrative_id").references(() => narratives.id),
  narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
  adUnitTypeId: varchar("ad_unit_type_id").references(() => adUnitTypes.id),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  concept: text("concept"),
  targetAudience: text("target_audience"),
  videoStyle: varchar("video_style", { length: 100 }),
  duration: varchar("duration", { length: 50 }),
  videoModel: varchar("video_model", { length: 50 }).default("seedance-2.0"),
  secondsPerScene: integer("seconds_per_scene").default(4),
  videoCount: integer("video_count").default(1),
  avatarCount: integer("avatar_count").default(1),
  campaignMode: varchar("campaign_mode", { length: 50 }).default("oneshot"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  notes: jsonb("notes"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  index("pixar_campaigns_brand_idx").on(table.brandId),
]);

export const pixarScripts = pgTable("pixar_scripts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pixarCampaignId: varchar("pixar_campaign_id").notNull().references(() => pixarCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  avatarId: varchar("avatar_id"),
  title: varchar("title", { length: 255 }),
  hook: text("hook"),
  body: text("body"),
  cta: text("cta"),
  fullScript: text("full_script"),
  talkingPoints: jsonb("talking_points"),
  visualDirections: jsonb("visual_directions"),
  thumbnailUrl: text("thumbnail_url"),
  videoUrl: text("video_url"),
  scenePrompts: jsonb("scene_prompts"),
  scenePromptStatus: varchar("scene_prompt_status", { length: 20 }).default("idle"),
  scenePromptError: text("scene_prompt_error"),
  sceneVideos: jsonb("scene_videos"),
  panelImages: jsonb("panel_images"),
  panelStatus: varchar("panel_status", { length: 20 }).default("idle"),
  panelError: text("panel_error"),
  storyboardPrompt: text("storyboard_prompt"),
  storyboardImageUrl: text("storyboard_image_url"),
  storyboardStatus: varchar("storyboard_status", { length: 20 }).default("idle"),
  storyboardError: text("storyboard_error"),
  storyboardPrompt2: text("storyboard_prompt_2"),
  storyboardImageUrl2: text("storyboard_image_url_2"),
  storyboardStatus2: varchar("storyboard_status_2", { length: 20 }).default("idle"),
  storyboardError2: text("storyboard_error_2"),
  storyboardPrompt3: text("storyboard_prompt_3"),
  storyboardImageUrl3: text("storyboard_image_url_3"),
  storyboardStatus3: varchar("storyboard_status_3", { length: 20 }).default("idle"),
  storyboardError3: text("storyboard_error_3"),
  voiceOverUrl: text("voice_over_url"),
  voiceOverStatus: varchar("voice_over_status", { length: 20 }).default("idle"),
  voiceOverError: text("voice_over_error"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("pixar_scripts_campaign_idx").on(table.pixarCampaignId),
  index("pixar_scripts_brand_idx").on(table.brandId),
]);

export const pixarScriptPlans = pgTable("pixar_script_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pixarCampaignId: varchar("pixar_campaign_id").notNull().references(() => pixarCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  scriptNumber: integer("script_number").notNull(),
  title: text("title").notNull(),
  conceptDescription: text("concept_description"),
  hookStyle: text("hook_style"),
  storyType: text("story_type"),
  primaryEmotion: text("primary_emotion"),
  primaryObjectionOrDesire: text("primary_objection_or_desire"),
  setting: text("setting"),
  keyDistinction: text("key_distinction"),
  conceptJson: jsonb("concept_json"),
  avatarId: varchar("avatar_id"),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("pixar_script_plans_campaign_idx").on(table.pixarCampaignId),
]);

export const pixarAvatars = pgTable("pixar_avatars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pixarCampaignId: varchar("pixar_campaign_id").notNull().references(() => pixarCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  imageUrl: varchar("image_url").notNull(),
  resolvedPrompt: text("resolved_prompt"),
  status: varchar("status", { length: 20 }).default("generated").notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("pixar_avatars_campaign_idx").on(table.pixarCampaignId),
]);

export const insertPixarCampaignSchema = createInsertSchema(pixarCampaigns).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertPixarScriptSchema = createInsertSchema(pixarScripts).omit({ id: true, createdAt: true, updatedAt: true });
export const insertPixarScriptPlanSchema = createInsertSchema(pixarScriptPlans).omit({ id: true, createdAt: true });
export const insertPixarAvatarSchema = createInsertSchema(pixarAvatars).omit({ id: true, createdAt: true });

export type PixarCampaign = typeof pixarCampaigns.$inferSelect;
export type InsertPixarCampaign = z.infer<typeof insertPixarCampaignSchema>;
export type PixarScript = typeof pixarScripts.$inferSelect;
export type InsertPixarScript = z.infer<typeof insertPixarScriptSchema>;
export type PixarScriptPlan = typeof pixarScriptPlans.$inferSelect;
export type InsertPixarScriptPlan = z.infer<typeof insertPixarScriptPlanSchema>;
export type PixarAvatar = typeof pixarAvatars.$inferSelect;
export type InsertPixarAvatar = z.infer<typeof insertPixarAvatarSchema>;

export const pixarRenders = pgTable("pixar_renders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pixarScriptId: varchar("pixar_script_id").notNull().references(() => pixarScripts.id, { onDelete: "cascade" }),
  pixarCampaignId: varchar("pixar_campaign_id").notNull().references(() => pixarCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("queued"),
  progress: integer("progress").default(0),
  outputUrl: text("output_url"),
  error: text("error"),
  durationSec: integer("duration_sec"),
  sceneCount: integer("scene_count"),
  captionedVideoUrl: text("captioned_video_url"),
  captionedVideoStatus: varchar("captioned_video_status", { length: 20 }).default("idle"),
  captionedVideoError: text("captioned_video_error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("pixar_renders_script_idx").on(table.pixarScriptId),
  index("pixar_renders_campaign_idx").on(table.pixarCampaignId),
]);

export const insertPixarRenderSchema = createInsertSchema(pixarRenders).omit({ id: true, createdAt: true, updatedAt: true });
export type PixarRender = typeof pixarRenders.$inferSelect;
export type InsertPixarRender = z.infer<typeof insertPixarRenderSchema>;

export const pixarCharacterVoices = pgTable("pixar_character_voices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ownerType: varchar("owner_type", { length: 30 }).notNull(),
  ownerId: varchar("owner_id").notNull(),
  brandId: varchar("brand_id").notNull(),
  elevenlabsVoiceId: varchar("elevenlabs_voice_id", { length: 100 }),
  voiceName: varchar("voice_name", { length: 255 }),
  voiceDescription: text("voice_description"),
  sourceImageUrl: text("source_image_url"),
  status: varchar("status", { length: 20 }).notNull().default("generating"),
  error: text("error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("pixar_character_voices_owner_uniq").on(table.ownerType, table.ownerId),
  index("pixar_character_voices_brand_idx").on(table.brandId),
]);

export const insertPixarCharacterVoiceSchema = createInsertSchema(pixarCharacterVoices).omit({ id: true, createdAt: true, updatedAt: true });
export type PixarCharacterVoice = typeof pixarCharacterVoices.$inferSelect;
export type InsertPixarCharacterVoice = z.infer<typeof insertPixarCharacterVoiceSchema>;


// -----------------------------------------------------------------------------
// SIMPSONS Video Campaigns — full pipeline clone (mirrors pixar_* / claymation_*).
// Brand-new feature; tables isolated under simpsons_* so data never collides.
// -----------------------------------------------------------------------------
export const simpsonsCampaigns = pgTable("simpsons_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  name: varchar("name", { length: 255 }).notNull(),
  personaId: varchar("persona_id").references(() => personas.id),
  productId: varchar("product_id").references(() => products.id),
  narrativeId: varchar("narrative_id").references(() => narratives.id),
  narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
  adUnitTypeId: varchar("ad_unit_type_id").references(() => adUnitTypes.id),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  concept: text("concept"),
  targetAudience: text("target_audience"),
  videoStyle: varchar("video_style", { length: 100 }),
  duration: varchar("duration", { length: 50 }),
  videoModel: varchar("video_model", { length: 50 }).default("seedance-2.0"),
  secondsPerScene: integer("seconds_per_scene").default(4),
  videoCount: integer("video_count").default(1),
  avatarCount: integer("avatar_count").default(1),
  campaignMode: varchar("campaign_mode", { length: 50 }).default("oneshot"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  notes: jsonb("notes"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  index("simpsons_campaigns_brand_idx").on(table.brandId),
]);

export const simpsonsScripts = pgTable("simpsons_scripts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  simpsonsCampaignId: varchar("simpsons_campaign_id").notNull().references(() => simpsonsCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  avatarId: varchar("avatar_id"),
  title: varchar("title", { length: 255 }),
  hook: text("hook"),
  body: text("body"),
  cta: text("cta"),
  fullScript: text("full_script"),
  talkingPoints: jsonb("talking_points"),
  visualDirections: jsonb("visual_directions"),
  thumbnailUrl: text("thumbnail_url"),
  videoUrl: text("video_url"),
  scenePrompts: jsonb("scene_prompts"),
  scenePromptStatus: varchar("scene_prompt_status", { length: 20 }).default("idle"),
  scenePromptError: text("scene_prompt_error"),
  sceneVideos: jsonb("scene_videos"),
  panelImages: jsonb("panel_images"),
  panelStatus: varchar("panel_status", { length: 20 }).default("idle"),
  panelError: text("panel_error"),
  storyboardPrompt: text("storyboard_prompt"),
  storyboardImageUrl: text("storyboard_image_url"),
  storyboardStatus: varchar("storyboard_status", { length: 20 }).default("idle"),
  storyboardError: text("storyboard_error"),
  storyboardPrompt2: text("storyboard_prompt_2"),
  storyboardImageUrl2: text("storyboard_image_url_2"),
  storyboardStatus2: varchar("storyboard_status_2", { length: 20 }).default("idle"),
  storyboardError2: text("storyboard_error_2"),
  storyboardPrompt3: text("storyboard_prompt_3"),
  storyboardImageUrl3: text("storyboard_image_url_3"),
  storyboardStatus3: varchar("storyboard_status_3", { length: 20 }).default("idle"),
  storyboardError3: text("storyboard_error_3"),
  // KIE task ids for in-flight storyboard grid renders (one per slot).
  // Persisted at createTask time so a server restart can resume polling the
  // existing task instead of failing the slot; cleared on terminal states.
  storyboardTaskId: text("storyboard_task_id"),
  storyboardTaskId2: text("storyboard_task_id_2"),
  storyboardTaskId3: text("storyboard_task_id_3"),
  voiceOverUrl: text("voice_over_url"),
  voiceOverStatus: varchar("voice_over_status", { length: 20 }).default("idle"),
  voiceOverError: text("voice_over_error"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("simpsons_scripts_campaign_idx").on(table.simpsonsCampaignId),
  index("simpsons_scripts_brand_idx").on(table.brandId),
]);

export const simpsonsScriptPlans = pgTable("simpsons_script_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  simpsonsCampaignId: varchar("simpsons_campaign_id").notNull().references(() => simpsonsCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  scriptNumber: integer("script_number").notNull(),
  title: text("title").notNull(),
  conceptDescription: text("concept_description"),
  hookStyle: text("hook_style"),
  storyType: text("story_type"),
  primaryEmotion: text("primary_emotion"),
  primaryObjectionOrDesire: text("primary_objection_or_desire"),
  setting: text("setting"),
  keyDistinction: text("key_distinction"),
  conceptJson: jsonb("concept_json"),
  avatarId: varchar("avatar_id"),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("simpsons_script_plans_campaign_idx").on(table.simpsonsCampaignId),
]);

export const simpsonsAvatars = pgTable("simpsons_avatars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  simpsonsCampaignId: varchar("simpsons_campaign_id").notNull().references(() => simpsonsCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  imageUrl: varchar("image_url").notNull(),
  resolvedPrompt: text("resolved_prompt"),
  status: varchar("status", { length: 20 }).default("generated").notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("simpsons_avatars_campaign_idx").on(table.simpsonsCampaignId),
]);

export const insertSimpsonsCampaignSchema = createInsertSchema(simpsonsCampaigns).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertSimpsonsScriptSchema = createInsertSchema(simpsonsScripts).omit({ id: true, createdAt: true, updatedAt: true });
export const insertSimpsonsScriptPlanSchema = createInsertSchema(simpsonsScriptPlans).omit({ id: true, createdAt: true });
export const insertSimpsonsAvatarSchema = createInsertSchema(simpsonsAvatars).omit({ id: true, createdAt: true });

export type SimpsonsCampaign = typeof simpsonsCampaigns.$inferSelect;
export type InsertSimpsonsCampaign = z.infer<typeof insertSimpsonsCampaignSchema>;
export type SimpsonsScript = typeof simpsonsScripts.$inferSelect;
export type InsertSimpsonsScript = z.infer<typeof insertSimpsonsScriptSchema>;
export type SimpsonsScriptPlan = typeof simpsonsScriptPlans.$inferSelect;
export type InsertSimpsonsScriptPlan = z.infer<typeof insertSimpsonsScriptPlanSchema>;
export type SimpsonsAvatar = typeof simpsonsAvatars.$inferSelect;
export type InsertSimpsonsAvatar = z.infer<typeof insertSimpsonsAvatarSchema>;

export const simpsonsRenders = pgTable("simpsons_renders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  simpsonsScriptId: varchar("simpsons_script_id").notNull().references(() => simpsonsScripts.id, { onDelete: "cascade" }),
  simpsonsCampaignId: varchar("simpsons_campaign_id").notNull().references(() => simpsonsCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("queued"),
  progress: integer("progress").default(0),
  outputUrl: text("output_url"),
  error: text("error"),
  durationSec: integer("duration_sec"),
  sceneCount: integer("scene_count"),
  captionedVideoUrl: text("captioned_video_url"),
  captionedVideoStatus: varchar("captioned_video_status", { length: 20 }).default("idle"),
  captionedVideoError: text("captioned_video_error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("simpsons_renders_script_idx").on(table.simpsonsScriptId),
  index("simpsons_renders_campaign_idx").on(table.simpsonsCampaignId),
]);

export const insertSimpsonsRenderSchema = createInsertSchema(simpsonsRenders).omit({ id: true, createdAt: true, updatedAt: true });
export type SimpsonsRender = typeof simpsonsRenders.$inferSelect;
export type InsertSimpsonsRender = z.infer<typeof insertSimpsonsRenderSchema>;

export const simpsonsCharacterVoices = pgTable("simpsons_character_voices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ownerType: varchar("owner_type", { length: 30 }).notNull(),
  ownerId: varchar("owner_id").notNull(),
  brandId: varchar("brand_id").notNull(),
  elevenlabsVoiceId: varchar("elevenlabs_voice_id", { length: 100 }),
  voiceName: varchar("voice_name", { length: 255 }),
  voiceDescription: text("voice_description"),
  sourceImageUrl: text("source_image_url"),
  status: varchar("status", { length: 20 }).notNull().default("generating"),
  error: text("error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("simpsons_character_voices_owner_uniq").on(table.ownerType, table.ownerId),
  index("simpsons_character_voices_brand_idx").on(table.brandId),
]);

export const insertSimpsonsCharacterVoiceSchema = createInsertSchema(simpsonsCharacterVoices).omit({ id: true, createdAt: true, updatedAt: true });
export type SimpsonsCharacterVoice = typeof simpsonsCharacterVoices.$inferSelect;
export type InsertSimpsonsCharacterVoice = z.infer<typeof insertSimpsonsCharacterVoiceSchema>;

// DAYINLIFE Video Campaigns — full pipeline clone (mirrors pixar_* / claymation_*).
// Brand-new feature; tables isolated under dayinlife_* so data never collides.
// -----------------------------------------------------------------------------
export const dayinlifeCampaigns = pgTable("dayinlife_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  name: varchar("name", { length: 255 }).notNull(),
  personaId: varchar("persona_id").references(() => personas.id),
  productId: varchar("product_id").references(() => products.id),
  narrativeId: varchar("narrative_id").references(() => narratives.id),
  narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
  adUnitTypeId: varchar("ad_unit_type_id").references(() => adUnitTypes.id),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  concept: text("concept"),
  targetAudience: text("target_audience"),
  videoStyle: varchar("video_style", { length: 100 }),
  duration: varchar("duration", { length: 50 }),
  videoModel: varchar("video_model", { length: 50 }).default("seedance-2.0"),
  secondsPerScene: integer("seconds_per_scene").default(4),
  videoCount: integer("video_count").default(1),
  avatarCount: integer("avatar_count").default(1),
  campaignMode: varchar("campaign_mode", { length: 50 }).default("oneshot"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  notes: jsonb("notes"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  index("dayinlife_campaigns_brand_idx").on(table.brandId),
]);

export const dayinlifeScripts = pgTable("dayinlife_scripts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dayinlifeCampaignId: varchar("dayinlife_campaign_id").notNull().references(() => dayinlifeCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  avatarId: varchar("avatar_id"),
  title: varchar("title", { length: 255 }),
  hook: text("hook"),
  body: text("body"),
  cta: text("cta"),
  fullScript: text("full_script"),
  talkingPoints: jsonb("talking_points"),
  visualDirections: jsonb("visual_directions"),
  thumbnailUrl: text("thumbnail_url"),
  videoUrl: text("video_url"),
  scenePrompts: jsonb("scene_prompts"),
  scenePromptStatus: varchar("scene_prompt_status", { length: 20 }).default("idle"),
  scenePromptError: text("scene_prompt_error"),
  sceneVideos: jsonb("scene_videos"),
  panelImages: jsonb("panel_images"),
  panelStatus: varchar("panel_status", { length: 20 }).default("idle"),
  panelError: text("panel_error"),
  storyboardPrompt: text("storyboard_prompt"),
  storyboardImageUrl: text("storyboard_image_url"),
  storyboardStatus: varchar("storyboard_status", { length: 20 }).default("idle"),
  storyboardError: text("storyboard_error"),
  storyboardPrompt2: text("storyboard_prompt_2"),
  storyboardImageUrl2: text("storyboard_image_url_2"),
  storyboardStatus2: varchar("storyboard_status_2", { length: 20 }).default("idle"),
  storyboardError2: text("storyboard_error_2"),
  storyboardPrompt3: text("storyboard_prompt_3"),
  storyboardImageUrl3: text("storyboard_image_url_3"),
  storyboardStatus3: varchar("storyboard_status_3", { length: 20 }).default("idle"),
  storyboardError3: text("storyboard_error_3"),
  // KIE task ids for in-flight storyboard grid renders (one per slot).
  // Persisted at createTask time so a server restart can resume polling the
  // existing task instead of failing the slot; cleared on terminal states.
  storyboardTaskId: text("storyboard_task_id"),
  storyboardTaskId2: text("storyboard_task_id_2"),
  storyboardTaskId3: text("storyboard_task_id_3"),
  voiceOverUrl: text("voice_over_url"),
  voiceOverStatus: varchar("voice_over_status", { length: 20 }).default("idle"),
  voiceOverError: text("voice_over_error"),
  voiceOverVoiceId: varchar("voice_over_voice_id", { length: 100 }),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("dayinlife_scripts_campaign_idx").on(table.dayinlifeCampaignId),
  index("dayinlife_scripts_brand_idx").on(table.brandId),
]);

export const dayinlifeScriptPlans = pgTable("dayinlife_script_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dayinlifeCampaignId: varchar("dayinlife_campaign_id").notNull().references(() => dayinlifeCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  scriptNumber: integer("script_number").notNull(),
  title: text("title").notNull(),
  conceptDescription: text("concept_description"),
  hookStyle: text("hook_style"),
  storyType: text("story_type"),
  primaryEmotion: text("primary_emotion"),
  primaryObjectionOrDesire: text("primary_objection_or_desire"),
  setting: text("setting"),
  keyDistinction: text("key_distinction"),
  conceptJson: jsonb("concept_json"),
  avatarId: varchar("avatar_id"),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("dayinlife_script_plans_campaign_idx").on(table.dayinlifeCampaignId),
]);

export const dayinlifeAvatars = pgTable("dayinlife_avatars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dayinlifeCampaignId: varchar("dayinlife_campaign_id").notNull().references(() => dayinlifeCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  imageUrl: varchar("image_url").notNull(),
  resolvedPrompt: text("resolved_prompt"),
  status: varchar("status", { length: 20 }).default("generated").notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("dayinlife_avatars_campaign_idx").on(table.dayinlifeCampaignId),
]);

export const insertDayinlifeCampaignSchema = createInsertSchema(dayinlifeCampaigns).omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true });
export const insertDayinlifeScriptSchema = createInsertSchema(dayinlifeScripts).omit({ id: true, createdAt: true, updatedAt: true });
export const insertDayinlifeScriptPlanSchema = createInsertSchema(dayinlifeScriptPlans).omit({ id: true, createdAt: true });
export const insertDayinlifeAvatarSchema = createInsertSchema(dayinlifeAvatars).omit({ id: true, createdAt: true });

export type DayinlifeCampaign = typeof dayinlifeCampaigns.$inferSelect;
export type InsertDayinlifeCampaign = z.infer<typeof insertDayinlifeCampaignSchema>;
export type DayinlifeScript = typeof dayinlifeScripts.$inferSelect;
export type InsertDayinlifeScript = z.infer<typeof insertDayinlifeScriptSchema>;
export type DayinlifeScriptPlan = typeof dayinlifeScriptPlans.$inferSelect;
export type InsertDayinlifeScriptPlan = z.infer<typeof insertDayinlifeScriptPlanSchema>;
export type DayinlifeAvatar = typeof dayinlifeAvatars.$inferSelect;
export type InsertDayinlifeAvatar = z.infer<typeof insertDayinlifeAvatarSchema>;

export const dayinlifeRenders = pgTable("dayinlife_renders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dayinlifeScriptId: varchar("dayinlife_script_id").notNull().references(() => dayinlifeScripts.id, { onDelete: "cascade" }),
  dayinlifeCampaignId: varchar("dayinlife_campaign_id").notNull().references(() => dayinlifeCampaigns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("queued"),
  progress: integer("progress").default(0),
  outputUrl: text("output_url"),
  error: text("error"),
  durationSec: integer("duration_sec"),
  sceneCount: integer("scene_count"),
  captionedVideoUrl: text("captioned_video_url"),
  captionedVideoStatus: varchar("captioned_video_status", { length: 20 }).default("idle"),
  captionedVideoError: text("captioned_video_error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("dayinlife_renders_script_idx").on(table.dayinlifeScriptId),
  index("dayinlife_renders_campaign_idx").on(table.dayinlifeCampaignId),
]);

export const insertDayinlifeRenderSchema = createInsertSchema(dayinlifeRenders).omit({ id: true, createdAt: true, updatedAt: true });
export type DayinlifeRender = typeof dayinlifeRenders.$inferSelect;
export type InsertDayinlifeRender = z.infer<typeof insertDayinlifeRenderSchema>;

export const dayinlifeCharacterVoices = pgTable("dayinlife_character_voices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ownerType: varchar("owner_type", { length: 30 }).notNull(),
  ownerId: varchar("owner_id").notNull(),
  brandId: varchar("brand_id").notNull(),
  elevenlabsVoiceId: varchar("elevenlabs_voice_id", { length: 100 }),
  voiceName: varchar("voice_name", { length: 255 }),
  voiceDescription: text("voice_description"),
  sourceImageUrl: text("source_image_url"),
  status: varchar("status", { length: 20 }).notNull().default("generating"),
  error: text("error"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("dayinlife_character_voices_owner_uniq").on(table.ownerType, table.ownerId),
  index("dayinlife_character_voices_brand_idx").on(table.brandId),
]);

export const insertDayinlifeCharacterVoiceSchema = createInsertSchema(dayinlifeCharacterVoices).omit({ id: true, createdAt: true, updatedAt: true });
export type DayinlifeCharacterVoice = typeof dayinlifeCharacterVoices.$inferSelect;
export type InsertDayinlifeCharacterVoice = z.infer<typeof insertDayinlifeCharacterVoiceSchema>;

export const insertProjectSchema = createInsertSchema(projects).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
});
export const insertAdGenerationSchema = createInsertSchema(adGenerations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

const safeZoneProductAppearanceSchema=z.object({
  decision:z.enum(["clear","blocked","unavailable","not_applicable"]),
  anchorCount:z.number().int().min(0).max(12),
  matchedPixels:z.number().int().nonnegative(),
  largestPatch:z.number().int().nonnegative(),
  supportFraction:z.number().min(0).max(1).optional(),
});
export const insertGeneratedAdSchema = createInsertSchema(generatedAds).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  providerJournal: campaignImageJournalSchema.nullable().optional(),
  safeZonePlan: z.object({
    mode: z.enum(["direct_916","expand_916"]),
    reason: z.enum(["native_template","four_five_template","other_template_ratio","no_reference","legacy_resume","direct_916_guard_fallback"]),
    referenceWidth: z.number().positive().optional(),
    referenceHeight: z.number().positive().optional(),
    retryId: z.string().uuid().optional(),
    direct916Guard: direct916GuardStateSchema.optional(),
    nativePromptSnapshot: nativePromptSnapshotSchema.optional(),
    renderMode: z.literal("via_45_reframe").optional(),
    portraitWorkerId: z.string().optional(),
    portraitReframe: portraitReframeStateSchema.optional(),
  }).or(z.object({ retryId: z.string().uuid().optional(),
    nativePromptSnapshot: nativePromptSnapshotSchema.optional(),
    renderMode: z.literal("via_45_reframe").optional(),
    portraitWorkerId: z.string().optional(),
    portraitReframe: portraitReframeStateSchema.optional() })).nullable().optional(),
  portraitOutcome: z.object({
    topRisk: z.boolean(), bottomRisk: z.boolean(), bottomEligible: z.boolean(),
    top: z.enum(["protected", "background", "fallback"]),
    bottom: z.enum(["continued", "baseline", "fallback"]),
    cause: z.enum(["delivered", "guard", "ineligible", "qa", "provider"]),
    observation: z.string(),
    topPolicy: z.literal("structure_free_background").optional(),
    output: z.object({
      content: z.string().nullable(), observation: z.string().max(240), safeBottom: z.boolean(),
      tiles:z.array(z.string()).max(4).optional(),
      appearance:safeZoneProductAppearanceSchema.optional(),
      reason:z.string().max(80).optional(),
    }).optional(),
    admission: z.object({
      version: z.literal(2),
      decision: z.enum(PORTRAIT_ADMISSION_DECISIONS),
      modelBottomEligible: z.boolean().nullable(),
      bottomContent: z.enum(PORTRAIT_BOTTOM_CONTENT).nullable(),
      featuredProductContained: z.boolean().nullable(),
      essentialContentAtEdge: z.boolean().nullable(),
      bottomObservation: z.string().max(240),
      layout: z.enum(["single_photo","collage_or_inset","uncertain"]).optional(),
      edge: z.object({
        decision: z.enum(["verified","rejected","invalid_response","unavailable"]),
        content: z.string().nullable(), observation: z.string().max(240),
        version: z.union([z.literal(3),z.literal(4)]).optional(),
        tiles:z.array(z.string()).length(4).optional(),
        appearance:safeZoneProductAppearanceSchema.optional(),
        personPresent:z.boolean().optional(),
        reason: z.string().max(80).optional(),
        objects: z.array(z.object({
          kind: z.enum(PORTRAIT_BOUNDARY_OBJECT_KINDS),
          box: z.tuple([
            z.number().min(0).max(1),z.number().min(0).max(1),
            z.number().min(0).max(1),z.number().min(0).max(1),
          ]).refine(([l,t,r,b])=>l<r && t<b,"Invalid boundary box"),
          observation: z.string().max(240),
        })).max(24).optional(),
      }).optional(),
    }).optional(),
  }).nullable().optional(),
  slideImages: z.array(z.object({
    slideId: z.string(),
    url: z.string(),
    objectKey: z.string(),
  })).nullable().optional(),
});

export type Project = typeof projects.$inferSelect;
export type InsertProject = z.infer<typeof insertProjectSchema>;
export type AdGeneration = typeof adGenerations.$inferSelect;
export type InsertAdGeneration = z.infer<typeof insertAdGenerationSchema>;
export type GeneratedAd = typeof generatedAds.$inferSelect;
export type InsertGeneratedAd = z.infer<typeof insertGeneratedAdSchema>;

export type CreativeAdMatch = typeof creativeAdMatches.$inferSelect;

export type CreativePerformanceIterationSource = typeof creativePerformanceIterationSources.$inferSelect;
export const adAnimationJobs = pgTable(
  "ad_animation_jobs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    // Source saved ad from the existing Ad Library. We reuse generated_ads
    // instead of a new saved_ads table.
    adId: varchar("ad_id").notNull().references(() => generatedAds.id),
    // User-facing display name for this animation. Nullable; the UI falls back
    // to a derived label (product/persona) when empty.
    name: text("name"),
    // Durable R2 image URL fed to Seedance, snapshotted so the job stays
    // reproducible even if the source ad is later changed/archived.
    sourceImageUrl: text("source_image_url"),
    // Settings.
    duration: integer("duration").notNull().default(10),
    aspectRatio: varchar("aspect_ratio", { length: 20 }).notNull().default("Original"),
    animationIntensity: varchar("animation_intensity", { length: 20 }).notNull().default("Standard"),
    musicStyle: varchar("music_style", { length: 50 }).notNull().default("No Music"),
    customMusicStyle: text("custom_music_style"),
    variations: integer("variations").notNull().default(1),
    // Short user note appended to the proven base prompt ("Add instructions"
    // mode). Persisted so retry / restart-resume rebuild the exact same prompt.
    customInstructions: text("custom_instructions"),
    // Full freeform prompt override ("Edit full prompt" mode). When present it
    // is used verbatim as the animation prompt, ignoring the auto-built base.
    promptOverride: text("prompt_override"),
    promptUsed: text("prompt_used"),
    // User bookmarked this animation into the "Saved" tab.
    saved: boolean("saved").notNull().default(false),
    // queued | running | completed | partial | failed | cancelled
    status: varchar("status", { length: 50 }).notNull().default("queued"),
    // Human-readable progress label, one of AD_ANIMATOR_PROGRESS_STATES.
    progress: varchar("progress", { length: 100 }),
    errorMessage: text("error_message"),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
  },
  (table) => [
    index("ad_animation_jobs_brand_idx").on(table.brandId),
    index("ad_animation_jobs_ad_idx").on(table.adId),
    index("ad_animation_jobs_status_idx").on(table.status),
  ],
);

export const adAnimationOutputs = pgTable(
  "ad_animation_outputs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    jobId: varchar("job_id").notNull().references(() => adAnimationJobs.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    adId: varchar("ad_id").notNull().references(() => generatedAds.id),
    variationNumber: integer("variation_number").notNull().default(1),
    // pending | rendering | ready | failed
    status: varchar("status", { length: 50 }).notNull().default("pending"),
    // Durable R2-backed video URL. Provider URLs expire, so this column must
    // hold the mirrored R2 URL once ready (registered with falToR2Reconciler).
    videoUrl: text("video_url"),
    thumbnailUrl: text("thumbnail_url"),
    // KIE Seedance task id, persisted so a resumed worker polls instead of
    // re-submitting (avoids double provider charges).
    providerTaskId: varchar("provider_task_id", { length: 200 }),
    // Prompt inputs snapshotted from the job so a resumed/retried variation
    // rebuilds the identical prompt instead of the auto-built default.
    customInstructions: text("custom_instructions"),
    promptOverride: text("prompt_override"),
    promptUsed: text("prompt_used"),
    errorMessage: text("error_message"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("ad_animation_outputs_job_idx").on(table.jobId),
    index("ad_animation_outputs_brand_idx").on(table.brandId),
  ],
);

export const insertAdAnimationJobSchema = createInsertSchema(adAnimationJobs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertAdAnimationOutputSchema = createInsertSchema(adAnimationOutputs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type AdAnimationJob = typeof adAnimationJobs.$inferSelect;
export type InsertAdAnimationJob = z.infer<typeof insertAdAnimationJobSchema>;
export type AdAnimationOutput = typeof adAnimationOutputs.$inferSelect;
export type InsertAdAnimationOutput = z.infer<typeof insertAdAnimationOutputSchema>;

// ----------------------------------------------------------------------------
// Image Edit Jobs — async "describe-your-edit" inline image edits
// ----------------------------------------------------------------------------
// The inline "Edit Image" path used to run the Fal edit synchronously inside a
// single HTTP request, bounded to ~55s to stay under the gateway connection
// cap — so a slow/deep Fal image queue surfaced a "running slow / timed out"
// popup. These rows let the edit run as a background job (submit→poll), with
// the client polling for completion, so mere slowness no longer times out and
// an edit in flight at restart can be resumed (or cleanly marked failed).
// ----------------------------------------------------------------------------
export const imageEditJobs = pgTable(
  "image_edit_jobs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    layeredEditMetadata: jsonb("layered_edit_metadata").$type<{
      brandId: string; decompositionId: string; baselineDiff: number; lowFidelity: boolean;
      fontFallback: boolean; cached: boolean; textEdits: number; hidden: number;
      outsideEditedRegionChangedPixels?: number; outsidePixelsCompared?: number;
      outcome?: "accept" | "discard";
    }>(),
    // pending | ready | failed
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    // Original source image URL (may be an expiring provider URL); re-hosted to
    // Fal at run time, and re-resolved on restart-resume.
    sourceImageUrl: text("source_image_url").notNull(),
    prompt: text("prompt").notNull(),
    // Optional product packaging / reference image URLs.
    referenceImageUrls: text("reference_image_urls").array(),
    // Task #1432 — USER-uploaded reference images (Edit Image modal "Add
    // reference image", beta flag `edit_reference_images`). Kept in a
    // DISTINCT column from the parent-supplied product refs above so the
    // server can gate them independently: no grant = the field is rejected
    // at submit and legacy jobs stay byte-identical. Durable R2 URLs
    // (persisted at upload time, never expiring provider URLs). Appended to
    // the model image inputs only — no prompt text rides them. Retry copies
    // the snapshot like every other job field.
    userReferenceImageUrls: text("user_reference_image_urls").array(),
    // Structured snapshot of the SAME user refs above: [{url, note, purpose}]
    // where purpose is RESOLVED at submit time ("environment" | "product" |
    // "product_environment", mirroring the Ads Express reference model —
    // never "auto"). The worker uses this to compose per-reference prompt
    // guidance ("Image N (PRODUCT reference): …"); the text[] column above
    // stays the image-input source so legacy rows (jsonb null) keep the
    // original guidance-free behavior. Retry copies both columns.
    userReferences: jsonb("user_references"),
    // Task #1075 — brand-logo URL for the post-edit logo-fidelity QA gate.
    // Resolved SERVER-SIDE at job creation (from the optional `adId` the edit
    // surfaces send: generated ad → parent generation's includeLogo → brand's
    // adgenLogoUrl/logoUrl, same demotion rules as fresh renders). Null means
    // "no logo QA": the edit didn't originate from a logo-bearing ad, so the
    // worker makes NO logo QA call and stays byte-identical to before.
    logoQaUrl: text("logo_qa_url"),
    // KIE nano-banana-2 render task id, persisted as soon as the task is
    // created (same pattern as storyboard slots / UGC renders). On a restart
    // mid-render the startup sweep polls THIS task instead of creating a brand-
    // new render — the resumed job picks up where it left off (no duplicate
    // render, and no double-bill since billing only happens after success).
    // Null when no KIE task has been created yet (or the legacy Fal path is in
    // use), which the resume path treats as "safe to render fresh".
    kieTaskId: text("kie_task_id"),
    // Durable cross-instance ownership for async worker/reconciliation passes.
    // Unlike a session advisory lock, this survives connection loss without
    // occupying a database connection during a long provider poll.
    recoveryOwner: text("recovery_owner"),
    recoveryHeartbeatAt: timestamp("recovery_heartbeat_at", { withTimezone: true }),
    recoveryClaimedAt: timestamp("recovery_claimed_at", { withTimezone: true }),
    recoveryLeaseExpiresAt: timestamp("recovery_lease_expires_at", { withTimezone: true }),
    // Fal gpt-image-2 restart-resume state for TEXT-edit renders (the region
    // band edit or the full-frame primary). Persisted as soon as the Fal
    // request is submitted: `{ requestId, mode: "full"|"region", tier,
    // cropBack, region? }` where `region` carries the located band/window
    // geometry so a restart resumes the in-flight Fal render (and skips the
    // GPT-4o band locate) instead of re-rendering from the top. Same contract
    // as kieTaskId: a persisted request id is polled, never re-submitted.
    // Null when no resumable Fal render has been submitted.
    falResume: jsonb("fal_resume"),
    // Per-paid-stage admission journal. Null on legacy jobs; accepted identities
    // always retain their original provider endpoint across model switches.
    providerStages: jsonb("provider_stages").$type<Record<string, {
      state: "submitting" | "submitted" | "complete" | "rejected";
      endpoint: string;
      requestId?: string;
      result?: unknown;
      restartContext?: unknown;
      gptImageJournal?: import("./campaign-gpt-image").CampaignImageJournal;
    }>>(),
    // Task #3084 — immutable deterministic typography plan for a 9:16 text
    // rewrite. It is created from the source once, before the background
    // removal render is submitted, and is reused on restart so a resumed job
    // never pays for a second analysis or changes its fixed text boxes.
    // The worker owns the shape because it includes rendering details; jsonb
    // intentionally keeps old jobs and non-text edits byte-for-byte unchanged.
    textRenderPlan: jsonb("text_render_plan"),
    // Bound only by the authenticated edit route after it verifies the source
    // URL belongs to this static generated-ad row. The deterministic 9:16
    // engine is never enabled for arbitrary external/uploads/video sources.
    trustedStaticAdId: varchar("trusted_static_ad_id").references(() => generatedAds.id, { onDelete: "set null" }),
    // Optional region mask (white = area to edit, black = preserve), persisted
    // as a durable URL (R2, or Fal storage fallback) at submit time so the
    // restart-resume sweep can re-run masked jobs. When set, the worker routes
    // to a mask-capable inpainting model (FLUX Pro Fill) instead of the
    // whole-frame edit models.
    maskImageUrl: text("mask_image_url"),
    // Multi-region edit: an ordered list of `{ maskUrl, prompt }` pairs, each
    // mask a durable URL (same persistence rules as maskImageUrl) and each
    // prompt the user's comment for THAT region. The worker runs a sequential
    // FLUX Fill chain — region 1's output feeds region 2, and so on — so each
    // comment applies only inside its own mask. Set only for 2+ regions; a
    // single-region submit is normalized to the legacy maskImageUrl+prompt
    // path at the route. Null for legacy/single-mask/whole-frame jobs.
    maskRegions: jsonb("mask_regions").$type<
      {
        maskUrl: string;
        prompt: string;
        refIndices?: number[];
        // Task #1971 — explicit quick-action intent override (skips the
        // per-region classifier). Absent on every legacy/freeform row.
        // Task #1972 — the goal picker's "Change color or style" goal adds
        // attribute_edit as a third override value (same mechanism).
        regionIntent?: "removal" | "text_edit" | "attribute_edit";
      }[]
    >(),
    // Durable result on success.
    resultUrl: text("result_url"),
    // User-facing failure message + a coarse code (SOURCE_UNREACHABLE,
    // SOURCE_NOT_ALLOWED, CONTENT_REJECTED, EDIT_FAILED, FAL_TIMEOUT) so the
    // client can show the same precise messages the sync path used to.
    errorMessage: text("error_message"),
    errorCode: varchar("error_code", { length: 40 }),
    failureDiagnostics: jsonb("failure_diagnostics").$type<import("./text-edit-diagnostics").TextEditDiagnostic | {
      kind: "adaptive_masked_removal";
      reason:
        | "invalid-mask"
        | "insufficient-context"
        | "dimension-mismatch"
        | "out-of-support-change"
        | "sharpness-loss"
        | "boundary-mismatch"
        | "reconstruction-failed"
        | "validation-unavailable";
      detail: string;
    }>(),
    // Task #1258 — precise edit timing. `startedAt` is stamped when the worker
    // BEGINS processing (not at row creation, which only measures queue wait);
    // a restart-resume preserves the ORIGINAL startedAt so the recorded
    // duration covers the whole edit including the outage. `completedAt` is
    // stamped on both terminal transitions (ready AND failed). Duration =
    // completedAt - startedAt. Null on legacy rows / rows not yet started.
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    // Task #1258 — attribution context, resolved FAIL-SOFT at job creation
    // from the optional source `adId` (generated ad → parent generation), the
    // same pattern as logoQaUrl above. All null when the edit has no ad
    // context (or resolution fails) — never blocks the edit.
    brandId: varchar("brand_id"),
    adUnitTypeId: varchar("ad_unit_type_id"),
    // Source run variant of the ad being edited: standard | kc | kc_legacy.
    sourceVariant: varchar("source_variant", { length: 20 }),
    // Task #1253 — QA verdict on the SHIPPED result. True only when the
    // garbled-text gate flagged the edit and the bounded re-rolls could not
    // produce a known-clean image (the flagged result shipped anyway). The
    // status endpoint exposes it so edit surfaces can show a subtle "text may
    // look off" warning + one-click retry. Clean jobs stay false.
    qaFlagged: boolean("qa_flagged").notNull().default(false),
    // Short reason for the flag: "garbled" | "framing" | "logo" | "face".
    qaFlagReason: varchar("qa_flag_reason", { length: 40 }),
    // DEPRECATED — the "Fix face" feature (Task #1298) was removed; the
    // worker no longer reads editKind and new jobs never set it. Column kept
    // (non-destructive) for legacy rows where it holds "fix_face".
    editKind: varchar("edit_kind", { length: 20 }),
    // DEPRECATED — located face-crop geometry persisted by the removed
    // fix_face path. Kept for legacy rows; never read or written anymore.
    faceRegion: jsonb("face_region"),
    // Task #1307 — the Ads Express feature gate (ads_express_settings.
    // enhanced_pipeline) read ONCE at job creation and persisted here, so a
    // restart-resume never re-reads a flipped flag mid-job. Gates the
    // enhanced edit-QA extras (logo QA on edits, source-baseline check,
    // cross-model garbled re-roll routing, maxRerolls 2). Null on legacy
    // rows = baseline behavior.
    enhancedPipeline: boolean("enhanced_pipeline"),
    // Task #1399 — the fast_image_edit_pipeline beta grant (per-user-per-
    // brand, Admin → Beta Features) resolved ONCE at job creation and
    // stamped here, so a mid-job grant change never alters an in-flight or
    // resumed job. Null/false on legacy rows = baseline pipeline
    // (byte-identical to pre-flag behavior).
    fastPipeline: boolean("fast_pipeline"),
    // Task #1473 — the image_edit_speed_boost beta grant (user grant for the brand,
    // Admin → Beta Features) resolved ONCE at job creation and stamped here,
    // same snapshot rule as fastPipeline (retry copies it; a mid-job grant
    // change never alters an in-flight or resumed job). Null/false on legacy
    // rows = baseline behavior (byte-identical to pre-flag behavior).
    speedBoost: boolean("speed_boost"),
    // Task #1476 — the region_edit_grouping beta grant (user grant for the brand,
    // Admin → Beta Features) resolved ONCE at job creation and stamped here,
    // same snapshot rule as fastPipeline/speedBoost (retry copies it; a
    // mid-job grant change never alters an in-flight or resumed job).
    // Null/false on legacy rows = baseline sequential region chain.
    regionGrouping: boolean("region_grouping"),
    // Task #1522 — the element_aware_text_edit beta grant (user grant for the brand,
    // Admin → Beta Features) resolved ONCE at job creation and stamped here,
    // same snapshot rule as fastPipeline/speedBoost/regionGrouping (retry
    // copies it; a mid-job grant change never alters an in-flight or resumed
    // job). Null/false on legacy rows = baseline glyph-only band geometry.
    elementAwareTextEdit: boolean("element_aware_text_edit"),
    // Task #1665 — the image_edit_quality_guards arm resolved ONCE at job
    // creation and stamped here, same snapshot rule as the arms above (retry
    // copies it; a mid-job flag flip never changes an in-flight or resumed
    // job's arm). True = paste-back on every masked pass + the post-render
    // sizing guards. Null/false on legacy rows = the 2026-07-18 ship-raw
    // experiment behavior, byte-identical. Task #1692 — the flag is now
    // user grant, so new jobs stamp true when the
    // brand opted out.
    qualityGuards: boolean("quality_guards"),
    // Task #1692 — the strict_reposition_reroute beta grant (user grant
    // toggle, Ads Express admin Features tab) resolved ONCE at job creation
    // and stamped here, same snapshot rule as the arms above (retry copies
    // it). True = masked edits reroute to the full-frame relocation path only
    // for genuinely-move prompts (strict detector). Null/false on legacy
    // rows = the Task #1637 detector, byte-identical.
    strictReposition: boolean("strict_reposition"),
    // Task #1901 — the confined_text_edit_paste beta grant (user grant
    // toggle, Ads Express admin Features tab) resolved ONCE at job creation
    // and stamped here, same snapshot rule as the arms above (retry copies
    // it). True = the locate-band text-edit paste is confined to the edited
    // text element with drift-limited change gating + seam color
    // normalization. Null/false on legacy rows = the full-band paste,
    // byte-identical.
    confinedPaste: boolean("confined_paste"),
    // Task #2122 — the reflow_safe_text_paste beta grant (user grant
    // toggle, Ads Express admin Features tab) resolved ONCE at job creation
    // and stamped here, same snapshot rule as the arms above (retry copies
    // it). True = the confined text-edit paste detects vertical reflow of
    // non-target lines and expands the shipped region to the union of the
    // old and new text-block extents. Null/false on legacy rows = the plain
    // confined paste, byte-identical.
    reflowSafePaste: boolean("reflow_safe_paste"),
    // Task #1934 — the erase_smudge_guards beta grant (user toggle,
    // Ads Express admin Features tab) resolved ONCE at job creation and
    // stamped here, same snapshot rule as the arms above (retry copies it).
    // True = the FLUX Erase downscale reject/retry + paste-band ink
    // protection run (composite arm only). Null/false on legacy rows = the
    // unguarded erase paste-back, byte-identical.
    eraseSmudgeGuards: boolean("erase_smudge_guards"),
    // Task #2022 — the erase_target_attribution beta grant (user grant
    // toggle, Ads Express admin Features tab) resolved ONCE at job creation
    // and stamped here, same snapshot rule as the arms above (retry copies
    // it). True = the paste-band ink protection attributes target vs
    // neighbor ink by connected component (target ink never protected).
    // Null/false on legacy rows = the ring-only protection, byte-identical.
    eraseTargetAttribution: boolean("erase_target_attribution"),
    // Default-ON brand feature snapshot. True preserves an immutable eroded
    // core of the normalized user-painted removal mask while neighbor
    // attribution may still carve its shallow boundary. Retry copies this
    // value so in-flight and replayed jobs never follow a live flag change.
    eraseMaskInteriorCore: boolean("erase_mask_interior_core"),
    // Task #2022 (user-directed follow-on) — the erase_hallucination_check
    // beta grant (user toggle, Ads Express admin Features tab)
    // resolved ONCE at job creation and stamped here, same snapshot rule as
    // the arms above (retry copies it). True = the composited erase output
    // is checked once for INVENTED text in the erased region (before/after
    // vision compare) and re-rolled once on a hit. Null/false on legacy
    // rows = no check, byte-identical.
    eraseHallucinationCheck: boolean("erase_hallucination_check"),
    // Task #2250 — the erase_band_local_repair beta grant (user grant
    // toggle, Ads Express admin Features tab) resolved ONCE at job creation
    // and stamped here, same snapshot rule as the arms above (retry copies
    // it). True = when a downscale-rejected erase must be composited (the
    // fill-fallback invented-text re-roll path), the paste band is repaired
    // locally first: deterministic flat/gradient reconstruction from source
    // pixels, else a sharp local resample of the downscaled render. Zero
    // extra provider calls. Null/false on legacy rows = today's soft
    // composite, byte-identical.
    eraseBandLocalRepair: boolean("erase_band_local_repair"),
    // Default-on brand-only adaptive removal contract, snapshotted per job.
    // Retry copies the value; null is reserved for legacy rows.
    adaptiveMaskedRemoval: boolean("adaptive_masked_removal"),
    // Task #3093 — the historical erase_tone_correction snapshot. New jobs
    // derive this value from the Text Removal Repairs master flag; null/false
    // legacy rows preserve their existing behavior.
    removalToneCorrection: boolean("erase_tone_correction"),
    // Task #3093 — Ads Express master repair-arm snapshot. New jobs always
    // stamp the effective per-brand value (default true); null is reserved
    // for legacy rows and preserves their already-saved removal snapshots and
    // selected-region routing behavior.
    textRemovalRepairs: boolean("text_removal_repairs"),
    // Task #2326 — the safe_zone_seam_blend beta grant (user toggle,
    // Ads Express admin Features tab) resolved ONCE at job creation and
    // stamped here, same snapshot rule as the arms above (retry copies it).
    // True = a brush-erase whose painted mask lies substantially in a 9:16
    // band/seam region deterministically REBUILDS the seam blend (outpainted
    // seam mode) instead of pasting back the source's striped seam pixels —
    // all existing erase protections still run first. Null/false on legacy
    // rows = byte-identical current erase behavior.
    safeZoneSeamBlend: boolean("safe_zone_seam_blend"),
    // Effective Ads Express user/brand grant resolved once when the job is
    // created. Default true preserves deterministic enforcement for jobs
    // already in flight when this snapshot column is deployed. New jobs and
    // retries always carry an explicit immutable value.
    textSafeZone916: boolean("text_safe_zone_916").notNull().default(true),
    // Task #3326 — immutable effective brand setting snapshot. NULL is
    // reserved for legacy rows and keeps their pre-adaptive behavior.
    adaptiveTextEditLayout: boolean("adaptive_text_edit_layout"),
    // Nullable immutable admission arm: legacy rows must never acquire it.
    copyDeliveryRepair: boolean("copy_delivery_repair"),
    // Immutable admission decision. Historical null values remain off.
    ideogramTextEdit: boolean("ideogram_text_edit"),
    textEngineState: jsonb("text_engine_state").$type<import("./image-edit-engines").TextEngineState>(),
    // Task #3093 — deterministic submit-time decision for pure unmasked text
    // removal. True opts the worker into bounded multi-target localization +
    // the existing masked Erase machinery; null/false on legacy rows keeps
    // the historical replacement/full-frame behavior byte-identical.
    textRemovalIsolation: boolean("text_removal_isolation"),
    // Task #3093 — durable bounded mask produced by the isolated-removal
    // locator. Once written, restart/resume/retry replays this exact target
    // set instead of asking vision to choose different bounds.
    textRemovalMaskUrl: text("text_removal_mask_url"),
    // Task #1968 — WRITE-ONLY tracking of the edit category the worker
    // actually resolved at run time (nothing reads it back; audits query it
    // directly). Unmasked jobs: "reposition_edit" | "text_edit" |
    // "text_removal" | "describe_edit" (the same value the logs carry).
    // Masked/erase jobs: the resolved per-region intent taxonomy
    // ("removal" | "attribute_edit" | "text_edit"); a multi-region job stores
    // the DISTINCT intents joined
    // with "+" in region order (e.g. "removal+attribute_edit") — one row,
    // whole-job view. Deliberately a NEW column: the deprecated edit_kind
    // below is legacy fix_face data and stays untouched. Null on legacy
    // rows and on jobs that failed before classification resolved.
    editCategory: varchar("edit_category", { length: 60 }),
    // Task #1968 — WRITE-ONLY link to the prior edit job this one redid.
    // Stamped at creation: the explicit /retry route stores the retried
    // job's id directly, and a fresh submit stores the most recent TERMINAL
    // prior job by the same user on the same source image within a short
    // window (~10 min) when one exists. Purely additive — no behavior
    // branches on it. Null = not a detected redo.
    retriedJobId: varchar("retried_job_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("image_edit_jobs_user_idx").on(table.userId),
    index("image_edit_jobs_status_idx").on(table.status),
  ],
);

export const imageEditMaskRegionSchema = z.object({
  maskUrl: z.string(),
  prompt: z.string(),
  refIndices: z.array(z.number()).optional(),
  regionIntent: z.enum(["removal", "text_edit", "attribute_edit"]).optional(),
});
export const insertImageEditJobSchema = createInsertSchema(imageEditJobs, {
  layeredEditMetadata: z.object({
    brandId: z.string(), decompositionId: z.string(), baselineDiff: z.number(), lowFidelity: z.boolean(),
    fontFallback: z.boolean(), cached: z.boolean(), textEdits: z.number(), hidden: z.number(),
    outsideEditedRegionChangedPixels: z.number().optional(), outsidePixelsCompared: z.number().optional(),
    outcome: z.enum(["accept", "discard"]).optional(),
  }).nullable().optional(),
  providerStages: z.record(z.object({
    state: z.enum(["submitting", "submitted", "complete", "rejected"]),
    endpoint: z.string(),
    requestId: z.string().optional(),
    result: z.unknown().optional(),
    restartContext: z.unknown().optional(),
    gptImageJournal: campaignImageJournalSchema.optional(),
  })).nullable().optional(),
  failureDiagnostics: z.object({
    kind: z.literal("adaptive_masked_removal"),
    reason: z.enum([
      "invalid-mask", "insufficient-context", "dimension-mismatch",
      "out-of-support-change", "sharpness-loss", "boundary-mismatch",
      "reconstruction-failed", "validation-unavailable",
    ]),
    detail: z.string(),
  }).nullable().optional(),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    maskRegions: z.array(imageEditMaskRegionSchema).nullable().optional(),
    textEngineState: textEngineStateSchema.nullable().optional(),
  });
export type ImageEditJob = typeof imageEditJobs.$inferSelect;
export type InsertImageEditJob = z.infer<typeof insertImageEditJobSchema>;

// ----------------------------------------------------------------------------
// Creative Tinder — isolated, toggleable swipe tool for ad direction discovery
// ----------------------------------------------------------------------------
export const creativeTinderSettings = pgTable("creative_tinder_settings", {
  id: varchar("id").primaryKey().default("global"),
  enabled: boolean("enabled").notNull().default(true),
  defaultBatchSize: integer("default_batch_size").notNull().default(30),
  prefetchThreshold: integer("prefetch_threshold").notNull().default(3),
  maxConcurrentRenders: integer("max_concurrent_renders").notNull().default(3),
  videoIterationPrompt: text("video_iteration_prompt"),
  videoIterationModel: varchar("video_iteration_model", { length: 32 }).notNull().default("seedance-2.5"),
  endpoints: jsonb("endpoints").notNull().default(sql`'{}'::jsonb`),
  defaults: jsonb("defaults").notNull().default(sql`'{}'::jsonb`),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const creativeTinderSessions = pgTable(
  "creative_tinder_sessions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    name: varchar("name", { length: 255 }),
    status: varchar("status", { length: 32 }).notNull().default("active"),
    inputs: jsonb("inputs").notNull().default(sql`'{}'::jsonb`),
    assetManifest: jsonb("asset_manifest").default(sql`'{}'::jsonb`),
    learningFile: jsonb("learning_file").notNull().default(sql`'{}'::jsonb`),
    stats: jsonb("stats").notNull().default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    endedAt: timestamp("ended_at"),
  },
  (t) => [index("ct_sessions_user_idx").on(t.userId)]
);

export const creativeTinderCards = pgTable(
  "creative_tinder_cards",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull().references(() => creativeTinderSessions.id, { onDelete: "cascade" }),
    batchId: varchar("batch_id", { length: 64 }).notNull(),
    status: varchar("status", { length: 32 }).notNull().default("concept_created"),
    concept: jsonb("concept").notNull().default(sql`'{}'::jsonb`),
    prompt: jsonb("prompt").notNull().default(sql`'{}'::jsonb`),
    render: jsonb("render").default(sql`'{}'::jsonb`),
    scores: jsonb("scores").default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => [index("ct_cards_session_idx").on(t.sessionId), index("ct_cards_batch_idx").on(t.batchId)]
);

export const creativeTinderSwipeEvents = pgTable(
  "creative_tinder_swipe_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull().references(() => creativeTinderSessions.id, { onDelete: "cascade" }),
    cardId: varchar("card_id").notNull().references(() => creativeTinderCards.id, { onDelete: "cascade" }),
    action: varchar("action", { length: 16 }).notNull(),
    reason: text("reason"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => [index("ct_swipes_session_idx").on(t.sessionId)]
);

export const creativeIterationRuns = pgTable(
  "creative_iteration_runs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    brandId: varchar("brand_id").notNull(),
    requestKey: varchar("request_key", { length: 200 }).notNull(),
    sessionId: varchar("session_id").notNull().references(() => creativeTinderSessions.id, { onDelete: "cascade" }),
    generatedAdIds: jsonb("generated_ad_ids").notNull().default(sql`'[]'::jsonb`),
    // Explicit for media-aware iteration runs. The default preserves every
    // historical row as an image run.
    mediaType: varchar("media_type", { length: 20 }).notNull().default("image"),
    // Canonical output ratio for every static set in this run. Historical
    // image runs retain the product default; video workers ignore this field.
    aspectRatio: varchar("aspect_ratio", { length: 10 }).notNull().default("4:5"),
    // Legacy run-level model hint. New video sets resolve their durable model
    // from the immutable source duration before first provider admission.
    videoModel: varchar("video_model", { length: 32 }).notNull().default("seedance-2.5"),
    status: varchar("status", { length: 32 }).notNull().default("queued"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => [
    uniqueIndex("creative_iteration_runs_user_brand_key_uq").on(t.userId, t.brandId, t.requestKey),
    index("creative_iteration_runs_user_created_idx").on(t.userId, t.createdAt),
  ],
);
export const creativeTinderVariationSets = pgTable(
  "creative_tinder_variation_sets",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull().references(() => creativeTinderSessions.id, { onDelete: "cascade" }),
    sourceCardId: varchar("source_card_id").notNull().references(() => creativeTinderCards.id, { onDelete: "cascade" }),
    userId: varchar("user_id").notNull(),
    brandId: varchar("brand_id"),
    // Null for ordinary Creative Tinder, populated for durable iteration runs.
    iterationRunId: varchar("iteration_run_id").references(() => creativeIterationRuns.id, { onDelete: "cascade" }),
    // Required durable My Ads destination for Creative Iterations. Persisted
    // before provider admission so retries reuse one generation context.
    libraryGenerationId: varchar("library_generation_id").references(() => adGenerations.id, { onDelete: "set null" }),
    mediaType: varchar("media_type", { length: 20 }).notNull().default("image"),
    // Resolved from source duration before first provider admission, then
    // retained so restart/retry never changes an already-paid task's model.
    videoModel: varchar("video_model", { length: 32 }).notNull().default("seedance-2.5"),
    // pending | storyboard | grid | variations | complete | partial | failed
    status: varchar("status", { length: 32 }).notNull().default("pending"),
    // The generation backend selected when this set is created. Legacy rows
    // remain on Fal so an interrupted historical job can resume safely; new
    // sets use KIE's durable image-to-image job flow.
    renderProvider: varchar("render_provider", { length: 16 }).notNull().default("fal"),
    aspectRatio: varchar("aspect_ratio", { length: 10 }),
    // Snapshot of the source winner ad image (the "original" of the set).
    originalImageUrl: text("original_image_url"),
    // Video iterations snapshot the durable source clip and a durable still
    // extracted from it before any paid provider work is admitted.
    sourceVideoUrl: text("source_video_url"),
    representativeFrameUrl: text("representative_frame_url"),
    // Stage 1 output (Sim) + stage 2 output (Fal grid). Persisted so a
    // restart/resume skips already-finished stages (no re-charge).
    storyboardPrompt: text("storyboard_prompt"),
    gridImageUrl: text("grid_image_url"),
    gridFalRequestId: varchar("grid_fal_request_id", { length: 200 }),
    gridFalEndpoint: text("grid_fal_endpoint"),
    // KIE task id persisted before polling. KIE jobs are asynchronous, so this
    // is the recovery handle for a grid submitted by the KIE-backed path.
    gridKieTaskId: varchar("grid_kie_task_id", { length: 200 }),
    error: text("error"),
    // Set when the user "locks in" their kept-image selection for this set.
    keptLockedAt: timestamp("kept_locked_at"),
    // Short renewable lease for cross-process worker ownership. A crashed
    // process leaves only an expiring claim, so startup/detail recovery can
    // safely resume the same checkpoint without duplicate provider calls.
    workerLeaseOwner: varchar("worker_lease_owner", { length: 100 }),
    workerLeaseExpiresAt: timestamp("worker_lease_expires_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => [
    index("ct_var_sets_session_idx").on(t.sessionId),
    index("ct_var_sets_status_idx").on(t.status),
    uniqueIndex("ct_var_sets_iteration_source_uq").on(t.iterationRunId, t.sourceCardId),
  ]
);

export const creativeTinderVariationImages = pgTable(
  "creative_tinder_variation_images",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    setId: varchar("set_id").notNull().references(() => creativeTinderVariationSets.id, { onDelete: "cascade" }),
    // 1 = original winner; 2..8 = the grid's variation cells (also the
    // {{VARIANT_NUMBER}} substituted into the recreate prompt).
    variantNumber: integer("variant_number").notNull(),
    isOriginal: boolean("is_original").notNull().default(false),
    mediaType: varchar("media_type", { length: 20 }).notNull().default("image"),
    // pending | rendering | rendered | failed
    status: varchar("status", { length: 32 }).notNull().default("pending"),
    // Whether the user chose to keep this image when locking in the set.
    kept: boolean("kept").notNull().default(false),
    imageUrl: text("image_url"),
    // Video variants are only considered rendered after this is an R2 URL.
    videoUrl: text("video_url"),
    // Temporary provider checkpoint used only to finish R2 mirroring after a
    // restart. It is never exposed as a completed result URL.
    providerVideoUrl: text("provider_video_url"),
    providerTaskId: varchar("provider_task_id", { length: 200 }),
    // Durable duplicate-submission boundary written before createTask. When
    // present without a task id, recovery waits for the signed KIE callback.
    providerSubmissionStartedAt: timestamp("provider_submission_started_at"),
    falRequestId: varchar("fal_request_id", { length: 200 }),
    falEndpoint: text("fal_endpoint"),
    // KIE task id persisted before polling, distinct from the legacy Fal
    // request id so in-flight legacy sets never get sent to the wrong provider.
    kieTaskId: varchar("kie_task_id", { length: 200 }),
    // Link to the Ad Library row created when this image is persisted.
    generatedAdId: varchar("generated_ad_id"),
    error: text("error"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => [index("ct_var_images_set_idx").on(t.setId)]
);

export const creativeIterationSizeDownloads = pgTable("creative_iteration_size_downloads", {
  token: varchar("token").primaryKey(),
  userId: varchar("user_id").notNull(),
  setId: varchar("set_id").notNull().references(() => creativeTinderVariationSets.id, { onDelete: "cascade" }),
  adId: varchar("ad_id").notNull(),
  ratio: varchar("ratio", { length: 10 }).notNull(),
  name: text("name").notNull(),
  currentRatio: varchar("current_ratio", { length: 10 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  message: text("message"),
  imageUrl: text("image_url"),
  elapsedMs: integer("elapsed_ms"),
  startedAt: timestamp("started_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, table => [
  uniqueIndex("ci_size_download_target_uq").on(table.userId, table.setId, table.adId, table.ratio),
  index("ci_size_download_active_idx").on(table.userId, table.status),
]);
export const insertCreativeTinderVariationSetSchema = createInsertSchema(creativeTinderVariationSets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCtVariationSet = z.infer<typeof insertCreativeTinderVariationSetSchema>;
export type CtVariationSet = typeof creativeTinderVariationSets.$inferSelect;
export type CtVariationImage = typeof creativeTinderVariationImages.$inferSelect;

// Creative Style modifiers for Instant Ad Generation. Three rows
// (precise / balanced / creative_freedom) are seeded at startup; admins
// can later edit `prepend_text` etc. via SQL or a future admin UI.
export const creativeStylePromptModifiers = pgTable(
  "creative_style_prompt_modifiers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    modeKey: varchar("mode_key", { length: 50 }).notNull().unique(),
    displayName: varchar("display_name", { length: 100 }).notNull(),
    description: text("description"),
    prependText: text("prepend_text").notNull().default(""),
    includeTemplatePrompt: boolean("include_template_prompt").notNull().default(true),
    isDefault: boolean("is_default").notNull().default(false),
    isActive: boolean("is_active").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
);

export const insertCreativeStyleModifierSchema = createInsertSchema(creativeStylePromptModifiers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCreativeStyleModifier = z.infer<typeof insertCreativeStyleModifierSchema>;
export type CreativeStyleModifier = typeof creativeStylePromptModifiers.$inferSelect;

// Singleton (one row) of settings for the Character Reference Sheet
// generator. Stores the URL of the example reference sheet used as a layout/
// style guide on every generation, plus an optional prompt override.
export const characterReferenceSheetSettings = pgTable(
  "character_reference_sheet_settings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    exampleSheetUrl: text("example_sheet_url").notNull(),
    promptTemplate: text("prompt_template"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
);
export type CharacterReferenceSheetSettings = typeof characterReferenceSheetSettings.$inferSelect;

export type CtSettings = typeof creativeTinderSettings.$inferSelect;
export type CtSession = typeof creativeTinderSessions.$inferSelect;
export type CtCard = typeof creativeTinderCards.$inferSelect;
export type CtSwipeEvent = typeof creativeTinderSwipeEvents.$inferSelect;

// ─── Persona Intelligence Sessions ──────────────────────────────────────
// Saves an in-progress Persona Intelligence Studio run so the user can
// leave the page and pick it back up later. One row per session. Step
// inputs + outputs are stored as jsonb so adding future steps (3-7)
// only requires more nullable columns, never a migration of existing
// rows. Ownership is enforced at the route layer via createdBy.
export const personaIntelligenceSessions = pgTable(
  "persona_intelligence_sessions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull(),
    createdBy: varchar("created_by").notNull(),
    title: varchar("title", { length: 255 }).notNull(),
    // 1..7, matches STAGE_NAMES on the client.
    currentStep: integer("current_step").notNull().default(1),
    // "draft" | "in_progress" | "complete" | "archived"
    status: varchar("status", { length: 20 }).notNull().default("draft"),
    // Step 1 request payload (the full form state used to call Workflow 1).
    inputPayload: jsonb("input_payload"),
    // Step 1 results — unwrapped workflow output + raw Sim envelope.
    step1Output: jsonb("step1_output"),
    step1Raw: jsonb("step1_raw"),
    // Step 2 results — user-chosen generation settings + Workflow 2 output.
    step2Settings: jsonb("step2_settings"),
    step2Output: jsonb("step2_output"),
    step2Raw: jsonb("step2_raw"),
    // Persona Seed Builder (Workflow 1) — rebuilt 2026-05 flow.
    // creationMode: "enhance" | "create" | "idea"
    creationMode: varchar("creation_mode", { length: 20 }),
    // Linked product OR offer id (caller decides which kind via payload).
    linkedProductOrOfferId: varchar("linked_product_or_offer_id"),
    // When enhance mode is chosen, the persona that seeded this run.
    sourceExistingPersonaId: varchar("source_existing_persona_id"),
    // Full Workflow 1 (persona seed builder) unwrapped output.
    workflow1Output: jsonb("workflow1_output"),
    // Persona row created from this session (set when Save is clicked).
    savedPersonaId: varchar("saved_persona_id"),
    // Workflow 2 (Customer Language Finder) — Step 2 of Persona
    // Intelligence Studio. Nullable / additive so old rows keep working.
    // workflow2Status: null | "pending" | "complete" | "failed"
    workflow2Status: varchar("workflow2_status", { length: 20 }),
    workflow2Output: jsonb("workflow2_output"),
    workflow2Error: text("workflow2_error"),
    workflow2StartedAt: timestamp("workflow2_started_at"),
    workflow2CompletedAt: timestamp("workflow2_completed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("pi_sessions_brand_idx").on(table.brandId),
    index("pi_sessions_created_by_idx").on(table.createdBy),
  ],
);

export const insertPersonaIntelligenceSessionSchema = createInsertSchema(
  personaIntelligenceSessions,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPersonaIntelligenceSession = z.infer<
  typeof insertPersonaIntelligenceSessionSchema
>;
export type PersonaIntelligenceSession =
  typeof personaIntelligenceSessions.$inferSelect;

// ─── Persona Research Lab Flows ─────────────────────────────────────────
// Saves a snapshot of a Persona Research Lab run so the user can leave the
// lab, return to a dashboard of saved flows, and reopen any flow later. The
// entire lab state (selected persona, per-module results, statuses, errors)
// is stored as a single jsonb blob — adding future modules never requires a
// migration. `currentStep`/`status` are denormalized for fast dashboard
// rendering. Ownership is enforced at the route layer via createdBy.
export const personaLabFlows = pgTable(
  "persona_lab_flows",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull(),
    createdBy: varchar("created_by").notNull(),
    title: varchar("title", { length: 255 }).notNull(),
    // Persona selected for this flow (snapshot id + name for display even if
    // the persona is later deleted).
    personaId: varchar("persona_id"),
    personaName: varchar("persona_name", { length: 255 }),
    // Number of completed workflow modules (0..9) for "Step X of 9" display.
    currentStep: integer("current_step").notNull().default(0),
    // "in_progress" | "final"
    status: varchar("status", { length: 20 }).notNull().default("in_progress"),
    // Full persistable lab state (LabState shape on the client).
    state: jsonb("state"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("persona_lab_flows_brand_idx").on(table.brandId),
    index("persona_lab_flows_created_by_idx").on(table.createdBy),
  ],
);

export const insertPersonaLabFlowSchema = createInsertSchema(
  personaLabFlows,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPersonaLabFlow = z.infer<typeof insertPersonaLabFlowSchema>;
export type PersonaLabFlow = typeof personaLabFlows.$inferSelect;

// ─── Internal "What's Been Updated" changelog ────────────────────────────
// Internal-only (master/superuser) changelog entries. Two kinds: "highlight"
// (notable feature/improvement, shown prominently) and "fix" (small fix,
// shown compactly). Entries are created manually via the quick-add form
// (status "published" immediately) or drafted by the AI git-scan (status
// "draft" until a human approves/edits/dismisses them). Deep-link page only —
// no sidebar entry. Entries are grouped BY FEATURE on the page and in the Slack
// digest; the feature is auto-assigned (AI on the auto-publish path, heuristic
// fallback/backfill via shared/internal-update-features.ts). Dual-write:
// mirrored in migrations/0080_internal_updates.sql +
// migrations/0083_internal_updates_feature.sql.
export const internalUpdates = pgTable(
  "internal_updates",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // "highlight" | "fix"
    type: varchar("type", { length: 20 }).notNull(),
    description: text("description").notNull(),
    // One of INTERNAL_UPDATE_FEATURES (shared/internal-update-features.ts).
    // Nullable only transiently before startup backfill assigns a value.
    feature: varchar("feature", { length: 40 }),
    // "published" | "draft" (draft = AI-scanned, awaiting review)
    status: varchar("status", { length: 20 }).notNull().default("published"),
    // "manual" | "git_scan"
    source: varchar("source", { length: 20 }).notNull().default("manual"),
    // Newest commit hash covered when this entry was drafted by a git scan
    // (audit/dedupe aid; nullable for manual entries).
    commitHash: varchar("commit_hash", { length: 64 }),
    createdBy: varchar("created_by"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("internal_updates_status_idx").on(table.status),
    index("internal_updates_created_at_idx").on(table.createdAt),
    index("internal_updates_feature_idx").on(table.feature),
  ],
);

// Singleton settings row (key = "singleton") for the internal changelog:
// which Slack channel the Monday digest posts to, the idempotency marker for
// the weekly digest (ISO date of the Monday whose week was last digested),
// and git-scan bookkeeping.
export const internalUpdatesSettings = pgTable("internal_updates_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 50 }).notNull().unique(),
  slackChannelId: varchar("slack_channel_id", { length: 64 }),
  slackChannelName: varchar("slack_channel_name", { length: 255 }),
  // ISO date (YYYY-MM-DD) of the MONDAY starting the week that was last
  // digested. The Monday digest job skips (idempotent) when this already
  // covers the previous week — safe against duplicate scheduled runs.
  lastDigestWeekStart: varchar("last_digest_week_start", { length: 10 }),
  lastScanAt: timestamp("last_scan_at"),
  lastScanCommitHash: varchar("last_scan_commit_hash", { length: 64 }),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertInternalUpdateSchema = createInsertSchema(internalUpdates).omit({
  id: true,
  createdAt: true,
});
export const insertInternalUpdatesSettingsSchema = createInsertSchema(
  internalUpdatesSettings,
).omit({ id: true, updatedAt: true });
export type InternalUpdate = typeof internalUpdates.$inferSelect;
export type InsertInternalUpdate = z.infer<typeof insertInternalUpdateSchema>;
export type InternalUpdatesSettings = typeof internalUpdatesSettings.$inferSelect;
export type InsertInternalUpdatesSettings = z.infer<typeof insertInternalUpdatesSettingsSchema>;

// ----------------------------------------------------------------------------
// PROD→DEV SYNC SETTINGS (dev-only singleton row)
// ----------------------------------------------------------------------------
// Persisted once-per-day claim marker + last-run status for the in-process
// nightly prod→dev data sync (server/services/prod-dev-sync.ts). Exists in
// prod schemas too but is only ever written from dev.
export const prodDevSyncSettings = pgTable("prod_dev_sync_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 50 }).notNull().unique(),
  // Chicago-local YYYY-MM-DD of the day whose sync has been claimed/run.
  // Advanced atomically via a conditional UPDATE (see claimProdDevSyncDay);
  // released (reverted) when the claimed pass fails so a later tick retries.
  lastSyncDate: varchar("last_sync_date", { length: 10 }),
  lastSyncAt: timestamp("last_sync_at"),
  lastSyncResult: jsonb("last_sync_result"),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export type ProdDevSyncSettings = typeof prodDevSyncSettings.$inferSelect;


export type BrandClientCategory = (typeof BRAND_CLIENT_CATEGORIES)[number];

export type BrandImageEngineOverride = typeof brandImageEngineOverrides.$inferSelect;

export const emailBrandFooterSettingsSchema = z
  .object({
    physicalAddress: z.string().trim().max(1000).optional(),
    unsubscribeUrl: z.string().trim().max(2048).optional(),
  })
  .strip();
export const insertEmailSectionDeviceSchema = createInsertSchema(
  emailSectionDevices,
).omit({ id: true, createdAt: true, updatedAt: true });

export type EmailSectionDevice = typeof emailSectionDevices.$inferSelect;

export type EmailExpressBrandSettings =
  typeof emailExpressBrandSettings.$inferSelect;

// ----------------------------------------------------------------------------
// Onboarding orchestration — machine-to-machine "onboarding recipe" surface.
// A separate onboarding project pushes a brand + products + personas +
// narrative angles through an API-key-authed intake endpoint, then kicks off
// one orchestration run that fans out a fixed recipe of generations (Ads
// Express units, Meme templates, Slideshow styles, UGC styles) using the
// existing internal worker entry points. See docs/onboarding-integration.md.
// ----------------------------------------------------------------------------

// Master-editable recipe config (single 'global' row, same singleton pattern
// as slideshow/meme settings). Selections are stored by STABLE reference —
// ad unit slug, Imgflip template id, slideshow/UGC style value — never by DB
// row id, so they survive template-library reseeds. They are resolved to live
// catalog rows at run time; unresolvable entries surface as warnings in the
// admin UI and are skipped (never silently substituted) at orchestration time.
export const onboardingRecipeSettings = pgTable("onboarding_recipe_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 50 }).notNull().unique(),
  // string[] of global ad_unit_types.slug — one Ads Express ad per unit per
  // persona x angle combo.
  adUnitSlugs: jsonb("ad_unit_slugs"),
  // string[] of meme_template_library.template_id — one meme per template per
  // product (the meme pipeline takes no persona/angle).
  memeTemplateIds: jsonb("meme_template_ids"),
  // string[] of slideshow_express_styles.value — one slideshow run per style
  // per persona x angle combo.
  slideshowStyleValues: jsonb("slideshow_style_values"),
  // string[] of ugc_express_styles.value — one UGC video per style per
  // persona (no angle input).
  ugcStyleValues: jsonb("ugc_style_values"),
  // Per-persona recipe sections (fixed 3 — matching the three personas the
  // external onboarding repl sends, in intake order). Each entry repeats the
  // four selection lists above ({ adUnitSlugs, memeTemplateIds,
  // slideshowStyleValues, ugcStyleValues }), capped at a combined 10
  // selections per section; the run generates EXACTLY the selected entries
  // for the corresponding intake persona. Null = row predates per-persona
  // sections — reads derive three identical sections from the legacy lists
  // above (which are kept dual-written as the union of the sections).
  personaSectionsJson: jsonb("persona_sections_json"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// One row per external onboarding: created by the idempotent intake call
// (client-supplied idempotencyKey — retries return the same run, never
// duplicate records) and advanced by the orchestration kickoff.
// status: intake → running → completed | completed_with_errors | failed
export const onboardingRuns = pgTable("onboarding_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  idempotencyKey: varchar("idempotency_key", { length: 160 }).notNull().unique(),
  brandId: varchar("brand_id").references(() => brands.id),
  status: varchar("status", { length: 30 }).notNull().default("intake"),
  // Sanitized copy of the intake payload (audit / debugging).
  intakeJson: jsonb("intake_json"),
  // Ids created by intake: { brandId, productIds, personaIds,
  // anglesByPersona: Record<personaId, angleIds[]> } — returned verbatim on
  // idempotent retries.
  createdIdsJson: jsonb("created_ids_json"),
  // The resolved recipe snapshotted at kickoff (stable keys + resolved live
  // ids + skipped/unresolvable keys + the per-persona random picks and the
  // resolved ad reference-image snapshot), so restarts and the status
  // endpoint report against what actually ran, not the settings' current
  // state.
  recipeSnapshotJson: jsonb("recipe_snapshot_json"),
  // Caller-supplied Ads Express reference images ([{ url, note, purpose }],
  // max 3 — the normal-flow cap), normalized + mirrored to durable R2 storage
  // at intake (external URLs rot) and persisted here so restarts/retries
  // replay the SAME references. Null = intake predates the field; [] = no
  // references (runs behave exactly as before). Consumed by the Ads Express
  // AND UGC Express targets — memes/slideshow never consume these.
  referenceImagesJson: jsonb("reference_images_json"),
  error: text("error"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("onboarding_runs_status_idx").on(table.status),
]);

// One row per fanned-out work item. The item records WHICH underlying
// generation it created (targetType + targetId) and only a creation-phase
// status of its own — live progress/final URLs are always read from the
// underlying run's own status fields (never duplicated here).
// status: pending (not created yet) → created | failed (creation failed)
export const onboardingRunItems = pgTable("onboarding_run_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => onboardingRuns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull(),
  // 'ads_express' | 'slideshow' | 'meme' | 'ugc'
  tool: varchar("tool", { length: 20 }).notNull(),
  // Deterministic fan-out key (e.g. "ads:<personaId>:<angleId>") — the
  // (runId, dedupeKey) unique index makes item creation idempotent across
  // restarts and concurrent kickoff calls.
  dedupeKey: varchar("dedupe_key", { length: 255 }).notNull(),
  personaId: varchar("persona_id"),
  angleId: varchar("angle_id"),
  productId: varchar("product_id"),
  // The stable recipe reference this item runs (style value / template id;
  // null for ads_express items, which carry ALL selected units in one run).
  refKey: varchar("ref_key", { length: 160 }),
  // 'ad_generation' | 'slideshow_run' | 'meme_run' | 'ugc_video'
  targetType: varchar("target_type", { length: 30 }),
  targetId: varchar("target_id"),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  // Bounded self-heal counter for creation retries (reconciler-capped).
  attempts: integer("attempts").notNull().default(0),
  error: text("error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("onboarding_run_items_run_dedupe_unique").on(table.runId, table.dedupeKey),
  index("onboarding_run_items_run_idx").on(table.runId),
]);

export const insertOnboardingRecipeSettingsSchema = createInsertSchema(onboardingRecipeSettings).omit({ id: true, updatedAt: true });
export const insertOnboardingRunSchema = createInsertSchema(onboardingRuns).omit({ id: true, createdAt: true, updatedAt: true });
export const insertOnboardingRunItemSchema = createInsertSchema(onboardingRunItems).omit({ id: true, createdAt: true, updatedAt: true });
export type OnboardingRecipeSettings = typeof onboardingRecipeSettings.$inferSelect;
export type InsertOnboardingRecipeSettings = z.infer<typeof insertOnboardingRecipeSettingsSchema>;
export type OnboardingRun = typeof onboardingRuns.$inferSelect;
export type InsertOnboardingRun = z.infer<typeof insertOnboardingRunSchema>;
export type OnboardingRunItem = typeof onboardingRunItems.$inferSelect;
export type InsertOnboardingRunItem = z.infer<typeof insertOnboardingRunItemSchema>;

export type EmailHeroCompositionArchetype =
  (typeof EMAIL_HERO_COMPOSITION_ARCHETYPES)[number];


// -----------------------------------------------------------------------------
// Windsor.ai Meta ad launcher (Task: Windsor ad-launch backend core)
//
// Server-side foundation for launching Meta campaigns/ad sets/ads exclusively
// through the Windsor.ai connectors API. Every table here is additive; the
// existing read-only Meta CLI integration (meta_ads_connections) is untouched.
// -----------------------------------------------------------------------------

export const WINDSOR_VERTICALS = ["leadgen", "ecom"] as const;

export const ADS_MANAGER_ASSOCIATION_ENTITY_TYPES = ["campaign", "adset", "ad"] as const;
export const windsorLaunchAccounts = pgTable(
  "windsor_launch_accounts",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    // Meta ad account id (numeric string, no "act_" prefix).
    accountId: varchar("account_id", { length: 64 }).notNull(),
    name: varchar("name", { length: 255 }).notNull(),
    // "leadgen" | "ecom" — drives fields asked, conversion counting and the
    // judgment window (14d vs 3d). Null = unclassified.
    vertical: varchar("vertical", { length: 20 }),
    // Exactly ONE declared lead counter field for leadgen accounts (never sum
    // Meta's overlapping lead fields).
    leadSource: varchar("lead_source", { length: 100 }),
    // { target_cpl?, target_cpql?, target_mer?, daily_budget_cap_minor? }
    economicsJson: jsonb("economics_json"),
    maturationDays: integer("maturation_days"),
    pageId: varchar("page_id", { length: 64 }),
    pagesJson: jsonb("pages_json").$type<import("./ad-launcher-account").LaunchPage[]>(),
    reachabilityState: varchar("reachability_state", { length: 24 }).$type<"verified" | "unverified">(),
    reachabilityCheckedAt: timestamp("reachability_checked_at"),
    // Never derivable via Windsor (no pixel endpoint) — always manual.
    pixelId: varchar("pixel_id", { length: 64 }),
    specialAdCategoriesJson: jsonb("special_ad_categories_json"),
    defaultTemplate: varchar("default_template", { length: 120 }),
    // House naming-convention block: { brand, product_line, product, ... }.
    namingJson: jsonb("naming_json"),
    // Last explicit Meta write rejection identifying this account as
    // rate-limited. This is evidence observed at a point in time, not a live
    // provider-health query. Confirmed later writes clear all three fields.
    metaRateLimitDetectedAt: timestamp("meta_rate_limit_detected_at"),
    metaRateLimitCode: varchar("meta_rate_limit_code", { length: 64 }),
    metaRateLimitMessage: text("meta_rate_limit_message"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [
    index("windsor_launch_accounts_brand_idx").on(table.brandId),
    uniqueIndex("windsor_launch_accounts_brand_account_unique").on(table.brandId, table.accountId),
  ],
);

export const adLauncherLaunchDefaults = pgTable(
  "ad_launcher_launch_defaults",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    name: varchar("name", { length: 120 }).notNull(),
    normalizedName: varchar("normalized_name", { length: 120 }).notNull(),
    pixelId: varchar("pixel_id", { length: 128 }),
    customEventType: varchar("custom_event_type", { length: 128 }),
    customConversionId: varchar("custom_conversion_id", { length: 128 }),
    targetingJson: jsonb("targeting_json").notNull(),
    isPrimary: boolean("is_primary").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    createdBy: varchar("created_by"),
  },
  (table) => [
    uniqueIndex("ad_launcher_launch_defaults_brand_normalized_name_uq")
      .on(table.brandId, table.normalizedName),
    uniqueIndex("ad_launcher_launch_defaults_one_primary_per_brand_uq")
      .on(table.brandId)
      .where(sql`${table.isPrimary} = true`),
    index("ad_launcher_launch_defaults_brand_idx").on(table.brandId),
    check(
      "ad_launcher_launch_defaults_conversion_xor_ck",
      sql`NOT (${table.customEventType} IS NOT NULL AND ${table.customConversionId} IS NOT NULL)`,
    ),
  ],
);
export const windsorLaunchPlans = pgTable(
  "windsor_launch_plans",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    accountId: varchar("account_id", { length: 64 }).notNull(),
    // Full resolved launch spec (values + provenance snapshot).
    specJson: jsonb("spec_json").notNull(),
    // planned | running | complete | partial | failed
    status: varchar("status", { length: 20 }).notNull().default("planned"),
    // True while no live write has ever been attempted for this launch.
    dryRun: boolean("dry_run").notNull().default(true),
    // Ids extracted from successful steps (campaign_id, adset_id, ad_ids[]).
    resultJson: jsonb("result_json"),
    error: text("error"),
    executionOwner: varchar("execution_owner", { length: 64 }),
    executionHeartbeatAt: timestamp("execution_heartbeat_at", { withTimezone: true }),
    executionExpiresAt: timestamp("execution_expires_at", { withTimezone: true }),
    executionVersion: integer("execution_version").notNull().default(0),
    // Only initial, explicitly live admissions enroll; never backfill history.
    continuationEnrolledAt: timestamp("continuation_enrolled_at", { withTimezone: true }),
    continuationState: text("continuation_state"),
    continuationAttempts: integer("continuation_attempts").notNull().default(0),
    continuationAfter: timestamp("continuation_after", { withTimezone: true }),
    continuationError: text("continuation_error"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
  },
  (table) => [index("windsor_launch_plans_brand_idx").on(table.brandId)],
);

// One durable pre-send fence per step. A non-resolved intent is never replayable.
export const windsorLaunchDispatches = pgTable("windsor_launch_dispatches", {
  launchId: varchar("launch_id").notNull(),
  stepKey: varchar("step_key", { length: 60 }).notNull(),
  owner: varchar("owner", { length: 64 }).notNull(),
  state: text("state").notNull().default("intent"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => [primaryKey({ columns: [t.launchId, t.stepKey] })]);

// Append-only observations; never rewrite dispatch history or invent responses.
export const windsorLaunchReconciliations = pgTable("windsor_launch_reconciliations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  launchId: varchar("launch_id").notNull(),
  actorId: varchar("actor_id").notNull(),
  executionVersion: integer("execution_version").notNull(),
  evidenceDigest: text("evidence_digest").notNull(),
  checkJson: jsonb("check_json").$type<import("./ad-launcher-incident-recovery").LaunchConnectionCheck>().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [index("windsor_launch_reconciliations_launch_idx").on(t.launchId, t.createdAt)]);

// Append-only write-ahead ledger: one row per live create attempt. The raw
// response is stored BEFORE parsing; errors are scrubbed of EAA…/api_key
// patterns before persisting. Mirrored to a local JSONL file with
// flush+fsync so a DB outage never aborts a launch whose objects exist.
export const windsorLaunchLedger = pgTable(
  "windsor_launch_ledger",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    launchId: varchar("launch_id").notNull(),
    // Ledger step key, possibly indexed for multi-creative fan-out
    // ("create_ad[2]"); the wire action is always the canonical name.
    stepKey: varchar("step_key", { length: 60 }).notNull(),
    action: varchar("action", { length: 60 }).notNull(),
    accountId: varchar("account_id", { length: 64 }).notNull(),
    paramsJson: jsonb("params_json"),
    rawResponse: text("raw_response"),
    // success | parse_warning | failed | ambiguous
    // "ambiguous" = Windsor request timed out or had a network error AFTER
    // the request was sent; Meta may have created the object. Resume must
    // fail-closed on this step pending operator reconciliation with Ads Manager.
    status: varchar("status", { length: 20 }).notNull(),
    parsedId: varchar("parsed_id", { length: 64 }),
    scrubbedError: text("scrubbed_error"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [index("windsor_launch_ledger_launch_idx").on(table.launchId)],
);

// Authoritative, provider-object-level provenance for ads launched by
// PersonaOS. This deliberately records identifiers returned by Windsor, rather
// than attempting to reconcile names, copy, URLs, or ledger position later.
// An ad can receive a replacement creative, so creative id is part of the
// identity and multiple creative versions of one provider ad are retained.
export const platformAdLaunchLineage = pgTable(
  "platform_ad_launch_lineage",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    platform: varchar("platform", { length: 32 }).notNull(),
    accountId: varchar("account_id", { length: 64 }).notNull(),
    campaignId: varchar("campaign_id", { length: 64 }).notNull(),
    adsetId: varchar("adset_id", { length: 64 }),
    adId: varchar("ad_id", { length: 64 }).notNull(),
    creativeId: varchar("creative_id", { length: 64 }).notNull(),
    generatedAdId: varchar("generated_ad_id").notNull().references(() => generatedAds.id, { onDelete: "restrict" }),
    launchId: varchar("launch_id").notNull().references(() => windsorLaunchPlans.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("platform_ad_launch_lineage_exact_uq")
      // Reconciliation of the same launch is idempotent, while a later launch
      // remains a separate append-only link even if Meta returns the same
      // provider identity.
      .on(table.launchId, table.generatedAdId, table.platform, table.accountId, table.adId, table.creativeId),
    index("platform_ad_launch_lineage_exact_lookup_idx")
      .on(table.brandId, table.platform, table.accountId, table.adId, table.creativeId),
    index("platform_ad_launch_lineage_lookup_idx")
      .on(table.brandId, table.platform, table.accountId, table.adId),
    index("platform_ad_launch_lineage_generated_ad_idx").on(table.generatedAdId),
    index("platform_ad_launch_lineage_launch_idx").on(table.launchId),
  ],
);

// Provider ads that have no authoritative PersonaOS launch lineage. These
// records keep external identity and provenance separate from PersonaOS ids.
export const externalPlatformAds = pgTable(
  "external_platform_ads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    platform: varchar("platform", { length: 32 }).notNull(),
    accountId: varchar("account_id", { length: 64 }).notNull(),
    campaignId: varchar("campaign_id", { length: 64 }),
    adsetId: varchar("adset_id", { length: 64 }),
    adId: varchar("ad_id", { length: 64 }).notNull(),
    creativeId: varchar("creative_id", { length: 64 }),
    originalName: varchar("original_name", { length: 255 }),
    mediaType: varchar("media_type", { length: 20 }).notNull(),
    // Native brand-owned source materialized after every durable import.
    sourceGeneratedAdId: varchar("source_generated_ad_id").references(() => generatedAds.id, { onDelete: "restrict" }),
    // Durable exact-identity claim acquired before any provider/R2 retrieval.
    // A second MCP key observes this claim and waits/re-kicks instead of
    // downloading the same external creative concurrently.
    claimOperationId: varchar("claim_operation_id").references(() => mcpOperations.id, { onDelete: "set null" }),
    claimLeaseExpiresAt: timestamp("claim_lease_expires_at"),
    importStatus: varchar("import_status", { length: 20 }).notNull().default("pending"),
    provider: varchar("provider", { length: 100 }).notNull(),
    retrievalMetadata: jsonb("retrieval_metadata").notNull(),
    retrievedAt: timestamp("retrieved_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("external_platform_ads_identity_uq")
      .on(table.brandId, table.platform, table.accountId, table.adId, sql`coalesce(${table.creativeId}, '')`),
    index("external_platform_ads_lookup_idx").on(table.brandId, table.platform, table.accountId, table.adId),
    uniqueIndex("external_platform_ads_source_generated_ad_uq").on(table.sourceGeneratedAdId),
  ],
);

// Ordered media from an external provider ad. durableObjectKey is required for
// full copies; thumbnail-only retrieval is explicitly represented by fidelity.
export const externalPlatformAdMedia = pgTable(
  "external_platform_ad_media",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    externalAdId: varchar("external_ad_id").notNull().references(() => externalPlatformAds.id, { onDelete: "cascade" }),
    sortOrder: integer("sort_order").notNull(),
    platformMediaId: varchar("platform_media_id", { length: 200 }),
    mediaType: varchar("media_type", { length: 20 }).notNull(),
    fidelity: varchar("fidelity", { length: 20 }).notNull(), // full | thumbnail_only
    durableObjectKey: varchar("durable_object_key", { length: 512 }),
    thumbnailObjectKey: varchar("thumbnail_object_key", { length: 512 }),
    originalRetrievalUrl: text("original_retrieval_url"),
    contentType: varchar("content_type", { length: 100 }),
    width: integer("width"),
    height: integer("height"),
    retrievedAt: timestamp("retrieved_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("external_platform_ad_media_order_uq").on(table.externalAdId, table.sortOrder),
  ],
);

// Immutable date-ranged provider performance snapshots with the provider's
// attribution context retained alongside the metrics payload.
export const externalPlatformAdEvidence = pgTable(
  "external_platform_ad_evidence",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    externalAdId: varchar("external_ad_id").notNull().references(() => externalPlatformAds.id, { onDelete: "cascade" }),
    dateStart: date("date_start").notNull(),
    dateEnd: date("date_end").notNull(),
    attribution: jsonb("attribution").notNull(),
    metrics: jsonb("metrics").notNull(),
    retrievedAt: timestamp("retrieved_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("external_platform_ad_evidence_range_uq")
      .on(table.externalAdId, table.dateStart, table.dateEnd, table.attribution),
  ],
);

// Operator-saved reusable templates (id prefix "saved-" at the API surface).
export const windsorSavedTemplates = pgTable(
  "windsor_saved_templates",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    name: varchar("name", { length: 255 }).notNull(),
    templateJson: jsonb("template_json").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [
    index("windsor_saved_templates_brand_idx").on(table.brandId),
    // Names are unique per brand on the NORMALIZED form (trimmed,
    // case-insensitive), enforced atomically by the index — not by a
    // check-then-insert race. Archived rows are excluded. Provisioned with
    // legacy dedup at startup (provisionWindsorTemplates) which hard-fails
    // boot rather than running half-enforced.
    uniqueIndex("windsor_saved_templates_brand_norm_name_uq")
      .on(table.brandId, sql`lower(trim(${table.name}))`)
      .where(sql`archived_at IS NULL`),
  ],
);

export const windsorLaunchProgress = pgTable(
  "windsor_launch_progress",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    launchId: varchar("launch_id").notNull(),
    stepKey: varchar("step_key", { length: 60 }).notNull(),
    phase: varchar("phase", { length: 20 }).notNull(), // started | done
    status: varchar("status", { length: 30 }), // step outcome when phase=done
    error: text("error"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [index("windsor_launch_progress_launch_idx").on(table.launchId)],
);
export const windsorClonedTemplates = pgTable(
  "windsor_cloned_templates",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    accountId: varchar("account_id", { length: 64 }).notNull(),
    vertical: varchar("vertical", { length: 20 }).notNull(),
    sourceCampaignId: varchar("source_campaign_id", { length: 64 }),
    templateJson: jsonb("template_json").notNull(),
    // Human-readable record of targeting repairs made at extraction.
    sanitizationNotesJson: jsonb("sanitization_notes_json"),
    createdAt: timestamp("created_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
  },
  (table) => [index("windsor_cloned_templates_brand_idx").on(table.brandId)],
);

// Uploaded creative image metadata. Bytes live in R2 (random object names);
// the public URL always uses a production origin (IMAGE_PUBLIC_ORIGIN
// override or the R2 public base) — never *.replit.dev/localhost.
export const windsorCreativeImages = pgTable(
  "windsor_creative_images",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    objectKey: varchar("object_key", { length: 512 }).notNull(),
    publicUrl: varchar("public_url", { length: 1024 }).notNull(),
    originalFilename: varchar("original_filename", { length: 255 }),
    contentType: varchar("content_type", { length: 60 }).notNull(),
    width: integer("width"),
    height: integer("height"),
    sizeBytes: integer("size_bytes"),
    createdAt: timestamp("created_at").defaultNow(),
    createdBy: varchar("created_by"),
    archivedAt: timestamp("archived_at"),
    // Task #2220 — when this creative was imported from a saved My Ads entry
    // (instead of a manual file upload), the source generated_ads id. Used to
    // dedupe re-imports of the same ad for the brand. Null on file uploads.
    sourceAdId: varchar("source_ad_id"),
    // Windsor launcher creative media. Existing rows remain image rows.
    kind: varchar("kind", { length: 20 }).notNull().default("image"),
    videoUrl: varchar("video_url", { length: 1024 }),
    thumbnailUrl: varchar("thumbnail_url", { length: 1024 }),
    thumbnailObjectKey: varchar("thumbnail_object_key", { length: 512 }),
    durationSec: integer("duration_sec"),
  },
  (table) => [
    index("windsor_creative_images_brand_idx").on(table.brandId),
    index("windsor_creative_images_source_ad_idx").on(table.brandId, table.sourceAdId),
  ],
);

export type WindsorLaunchAccount = typeof windsorLaunchAccounts.$inferSelect;
export type WindsorLaunchPlan = typeof windsorLaunchPlans.$inferSelect;
export type WindsorLaunchLedgerRow = typeof windsorLaunchLedger.$inferSelect;
export type PlatformAdLaunchLineage = typeof platformAdLaunchLineage.$inferSelect;
export type ExternalPlatformAd = typeof externalPlatformAds.$inferSelect;
export type ExternalPlatformAdMedia = typeof externalPlatformAdMedia.$inferSelect;
export type ExternalPlatformAdEvidence = typeof externalPlatformAdEvidence.$inferSelect;
export type CreativeLineageNode = typeof creativeLineageNodes.$inferSelect;
export type CreativeLineageEdge = typeof creativeLineageEdges.$inferSelect;
export type CreativeExternalLink = typeof creativeExternalLinks.$inferSelect;
export type WindsorSavedTemplate = typeof windsorSavedTemplates.$inferSelect;
export type WindsorClonedTemplate = typeof windsorClonedTemplates.$inferSelect;
export type WindsorCreativeImage = typeof windsorCreativeImages.$inferSelect;

export type EmailPolishPromptTemplate =
  typeof emailPolishPromptTemplates.$inferSelect;

// ---------------------------------------------------------------------------
// CREATIVE STRATEGIST — Phase 1A/1B (Task #2375)
//
// An AI project manager that ORCHESTRATES the existing PersonaOS tools
// (Brands, Personas, Offers, Angles, Ads Express, LP Express). Everything
// lives in its own `creative_strategist_*` namespace so the whole feature can
// be switched off (brand flag + global kill switch) or dropped without
// touching any existing table.
//
// NOT related to the "Creative Strategist" Sim workflow STEP used by Slideshow
// Express / Video Express Max (`simCreativeStrategistUrl` above) — different
// concept, deliberately untouched.
// ---------------------------------------------------------------------------

// Global kill switch modes (master-only, singleton settings row).
//   enabled           — normal operation
//   disable_new_work  — refuse NEW projects/tasks/dispatch, let in-flight finish
//   hard_stop         — feature closed: every route 403s
export const CREATIVE_STRATEGIST_GLOBAL_MODES = [
  "enabled",
  "disable_new_work",
  "hard_stop",
] as const;
export type CreativeStrategistGlobalMode =
  (typeof CREATIVE_STRATEGIST_GLOBAL_MODES)[number];

// Singleton settings row (key = "global"), same pattern as
// adsExpressSettings / slideshowExpressSettings / memeGeneratorSettings.
export const creativeStrategistSettings = pgTable(
  "creative_strategist_settings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    key: varchar("key", { length: 50 }).notNull().unique(),
    globalMode: varchar("global_mode", { length: 32 })
      .notNull()
      .default("enabled"),
    // ── Task #2376 — Admin → Creative Strategist → General ────────────────
    // Everything the AI Decision Gateway reads at call time lives on this same
    // singleton row. Defaults reproduce today's behavior exactly, so a DB that
    // has never been touched by the settings pane behaves as before.
    defaultBrandFeatureState: boolean("default_brand_feature_state")
      .notNull()
      .default(false),
    maxConcurrentJobs: integer("max_concurrent_jobs").notNull().default(3),
    maxAutomaticRevisions: integer("max_automatic_revisions")
      .notNull()
      .default(2),
    defaultInternalModel: varchar("default_internal_model", { length: 128 })
      .notNull()
      .default("gpt-4o-mini"),
    defaultSimTimeoutMs: integer("default_sim_timeout_ms")
      .notNull()
      .default(120000),
    allowSimFallback: boolean("allow_sim_fallback").notNull().default(true),
    pauseAllJobs: boolean("pause_all_jobs").notNull().default(false),
    updatedAt: timestamp("updated_at").defaultNow(),
    updatedBy: varchar("updated_by"),
  },
);

export const CREATIVE_STRATEGIST_PROJECT_STATUSES = [
  "planning",
  "awaiting_approval",
  "running",
  "blocked",
  "complete",
  "canceled",
] as const;
export type CreativeStrategistProjectStatus =
  (typeof CREATIVE_STRATEGIST_PROJECT_STATUSES)[number];

// A unit of work the user asked for ("Launch the summer sale"). Scoped to
// workspace + brand; `planning` until a plan is generated (later phase).
export const creativeStrategistProjects = pgTable(
  "creative_strategist_projects",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    workspaceId: varchar("workspace_id").notNull(),
    brandId: varchar("brand_id")
      .notNull()
      .references(() => brands.id, { onDelete: "cascade" }),
    createdBy: varchar("created_by"),
    title: varchar("title", { length: 255 }).notNull(),
    objective: text("objective"),
    description: text("description"),
    status: varchar("status", { length: 32 }).notNull().default("planning"),
    currentPhase: varchar("current_phase", { length: 64 }),
    progressPercentage: integer("progress_percentage").notNull().default(0),
    // Chat lives in a later phase; the pointer is reserved here.
    chatThreadId: varchar("chat_thread_id"),
    // ── Task #2378 — dependency resolution + the proposed plan ────────────
    // Both live on the project row (not a side table) because there is exactly
    // ONE current resolution and ONE current proposal per project, and the
    // Plan tab has to survive a reload without replaying an AI call. Superseded
    // versions keep their lineage as `creative_strategist_decisions` rows.
    resolutionStatus: varchar("resolution_status", { length: 32 })
      .notNull()
      .default("none"),
    resolutionJson: jsonb("resolution_json"),
    resolutionError: text("resolution_error"),
    resolvedAt: timestamp("resolved_at"),
    // none → proposed → approved. The approval gate is a conditional UPDATE
    // off this column, which is what makes a double-clicked approve safe.
    planStatus: varchar("plan_status", { length: 32 }).notNull().default("none"),
    planJson: jsonb("plan_json"),
    planVersion: integer("plan_version").notNull().default(0),
    planApprovedAt: timestamp("plan_approved_at"),
    planApprovedBy: varchar("plan_approved_by"),
    // ── Task #2382 — the Results page ─────────────────────────────────────
    // Assembled from project data (counts, initiatives, decisions) in CODE;
    // only the narrative sections come from PROJECT.RESULTS_SUMMARY. Cached
    // here so a completed project renders its Results without replaying an
    // AI call, and stays readable for as long as the project exists.
    resultsStatus: varchar("results_status", { length: 32 })
      .notNull()
      .default("none"),
    resultsJson: jsonb("results_json"),
    resultsGeneratedAt: timestamp("results_generated_at"),
    // Fencing token for Results generation. Stamped by the claim; save/fail
    // must present it. A claimant whose claim was taken over (stale takeover)
    // no longer matches, so its late write hits nothing instead of
    // overwriting the newer claimant's Results.
    resultsClaimToken: varchar("results_claim_token", { length: 64 }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    completedAt: timestamp("completed_at"),
  },
  (table) => [
    index("creative_strategist_projects_brand_idx").on(table.brandId),
    index("creative_strategist_projects_workspace_idx").on(table.workspaceId),
  ],
);

// `internal_review` and `revision` are the two states Task #2382 adds real
// behaviour to: an output that exists is not finished work — it sits in
// internal review, and a failed review parks the task in `revision` while a
// child revision task re-runs the same generator.
export const CREATIVE_STRATEGIST_TASK_STATUSES = [
  "not_started",
  "ready",
  "running",
  "blocked",
  "internal_review",
  "revision",
  "user_review",
  "complete",
  "failed",
  "canceled",
] as const;
export type CreativeStrategistTaskStatus =
  (typeof CREATIVE_STRATEGIST_TASK_STATUSES)[number];

export const creativeStrategistTasks = pgTable(
  "creative_strategist_tasks",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    // Self reference kept as a plain column (no FK) so subtask trees can be
    // re-parented without fighting the constraint.
    parentTaskId: varchar("parent_task_id"),
    title: varchar("title", { length: 255 }).notNull(),
    description: text("description"),
    taskType: varchar("task_type", { length: 64 }),
    status: varchar("status", { length: 32 }).notNull().default("not_started"),
    // Which system executes it (ads_express, lp_express, internal, user, ...).
    assignedExecutor: varchar("assigned_executor", { length: 64 }),
    priority: integer("priority").notNull().default(0),
    inputsJson: jsonb("inputs_json"),
    expectedOutputType: varchar("expected_output_type", { length: 64 }),
    acceptanceCriteriaJson: jsonb("acceptance_criteria_json"),
    decisionRefsJson: jsonb("decision_refs_json"),
    contextAssetRefsJson: jsonb("context_asset_refs_json"),
    attemptCount: integer("attempt_count").notNull().default(0),
    // Automatic revisions spent on this task's deliverable (Task #2382). Held
    // on the ORIGINAL task — a revision child never starts its own budget —
    // and compared against the admin's `maxAutomaticRevisions` before another
    // revision may be created. This is what stops a review/revise loop.
    revisionCount: integer("revision_count").notNull().default(0),
    // Why the task needs a human, in the user's words. Set when the revision
    // budget runs out or a review escalates; cleared when the user acts.
    attentionReason: text("attention_reason"),
    lastError: text("last_error"),
    outputRef: varchar("output_ref"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
  },
  (table) => [
    index("creative_strategist_tasks_project_idx").on(table.projectId),
    index("creative_strategist_tasks_status_idx").on(table.projectId, table.status),
  ],
);

export const CREATIVE_STRATEGIST_DEPENDENCY_TYPES = ["hard", "soft"] as const;

export const creativeStrategistTaskDependencies = pgTable(
  "creative_strategist_task_dependencies",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    taskId: varchar("task_id")
      .notNull()
      .references(() => creativeStrategistTasks.id, { onDelete: "cascade" }),
    dependsOnTaskId: varchar("depends_on_task_id")
      .notNull()
      .references(() => creativeStrategistTasks.id, { onDelete: "cascade" }),
    dependencyType: varchar("dependency_type", { length: 16 })
      .notNull()
      .default("hard"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_task_deps_task_idx").on(table.taskId),
    uniqueIndex("creative_strategist_task_deps_unique").on(
      table.taskId,
      table.dependsOnTaskId,
    ),
  ],
);

// Coarse category inferred from the uploaded file's type (Task #2377). It is
// what the Files tab shows as a badge — the user never picks it; they describe
// the file in their own words instead (`userDescription`).
export const CREATIVE_STRATEGIST_ASSET_PURPOSES = [
  "reference_image",
  "document",
  "data",
  "notes",
  "other",
] as const;
export type CreativeStrategistAssetPurpose =
  (typeof CREATIVE_STRATEGIST_ASSET_PURPOSES)[number];

// Lifecycle of the two background enrichment passes an upload goes through.
//   pending      — queued, not started
//   running      — a worker holds it
//   done         — text/analysis stored
//   unsupported  — this file type has no extraction/analysis pass (not a failure)
//   failed       — the pass errored; the file is still usable as an attachment
export const CREATIVE_STRATEGIST_ASSET_PROCESSING_STATUSES = [
  "pending",
  "running",
  "done",
  "unsupported",
  "failed",
] as const;
export type CreativeStrategistAssetProcessingStatus =
  (typeof CREATIVE_STRATEGIST_ASSET_PROCESSING_STATUSES)[number];

// Files the user attaches to a project — the project's Context Assets library
// (Task #2377). Every row owns a durable object-storage file; `storageKey` is
// what the delete path reclaims.
export const creativeStrategistContextAssets = pgTable(
  "creative_strategist_context_assets",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull(),
    uploadedBy: varchar("uploaded_by"),
    // Chat message that introduced this file — the Files tab links back to it.
    messageId: varchar("message_id"),
    fileName: varchar("file_name", { length: 512 }),
    fileType: varchar("file_type", { length: 64 }),
    fileSize: integer("file_size"),
    storageUrl: varchar("storage_url", { length: 2048 }),
    storageKey: varchar("storage_key", { length: 1024 }),
    extractedText: text("extracted_text"),
    extractionStatus: varchar("extraction_status", { length: 24 })
      .notNull()
      .default("pending"),
    analysisJson: jsonb("analysis_json"),
    analysisStatus: varchar("analysis_status", { length: 24 })
      .notNull()
      .default("pending"),
    // Last enrichment failure, kept for the Files tab tooltip. Never blocks use.
    processingError: text("processing_error"),
    purpose: varchar("purpose", { length: 64 }),
    userDescription: text("user_description"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_context_assets_project_idx").on(table.projectId),
  ],
);

// Who wrote a chat message (Task #2377).
//   user       — the person typing in the composer
//   strategist — the Creative Strategist's reply
//   system     — machine-written notices (never AI prose)
export const CREATIVE_STRATEGIST_MESSAGE_ROLES = [
  "user",
  "strategist",
  "system",
] as const;
export type CreativeStrategistMessageRole =
  (typeof CREATIVE_STRATEGIST_MESSAGE_ROLES)[number];

// A strategist reply is written PENDING the moment the user's message lands so
// the thread shows a thinking bubble, then flips to sent/failed. Failed rows
// stay in the thread — that is what the retry button acts on.
export const CREATIVE_STRATEGIST_MESSAGE_STATUSES = [
  "pending",
  "sent",
  "failed",
] as const;
export type CreativeStrategistMessageStatus =
  (typeof CREATIVE_STRATEGIST_MESSAGE_STATUSES)[number];

// The persistent project conversation. One row per message; ordering is by the
// `seq` sequence rather than `createdAt` so a reply written in the same
// millisecond as its question can never sort above it.
export const creativeStrategistMessages = pgTable(
  "creative_strategist_messages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    seq: bigserial("seq", { mode: "number" }).notNull(),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull(),
    authorRole: varchar("author_role", { length: 16 }).notNull().default("user"),
    authorUserId: varchar("author_user_id"),
    body: text("body").notNull().default(""),
    status: varchar("status", { length: 16 }).notNull().default("sent"),
    // Ordered context-asset ids introduced by this message.
    attachmentIdsJson: jsonb("attachment_ids_json"),
    // What the user had open when they sent it — {tab, taskId, deliverableId,
    // label}. This is how "redo this, it feels too corporate" resolves to an
    // object without the user naming an id.
    viewingContextJson: jsonb("viewing_context_json"),
    // Strategist rows: the validated intent-parse output plus the context
    // assets the selector picked for this turn.
    intentJson: jsonb("intent_json"),
    replyToMessageId: varchar("reply_to_message_id"),
    errorMessage: text("error_message"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_messages_project_idx").on(table.projectId, table.seq),
  ],
);

export const CREATIVE_STRATEGIST_DECISION_SOURCE_TYPES = [
  "existing",
  "user",
  "backfilled",
  "generated",
] as const;
export const CREATIVE_STRATEGIST_DECISION_STATUSES = [
  "proposed",
  "approved",
  "rejected",
  "superseded",
] as const;

// A resolved strategic choice for the project (persona, offer, angle, ...).
export const creativeStrategistDecisions = pgTable(
  "creative_strategist_decisions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    decisionType: varchar("decision_type", { length: 64 }).notNull(),
    title: varchar("title", { length: 255 }),
    valueJson: jsonb("value_json"),
    sourceType: varchar("source_type", { length: 32 }).notNull().default("generated"),
    status: varchar("status", { length: 32 }).notNull().default("proposed"),
    rationale: text("rationale"),
    version: integer("version").notNull().default(1),
    supersedesId: varchar("supersedes_id"),
    decidedBy: varchar("decided_by"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_decisions_project_idx").on(table.projectId),
    // Version numbers are a linear chain per (project, decision type). Without
    // this, two racing writers can both append "version N+1" and fork the
    // supersede lineage; with it, the loser's insert conflicts and retries.
    uniqueIndex("creative_strategist_decisions_version_uidx").on(
      table.projectId,
      table.decisionType,
      table.version,
    ),
  ],
);

export const CREATIVE_STRATEGIST_DELIVERABLE_STATUSES = [
  "pending",
  "generating",
  "ready",
  "internal_review",
  // A review said "revise" and a revision task is producing the next version.
  "revision",
  "user_review",
  "approved",
  "rejected",
  "failed",
] as const;

// What the project hands back to the user (ad set, landing page, doc, ...).
export const creativeStrategistDeliverables = pgTable(
  "creative_strategist_deliverables",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    taskId: varchar("task_id"),
    brandId: varchar("brand_id").notNull(),
    deliverableType: varchar("deliverable_type", { length: 64 }).notNull(),
    title: varchar("title", { length: 255 }),
    status: varchar("status", { length: 32 }).notNull().default("pending"),
    // Document-style deliverables (briefs, plans) store their body inline;
    // system-backed ones point at an output link row.
    documentContent: text("document_content"),
    outputLinkId: varchar("output_link_id"),
    previewUrl: varchar("preview_url", { length: 2048 }),
    // The version currently shown. Every attempt is a row in
    // `creative_strategist_deliverable_versions`; this column points at the
    // latest one so the common read needs no join.
    version: integer("version").notNull().default(1),
    approvedBy: varchar("approved_by"),
    approvedAt: timestamp("approved_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_deliverables_project_idx").on(table.projectId),
  ],
);

export const CREATIVE_STRATEGIST_REVIEW_VERDICTS = [
  "pass",
  "revise",
  "escalate",
  "user_approved",
  "not_reviewed",
] as const;
export const creativeStrategistOutputLinks = pgTable(
  "creative_strategist_output_links",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    taskId: varchar("task_id"),
    outputSystem: varchar("output_system", { length: 64 }).notNull(),
    outputType: varchar("output_type", { length: 64 }),
    outputId: varchar("output_id").notNull(),
    outputUrl: varchar("output_url", { length: 2048 }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_output_links_project_idx").on(table.projectId),
  ],
);

export const CREATIVE_STRATEGIST_EXECUTION_STATUSES = [
  "dispatched",
  "running",
  "complete",
  "failed",
  "canceled",
] as const;
export type CreativeStrategistExecutionStatus =
  (typeof CREATIVE_STRATEGIST_EXECUTION_STATUSES)[number];

// One row per DISPATCH of a task through a tool adapter (spec §66). The
// execution key `cs:{projectId}:{taskId}:{attempt}` is unique, so a duplicate
// trigger, a retry that races the loop, or a restart that re-reads the same
// ready task can never start (or bill) the same attempt twice: the second
// insert loses the conflict and re-attaches to the row that won.
export const creativeStrategistExecutions = pgTable(
  "creative_strategist_executions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    taskId: varchar("task_id").notNull(),
    brandId: varchar("brand_id").notNull(),
    attemptNumber: integer("attempt_number").notNull(),
    executionKey: varchar("execution_key", { length: 255 }).notNull(),
    jobType: varchar("job_type", { length: 64 }).notNull().default("cs.task_execute"),
    executor: varchar("executor", { length: 64 }).notNull(),
    status: varchar("status", { length: 32 }).notNull().default("dispatched"),
    // The PersonaOS system doing the work and its own run id, so the executor
    // can re-attach after a restart instead of starting a second run.
    underlyingSystem: varchar("underlying_system", { length: 64 }),
    underlyingRunId: varchar("underlying_run_id"),
    underlyingRefsJson: jsonb("underlying_refs_json"),
    // Exactly what the adapter sent the tool — shown on the task detail so a
    // surprising output can be traced back to its inputs.
    inputsJson: jsonb("inputs_json"),
    lastError: text("last_error"),
    cancelRequestedAt: timestamp("cancel_requested_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
  },
  (table) => [
    uniqueIndex("creative_strategist_executions_key_uq").on(table.executionKey),
    index("creative_strategist_executions_project_idx").on(table.projectId),
    index("creative_strategist_executions_task_idx").on(table.taskId),
    index("creative_strategist_executions_status_idx").on(table.status),
  ],
);

export const CREATIVE_STRATEGIST_OBJECT_SOURCE_TYPES = [
  "user_supplied",
  "backfilled",
  "generated",
] as const;

// Foundation objects (personas, offers, angles) the project CREATED in the
// existing tables, so a rollback can find and review them.
export const creativeStrategistCreatedObjects = pgTable(
  "creative_strategist_created_objects",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    taskId: varchar("task_id"),
    brandId: varchar("brand_id").notNull(),
    objectType: varchar("object_type", { length: 64 }).notNull(),
    objectId: varchar("object_id").notNull(),
    sourceType: varchar("source_type", { length: 32 }).notNull().default("generated"),
    decisionRunId: varchar("decision_run_id"),
    // Set while the object id is RESERVED but the object itself has not been
    // written yet. The row is the crash-safe idempotency marker: it is
    // persisted before the create, so a worker that dies mid-create finds the
    // id it reserved on the next attempt and creates with that same id rather
    // than making a duplicate. Cleared the moment the object exists; a row
    // still carrying it is not shown as a created object.
    pendingAt: timestamp("pending_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_created_objects_project_idx").on(table.projectId),
    index("creative_strategist_created_objects_brand_idx").on(table.brandId),
    // One reservation per task and object type, enforced by the database.
    // Reserving is a check-then-insert, and two workers can be inside it at
    // once (a recovered lease runs alongside the worker it replaced), so this
    // is what actually stops a second id — and therefore a second persona,
    // offer or angle — from ever being reserved for the same task.
    uniqueIndex("creative_strategist_created_objects_task_type_uq").on(
      table.taskId,
      table.objectType,
    ),
  ],
);

export const CREATIVE_STRATEGIST_TEMPLATE_STATUSES = [
  "UNREVIEWED",
  "TESTING",
  "APPROVED",
  "DEFAULT",
  "RETIRED",
] as const;

// Per-brand judgement layer over the templates the underlying systems already
// own — which ad unit / LP template this brand may actually use.
export const creativeStrategistBrandTemplates = pgTable(
  "creative_strategist_brand_templates",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id")
      .notNull()
      .references(() => brands.id, { onDelete: "cascade" }),
    assetType: varchar("asset_type", { length: 64 }).notNull(),
    underlyingSystem: varchar("underlying_system", { length: 64 }).notNull(),
    underlyingTemplateType: varchar("underlying_template_type", { length: 64 }),
    underlyingTemplateId: varchar("underlying_template_id"),
    name: varchar("name", { length: 255 }),
    status: varchar("status", { length: 32 }).notNull().default("UNREVIEWED"),
    isDefault: boolean("is_default").notNull().default(false),
    approvedForJson: jsonb("approved_for_json"),
    avoidForJson: jsonb("avoid_for_json"),
    lockedRulesJson: jsonb("locked_rules_json"),
    flexibleRulesJson: jsonb("flexible_rules_json"),
    notes: text("notes"),
    performanceScore: real("performance_score"),
    source: varchar("source", { length: 64 }),
    approvedBy: varchar("approved_by"),
    approvedAt: timestamp("approved_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_brand_templates_brand_idx").on(table.brandId),
    // Exactly one DEFAULT per asset type per brand, enforced by the database
    // rather than by whoever happens to be writing (spec §33/§34). A partial
    // index leaves every non-default row unconstrained.
    uniqueIndex("creative_strategist_brand_templates_default_unique")
      .on(table.brandId, table.assetType)
      .where(sql`${table.isDefault}`),
    // Deliberately NOT unique on the underlying reference: "Duplicate" (spec
    // §32) exists so one underlying template can carry two different rule
    // sets. The approve-a-candidate path looks for an existing row by
    // reference first, so ordinary approval still never doubles a card.
    index("creative_strategist_brand_templates_ref_idx").on(
      table.brandId,
      table.assetType,
      table.underlyingTemplateId,
    ),
  ],
);

export const creativeStrategistBlockers = pgTable(
  "creative_strategist_blockers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull(),
    taskId: varchar("task_id"),
    blockerType: varchar("blocker_type", { length: 64 }).notNull(),
    // The asset type the blocker is about, when it has one. Doubles as the
    // idempotency key so one project cannot collect ten identical blockers.
    assetType: varchar("asset_type", { length: 64 }),
    title: varchar("title", { length: 255 }).notNull(),
    detail: text("detail"),
    // [{ key, label, description }] — the resolution options offered.
    optionsJson: jsonb("options_json"),
    status: varchar("status", { length: 16 }).notNull().default("open"),
    resolutionOption: varchar("resolution_option", { length: 64 }),
    resolutionNote: text("resolution_note"),
    // The registry row the resolution settled on, when the chosen option names
    // one (the approved fallback, or the template the user picked). Selection
    // reads it back so a resolved blocker actually changes what happens next.
    resolutionTemplateId: varchar("resolution_template_id"),
    resolvedBy: varchar("resolved_by"),
    resolvedAt: timestamp("resolved_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_blockers_project_idx").on(
      table.projectId,
      table.status,
    ),
    // At most one OPEN blocker of a kind per asset type per project: a retried
    // selection re-uses the existing blocker instead of stacking duplicates.
    uniqueIndex("creative_strategist_blockers_open_unique")
      .on(table.projectId, table.blockerType, table.assetType)
      .where(sql`${table.status} = 'open'`),
  ],
);
export const CREATIVE_STRATEGIST_DECISION_PROVIDERS = ["internal", "sim"] as const;
export const CREATIVE_STRATEGIST_EXECUTION_POLICIES = [
  "internal_only",
  "sim_only",
  "sim_with_fallback",
] as const;
export type CreativeStrategistDecisionProvider =
  (typeof CREATIVE_STRATEGIST_DECISION_PROVIDERS)[number];
export type CreativeStrategistExecutionPolicy =
  (typeof CREATIVE_STRATEGIST_EXECUTION_POLICIES)[number];

// Sim workflows in this repo genuinely differ on whether inputs are posted
// wrapped (`{ input: {...} }`) or at the top level — it is per-workflow
// configuration, never a guess made at the call site.
export const CREATIVE_STRATEGIST_INPUT_ENVELOPES = ["wrapped", "top_level"] as const;
export type CreativeStrategistInputEnvelope =
  (typeof CREATIVE_STRATEGIST_INPUT_ENVELOPES)[number];

// Registry of the reasoning calls the Decision Gateway may make (that gateway
// is a later phase — this table is its catalog).
export const creativeStrategistDecisionDefinitions = pgTable(
  "creative_strategist_decision_definitions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    key: varchar("key", { length: 128 }).notNull().unique(),
    name: varchar("name", { length: 255 }),
    description: text("description"),
    version: integer("version").notNull().default(1),
    provider: varchar("provider", { length: 32 }).notNull().default("internal"),
    executionPolicy: varchar("execution_policy", { length: 32 })
      .notNull()
      .default("internal_only"),
    endpointUrl: varchar("endpoint_url", { length: 2048 }),
    // Never a raw secret — the NAME of the secret to read at call time.
    authSecretKey: varchar("auth_secret_key", { length: 128 }),
    // Task #2376 — HTTP header the Sim API key is sent in (Sim's own workflow
    // API uses X-API-Key; a proxy in front of one may want Authorization).
    authHeaderName: varchar("auth_header_name", { length: 128 }),
    // Task #2376 — per-workflow request-body shape, see the envelope constant.
    inputEnvelope: varchar("input_envelope", { length: 16 })
      .notNull()
      .default("wrapped"),
    timeoutMs: integer("timeout_ms"),
    retryPolicyJson: jsonb("retry_policy_json"),
    inputSchemaJson: jsonb("input_schema_json"),
    outputSchemaJson: jsonb("output_schema_json"),
    exampleInputJson: jsonb("example_input_json"),
    exampleOutputJson: jsonb("example_output_json"),
    active: boolean("active").notNull().default(true),
    // Task #2376 — activation gate. A Sim-backed decision may only be made
    // active once a Test Endpoint run against the CURRENT endpoint validated
    // against the output schema; these columns are that receipt.
    lastTestStatus: varchar("last_test_status", { length: 32 }),
    lastTestEndpointUrl: varchar("last_test_endpoint_url", { length: 2048 }),
    lastTestAt: timestamp("last_test_at"),
    lastTestJson: jsonb("last_test_json"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
);

// One recorded execution of a decision definition (audit + replay).
export const creativeStrategistDecisionRuns = pgTable(
  "creative_strategist_decision_runs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id"),
    taskId: varchar("task_id"),
    brandId: varchar("brand_id"),
    decisionKey: varchar("decision_key", { length: 128 }).notNull(),
    decisionVersion: integer("decision_version"),
    provider: varchar("provider", { length: 32 }),
    modelOrEndpoint: varchar("model_or_endpoint", { length: 2048 }),
    inputJson: jsonb("input_json"),
    outputJson: jsonb("output_json"),
    validationStatus: varchar("validation_status", { length: 32 }),
    durationMs: integer("duration_ms"),
    retryCount: integer("retry_count").notNull().default(0),
    error: text("error"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_decision_runs_project_idx").on(table.projectId),
    // Task #2376 — the run log has to be queryable by decision key (admin
    // "recent runs" / "errors") and by task (executor drill-down).
    index("creative_strategist_decision_runs_key_idx").on(
      table.decisionKey,
      table.createdAt,
    ),
    index("creative_strategist_decision_runs_task_idx").on(table.taskId),
  ],
);

export const CREATIVE_STRATEGIST_ACTOR_TYPES = ["user", "system", "ai"] as const;

// Append-only audit trail rendered by the project Activity tab. Every later
// phase writes here; nothing ever updates or deletes a row.
export const creativeStrategistActivity = pgTable(
  "creative_strategist_activity",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull(),
    taskId: varchar("task_id"),
    eventType: varchar("event_type", { length: 64 }).notNull(),
    actorType: varchar("actor_type", { length: 16 }).notNull().default("system"),
    actorUserId: varchar("actor_user_id"),
    summary: text("summary").notNull(),
    detailJson: jsonb("detail_json"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_activity_project_idx").on(
      table.projectId,
      table.createdAt,
    ),
  ],
);

export type CreativeStrategistSettingsRow =
  typeof creativeStrategistSettings.$inferSelect;
export type CreativeStrategistProject =
  typeof creativeStrategistProjects.$inferSelect;
export type CreativeStrategistTask = typeof creativeStrategistTasks.$inferSelect;
export type CreativeStrategistTaskDependency =
  typeof creativeStrategistTaskDependencies.$inferSelect;
export type CreativeStrategistContextAsset =
  typeof creativeStrategistContextAssets.$inferSelect;
export type CreativeStrategistMessage =
  typeof creativeStrategistMessages.$inferSelect;
export type CreativeStrategistDecision =
  typeof creativeStrategistDecisions.$inferSelect;
export type CreativeStrategistDeliverable =
  typeof creativeStrategistDeliverables.$inferSelect;
export type CreativeStrategistOutputLink =
  typeof creativeStrategistOutputLinks.$inferSelect;
export type CreativeStrategistCreatedObject =
  typeof creativeStrategistCreatedObjects.$inferSelect;
export type CreativeStrategistBrandTemplate =
  typeof creativeStrategistBrandTemplates.$inferSelect;

export type CreativeStrategistBlocker =
  typeof creativeStrategistBlockers.$inferSelect;
export type CreativeStrategistDecisionDefinition =
  typeof creativeStrategistDecisionDefinitions.$inferSelect;
export type CreativeStrategistDecisionRun =
  typeof creativeStrategistDecisionRuns.$inferSelect;
export type CreativeStrategistActivityRow =
  typeof creativeStrategistActivity.$inferSelect;

export const creativeEditorProjects = pgTable(
  "creative_editor_projects",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    brandId: varchar("brand_id")
      .notNull()
      .references(() => brands.id, { onDelete: "cascade" }),
    // Provenance only — never joined at edit time (see header comment).
    sourceAdId: varchar("source_ad_id"),
    // Project-owned R2 copy of the imported ad image (the background layer).
    sourceImageUrl: text("source_image_url"),
    // Normalized CreativeSource snapshot captured at import (prompt, persona/
    // product/angle names, brand colors/logo/font). Read-only context for
    // later AI features; the editor itself only needs designJson.
    sourceSnapshotJson: jsonb("source_snapshot_json"),
    projectName: varchar("project_name", { length: 255 }).notNull().default("Untitled creative"),
    // CreativeEditorDesign (shared/creative-editor.ts): canvas + ordered
    // layers array (index 0 = bottom). The client is the editor of record;
    // the server validates shape/size and stores it verbatim.
    designJson: jsonb("design_json").notNull(),
    // Monotonic save counter for optimistic concurrency (PUT /design): each
    // successful save bumps it, and a save carrying a stale baseRevision is
    // rejected with 409 instead of overwriting newer work.
    designRevision: integer("design_revision").notNull().default(0),
    // Latest export image, used as the project thumbnail.
    previewUrl: text("preview_url"),
    status: varchar("status", { length: 32 }).notNull().default("ready"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_editor_projects_brand_idx").on(table.brandId),
    index("creative_editor_projects_user_idx").on(table.userId),
    index("creative_editor_projects_source_ad_idx").on(table.sourceAdId),
  ],
);

export const creativeEditorExports = pgTable(
  "creative_editor_exports",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeEditorProjects.id, { onDelete: "cascade" }),
    fileUrl: text("file_url").notNull(),
    format: varchar("format", { length: 8 }).notNull(),
    width: integer("width"),
    height: integer("height"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [index("creative_editor_exports_project_idx").on(table.projectId)],
);

export type CreativeEditorAssetVersion = typeof creativeEditorAssetVersions.$inferSelect;

export type CreativeEditorExport = typeof creativeEditorExports.$inferSelect;

export type CreativeEditorJob = typeof creativeEditorJobs.$inferSelect;

export const creativeEditorAssetVersions = pgTable(
  "creative_editor_asset_versions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    assetId: varchar("asset_id")
      .notNull()
      .references(() => creativeEditorAssets.id, { onDelete: "cascade" }),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeEditorProjects.id, { onDelete: "cascade" }),
    layerId: varchar("layer_id"),
    fileUrl: text("file_url").notNull(),
    prompt: text("prompt"),
    referenceMetadata: jsonb("reference_metadata"),
    provider: varchar("provider", { length: 64 }),
    model: varchar("model", { length: 128 }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("creative_editor_asset_versions_asset_idx").on(table.assetId),
    index("creative_editor_asset_versions_project_idx").on(table.projectId),
  ],
);

export type CreativeEditorAsset = typeof creativeEditorAssets.$inferSelect;

export const creativeEditorJobs = pgTable(
  "creative_editor_jobs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeEditorProjects.id, { onDelete: "cascade" }),
    // Design layer this job targets (ai_edit jobs; null for project-wide
    // jobs like decompose).
    layerId: varchar("layer_id"),
    type: varchar("type", { length: 64 }).notNull(),
    status: varchar("status", { length: 32 }).notNull().default("queued"),
    inputJson: jsonb("input_json"),
    outputJson: jsonb("output_json"),
    error: text("error"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("creative_editor_jobs_project_idx").on(table.projectId),
    // Atomic single-flight admission for decomposition: at most ONE live
    // (queued/running) decompose job may exist per project. Concurrent kick
    // requests race on the INSERT — the loser gets a unique violation and
    // returns the winner's job instead of starting a second paid pipeline.
    uniqueIndex("creative_editor_jobs_live_decompose_uniq")
      .on(table.projectId)
      .where(sql`${table.type} = 'decompose' AND ${table.status} IN ('queued', 'running')`),
    // Same admission pattern for per-layer AI edits (Task #2390): at most ONE
    // live ai_edit job per (project, layer). Edits on DIFFERENT layers may
    // run concurrently — the editor stays usable — but a double-submit for
    // one layer races on the INSERT and reuses the winner's job instead of
    // paying for a second generation.
    uniqueIndex("creative_editor_jobs_live_ai_edit_uniq")
      .on(table.projectId, table.layerId)
      .where(sql`${table.type} = 'ai_edit' AND ${table.status} IN ('queued', 'running')`),
  ],
);

export type CreativeEditorProject = typeof creativeEditorProjects.$inferSelect;

export const creativeEditorAssets = pgTable(
  "creative_editor_assets",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeEditorProjects.id, { onDelete: "cascade" }),
    // Design-JSON layer this asset backs (null for project-scoped files like
    // the imported source copy).
    layerId: varchar("layer_id"),
    // Semantic role: "source" | "background" | "layer_image" | "reference"…
    role: varchar("role", { length: 64 }),
    assetType: varchar("asset_type", { length: 32 }).notNull().default("image"),
    fileUrl: text("file_url").notNull(),
    maskUrl: text("mask_url"),
    contextCropUrl: text("context_crop_url"),
    // "import" (copied from the source ad) | "upload" (user file) | "ai"
    // (reserved for the decomposition/edit follow-ups).
    sourceType: varchar("source_type", { length: 32 }).notNull().default("import"),
    parentAssetId: varchar("parent_asset_id"),
    generationPrompt: text("generation_prompt"),
    metadataJson: jsonb("metadata_json"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [index("creative_editor_assets_project_idx").on(table.projectId)],
);

export const creativeStrategistDeliverableVersions = pgTable(
  "creative_strategist_deliverable_versions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => creativeStrategistProjects.id, { onDelete: "cascade" }),
    deliverableId: varchar("deliverable_id")
      .notNull()
      .references(() => creativeStrategistDeliverables.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull(),
    /** The task whose run produced this version — the original or a revision. */
    taskId: varchar("task_id"),
    versionNumber: integer("version_number").notNull(),
    title: varchar("title", { length: 255 }),
    previewUrl: varchar("preview_url", { length: 2048 }),
    documentContent: text("document_content"),
    outputLinkId: varchar("output_link_id"),
    /** Every output link this attempt produced, so history stays clickable. */
    outputLinkIdsJson: jsonb("output_link_ids_json"),
    reviewVerdict: varchar("review_verdict", { length: 24 })
      .notNull()
      .default("not_reviewed"),
    reviewScore: integer("review_score"),
    reviewSummary: text("review_summary"),
    reviewIssuesJson: jsonb("review_issues_json"),
    /** Gateway run id for the review — provenance for the verdict shown. */
    reviewRunId: varchar("review_run_id"),
    reviewedAt: timestamp("reviewed_at"),
    // Review claim, so two passes (or two processes) cannot review the same
    // attempt twice. A claim older than the lease is treated as abandoned.
    reviewClaimedAt: timestamp("review_claimed_at"),
    reviewAttempts: integer("review_attempts").notNull().default(0),
    /** Instructions the revision produced FROM this version's review. */
    revisionInstructionsJson: jsonb("revision_instructions_json"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("creative_strategist_deliverable_versions_deliverable_idx").on(
      table.deliverableId,
    ),
    index("creative_strategist_deliverable_versions_project_idx").on(table.projectId),
    uniqueIndex("creative_strategist_deliverable_versions_unique").on(
      table.deliverableId,
      table.versionNumber,
    ),
  ],
);

export type CreativeStrategistReviewVerdict =
  (typeof CREATIVE_STRATEGIST_REVIEW_VERDICTS)[number];

export const brollExpressSettings = pgTable("broll_express_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 50 }).notNull().unique(),
  elevenlabsVoiceId: varchar("elevenlabs_voice_id", { length: 120 }),
  // Background music (fal Stable Audio) blended under the voice-over. ON by
  // default; NULL musicPrompt falls back to the in-code default prompt.
  musicEnabled: boolean("music_enabled").notNull().default(true),
  musicPrompt: text("music_prompt"),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const brollExpressVideos = pgTable("broll_express_videos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => brollExpressRuns.id, { onDelete: "cascade" }),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  personaId: varchar("persona_id").notNull().references(() => personas.id),
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  rawScriptOutput: text("raw_script_output"),
  spokenScript: text("spoken_script"),
  videoPrompt: text("video_prompt"),
  scriptApprovedAt: timestamp("script_approved_at"),
  kieTaskId: varchar("kie_task_id", { length: 120 }),
  providerVideoUrl: text("provider_video_url"),
  // Durable R2 mirror of the SILENT Seedance render (audio is disabled at the
  // provider; the voice-over is ElevenLabs TTS muxed on afterwards). Persisted
  // before the TTS/mux steps so retries/resume never re-render.
  silentVideoUrl: text("silent_video_url"),
  // Durable R2 URL of the generated background-music track (fal Stable
  // Audio), persisted before the mux so a retry never regenerates it.
  musicUrl: text("music_url"),
  // Durable R2 URL of the ElevenLabs voice-over MP3. Persisted on TTS success
  // so retries/resume never re-synthesize (no double billing).
  voiceOverUrl: text("voice_over_url"),
  videoUrl: text("video_url"),
  durationSec: integer("duration_sec").default(30),
  captionedVideoUrl: text("captioned_video_url"),
  captionedVideoStatus: varchar("captioned_video_status", { length: 50 }),
  captionedVideoError: text("captioned_video_error"),
  error: text("error"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("broll_express_videos_run_idx").on(table.runId),
  index("broll_express_videos_brand_idx").on(table.brandId),
]);

export const insertBrollExpressRunSchema = createInsertSchema(brollExpressRuns).omit({ id: true, createdAt: true, updatedAt: true });

export const insertBrollExpressVideoSchema = createInsertSchema(brollExpressVideos).omit({ id: true, createdAt: true, updatedAt: true });

export type BrollExpressVideo = typeof brollExpressVideos.$inferSelect;

export type InsertBrollExpressVideo = z.infer<typeof insertBrollExpressVideoSchema>;

export type InsertBrollExpressRun = z.infer<typeof insertBrollExpressRunSchema>;

export type BrollExpressRun = typeof brollExpressRuns.$inferSelect;

export type BrollExpressSettings = typeof brollExpressSettings.$inferSelect;

export const videoReferenceAdAssets = pgTable("video_reference_ad_assets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Null = master-managed shared catalog asset. Non-null = private upload
  // owned by one brand from the Video Reference Ads page.
  brandId: varchar("brand_id").references(() => brands.id),
  kind: varchar("kind", { length: 20 }).notNull(),
  label: varchar("label", { length: 255 }).notNull(),
  fileName: varchar("file_name", { length: 255 }).notNull(),
  format: varchar("format", { length: 10 }).notNull(),
  contentType: varchar("content_type", { length: 100 }).notNull(),
  fontFamily: varchar("font_family", { length: 255 }),
  durationSec: real("duration_sec"),
  sizeBytes: integer("size_bytes").notNull(),
  sha256: varchar("sha256", { length: 64 }).notNull(),
  objectKey: text("object_key").notNull(),
  publicUrl: text("public_url").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  archivedAt: timestamp("archived_at"),
  createdBy: varchar("created_by"),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("video_reference_ad_assets_picker_idx").on(
    table.brandId,
    table.kind,
    table.isActive,
    table.archivedAt,
  ),
  uniqueIndex("video_reference_ad_assets_global_kind_sha_unique")
    .on(table.kind, table.sha256)
    .where(sql`${table.brandId} IS NULL`),
  uniqueIndex("video_reference_ad_assets_brand_kind_sha_unique")
    .on(table.brandId, table.kind, table.sha256)
    .where(sql`${table.brandId} IS NOT NULL`),
]);

export const insertVideoReferenceAdAssetSchema = createInsertSchema(videoReferenceAdAssets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertVideoReferenceAdAsset = z.infer<typeof insertVideoReferenceAdAssetSchema>;
export type VideoReferenceAdAsset = typeof videoReferenceAdAssets.$inferSelect;

export const videoReferenceAdPromptVersions = pgTable("video_reference_ad_prompt_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  promptType: varchar("prompt_type", { length: 32 }).notNull().default("general"),
  versionNumber: integer("version_number").notNull(),
  promptText: text("prompt_text").notNull(),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("video_reference_ad_prompt_versions_brand_version_unique")
    .on(table.brandId, table.promptType, table.versionNumber),
  index("video_reference_ad_prompt_versions_brand_idx").on(table.brandId),
]);
export const videoReferenceAdWorkflowConfigs = pgTable("video_reference_ad_workflow_configs", {
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  promptType: varchar("prompt_type", { length: 32 }).notNull().default("general"),
  videoPromptVariantEditorSimUrl: text("video_prompt_variant_editor_sim_url"),
  currentPromptVersionId: varchar("current_prompt_version_id")
    .references(() => videoReferenceAdPromptVersions.id, { onDelete: "set null" }),
  createdBy: varchar("created_by"),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // Publish adds new PKs before new columns. Keep this NOT NULL pair unique
  // through an index so prompt_type is added before its dependent index.
  uniqueIndex("video_reference_ad_workflow_configs_brand_prompt_unique")
    .on(table.brandId, table.promptType),
]);

export const insertVideoReferenceAdWorkflowConfigSchema = createInsertSchema(
  videoReferenceAdWorkflowConfigs,
).omit({
  createdAt: true,
  updatedAt: true,
});
export type InsertVideoReferenceAdWorkflowConfig = z.infer<
  typeof insertVideoReferenceAdWorkflowConfigSchema
>;
export type VideoReferenceAdWorkflowConfig =
  typeof videoReferenceAdWorkflowConfigs.$inferSelect;

// One paid flatlay per product/selected-variant identity. A separate table
// allows distinct Ads Express runs to reuse the same durable asset and resume
// the same provider task rather than creating one flatlay per ad.
export const adsExpressProductFlatlays = pgTable("ads_express_product_flatlays", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  productId: varchar("product_id").notNull().references(() => products.id, { onDelete: "cascade" }),
  variantKey: text("variant_key").notNull(),
  variantName: text("variant_name"),
  productName: text("product_name").notNull(),
  sourceImageUrl: text("source_image_url").notNull(),
  createdBy: varchar("created_by"),
  prompt: text("prompt").notNull(),
  submitStartedAt: timestamp("submit_started_at").notNull().defaultNow(),
  taskId: varchar("task_id", { length: 120 }),
  providerUrl: text("provider_url"),
  durableUrl: text("durable_url"),
  billedAt: timestamp("billed_at"),
  rejectedAt: timestamp("rejected_at"),
  failedAt: timestamp("failed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("ads_express_product_flatlays_identity_unique").on(table.brandId, table.productId, table.variantKey),
]);
export type AdsExpressProductFlatlay = typeof adsExpressProductFlatlays.$inferSelect;

export const videoReferenceAdRuns = pgTable("video_reference_ad_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  typeSplitEnabled: boolean("type_split_enabled").notNull().default(false),
  promptType: varchar("prompt_type", { length: 32 }).notNull().default("general"),
  promotedItemType: varchar("promoted_item_type", { length: 20 }).notNull().default("product"),
  productId: varchar("product_id").references(() => products.id),
  offerId: varchar("offer_id").references(() => offers.id),
  offerSnapshotJson: jsonb("offer_snapshot_json").$type<{ name: string; summary?: unknown }>(),
  // Display snapshot so run cards stay labeled even if the product is later
  // renamed/archived.
  productName: varchar("product_name", { length: 255 }),
  // Immutable selected-product image snapshot. New runs resolve this before
  // queueing; legacy rows persist the current product image before their next
  // paid model-preparation submission.
  productImageUrl: text("product_image_url"),
  // Always-on product preprocessing. The raw productImageUrl remains the
  // immutable source snapshot; downstream stages prefer the durable flatlay.
  productFlatlayPrompt: text("product_flatlay_prompt"),
  productFlatlaySubmitStartedAt: timestamp("product_flatlay_submit_started_at"),
  productFlatlayTaskId: varchar("product_flatlay_task_id", { length: 120 }),
  productFlatlayProviderUrl: text("product_flatlay_provider_url"),
  productFlatlayUrl: text("product_flatlay_url"),
  productFlatlayBilledAt: timestamp("product_flatlay_billed_at"),
  // A confirmed createTask rejection proves no paid task exists and allows
  // this run to continue with its immutable raw snapshot without resubmitting.
  productFlatlayCreateRejectedAt: timestamp("product_flatlay_create_rejected_at"),
  // A confirmed terminal failure for an admitted provider task. Unlike a
  // create rejection, this never degrades to the raw snapshot automatically;
  // it only unlocks explicit master regeneration.
  productFlatlayTaskFailedAt: timestamp("product_flatlay_task_failed_at"),
  // Tokenized lease for destructive master maintenance. Separate from paid
  // submission state so crashed maintenance can be reclaimed unambiguously.
  productFlatlayMaintenanceToken: varchar("product_flatlay_maintenance_token", { length: 120 }),
  productFlatlayMaintenanceClaimedAt: timestamp("product_flatlay_maintenance_claimed_at"),
  productFlatlayRegenerationRequestedAt: timestamp("product_flatlay_regeneration_requested_at"),
  // Full catalog variant resolved against the selected brand-owned product at
  // admission. NULL means no specific variant (and preserves legacy runs).
  // The effective variant-first image is frozen separately in productImageUrl.
  selectedVariantSnapshot: jsonb("selected_variant_snapshot").$type<ProductVariant>(),
  // Immutable persona-model snapshot used to prepare the product-wearing
  // reference image. Nullable keeps historical runs resumable with their
  // original fictional-model path; new runs always resolve this server-side.
  personaId: varchar("persona_id"),
  personaCharacterId: varchar("persona_character_id"),
  personaCharacterName: varchar("persona_character_name", { length: 255 }),
  personaCharacterAvatarUrl: text("persona_character_avatar_url"),
  // Frozen once the worker resolves the character's reusable identity sheet.
  // Recovery and retries must not re-read mutable character state.
  personaCharacterReferenceSheetUrl: text("persona_character_reference_sheet_url"),
  // Internal-only, default-off experiment snapshot. All fields in this block
  // are server diagnostics and are stripped from normal run responses.
  multiAngleIdentityReferencesEnabled: boolean("multi_angle_identity_references_enabled").notNull().default(false),
  identityView1Prompt: text("identity_view_1_prompt"),
  identityView1TaskId: varchar("identity_view_1_task_id", { length: 120 }),
  identityView1SubmitStartedAt: timestamp("identity_view_1_submit_started_at"),
  identityView1ProviderUrl: text("identity_view_1_provider_url"),
  identityView1Url: text("identity_view_1_url"),
  identityView1BilledAt: timestamp("identity_view_1_billed_at"),
  identityView2Prompt: text("identity_view_2_prompt"),
  identityView2TaskId: varchar("identity_view_2_task_id", { length: 120 }),
  identityView2SubmitStartedAt: timestamp("identity_view_2_submit_started_at"),
  identityView2ProviderUrl: text("identity_view_2_provider_url"),
  identityView2Url: text("identity_view_2_url"),
  identityView2BilledAt: timestamp("identity_view_2_billed_at"),
  seedanceReferenceUrlsJson: jsonb("seedance_reference_urls_json").$type<string[]>(),
  // Optional one-off identity photo uploaded for this run. Persona/character
  // metadata remains mandatory and immutable; direct model preparation prefers
  // this durable snapshot over the selected character avatar.
  modelReferenceOverrideUrl: text("model_reference_override_url"),
  // Immutable workflow endpoint selected from the brand configuration at
  // admission. Legacy rows keep NULL and resolve to the code-owned default.
  videoPromptVariantEditorSimUrl: text("video_prompt_variant_editor_sim_url"),
  videoPromptVersionId: varchar("video_prompt_version_id"),
  videoPromptVersionNumber: integer("video_prompt_version_number"),
  videoPromptTextSnapshot: text("video_prompt_text_snapshot"),
  // Durable fail-closed fence for the non-idempotent direct OpenAI prompt
  // submission. Once stamped, an absent prompt is an ambiguous outcome that
  // requires manual review rather than another paid submission.
  videoPromptSubmitStartedAt: timestamp("video_prompt_submit_started_at"),
  referenceVideoUrl: text("reference_video_url").notNull(),
  // Probed server-side at upload (ffprobe, post-normalization). The Seedance
  // duration is this rounded to the nearest second and clamped to 4–30.
  referenceDurationSec: real("reference_duration_sec").notNull(),
  variant: text("variant"),
  // Optional user-supplied wording composited locally after AI generation.
  // This immutable raw snapshot is never sent to Sim/KIE/fal.
  exactText: text("exact_text"),
  // Immutable, validated typography/layout snapshot for deterministic local
  // compositing. The requested font is mirrored before the run is queued and
  // its content hash is persisted so retries never depend on changed bytes at
  // the caller's URL.
  textStyleJson: jsonb("text_style_json").$type<Record<string, unknown>>(),
  textFontAssetId: varchar("text_font_asset_id"),
  textFontAssetLabel: varchar("text_font_asset_label", { length: 255 }),
  textFontFamily: varchar("text_font_family", { length: 255 }),
  textFontUrl: text("text_font_url"),
  textFontSha256: varchar("text_font_sha256", { length: 64 }),
  // Durable, versioned, audio-free video after exact text has been composited.
  // Music and the final mux always consume this boundary when exactText exists.
  textOverlayVideoUrl: text("text_overlay_video_url"),
  // Incremented whenever a completed run's exact text is regenerated so R2
  // object keys cannot accidentally reuse an earlier overlay/final mux.
  textOverlayRevision: integer("text_overlay_revision").notNull().default(0),
  // Inline quick-editor state. The previous videoUrl remains published while
  // quickEditStatus is rendering, so cards never lose their last good output.
  // Last successfully published quick-edit recipe. The browser composes later
  // edits from this only; an in-flight/failed request never changes it.
  quickEditRecipeJson: jsonb("quick_edit_recipe_json").$type<Record<string, unknown>>(),
  // Server-only request snapshot promoted to quickEditRecipeJson atomically on
  // successful publication.
  quickEditPendingRecipeJson: jsonb("quick_edit_pending_recipe_json").$type<Record<string, unknown>>(),
  // Candidate copy/style/font locator snapshot. Kept separate from the
  // currently displayed text metadata until its MP4 is successfully published.
  quickEditPendingTextSnapshotJson: jsonb("quick_edit_pending_text_snapshot_json").$type<Record<string, unknown>>(),
  quickEditRevision: integer("quick_edit_revision").notNull().default(0),
  quickEditStatus: varchar("quick_edit_status", { length: 20 }).notNull().default("idle"),
  quickEditClaimToken: varchar("quick_edit_claim_token", { length: 120 }),
  quickEditClaimedAt: timestamp("quick_edit_claimed_at"),
  quickEditError: text("quick_edit_error"),
  // JSON batch imports create one normal resumable run per item. The grouping
  // fields preserve upload order and make the request idempotent.
  batchId: varchar("batch_id", { length: 120 }),
  batchIndex: integer("batch_index"),
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  // Server-only custom-model preparation state. customModelPrompt now stores
  // the deterministic direct-KIE instruction. The legacy Sim usage columns
  // remain so charges from already-started historical runs can settle.
  customModelPrompt: text("custom_model_prompt"),
  customModelSimUsageJson: jsonb("custom_model_sim_usage_json"),
  customModelSimBilledAt: timestamp("custom_model_sim_billed_at"),
  // KIE image task/result boundaries. The provider URL is temporary; the
  // durable R2 URL is the only model reference allowed into later stages.
  customModelKieTaskId: varchar("custom_model_kie_task_id", { length: 120 }),
  // Durable ambiguity guard set immediately before direct KIE createTask and
  // cleared atomically with the returned task id (or a confirmed rejection).
  // A set marker with no task id blocks automatic duplicate paid submissions.
  customModelKieSubmitStartedAt: timestamp("custom_model_kie_submit_started_at"),
  customModelProviderImageUrl: text("custom_model_provider_image_url"),
  customModelImageUrl: text("custom_model_image_url"),
  // Provider provenance + terminal evidence remain server-only. A confirmed
  // KIE failure keeps its original task id/error while fal owns a separate
  // restart-safe request boundary.
  customModelProvider: varchar("custom_model_provider", { length: 20 }),
  customModelKieError: text("custom_model_kie_error"),
  customModelKieBilledAt: timestamp("custom_model_kie_billed_at"),
  customModelFalRequestId: varchar("custom_model_fal_request_id", { length: 120 }),
  customModelFalEndpoint: text("custom_model_fal_endpoint"),
  customModelFalSubmitStartedAt: timestamp("custom_model_fal_submit_started_at"),
  customModelFalError: text("custom_model_fal_error"),
  customModelFalBilledAt: timestamp("custom_model_fal_billed_at"),
  // Cross-instance provider-submission leases. These guard only the short
  // create/ID-persist window; polling remains freely resumable by any worker.
  customModelSimClaimToken: varchar("custom_model_sim_claim_token", { length: 120 }),
  customModelSimClaimedAt: timestamp("custom_model_sim_claimed_at"),
  customModelKieClaimToken: varchar("custom_model_kie_claim_token", { length: 120 }),
  customModelKieClaimedAt: timestamp("custom_model_kie_claimed_at"),
  customModelFalClaimToken: varchar("custom_model_fal_claim_token", { length: 120 }),
  customModelFalClaimedAt: timestamp("custom_model_fal_claimed_at"),
  videoRenderClaimToken: varchar("video_render_claim_token", { length: 120 }),
  videoRenderClaimedAt: timestamp("video_render_claimed_at"),
  // Provider admission fences. A marker without its corresponding provider id
  // is an ambiguous paid submission and must never be retried automatically.
  kieSubmitStartedAt: timestamp("kie_submit_started_at"),
  falSubmitStartedAt: timestamp("fal_submit_started_at"),
  // Server-only (stripped by sanitizeVideoReferenceAdRun): the Sim-returned
  // Seedance 2.5 generation prompt. Written only on Sim SUCCESS so retries
  // reuse it and skip the analysis stage.
  seedancePrompt: text("seedance_prompt"),
  // Trimmed Sim usage envelope (executionId + output cost/tokens/model),
  // persisted in the SAME update as seedancePrompt so billing can be
  // reconciled exactly-once after a crash between persist and charge.
  simUsageJson: jsonb("sim_usage_json"),
  // Exactly-once billing claims (conditional UPDATE ... WHERE ... IS NULL):
  // stamped before charging, released if the charge attempt throws. A crash
  // between persist and charge is healed by the worker/finalize reconcile.
  simBilledAt: timestamp("sim_billed_at"),
  kieBilledAt: timestamp("kie_billed_at"),
  kieTaskId: varchar("kie_task_id", { length: 120 }),
  // fal.ai fallback render (bytedance/seedance-2.5/reference-to-video):
  // persisted BEFORE polling, same restart-resume contract as kieTaskId.
  // When set, recovery polls fal FIRST (the later provider owns the render).
  falRequestId: varchar("fal_request_id", { length: 120 }),
  providerVideoUrl: text("provider_video_url"),
  // Audio finalization is a separate restart-safe pipeline. The silent render
  // and Stable Audio asset are both durable R2 boundaries; videoUrl is only
  // set after the controlled music mux succeeds.
  silentVideoUrl: text("silent_video_url"),
  musicPrompt: text("music_prompt"),
  musicFalRequestId: varchar("music_fal_request_id", { length: 120 }),
  musicProviderUrl: text("music_provider_url"),
  musicUrl: text("music_url"),
  musicAssetId: varchar("music_asset_id"),
  musicAssetLabel: varchar("music_asset_label", { length: 255 }),
  musicAssetUrl: text("music_asset_url"),
  musicAssetSha256: varchar("music_asset_sha256", { length: 64 }),
  musicAssetFormat: varchar("music_asset_format", { length: 10 }),
  musicBilledAt: timestamp("music_billed_at"),
  musicWebhookBaseUrl: varchar("music_webhook_base_url", { length: 500 }),
  musicClaimToken: varchar("music_claim_token", { length: 120 }),
  musicClaimedAt: timestamp("music_claimed_at"),
  musicSubmitStartedAt: timestamp("music_submit_started_at"),
  videoUrl: text("video_url"),
  // Immutable retry lineage. Failed provider attempts remain untouched;
  // recovery inserts at most one clean successor.
  recoveryFromRunId: varchar("recovery_from_run_id"),
  // Media-only derivatives are separate run cards. The immutable source URL
  // identifies the exact published MP4 selected by the user; no provider
  // state is copied and the source row is never modified.
  derivedFromRunId: varchar("derived_from_run_id"),
  regeneratedFromRunId: varchar("regenerated_from_run_id"),
  derivedTransform: varchar("derived_transform", { length: 40 }),
  derivedSourceVideoUrl: text("derived_source_video_url"),
  derivedClaimToken: varchar("derived_claim_token", { length: 120 }),
  derivedClaimedAt: timestamp("derived_claimed_at"),
  // The duration actually requested from Seedance (rounded + clamped).
  durationSec: integer("duration_sec"),
  error: text("error"),
  // Terminal failure metadata is intentionally separate from the public error
  // copy. Values are sanitized/closed-set classifications; provider diagnostics
  // remain in operational telemetry rather than this user-facing row.
  failureStage: varchar("failure_stage", { length: 40 }),
  failureCategory: varchar("failure_category", { length: 40 }),
  failureProviderCode: varchar("failure_provider_code", { length: 120 }),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("video_reference_ad_runs_brand_idx").on(table.brandId),
  index("video_reference_ad_runs_batch_idx").on(table.batchId, table.batchIndex),
  uniqueIndex("video_reference_ad_runs_batch_item_unique")
    .on(table.brandId, table.batchId, table.batchIndex),
  uniqueIndex("video_reference_ad_runs_derived_transform_unique")
    .on(table.brandId, table.derivedFromRunId, table.derivedTransform)
    .where(sql`${table.status} <> 'deleted'`),
  uniqueIndex("video_reference_ad_runs_recovery_source_unique")
    .on(table.recoveryFromRunId)
    .where(sql`${table.recoveryFromRunId} IS NOT NULL`),
  index("video_reference_ad_runs_derived_source_idx").on(table.derivedFromRunId),
]);

export const videoReferenceAdTemplates = pgTable("video_reference_ad_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").references(() => brands.id),
  kind: varchar("kind", { length: 32 }),
  name: varchar("name", { length: 255 }).notNull(),
  referenceVideoUrl: text("reference_video_url").notNull(),
  referenceDurationSec: real("reference_duration_sec").notNull(),
  thumbnailUrl: text("thumbnail_url"),
  status: varchar("status", { length: 50 }).notNull().default("active"),
  archivedAt: timestamp("archived_at"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("video_reference_ad_templates_brand_idx").on(table.brandId),
]);
export const videoReferenceUploads = pgTable("video_reference_uploads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  kind: varchar("kind", { length: 32 }),
  url: text("url").notNull(),
  fileName: varchar("file_name", { length: 255 }),
  durationSec: real("duration_sec").notNull(),
  width: integer("width"),
  height: integer("height"),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("video_reference_uploads_brand_idx").on(table.brandId),
]);

export const insertVideoReferenceUploadSchema = createInsertSchema(videoReferenceUploads).omit({ id: true, createdAt: true });
export type InsertVideoReferenceUpload = z.infer<typeof insertVideoReferenceUploadSchema>;
export type VideoReferenceUpload = typeof videoReferenceUploads.$inferSelect;

// Brand-scoped display names cover both persisted uploads and legacy
// run-only sources. Upload rows also receive the canonical name update, while
// this table is the durable fallback for sources that never had an upload row.
export const videoReferenceSourceMetadata = pgTable(
  "video_reference_source_metadata",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    sourceKey: text("source_key").notNull(),
    displayName: varchar("display_name", { length: 255 }).notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("video_reference_source_metadata_brand_source_unique")
      .on(table.brandId, table.sourceKey),
    index("video_reference_source_metadata_brand_idx").on(table.brandId),
  ],
);
export const insertVideoReferenceSourceMetadataSchema = createInsertSchema(
  videoReferenceSourceMetadata,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertVideoReferenceSourceMetadata = z.infer<
  typeof insertVideoReferenceSourceMetadataSchema
>;
export type VideoReferenceSourceMetadata = typeof videoReferenceSourceMetadata.$inferSelect;

// Personal, brand-scoped source bookmarks. `sourceKey` is the normalized
// durable URL rather than an upload id because legacy references can exist
// only on prior runs and signed R2 URLs can change their query string.
export const videoReferenceSourceFavorites = pgTable(
  "video_reference_source_favorites",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    sourceKey: text("source_key").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("video_reference_source_favorites_user_brand_source_unique")
      .on(table.userId, table.brandId, table.sourceKey),
    index("video_reference_source_favorites_user_brand_idx").on(table.userId, table.brandId),
  ],
);
export type VideoReferenceSourceFavorite = typeof videoReferenceSourceFavorites.$inferSelect;

export const insertVideoReferenceAdRunSchema = createInsertSchema(videoReferenceAdRuns, {
  selectedVariantSnapshot: productVariantSchema.nullable().optional(),
  seedanceReferenceUrlsJson: z.array(z.string()).nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });

export type InsertVideoReferenceAdRun = z.infer<typeof insertVideoReferenceAdRunSchema>;

export type VideoReferenceAdRun = typeof videoReferenceAdRuns.$inferSelect;

export function resolveEmailBodyImagePreference(
  copy: EmailInstanceCopy | null | undefined,
): EmailBodyImagePreference {
  const pref = copy?.bodyImage;
  if (pref?.mode === "selected" && typeof pref.url === "string") {
    const url = pref.url.trim();
    try {
      const parsed = new URL(url);
      if (parsed.protocol === "https:" || parsed.protocol === "http:") {
        return { mode: "selected", url };
      }
    } catch {
      // Malformed legacy JSON falls through to automatic.
    }
  }
  if (pref?.mode === "none") return { mode: "none" };
  return { mode: "automatic" };
}

export const deliveryItems = pgTable(
  "delivery_items",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    deliveryId: varchar("delivery_id").notNull().references(() => weeklyDeliveries.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    projectId: varchar("project_id").references(() => projects.id, { onDelete: "set null" }),
    projectNameSnapshot: varchar("project_name_snapshot", { length: 255 }).notNull(),
    note: text("note"),
    sortOrder: integer("sort_order").notNull().default(0),
    submittedAt: timestamp("submitted_at"),
    submittedBy: varchar("submitted_by").references(() => users.id, { onDelete: "set null" }),
    projectRejectedAt: timestamp("project_rejected_at"),
    projectRejectedBy: varchar("project_rejected_by").references(() => users.id, { onDelete: "set null" }),
    projectRejectReasons: jsonb("project_reject_reasons").$type<string[]>(),
    projectRejectComment: text("project_reject_comment"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("delivery_items_delivery_project_uniq").on(table.deliveryId, table.projectId),
    index("delivery_items_delivery_idx").on(table.deliveryId),
    index("delivery_items_brand_idx").on(table.brandId),
  ],
);
export function isEmailImageTreatmentAllowedForBlock(
  blockType: string,
  slot: string,
  treatment: EmailImageTreatment,
  productListTileCount?: number,
): boolean {
  if (!isEmailImageTreatmentEligible(blockType)) return false;
  if (blockType === "product_list") {
    const match = EMAIL_PRODUCT_LIST_TILE_SLOT_RE.exec(slot);
    const tileIndex = match ? Number(match[1]) : -1;
    return (
      tileIndex >= 0 &&
      treatment.mode !== "none" &&
      (productListTileCount === undefined ||
        tileIndex < Math.max(0, productListTileCount))
    );
  }
  return slot === EMAIL_IMAGE_TREATMENT_MAIN_SLOT;
}

export function productListImageTreatmentSlots(tileCount: number): string[] {
  if (tileCount <= 0) return [];
  return Array.from({ length: tileCount }, (_, i) => productListTileSlot(i));
}

export const EMAIL_IMAGE_TREATMENT_ELIGIBLE_TYPES: ReadonlySet<string> = new Set([
  "selling_points",
  "did_you_know",
  "us_vs_them",
  "comparison_table",
  "product_list",
]);

export function isEmailImageTreatmentEligible(type: string): boolean {
  return EMAIL_IMAGE_TREATMENT_ELIGIBLE_TYPES.has(type);
}

const EMAIL_PRODUCT_LIST_TILE_SLOT_RE = /^tile_(0|[1-9]\d*)$/;

export function collectEmailImageTreatmentUrls(
  instance: EmailInstanceCopy | null | undefined,
  blockType?: string,
): string[] {
  const treatments = instance?.imageTreatments;
  if (!treatments) return [];
  const out: string[] = [];
  for (const [slot, t] of Object.entries(treatments)) {
    if (
      t?.mode === "selected" &&
      (!blockType ||
        isEmailImageTreatmentAllowedForBlock(blockType, slot, t)) &&
      typeof (t as any).url === "string" &&
      (t as any).url.trim()
    ) {
      out.push((t as any).url.trim());
    }
  }
  return out;
}

export const EMAIL_IMAGE_TREATMENT_MAIN_SLOT = "main";

export function resolveEmailProductListImageTreatment(
  instance: EmailInstanceCopy | null | undefined,
  slot: string,
): EmailImageTreatment {
  const treatment = resolveEmailImageTreatment(instance, slot);
  return isEmailImageTreatmentAllowedForBlock(
    "product_list",
    slot,
    treatment,
  )
    ? treatment
    : { mode: "automatic" };
}

export function resolveEmailImageTreatment(
  instance: EmailInstanceCopy | null | undefined,
  slot: string,
): EmailImageTreatment {
  const raw = instance?.imageTreatments?.[slot];
  if (!raw) return { mode: "automatic" };
  if (raw.mode === "selected") {
    const url = (raw as any).url;
    if (typeof url === "string" && url.trim()) {
      try {
        const parsed = new URL(url.trim());
        if (parsed.protocol === "https:" || parsed.protocol === "http:") {
          return { mode: "selected", url: url.trim() };
        }
      } catch {
        // Malformed → automatic.
      }
    }
    return { mode: "automatic" };
  }
  if (raw.mode === "none") {
    // "none" is only safe on image-bearing variants — on a type/graphic-only
    // variant the block already renders copy-only, so "none" would have no
    // effect either way. We pass it through; callers that render "none" as
    // "skip the image" only invoke this when variantNeedsImage is true.
    return { mode: "none" };
  }
  return { mode: "automatic" };
}

export function productListTileSlot(pos: number): string {
  return `tile_${pos}`;
}

export const deliveryItemAds = pgTable(
  "delivery_item_ads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    deliveryItemId: varchar("delivery_item_id").notNull().references(() => deliveryItems.id, { onDelete: "cascade" }),
    adId: varchar("ad_id").references(() => generatedAds.id, { onDelete: "set null" }),
    displayNameSnapshot: varchar("display_name_snapshot", { length: 500 }).notNull(),
    imageUrlSnapshot: text("image_url_snapshot"),
    aspectRatioSnapshot: varchar("aspect_ratio_snapshot", { length: 20 }),
    sortOrder: integer("sort_order").notNull().default(0),
  },
  (table) => [
    uniqueIndex("delivery_item_ads_item_ad_uniq").on(table.deliveryItemId, table.adId),
    index("delivery_item_ads_item_idx").on(table.deliveryItemId),
  ],
);

export const insertWeeklyDeliverySchema = createInsertSchema(weeklyDeliveries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDeliveryItemAd = z.infer<typeof insertDeliveryItemAdSchema>;

export type DeliveryItem = typeof deliveryItems.$inferSelect;

export type AdReviewDecision = typeof adReviewDecisions.$inferSelect;

export const adReviewDecisions = pgTable(
  "ad_review_decisions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    deliveryItemAdId: varchar("delivery_item_ad_id").notNull().references(() => deliveryItemAds.id, { onDelete: "cascade" }),
    source: varchar("source", { length: 40 }).notNull().default("weekly_delivery"),
    authorKind: varchar("author_kind", { length: 20 }).notNull().default("user"),
    authorId: varchar("author_id").references(() => users.id, { onDelete: "set null" }),
    authorLabel: varchar("author_label", { length: 255 }),
    decision: varchar("decision", { length: 20 }).notNull(),
    reasons: jsonb("reasons").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    comment: text("comment"),
    disposition: varchar("disposition", { length: 20 }).notNull().default("rework"),
    positiveTags: jsonb("positive_tags").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    pinX: real("pin_x"),
    pinY: real("pin_y"),
    imageUrlSnapshot: text("image_url_snapshot"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("ad_review_decisions_item_ad_author_uniq").on(table.deliveryItemAdId, table.authorId),
    index("ad_review_decisions_item_ad_idx").on(table.deliveryItemAdId),
    check("ad_review_decisions_decision_check", sql`${table.decision} IN ('approved', 'rejected')`),
    check("ad_review_decisions_disposition_check", sql`${table.disposition} IN ('rework', 'drop')`),
    check(
      "ad_review_decisions_pin_pair_check",
      sql`(${table.pinX} IS NULL AND ${table.pinY} IS NULL) OR (${table.pinX} BETWEEN 0 AND 1 AND ${table.pinY} BETWEEN 0 AND 1)`,
    ),
  ],
);
// Keep these schema helpers after every referenced table declaration. This
// module is evaluated by both the server and Vite's browser dependency graph,
// where accessing a later `const` would throw during module initialization.
export const insertAdReviewDecisionSchema = createInsertSchema(adReviewDecisions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type DeliveryItemAd = typeof deliveryItemAds.$inferSelect;

export type InsertWeeklyDelivery = z.infer<typeof insertWeeklyDeliverySchema>;

export type WeeklyDelivery = typeof weeklyDeliveries.$inferSelect;

export type InsertDeliveryItem = z.infer<typeof insertDeliveryItemSchema>;

export type InsertAdReviewDecision = z.infer<typeof insertAdReviewDecisionSchema>;

export const insertDeliveryItemAdSchema = createInsertSchema(deliveryItemAds).omit({ id: true });

export const insertDeliveryItemSchema = createInsertSchema(deliveryItems).omit({
  id: true,
  createdAt: true,
});

export const BRAND_NAV_ITEM_KEYS = [
  "ads-express",
  "killcrew-ad-studio",
  "landing-pages-express",
  "landing-pages-express-2",
  "email-express",
  "email-express-2",
  "meme-generator",
  "ad-animator",
  "grwmugc-pb",
  "video-reference-ads",
  "video-reference-templates",
  "slideshow-express",
  "ugc-express",
  "broll-express",
  "video-express-max",
  "my-ads",
  "deliverables",
  "creative-tinder",
  "creative-iterations",
  "concept-board-types",
  "concept-boards",
  "concept-board-references",
  "ad-launcher",
  "meta-ads",
  "static-campaigns",
  "ugc-video-campaigns",
  "claymation-video-campaigns",
  "pixar-video-campaigns",
  "simpsons-video-campaigns",
  "dayinlife-video-campaigns",
  "static-ad-units",
  "pixar-ad-units",
  "ugc-ad-units",
  "creative-performance",
  "products",
  "offerings",
  "sales-discounts",
  "customer-evidence",
  "all-personas",
  "persona-ideator",
  "persona-research-lab",
  "pain-pillars",
  "brand-spy",
  "learnings",
  "narrative-engine",
  "creative-strategist",
] as const;

export type BrandAdUnitApproval = typeof brandAdUnitApprovals.$inferSelect;

export type InsertBrandAdUnitApproval = z.infer<typeof insertBrandAdUnitApprovalSchema>;

export const insertBrandAdUnitApprovalSchema = createInsertSchema(brandAdUnitApprovals).omit({
  id: true,
  approvedAt: true,
});

export type InsertUserNavPin = z.infer<typeof insertUserNavPinSchema>;

export type UserNavPin = typeof userNavPins.$inferSelect;

export const isBrandNavItemKey = (value: unknown): value is BrandNavItemKey =>
  typeof value === "string" &&
  (BRAND_NAV_ITEM_KEYS as readonly string[]).includes(value);

export const insertUserNavPinSchema = createInsertSchema(userNavPins).omit({
  id: true,
  createdAt: true,
});

export type BrandNavItemKey = (typeof BRAND_NAV_ITEM_KEYS)[number];

export const starterPersonaJobs = pgTable(
  "starter_persona_jobs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    batchId: varchar("batch_id").notNull().references(() => starterPersonaBatches.id, { onDelete: "cascade" }),
    personaId: varchar("persona_id").notNull().references(() => personas.id, { onDelete: "cascade" }),
    sortOrder: integer("sort_order").notNull(),
    status: varchar("status", { length: 24 }).notNull().default("waiting_idea"),
    ideaPayload: jsonb("idea_payload"),
    profileInput: text("profile_input"),
    cardQuote: text("card_quote"),
    providerHandle: jsonb("provider_handle").$type<{ statusUrl: string; jobId?: string | null }>(),
    providerExecutionId: varchar("provider_execution_id"),
    providerResult: jsonb("provider_result"),
    providerSubmissionState: varchar("provider_submission_state", { length: 24 }),
    attempt: integer("attempt").notNull().default(0),
    infrastructureAttempt: integer("infrastructure_attempt").notNull().default(0),
    leaseOwner: varchar("lease_owner", { length: 100 }),
    leaseExpiresAt: timestamp("lease_expires_at"),
    errorCode: varchar("error_code", { length: 80 }),
    errorMessage: text("error_message"),
    usageSettledAt: timestamp("usage_settled_at"),
    profileAppliedAt: timestamp("profile_applied_at"),
    downstreamStartedAt: timestamp("downstream_started_at"),
    settledAt: timestamp("settled_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("starter_persona_jobs_persona_unique").on(table.personaId),
    uniqueIndex("starter_persona_jobs_batch_order_unique").on(table.batchId, table.sortOrder),
    index("starter_persona_jobs_claim_idx").on(table.status, table.leaseExpiresAt, table.createdAt),
    index("starter_persona_jobs_batch_idx").on(table.batchId),
    check("starter_persona_jobs_order_check", sql`${table.sortOrder} BETWEEN 0 AND 9`),
    check(
      "starter_persona_jobs_status_check",
      sql`${table.status} IN ('waiting_idea', 'queued', 'running', 'downstream', 'completed', 'failed', 'interrupted', 'discarded')`,
    ),
  ],
);

export const starterPersonaDownstreamSteps = pgTable(
  "starter_persona_downstream_steps",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    jobId: varchar("job_id").notNull().references(() => starterPersonaJobs.id, { onDelete: "cascade" }),
    step: varchar("step", { length: 32 }).notNull(),
    sortOrder: integer("sort_order").notNull(),
    status: varchar("status", { length: 24 }).notNull().default("queued"),
    attempt: integer("attempt").notNull().default(0),
    infrastructureAttempt: integer("infrastructure_attempt").notNull().default(0),
    providerResult: jsonb("provider_result"),
    resultAppliedAt: timestamp("result_applied_at"),
    leaseOwner: varchar("lease_owner", { length: 100 }),
    leaseExpiresAt: timestamp("lease_expires_at"),
    errorMessage: text("error_message"),
    settledAt: timestamp("settled_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("starter_persona_downstream_job_step_unique").on(table.jobId, table.step),
    index("starter_persona_downstream_claim_idx").on(table.status, table.leaseExpiresAt, table.sortOrder),
    check(
      "starter_persona_downstream_step_check",
      sql`${table.step} IN ('sketch', 'traits', 'fingerprint', 'image', 'research', 'characters')`,
    ),
    check(
      "starter_persona_downstream_status_check",
      sql`${table.status} IN ('queued', 'running', 'completed', 'failed', 'interrupted', 'discarded')`,
    ),
  ],
);

export const insertStarterPersonaJobSchema = createInsertSchema(starterPersonaJobs).omit({ id: true, createdAt: true, updatedAt: true });

export type StarterPersonaJob = typeof starterPersonaJobs.$inferSelect;

export const insertStarterPersonaDownstreamStepSchema = createInsertSchema(starterPersonaDownstreamSteps).omit({ id: true, createdAt: true, updatedAt: true });

export type InsertStarterPersonaBatch = z.infer<typeof insertStarterPersonaBatchSchema>;

export type StarterPersonaDownstreamStep = typeof starterPersonaDownstreamSteps.$inferSelect;

export type InsertStarterPersonaJob = z.infer<typeof insertStarterPersonaJobSchema>;

export type InsertStarterPersonaDownstreamStep = z.infer<typeof insertStarterPersonaDownstreamStepSchema>;

export const EMAIL_EXPRESS_2_STATUSES = [
  "draft", // created via a create call, generation not yet kicked off
  "generating", // MP create/prompt in flight (create OR a chat revision)
  "ready", // html is populated and safe to render
  "failed", // generation attempt errored — html (if any) is the last good version
] as const;

export const EMAIL_EXPRESS_2_CAMPAIGN_TYPES = [
  "education",
  "sale",
  "product_highlight",
  "review_led",
  "announcement",
  "welcome",
  "cart_abandonment",
  "browse_abandonment",
  "retention",
] as const;

export const EMAIL_EXPRESS_2_EMAIL_TYPES = [
  "promotional",
  "newsletter",
  "announcement",
  "abandoned_cart",
  "welcome",
  "post_purchase",
  "re_engagement",
] as const;

export const EMAIL_EXPRESS_2_KLAVIYO_STATUSES = [
  "not_sent",
  "sending",
  "sent",
  "failed",
] as const;

export const EMAIL_EXPRESS_2_KLAVIYO_MODES = [
  "campaign",
  "template",
  "flow",
  "flow_template",
] as const;

export const EMAIL_EXPRESS_2_VERSION_KINDS = [
  "create", // the first generation that produced html
  "chat_revision", // a chat instruction regenerated the design
  "manual_save", // the user hand-edited the rendered markup and saved
] as const;

export function emailExpress2HasInitialContext(v: {
  productId?: string | null;
  personaId?: string | null;
  narrativeAngleId?: string | null;
  customAngle?: string | null;
  briefText?: string | null;
}): boolean {
  return Boolean(
    v.productId?.trim()
      || v.personaId?.trim()
      || v.narrativeAngleId?.trim()
      || v.customAngle?.trim()
      || v.briefText?.trim(),
  );
}
export const insertVideoReferenceAdPromptVersionSchema = createInsertSchema(
  videoReferenceAdPromptVersions,
).omit({ id: true, createdAt: true });

export type InsertVideoReferenceAdPromptVersion = z.infer<
  typeof insertVideoReferenceAdPromptVersionSchema
>;

export type VideoReferenceAdPromptVersion =
  typeof videoReferenceAdPromptVersions.$inferSelect;

export const emailExpress2Designs = pgTable(
  "email_express_2_designs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    createdBy: varchar("created_by"),
    name: varchar("name", { length: 255 }).notNull(),
    status: varchar("status", { length: 20 }).notNull().default("draft"),
    productId: varchar("product_id").references(() => products.id),
    personaId: varchar("persona_id").references(() => personas.id),
    narrativeAngleId: varchar("narrative_angle_id").references(() => narrativeAngles.id),
    customAngle: text("custom_angle"),
    offerId: varchar("offer_id").references(() => offers.id),
    campaignType: varchar("campaign_type", { length: 50 }).notNull(),
    emailType: varchar("email_type", { length: 50 }).notNull(),
    briefText: text("brief_text").notNull(),
    referenceImageUrl: text("reference_image_url"),
    ctaUrl: text("cta_url"),
    contextSnapshot: jsonb("context_snapshot"),
    // Initial-generation prerequisites are owned by EE2 and checkpointed after
    // each paid stage.  Keeping the prepared campaign separate from the context
    // snapshot lets a restart reuse finished Sim/Fal work without mutating the
    // immutable user-selected facts that revisions continue to consume.
    preparedInput: jsonb("prepared_input").$type<EmailExpress2PreparedInput | null>(),
    generationStage: varchar("generation_stage", { length: 40 }),
    mpEditorId: varchar("mp_editor_id", { length: 100 }),
    mpArtifactId: varchar("mp_artifact_id", { length: 100 }),
    mpRequestId: varchar("mp_request_id", { length: 100 }),
    outputPath: varchar("output_path", { length: 30 }),
    html: text("html"),
    subject: varchar("subject", { length: 255 }),
    previewText: varchar("preview_text", { length: 160 }),
    klaviyoStatus: varchar("klaviyo_status", { length: 20 }).notNull().default("not_sent"),
    klaviyoMode: varchar("klaviyo_mode", { length: 20 }),
    klaviyoResourceId: varchar("klaviyo_resource_id", { length: 255 }),
    klaviyoError: text("klaviyo_error"),
    klaviyoSentAt: timestamp("klaviyo_sent_at"),
    error: text("error"),
    generatingStartedAt: timestamp("generating_started_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [index("email_express_2_designs_brand_idx").on(table.brandId)],
);

// Global, master-managed Email Express 2 prompt settings. The fixed key makes
// this a versioned singleton while leaving room for a future prompt version
// without changing generation call sites.
export const emailExpress2PromptSettings = pgTable("email_express_2_prompt_settings", {
  key: varchar("key", { length: 100 }).primaryKey(),
  version: integer("version").notNull().default(1),
  designDirection: text("design_direction").notNull(),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const emailExpress2Versions = pgTable(
  "email_express_2_versions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    designId: varchar("design_id").notNull().references(() => emailExpress2Designs.id),
    kind: varchar("kind", { length: 20 }).notNull(),
    // Full HTML snapshot at this version (not a diff) — cheap at this scale
    // and lets the history panel show any past entry without replaying.
    html: text("html").notNull(),
    // Present only for kind = "chat_revision".
    chatInstruction: text("chat_instruction"),
    // Human-readable one-liner: the chat instruction (revisions) or a fixed
    // "Manual edit" / "Initial design" label — what the history list shows.
    changeSummary: text("change_summary"),
    // Immutable snapshot of the image attachments that rode along with a
    // chat revision ([{id, fileName, url, contentType, sizeBytes}]). NULL
    // for versions without attachments.
    attachmentsJson: jsonb("attachments_json").$type<EmailExpress2VersionAttachment[] | null>(),
    createdBy: varchar("created_by"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [index("email_express_2_versions_design_idx").on(table.designId)],
);

// User-uploaded images attached to an EE2 chat revision (Task #2745).
// Lifecycle: uploaded → status "pending" (removable by the uploader);
// a revision submission atomically claims them (status "claimed"); when
// the revision completes, versionId stamps them onto the immutable
// version record. A failed generation releases them back to "pending" so
// the user can retry without re-uploading.
export const emailExpress2RevisionImages = pgTable(
  "email_express_2_revision_images",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    designId: varchar("design_id").notNull().references(() => emailExpress2Designs.id),
    uploadedBy: varchar("uploaded_by").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    fileName: varchar("file_name", { length: 255 }).notNull(),
    contentType: varchar("content_type", { length: 100 }).notNull(),
    sizeBytes: integer("size_bytes").notNull(),
    // Durable R2 object key; the public URL is derived from it at read time.
    objectKey: varchar("object_key", { length: 500 }).notNull(),
    versionId: varchar("version_id").references(() => emailExpress2Versions.id),
    claimedAt: timestamp("claimed_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("email_express_2_revision_images_design_idx").on(table.designId),
    check(
      "email_express_2_revision_images_status_check",
      sql`${table.status} IN ('pending', 'claimed')`,
    ),
  ],
);

export interface EmailExpress2VersionAttachment {
  id: string;
  fileName: string;
  url: string;
  contentType: string;
  sizeBytes: number;
}

export type EmailExpress2RevisionImage = typeof emailExpress2RevisionImages.$inferSelect;

export const EMAIL_EXPRESS_2_OUTPUT_PATHS = [
  "direct_html", // artifact's HTML file needed no extraction (never observed in the probe, kept for forward-compat)
  "flattened_extract", // fast path: string-literal extracted out of the React/TSX scaffold (the path the probe always took)
  // NOTE: a headless-render fallback (bundle + execute the artifact's own
  // React/TSX in a browser to snapshot its DOM) was deliberately NOT built.
  // The artifact is provider/prompt-controlled content, so executing it
  // in-process — even inside headless Chromium — would run untrusted code
  // with this server's network access (a real SSRF/code-execution vector),
  // for a path the real probe never exercised. Extraction failure fails
  // the generation attempt closed instead.
] as const;

export const emailExpress2DesignContextSchema = createInsertSchema(emailExpress2Designs)
  .omit({
    id: true,
    status: true,
    contextSnapshot: true,
    preparedInput: true,
    generationStage: true,
    mpEditorId: true,
    mpArtifactId: true,
    mpRequestId: true,
    outputPath: true,
    html: true,
    subject: true,
    previewText: true,
    klaviyoStatus: true,
    klaviyoMode: true,
    klaviyoResourceId: true,
    klaviyoError: true,
    klaviyoSentAt: true,
    error: true,
    generatingStartedAt: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: z.string().trim().min(1).max(255),
    productId: z.string().trim().min(1).nullish(),
    personaId: z.string().trim().min(1).nullish(),
    narrativeAngleId: z.string().trim().min(1).nullish(),
    customAngle: z.string().trim().max(2000).nullish(),
    offerId: z.string().trim().min(1).nullish(),
    campaignType: z.enum(EMAIL_EXPRESS_2_CAMPAIGN_TYPES),
    emailType: z.enum(EMAIL_EXPRESS_2_EMAIL_TYPES),
    briefText: z.string().trim().max(4000).default(""),
    referenceImageUrl: z.string()
      .trim()
      .url()
      .max(2048)
      .refine((value) => /^https?:\/\//i.test(value), "Reference image URL must use http:// or https://")
      .nullish(),
    ctaUrl: z.string()
      .trim()
      .url()
      .max(2048)
      .refine((value) => /^https?:\/\//i.test(value), "CTA URL must use http:// or https://")
      .nullish(),
  });

export type EmailExpress2Version = typeof emailExpress2Versions.$inferSelect;

export type EmailExpress2CampaignType = (typeof EMAIL_EXPRESS_2_CAMPAIGN_TYPES)[number];

export const insertEmailExpress2DesignSchema = emailExpress2DesignContextSchema;

export type EmailExpress2Status = (typeof EMAIL_EXPRESS_2_STATUSES)[number];
export type EmailExpress2OutputPath = (typeof EMAIL_EXPRESS_2_OUTPUT_PATHS)[number];
export type EmailExpress2KlaviyoStatus = (typeof EMAIL_EXPRESS_2_KLAVIYO_STATUSES)[number];
export type EmailExpress2KlaviyoMode = (typeof EMAIL_EXPRESS_2_KLAVIYO_MODES)[number];

export type EmailExpress2VersionKind = (typeof EMAIL_EXPRESS_2_VERSION_KINDS)[number];

export type InsertEmailExpress2Design = z.infer<typeof insertEmailExpress2DesignSchema>;

export type EmailExpress2Design = typeof emailExpress2Designs.$inferSelect;

export interface EmailExpress2ContextSnapshot {
  brand?: {
    name: string;
    logoUrl?: string | null;
  };
  product: {
    id: string;
    name: string;
    description?: string | null;
    url?: string | null;
    imageUrl?: string | null;
    ingredientsOrFeatures?: unknown;
    proofPoints?: unknown;
    claimNotes?: unknown;
    priceText?: string | null;
  } | null;
  persona: { id: string; name: string; summary?: string | null } | null;
  narrativeAngle: {
    id: string;
    name: string;
    summary?: string | null;
    coreTension?: string | null;
    beliefShift?: string | null;
    promise?: string | null;
  } | null;
  customAngle: string | null;
  offer: {
    id: string;
    name: string;
    summary?: string | null;
    offerType?: string | null;
    valueFraming?: string | null;
  } | null;
  campaignType: EmailExpress2CampaignType;
  emailType: EmailExpress2EmailType;
  briefText: string;
  referenceImageUrl: string | null;
  ctaUrl?: string | null;
}

export const TEMPLATE_STUDIO_STATUSES = ["draft", "generating", "ready", "failed"] as const;
export type EmailExpress2PreparationStatus = "pending" | "in_progress" | "ready";
export type EmailExpress2EmailType = (typeof EMAIL_EXPRESS_2_EMAIL_TYPES)[number];

export const brandVideoReferenceTemplateAccess = pgTable(
  "brand_video_reference_template_access",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id),
    templateId: varchar("template_id").notNull().references(() => videoReferenceAdTemplates.id),
    assignedAt: timestamp("assigned_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("brand_video_reference_template_access_unique").on(table.brandId, table.templateId),
  ],
);

export type VideoReferenceAdTemplate = typeof videoReferenceAdTemplates.$inferSelect;

export const insertVideoReferenceAdTemplateSchema = createInsertSchema(videoReferenceAdTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
});

export type BrandVideoReferenceTemplateAccess = typeof brandVideoReferenceTemplateAccess.$inferSelect;

export type InsertBrandVideoReferenceTemplateAccess = z.infer<typeof insertBrandVideoReferenceTemplateAccessSchema>;

export type InsertVideoReferenceAdTemplate = z.infer<typeof insertVideoReferenceAdTemplateSchema>;

export const insertBrandVideoReferenceTemplateAccessSchema = createInsertSchema(brandVideoReferenceTemplateAccess).omit({
  id: true,
  assignedAt: true,
});

export interface EmailExpress2PreparedCampaignCopy {
  subjectLine: string;
  previewText: string;
  headline: string;
  heroCtaText: string;
  eyebrow?: string;
  bodyCopy: string;
  pullQuote?: string;
  ctaHeadline: string;
  ctaBody: string;
  ctaButton: string;
}
export const shopifyImportProducts = pgTable(
  "shopify_import_products",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    runId: varchar("run_id").notNull().references(() => shopifyImportRuns.id, { onDelete: "cascade" }),
    handle: varchar("handle", { length: 255 }).notNull(),
    catalogOrder: integer("catalog_order").notNull().default(0),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    catalogPayload: jsonb("catalog_payload").notNull(),
    productId: varchar("product_id").references(() => products.id, { onDelete: "set null" }),
    error: text("error"),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("shopify_import_products_run_handle_unique").on(table.runId, table.handle),
    index("shopify_import_products_pending_idx").on(table.runId, table.status),
  ],
);

export const shopifyImportMedia = pgTable(
  "shopify_import_media",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    runId: varchar("run_id").notNull().references(() => shopifyImportRuns.id, { onDelete: "cascade" }),
    sourceUrl: text("source_url").notNull(),
    keyPrefix: text("key_prefix").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    durableUrl: text("durable_url"),
    error: text("error"),
    attempt: integer("attempt").notNull().default(0),
    leaseOwner: varchar("lease_owner", { length: 120 }),
    leaseExpiresAt: timestamp("lease_expires_at"),
    completedAt: timestamp("completed_at"),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("shopify_import_media_run_source_unique").on(table.runId, table.sourceUrl),
    index("shopify_import_media_pending_idx").on(table.runId, table.status, table.leaseExpiresAt),
  ],
);

export const templateStudioPromptSettings = pgTable("template_studio_prompt_settings", {
  key: varchar("key", { length: 100 }).primaryKey(),
  version: integer("version").notNull().default(1),
  template: text("template").notNull(),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const templateStudioPromptStarters = pgTable(
  "template_studio_prompt_starters",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    label: varchar("label", { length: 255 }).notNull(),
    promptText: text("prompt_text").notNull(),
    createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("template_studio_prompt_starters_label_uq").on(table.label),
    index("template_studio_prompt_starters_updated_idx").on(table.updatedAt),
  ],
);
export interface EmailExpress2PreparedInput {
  version: 1;
  copy: {
    status: EmailExpress2PreparationStatus;
    value?: EmailExpress2PreparedCampaignCopy;
  };
  hero: {
    status: EmailExpress2PreparationStatus;
    url?: string;
    objectKey?: string;
  };
  productImages: {
    status: "ready";
    urls: string[];
  };
  ctaUrl: string | null;
}

// Task #2830 — Growth Operations. These records are intentionally separate
// from campaigns/providers: Persona OS may plan and document work without
// claiming that a provider connection, spend, attribution, or audience data
// exists.
export const growthSetups = pgTable("growth_setups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }).unique(),
  businessModel: varchar("business_model", { length: 100 }).notNull(),
  offer: text("offer").notNull(),
  audience: text("audience").notNull(),
  channelsJson: jsonb("channels_json").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  constraintsJson: jsonb("constraints_json").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  dataAvailabilityJson: jsonb("data_availability_json").$type<Record<string, boolean>>().notNull().default(sql`'{}'::jsonb`),
  budget: numeric("budget"),
  firstGoalId: varchar("first_goal_id"),
  planGeneratedAt: timestamp("plan_generated_at"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const growthGoals = pgTable("growth_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  title: varchar("title", { length: 255 }).notNull(),
  kpiDefinition: text("kpi_definition").notNull(),
  baseline: numeric("baseline"),
  target: numeric("target"),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  source: varchar("source", { length: 128 }).notNull(),
  timezone: varchar("timezone", { length: 64 }).notNull().default("UTC"),
  currency: varchar("currency", { length: 3 }),
  denominator: text("denominator"),
  attributionWindow: text("attribution_window"),
  freshnessAt: timestamp("freshness_at"),
  completeness: real("completeness"),
  observationStatus: varchar("observation_status", { length: 16 }).notNull().default("observed"),
  caveats: text("caveats"),
  status: varchar("status", { length: 16 }).notNull().default("active"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [index("growth_goals_brand_status_idx").on(table.brandId, table.status)]);

export const growthBriefs = pgTable("growth_briefs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  version: integer("version").notNull().default(1),
  supersedesId: varchar("supersedes_id"),
  objective: text("objective").notNull(),
  persona: text("persona").notNull(),
  offer: text("offer").notNull(),
  proof: text("proof"),
  claimBoundaries: text("claim_boundaries").notNull(),
  channel: varchar("channel", { length: 100 }).notNull(),
  cta: text("cta").notNull(),
  budget: numeric("budget"),
  deadline: timestamp("deadline"),
  approversJson: jsonb("approvers_json").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  successMetric: text("success_metric").notNull(),
  contentJson: jsonb("content_json").notNull().default(sql`'{}'::jsonb`),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [uniqueIndex("growth_briefs_brand_version_uq").on(table.brandId, table.version)]);

export const growthEvidence = pgTable("growth_evidence", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  briefId: varchar("brief_id").references(() => growthBriefs.id, { onDelete: "set null" }),
  claim: text("claim").notNull(),
  sourceType: varchar("source_type", { length: 16 }).notNull(),
  sourceRef: text("source_ref"),
  confidence: real("confidence").notNull().default(0),
  observedAt: timestamp("observed_at"),
  detailJson: jsonb("detail_json"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("growth_evidence_brand_idx").on(table.brandId)]);

export const growthWorkItems = pgTable("growth_work_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").references(() => growthGoals.id, { onDelete: "set null" }),
  briefId: varchar("brief_id").references(() => growthBriefs.id, { onDelete: "set null" }),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  status: varchar("status", { length: 20 }).notNull().default("open"),
  ownerId: varchar("owner_id").references(() => users.id, { onDelete: "set null" }),
  dueAt: timestamp("due_at"),
  blockersJson: jsonb("blockers_json").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  dependenciesJson: jsonb("dependencies_json").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  impact: real("impact").notNull().default(0),
  confidence: real("confidence").notNull().default(0),
  speed: real("speed").notNull().default(0),
  cost: real("cost").notNull().default(0),
  risk: real("risk").notNull().default(0),
  acknowledgedAt: timestamp("acknowledged_at"),
  snoozedUntil: timestamp("snoozed_until"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [index("growth_work_items_brand_status_idx").on(table.brandId, table.status)]);

export const growthExperiments = pgTable("growth_experiments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").references(() => growthGoals.id, { onDelete: "set null" }),
  briefId: varchar("brief_id").references(() => growthBriefs.id, { onDelete: "set null" }),
  parentExperimentId: varchar("parent_experiment_id"),
  hypothesis: text("hypothesis").notNull(),
  controlJson: jsonb("control_json").notNull(),
  variantsJson: jsonb("variants_json").notNull(),
  allocationJson: jsonb("allocation_json").notNull(),
  budget: numeric("budget"),
  primaryMetric: text("primary_metric").notNull(),
  guardrailsJson: jsonb("guardrails_json").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  stoppingCriteria: text("stopping_criteria").notNull(),
  readiness: varchar("readiness", { length: 20 }).notNull().default("draft"),
  decision: varchar("decision", { length: 32 }).notNull().default("pending"),
  outcomeJson: jsonb("outcome_json"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [index("growth_experiments_brand_idx").on(table.brandId)]);

export const growthApprovals = pgTable("growth_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  briefId: varchar("brief_id").notNull().references(() => growthBriefs.id, { onDelete: "cascade" }),
  briefVersion: integer("brief_version").notNull(),
  stage: varchar("stage", { length: 64 }).notNull(),
  assigneeId: varchar("assignee_id").references(() => users.id, { onDelete: "set null" }),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  comment: text("comment"),
  snapshotJson: jsonb("snapshot_json").notNull(),
  diffJson: jsonb("diff_json"),
  expiresAt: timestamp("expires_at"),
  decidedAt: timestamp("decided_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("growth_approvals_brand_status_idx").on(table.brandId, table.status)]);

export const growthReports = pgTable("growth_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  snapshotJson: jsonb("snapshot_json").notNull(),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("growth_reports_brand_period_idx").on(table.brandId, table.periodEnd)]);

export const growthAlerts = pgTable("growth_alerts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  dedupeKey: varchar("dedupe_key", { length: 255 }).notNull(),
  severity: varchar("severity", { length: 16 }).notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  recommendedAction: text("recommended_action"),
  evidenceId: varchar("evidence_id").references(() => growthEvidence.id, { onDelete: "set null" }),
  ownerId: varchar("owner_id").references(() => users.id, { onDelete: "set null" }),
  acknowledgedAt: timestamp("acknowledged_at"),
  suppressedUntil: timestamp("suppressed_until"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [uniqueIndex("growth_alerts_brand_dedupe_uq").on(table.brandId, table.dedupeKey)]);

export const growthContext = pgTable("growth_context", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  kind: varchar("kind", { length: 20 }).notNull(),
  value: text("value").notNull(),
  source: varchar("source", { length: 255 }).notNull(),
  confidence: real("confidence").notNull().default(0),
  expiresAt: timestamp("expires_at"),
  contradictsId: varchar("contradicts_id"),
  supersedesId: varchar("supersedes_id"),
  forgottenAt: timestamp("forgotten_at"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [index("growth_context_brand_active_idx").on(table.brandId, table.forgottenAt)]);

// Links are deliberately polymorphic, but constrained to this small, owned
// vocabulary in the service. No unverified external provider IDs are accepted.
export const growthGoalLinks = pgTable("growth_goal_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").notNull().references(() => growthGoals.id, { onDelete: "cascade" }),
  entityType: varchar("entity_type", { length: 32 }).notNull(),
  entityId: varchar("entity_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [uniqueIndex("growth_goal_links_unique").on(table.goalId, table.entityType, table.entityId), index("growth_goal_links_brand_idx").on(table.brandId)]);

// Production references bind an exact immutable brief version, not merely a
// mutable "current brief" pointer.
export const growthBriefBindings = pgTable("growth_brief_bindings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  briefId: varchar("brief_id").notNull().references(() => growthBriefs.id, { onDelete: "cascade" }),
  briefVersion: integer("brief_version").notNull(),
  entityType: varchar("entity_type", { length: 32 }).notNull(),
  entityId: varchar("entity_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [uniqueIndex("growth_brief_bindings_unique").on(table.entityType, table.entityId), index("growth_brief_bindings_brand_idx").on(table.brandId)]);

export const templateStudioSessionInputSchema = z.object({
  rendererTemplateKey: z.enum(["product-hero", "smb-home"]).default("product-hero"),
  name: z.string().trim().min(1).max(255),
  briefText: z.string().trim().min(1).max(20000),
  referenceUploadId: z.string().trim().min(1).nullish(),
  // Omitted means the reserved Base system. The server resolves and persists
  // the live provider ID/name before reserving any paid generation work.
  designSystemId: z.string().trim().min(1).max(255).nullish(),
}).strict();

export const slideshowExpressContentPlanItemVersions = pgTable("slideshow_express_content_plan_item_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  planVersionId: varchar("plan_version_id").notNull().references(() => slideshowExpressContentPlanVersions.id, { onDelete: "cascade" }),
  itemId: varchar("item_id").notNull().references(() => slideshowExpressContentPlanItems.id, { onDelete: "cascade" }),
  sortOrder: integer("sort_order").notNull(),
  scheduledFor: timestamp("scheduled_for"),
  wording: text("wording").notNull(),
  source: jsonb("source").notNull(),
  resolved: jsonb("resolved"),
  blockers: jsonb("blockers").notNull().default([]),
  status: varchar("status", { length: 32 }).notNull().default("pending"),
  retryCount: integer("retry_count").notNull().default(0),
  pausedAt: timestamp("paused_at"),
  revokedAt: timestamp("revoked_at"),
  runBatchId: varchar("run_batch_id", { length: 60 }),
  selectedRunId: varchar("selected_run_id").references(() => slideshowExpressRuns.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("slideshow_express_content_plan_item_versions_unique").on(table.planVersionId, table.itemId),
  uniqueIndex("slideshow_express_content_plan_item_versions_order_unique").on(table.planVersionId, table.sortOrder),
  index("slideshow_express_content_plan_item_versions_work_idx").on(table.status, table.scheduledFor),
]);

export const slideshowExpressContentPlanItems = pgTable("slideshow_express_content_plan_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  planId: varchar("plan_id").notNull().references(() => slideshowExpressContentPlans.id, { onDelete: "cascade" }),
  stableKey: varchar("stable_key", { length: 160 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("slideshow_express_content_plan_items_stable_unique").on(table.planId, table.stableKey),
  index("slideshow_express_content_plan_items_plan_idx").on(table.planId, table.createdAt),
]);

export type SlideshowExpressContentPlan = typeof slideshowExpressContentPlans.$inferSelect;

export type SlideshowExpressContentPlanVersion = typeof slideshowExpressContentPlanVersions.$inferSelect;

export const slideshowExpressContentPlanGateDecisions = pgTable("slideshow_express_content_plan_gate_decisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  itemVersionId: varchar("item_version_id").notNull().references(() => slideshowExpressContentPlanItemVersions.id, { onDelete: "cascade" }),
  grantId: varchar("grant_id").references(() => slideshowExpressContentPlanGrants.id, { onDelete: "set null" }),
  contentVersion: integer("content_version").notNull(),
  stage: varchar("stage", { length: 32 }).notNull(),
  decision: varchar("decision", { length: 24 }).notNull(),
  rationale: text("rationale"),
  edits: jsonb("edits"),
  selectedOption: integer("selected_option"),
  actorType: varchar("actor_type", { length: 24 }).notNull(),
  actorId: varchar("actor_id"),
  decisionKey: varchar("decision_key", { length: 240 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("slideshow_express_content_plan_gate_decisions_item_idx").on(table.itemVersionId, table.createdAt),
  uniqueIndex("slideshow_express_content_plan_gate_decisions_key_unique").on(table.decisionKey),
]);

export const insertSlideshowExpressContentPlanSchema = createInsertSchema(slideshowExpressContentPlans).omit({ id: true, createdAt: true, updatedAt: true });

export type SlideshowExpressContentPlanItem = typeof slideshowExpressContentPlanItems.$inferSelect;

export const slideshowExpressContentPlanVersions = pgTable("slideshow_express_content_plan_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  planId: varchar("plan_id").notNull().references(() => slideshowExpressContentPlans.id, { onDelete: "cascade" }),
  version: integer("version").notNull(),
  status: varchar("status", { length: 32 }).notNull().default("draft"),
  snapshot: jsonb("snapshot").notNull(),
  estimate: jsonb("estimate"),
  blockers: jsonb("blockers").notNull().default([]),
  pausedAt: timestamp("paused_at"),
  revokedAt: timestamp("revoked_at"),
  authorizedAt: timestamp("authorized_at"),
  authorizedBy: varchar("authorized_by").references(() => users.id, { onDelete: "set null" }),
  authorizationExpiresAt: timestamp("authorization_expires_at"),
  revokedBy: varchar("revoked_by").references(() => users.id, { onDelete: "set null" }),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("slideshow_express_content_plan_versions_unique").on(table.planId, table.version),
  index("slideshow_express_content_plan_versions_execute_idx").on(table.status, table.authorizationExpiresAt),
]);

export const slideshowExpressContentPlanClaims = pgTable("slideshow_express_content_plan_claims", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  itemVersionId: varchar("item_version_id").notNull().references(() => slideshowExpressContentPlanItemVersions.id, { onDelete: "cascade" }),
  workerId: varchar("worker_id", { length: 160 }).notNull(),
  status: varchar("status", { length: 24 }).notNull().default("claimed"),
  attempt: integer("attempt").notNull().default(1),
  claimedAt: timestamp("claimed_at").defaultNow(),
  leaseExpiresAt: timestamp("lease_expires_at").notNull(),
  completedAt: timestamp("completed_at"),
  error: text("error"),
}, (table) => [
  index("slideshow_express_content_plan_claims_lease_idx").on(table.status, table.leaseExpiresAt),
  index("slideshow_express_content_plan_claims_item_idx").on(table.itemVersionId),
]);

export type SlideshowExpressContentPlanItemVersion = typeof slideshowExpressContentPlanItemVersions.$inferSelect;

export const slideshowExpressContentPlanSpendReservations = pgTable("slideshow_express_content_plan_spend_reservations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  grantId: varchar("grant_id").notNull().references(() => slideshowExpressContentPlanGrants.id, { onDelete: "cascade" }),
  itemVersionId: varchar("item_version_id").notNull().references(() => slideshowExpressContentPlanItemVersions.id, { onDelete: "cascade" }),
  workKey: varchar("work_key", { length: 200 }).notNull(),
  reservedCredits: numeric("reserved_credits", { precision: 14, scale: 2 }).notNull().default("0"),
  reservedUsd: numeric("reserved_usd", { precision: 14, scale: 6 }).notNull().default("0"),
  actualCredits: numeric("actual_credits", { precision: 14, scale: 2 }),
  actualUsd: numeric("actual_usd", { precision: 14, scale: 6 }),
  status: varchar("status", { length: 24 }).notNull().default("reserved"),
  createdAt: timestamp("created_at").defaultNow(),
  reconciledAt: timestamp("reconciled_at"),
}, (table) => [
  uniqueIndex("slideshow_express_content_plan_spend_work_unique").on(table.grantId, table.workKey),
  index("slideshow_express_content_plan_spend_grant_idx").on(table.grantId, table.status),
]);

export const slideshowExpressContentPlanRunLinks = pgTable("slideshow_express_content_plan_run_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  itemVersionId: varchar("item_version_id").notNull().references(() => slideshowExpressContentPlanItemVersions.id, { onDelete: "cascade" }),
  variantIndex: integer("variant_index").notNull(),
  runId: varchar("run_id").notNull().references(() => slideshowExpressRuns.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("slideshow_express_content_plan_run_links_variant_unique").on(table.itemVersionId, table.variantIndex),
  uniqueIndex("slideshow_express_content_plan_run_links_run_unique").on(table.runId),
]);

export type SpyAdStatusEvent = typeof spyAdStatusEvents.$inferSelect;

export const ADS_MANAGER_ASSOCIATION_MODES = ["inherit", "override", "none"] as const;

export const ADS_MANAGER_TAG_MODES = ["inherit", "replace", "none"] as const;

export const adsManagerAssociations = pgTable(
  "ads_manager_associations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    // Provider account identity, normalized without the Meta `act_` prefix.
    // No FK: an account can be visible through the authorized read boundary
    // without being configured for PersonaOS launching.
    accountId: varchar("account_id", { length: 64 }).notNull(),
    platform: varchar("platform", { length: 32 }).notNull().default("meta"),
    entityType: varchar("entity_type", { length: 16 }).notNull(),
    campaignId: varchar("campaign_id", { length: 64 }).notNull(),
    adsetId: varchar("adset_id", { length: 64 }),
    adId: varchar("ad_id", { length: 64 }),

    productMode: varchar("product_mode", { length: 16 }).notNull().default("inherit"),
    productId: varchar("product_id").references(() => products.id, { onDelete: "restrict" }),
    offerMode: varchar("offer_mode", { length: 16 }).notNull().default("inherit"),
    offerId: varchar("offer_id").references(() => offers.id, { onDelete: "restrict" }),
    // Services are service-style offers; retaining an explicit column lets the
    // setting evolve without inventing a parallel service identity.
    serviceMode: varchar("service_mode", { length: 16 }).notNull().default("inherit"),
    serviceOfferId: varchar("service_offer_id").references(() => offers.id, { onDelete: "restrict" }),
    promotionMode: varchar("promotion_mode", { length: 16 }).notNull().default("inherit"),
    promotionId: varchar("promotion_id").references(() => salesDiscounts.id, { onDelete: "restrict" }),
    tagMode: varchar("tag_mode", { length: 16 }).notNull().default("inherit"),
    tagIds: jsonb("tag_ids").notNull().default(sql`'[]'::jsonb`),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("ads_manager_associations_identity_uq").on(
      table.brandId,
      table.accountId,
      table.platform,
      table.entityType,
      table.campaignId,
      sql`coalesce(${table.adsetId}, '')`,
      sql`coalesce(${table.adId}, '')`,
    ),
    index("ads_manager_associations_scope_idx").on(table.brandId, table.accountId, table.platform, table.campaignId),
    check(
      "ads_manager_associations_entity_shape",
      sql`(${table.entityType} = 'campaign' and ${table.adsetId} is null and ${table.adId} is null)
        or (${table.entityType} = 'adset' and ${table.adsetId} is not null and ${table.adId} is null)
        or (${table.entityType} = 'ad' and ${table.adsetId} is not null and ${table.adId} is not null)`,
    ),
    check("ads_manager_associations_entity_type", sql`${table.entityType} in ('campaign', 'adset', 'ad')`),
    check("ads_manager_associations_product_mode", sql`${table.productMode} in ('inherit', 'override', 'none')`),
    check("ads_manager_associations_product_value", sql`(${table.productMode} = 'override' and ${table.productId} is not null) or (${table.productMode} != 'override' and ${table.productId} is null)`),
    check("ads_manager_associations_offer_mode", sql`${table.offerMode} in ('inherit', 'override', 'none')`),
    check("ads_manager_associations_offer_value", sql`(${table.offerMode} = 'override' and ${table.offerId} is not null) or (${table.offerMode} != 'override' and ${table.offerId} is null)`),
    check("ads_manager_associations_service_mode", sql`${table.serviceMode} in ('inherit', 'override', 'none')`),
    check("ads_manager_associations_service_value", sql`(${table.serviceMode} = 'override' and ${table.serviceOfferId} is not null) or (${table.serviceMode} != 'override' and ${table.serviceOfferId} is null)`),
    check("ads_manager_associations_promotion_mode", sql`${table.promotionMode} in ('inherit', 'override', 'none')`),
    check("ads_manager_associations_promotion_value", sql`(${table.promotionMode} = 'override' and ${table.promotionId} is not null) or (${table.promotionMode} != 'override' and ${table.promotionId} is null)`),
    check("ads_manager_associations_tag_mode", sql`${table.tagMode} in ('inherit', 'replace', 'none')`),
    check("ads_manager_associations_tag_value", sql`(${table.tagMode} = 'replace' or ${table.tagIds} = '[]'::jsonb)`),
  ],
);

/**
 * Environment-scoped pilot settings. Legacy snapshot-cache rows remain inert.
 * Runtime activation now follows the brand's Ads Manager visibility settings;
 * these historical rows do not enable or disable snapshot use.
 */
export const adsManagerSnapshotSettings = pgTable(
  "ads_manager_snapshot_settings",
  {
    environment: varchar("environment", { length: 20 }).notNull(),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    flagKey: varchar("flag_key", { length: 64 }).notNull().default("ads_manager_snapshot_cache"),
    enabled: boolean("enabled").notNull().default(false),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.environment, table.brandId, table.flagKey], name: "ads_manager_snapshot_settings_pk" }),
    check("ads_manager_snapshot_settings_environment", sql`${table.environment} in ('development','production')`),
    check("ads_manager_snapshot_settings_flag", sql`${table.flagKey} in ('ads_manager_snapshot_cache','ads_manager_partial_saved_grid')`),
  ],
);
export type AdsManagerAssociation = typeof adsManagerAssociations.$inferSelect;

export const insertAdsManagerAssociationSchema = createInsertSchema(adsManagerAssociations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAdsManagerAssociation = z.infer<typeof insertAdsManagerAssociationSchema>;

/** Shared Brief state deliberately lives outside the Ads Manager snapshot rollout. */
export const performanceBriefCronState = pgTable("performance_brief_cron_state", {
  id: integer("id").primaryKey().default(1),
  discoveryCursor: text("discovery_cursor"),
}, t => [check("performance_brief_cron_singleton", sql`${t.id} = 1`)]);

export const performanceBriefGenerations = pgTable("performance_brief_generations", {
  brandId: varchar("brand_id").primaryKey().references(() => brands.id, { onDelete: "cascade" }),
  generation: integer("generation").notNull().default(0),
  lastTransaction: text("last_transaction"),
});
export const adsPerformanceSettings = pgTable("ads_performance_settings", {
  brandId: varchar("brand_id").primaryKey().references(() => brands.id, { onDelete: "cascade" }),
  defaultScope: varchar("default_scope", { length: 32 }).notNull().default("full_account"),
  monthlyBudget: numeric("monthly_budget", { precision: 16, scale: 4 }),
  budgetCurrency: varchar("budget_currency", { length: 8 }),
  updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => [check("ads_performance_settings_scope", sql`${t.defaultScope} in ('full_account','our_managed','client_managed','shared','persona_creative')`)]);

export const adsPerformanceMonthlyBudgets = pgTable("ads_performance_monthly_budgets", {
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  cadence: varchar("cadence", { length: 8 }).notNull().default("monthly"),
  periodStart: date("period_start"),
  monthlyBudget: numeric("monthly_budget", { precision: 16, scale: 4 }),
  // Deprecated rollout columns remain mapped so existing monthly rows can be migrated safely.
  calendarMonth: date("calendar_month"),
  budgetCurrency: varchar("budget_currency", { length: 8 }),
  metaNcacGoal: numeric("meta_ncac_goal", { precision: 16, scale: 4 }),
  updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => [
  uniqueIndex("ads_performance_monthly_budgets_brand_period_uq").on(t.brandId, t.cadence, t.periodStart),
  check("ads_performance_monthly_budgets_cadence", sql`${t.cadence} in ('weekly','monthly')`),
]);
export type SpyAdTemplateLink = typeof spyAdTemplateLinks.$inferSelect;

export type SpyAdObservation = typeof spyAdObservations.$inferSelect;

/** Immutable source facts. Absence is represented only by a run limitation,
 * never by synthesizing an inactive observation. */
export const spyAdObservations = pgTable("spy_ad_observations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spyAdId: varchar("spy_ad_id").notNull().references(() => spyAds.id, { onDelete: "cascade" }),
  syncRunId: varchar("sync_run_id").notNull().references(() => spySyncRuns.id, { onDelete: "cascade" }),
  observedAt: timestamp("observed_at").notNull().defaultNow(),
  explicitStatus: varchar("explicit_status", { length: 32 }),
  snapshot: jsonb("snapshot").notNull(),
  creativeFingerprint: varchar("creative_fingerprint", { length: 128 }),
}, (table) => [
  uniqueIndex("spy_ad_observation_run_uq").on(table.spyAdId, table.syncRunId),
  index("spy_ad_observations_ad_at_idx").on(table.spyAdId, table.observedAt),
]);

export const spyAdStatusEvents = pgTable("spy_ad_status_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spyAdId: varchar("spy_ad_id").notNull().references(() => spyAds.id, { onDelete: "cascade" }),
  observationId: varchar("observation_id").references(() => spyAdObservations.id, { onDelete: "cascade" }),
  fromStatus: varchar("from_status", { length: 32 }),
  toStatus: varchar("to_status", { length: 32 }).notNull(),
  eventType: varchar("event_type", { length: 32 }).notNull().default("status_transition"),
  occurredAt: timestamp("occurred_at").notNull().defaultNow(),
}, (table) => [uniqueIndex("spy_ad_status_event_observation_type_uq").on(table.spyAdId, table.observationId, table.eventType)]);

/** Links existing static templates without changing template ownership. */
export const spyAdTemplateLinks = pgTable("spy_ad_template_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spyAdId: varchar("spy_ad_id").notNull().references(() => spyAds.id, { onDelete: "cascade" }),
  adUnitTypeId: varchar("ad_unit_type_id").references(() => adUnitTypes.id, { onDelete: "restrict" }),
  observationId: varchar("observation_id").references(() => spyAdObservations.id, { onDelete: "set null" }),
  createdBy: varchar("created_by").references(() => users.id),
  idempotencyKey: varchar("idempotency_key", { length: 255 }),
  state: varchar("state", { length: 16 }).notNull().default("complete"),
  error: text("error"),
  leaseOwner: varchar("lease_owner", { length: 255 }),
  leaseExpiresAt: timestamp("lease_expires_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [uniqueIndex("spy_ad_template_idempotency_uq").on(table.spyAdId, table.idempotencyKey)]);

export const adsPerformanceManagementHistory = pgTable("ads_performance_management_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  entityType: varchar("entity_type", { length: 16 }).notNull(),
  campaignId: varchar("campaign_id", { length: 64 }).notNull(),
  adsetId: varchar("adset_id", { length: 64 }),
  adId: varchar("ad_id", { length: 64 }),
  owner: varchar("owner", { length: 16 }).notNull(),
  effectiveOn: date("effective_on").notNull(),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => [index("ads_performance_management_lookup_idx").on(t.brandId, t.accountId, t.campaignId, t.adsetId, t.adId, t.effectiveOn), check("ads_performance_management_owner", sql`${t.owner} in ('our_team','client','shared','unassigned','inherit')`)]);

export const adsPerformanceDailyFacts = pgTable("ads_performance_daily_facts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  day: date("day").notNull(),
  campaignId: varchar("campaign_id", { length: 64 }).notNull(), campaignName: text("campaign_name"),
  adsetId: varchar("adset_id", { length: 64 }).notNull(), adsetName: text("adset_name"),
  adId: varchar("ad_id", { length: 64 }).notNull(), adName: text("ad_name"),
  currency: varchar("currency", { length: 8 }), timezone: varchar("timezone", { length: 64 }),
  event: varchar("event", { length: 32 }),
  leadEvidence: jsonb("lead_evidence"),
  selectedOutcome: jsonb("selected_outcome").$type<import("./performance-selected-outcome").PerformanceSelectedOutcome>(),
  campaignObjective: varchar("campaign_objective", { length: 64 }),
  campaignObjectiveKnown: boolean("campaign_objective_known").notNull().default(false),
  adsetOptimizationGoal: varchar("adset_optimization_goal", { length: 64 }),
  adsetOptimizationGoalKnown: boolean("adset_optimization_goal_known").notNull().default(false),
  adsetConversionEvent: varchar("adset_conversion_event", { length: 32 }),
  adsetConversionEventKnown: boolean("adset_conversion_event_known").notNull().default(false),
  compatibilityMetadataSource: varchar("compatibility_metadata_source", { length: 16 }),
  spend: numeric("spend", { precision: 18, scale: 6 }).notNull().default("0"), impressions: bigint("impressions", { mode: "number" }).notNull().default(0), reach: bigint("reach", { mode: "number" }).notNull().default(0), clicks: bigint("clicks", { mode: "number" }).notNull().default(0),
  results: numeric("results", { precision: 18, scale: 6 }).notNull().default("0"), resultValue: numeric("result_value", { precision: 18, scale: 6 }).notNull().default("0"),
  refreshedAt: timestamp("refreshed_at").notNull().defaultNow(), partial: boolean("partial").notNull().default(true), coverageNote: text("coverage_note"),
}, (t) => [uniqueIndex("ads_performance_daily_fact_uq").on(t.brandId, t.accountId, t.day, t.adId, sql`coalesce(${t.event}, '')`, sql`coalesce(${t.currency}, '')`), index("ads_performance_daily_range_idx").on(t.brandId, t.accountId, t.day)]);

export const adsPerformanceLeadBackfillJobs = pgTable("ads_performance_lead_backfill_jobs", {
  id: varchar("id").primaryKey(),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  requestedBy: varchar("requested_by").references(() => users.id, { onDelete: "set null" }),
  status: varchar("status", { length: 24 }).notNull(),
  payload: jsonb("payload").$type<import("./lead-backfill").LeadBackfillJob>().notNull(),
  leaseToken: varchar("lease_token", { length: 80 }),
  leaseExpiresAt: timestamp("lease_expires_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => [index("ads_performance_lead_backfill_account_idx").on(t.brandId, t.accountId, t.createdAt)]);

export const adsPerformanceRefreshRuns = pgTable("ads_performance_refresh_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  timezone: varchar("timezone", { length: 64 }),
  since: date("since").notNull(), until: date("until").notNull(),
  status: varchar("status", { length: 16 }).notNull().default("running"),
  requestedBy: varchar("requested_by").references(() => users.id, { onDelete: "set null" }),
  startedAt: timestamp("started_at").notNull().defaultNow(), completedAt: timestamp("completed_at"),
  claimToken: varchar("claim_token", { length: 80 }),
  claimExpiresAt: timestamp("claim_expires_at"),
  rowsPersisted: integer("rows_persisted").notNull().default(0),
  partial: boolean("partial").notNull().default(true), limitation: text("limitation"), error: text("error"),
}, (t) => [
  index("ads_performance_refresh_latest_idx").on(t.brandId, t.accountId, t.startedAt),
  index("ads_performance_refresh_claim_expiry_idx").on(t.status, t.claimExpiresAt),
  check("ads_performance_refresh_status", sql`${t.status} in ('running','succeeded','failed')`),
]);

export const adsPerformanceRefreshDays = pgTable("ads_performance_refresh_days", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  day: date("day").notNull(),
  runId: varchar("run_id").notNull().references(() => adsPerformanceRefreshRuns.id, { onDelete: "cascade" }),
  timezone: varchar("timezone", { length: 64 }).notNull(),
  partial: boolean("partial").notNull().default(true),
  limitation: text("limitation"),
  refreshedAt: timestamp("refreshed_at").notNull().defaultNow(),
}, (t) => [uniqueIndex("ads_performance_refresh_day_uq").on(t.brandId, t.accountId, t.day), index("ads_performance_refresh_day_range_idx").on(t.brandId, t.accountId, t.day)]);

export const shopifyPerformanceOrderFacts = pgTable("shopify_performance_order_facts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 128 }).notNull(),
  day: date("day").notNull(),
  orderId: varchar("order_id", { length: 128 }).notNull(),
  customerType: varchar("customer_type", { length: 16 }).notNull(),
  metaAttributed: boolean("meta_attributed").notNull(),
  utmSource: varchar("utm_source", { length: 128 }),
  currency: varchar("currency", { length: 8 }),
  refreshedAt: timestamp("refreshed_at").notNull().defaultNow(),
}, (t) => [
  uniqueIndex("shopify_performance_order_fact_uq").on(t.brandId, t.accountId, t.day, t.orderId),
  index("shopify_performance_order_range_idx").on(t.brandId, t.accountId, t.day),
  check("shopify_performance_order_customer_type", sql`${t.customerType} in ('new','returning')`),
]);

export const squarePerformanceAccounts = pgTable("square_performance_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 128 }).notNull(),
  accountName: varchar("account_name", { length: 240 }).notNull(),
  enabled: boolean("enabled").notNull().default(false),
  providerDisconnected: boolean("provider_disconnected").notNull().default(false),
  revision: varchar("revision").notNull().default(sql`gen_random_uuid()`),
  locations: jsonb("locations").notNull().$type<Array<{ locationId: string; merchantId: string; locationName: string; currency: string; timezone: string }>>(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("square_performance_brand_unique").on(table.brandId),
  // Disabling preserves ownership. Transfer requires an explicit admin remap.
  uniqueIndex("square_performance_account_unique").on(table.accountId),
]);
export const squarePerformanceDailyFacts = pgTable("square_performance_daily_facts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 128 }).notNull(),
  revision: varchar("revision").notNull(),
  locationId: varchar("location_id", { length: 128 }).notNull(),
  day: varchar("day", { length: 10 }).notNull(),
  currency: varchar("currency", { length: 3 }).notNull(),
  timezone: varchar("timezone", { length: 64 }).notNull(),
  // Integer minor units serialized without floating-point arithmetic.
  revenueMinor: numeric("revenue_minor", { precision: 24, scale: 0 }),
  transactionCount: integer("transaction_count"),
  paymentEvidence: jsonb("payment_evidence").$type<import("./square-sales").SquarePaymentDayEvidence>(),
  accountingVersion: varchar("accounting_version", { length: 64 }).notNull(),
  settledAt: timestamp("settled_at"),
  refreshedAt: timestamp("refreshed_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("square_performance_fact_scope_unique").on(table.brandId, table.accountId, table.revision, table.locationId, table.day),
]);

export const squarePerformanceRefreshDays = pgTable("square_performance_refresh_days", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 128 }).notNull(),
  revision: varchar("revision").notNull(),
  locationId: varchar("location_id", { length: 128 }).notNull(),
  day: varchar("day", { length: 10 }).notNull(),
  status: varchar("status", { length: 24 }).notNull(),
  limitation: text("limitation"),
  accountingVersion: varchar("accounting_version", { length: 64 }).notNull(),
  settledAt: timestamp("settled_at"),
  refreshedAt: timestamp("refreshed_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("square_performance_day_scope_unique").on(table.brandId, table.accountId, table.revision, table.locationId, table.day),
]);

export const shopifyPerformanceAccounts = pgTable("shopify_performance_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 128 }).notNull(),
  reportTimezone: varchar("report_timezone", { length: 64 }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => [
  uniqueIndex("shopify_performance_account_brand_uq").on(t.brandId),
  uniqueIndex("shopify_performance_account_owner_uq").on(t.accountId),
]);
export const adsPerformanceCreativeSources = pgTable("ads_performance_creative_sources", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  adId: varchar("ad_id", { length: 64 }).notNull(),
  source: varchar("source", { length: 16 }).notNull(),
  reliableLineage: boolean("reliable_lineage").notNull().default(false),
  effectiveOn: date("effective_on").notNull(),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => [index("ads_performance_creative_lookup_idx").on(t.brandId, t.accountId, t.adId, t.effectiveOn), check("ads_performance_creative_source", sql`${t.source} in ('persona','client','other','unknown')`)]);

export const adsPerformanceTargets = pgTable("ads_performance_targets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  entityType: varchar("entity_type", { length: 16 }).notNull(), // brand, campaign, adset
  campaignId: varchar("campaign_id", { length: 64 }),
  adsetId: varchar("adset_id", { length: 64 }),
  metric: varchar("metric", { length: 32 }).notNull(),
  event: varchar("event", { length: 32 }),
  state: varchar("state", { length: 16 }).notNull().default("custom"),
  targetValue: numeric("target_value", { precision: 18, scale: 6 }),
  windowDays: integer("window_days"),
  isPrimary: boolean("is_primary").notNull().default(false),
  version: integer("version").notNull().default(1),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  // A target is evaluated only for account-local fact days on/after this date.
  effectiveOn: date("effective_on").notNull().default(sql`CURRENT_DATE`),
  supersededAt: timestamp("superseded_at"),
}, (t) => [
  index("ads_performance_targets_scope_idx").on(t.brandId, t.accountId, t.campaignId, t.adsetId),
  check("ads_performance_targets_shape", sql`(${t.entityType} = 'brand' and ${t.campaignId} is null and ${t.adsetId} is null) or (${t.entityType} = 'campaign' and ${t.campaignId} is not null and ${t.adsetId} is null) or (${t.entityType} = 'adset' and ${t.campaignId} is not null and ${t.adsetId} is not null)`),
  check("ads_performance_targets_state", sql`${t.state} in ('inherit','custom','disabled')`),
]);

export const templateStudioVersions = pgTable(
  "template_studio_versions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull().references(() => templateStudioSessions.id, { onDelete: "cascade" }),
    kind: varchar("kind", { length: 20 }).notNull(),
    html: text("html").notNull(),
    chatInstruction: text("chat_instruction"),
    changeSummary: text("change_summary"),
    // Immutable snapshot of images attached to a chat revision.
    attachmentsJson: jsonb("attachments_json").$type<TemplateStudioVersionAttachment[] | null>(),
    createdBy: varchar("created_by"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("template_studio_versions_session_idx").on(table.sessionId, table.createdAt),
    check(
      "template_studio_versions_kind_v4_check",
      sql`${table.kind} IN ('initial', 'chat_revision', 'manual_edit', 'external_sync')`,
    ),
  ],
);

export const TEMPLATE_STUDIO_VERSION_KINDS = ["initial", "chat_revision", "manual_edit", "external_sync"] as const;

export type TemplateStudioVersion = typeof templateStudioVersions.$inferSelect;

export interface TemplateStudioVersionAttachment {
  id: string;
  fileName: string;
  url: string;
  contentType: string;
  sizeBytes: number;
}
export const templateStudioReferenceUploads = pgTable(
  "template_studio_reference_uploads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").references(() => brands.id),
    uploadedBy: varchar("uploaded_by").notNull(),
    ownershipToken: varchar("ownership_token", { length: 100 }).notNull(),
    sessionId: varchar("session_id").references(() => templateStudioSessions.id, { onDelete: "set null" }),
    objectKey: varchar("object_key", { length: 500 }).notNull(),
    fileName: varchar("file_name", { length: 255 }).notNull(),
    contentType: varchar("content_type", { length: 100 }).notNull(),
    sizeBytes: integer("size_bytes").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    expiresAt: timestamp("expires_at").notNull(),
    claimedAt: timestamp("claimed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("template_studio_reference_uploads_brand_idx").on(table.brandId, table.status, table.expiresAt),
    check(
      "template_studio_reference_uploads_status_check",
      sql`${table.status} IN ('pending', 'uploaded', 'claimed', 'released', 'deleted', 'expired')`,
    ),
  ],
);

export type TemplateStudioStatus = (typeof TEMPLATE_STUDIO_STATUSES)[number];

export type TemplateStudioSession = typeof templateStudioSessions.$inferSelect;

export interface TemplateStudioContextSnapshot {
  brand: {
    name: string;
    logoUrl: string | null;
    colorPalette: unknown;
    designSystem: unknown;
  };
  product: {
    id: string;
    name: string;
    summary: string | null;
    proofPoints: unknown;
    priceText: string | null;
    imageUrl: string | null;
  } | null;
  persona: {
    id: string;
    name: string;
    summary: string | null;
    primaryPain: string | null;
    desiredOutcome: string | null;
  } | null;
  briefText: string;
  referenceImageUrl: string | null;
  referenceImageUrls?: string[];
}

export const templateStudioSessions = pgTable(
  "template_studio_sessions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    rendererTemplateKey: varchar("renderer_template_key", { length: 32 }).$type<"product-hero" | "smb-home">().notNull().default("product-hero"),
    brandId: varchar("brand_id").references(() => brands.id),
    createdBy: varchar("created_by"),
    availableForSmb: boolean("available_for_smb").notNull().default(false),
    name: varchar("name", { length: 255 }).notNull(),
    status: varchar("status", { length: 20 }).notNull().default("draft"),
    productId: varchar("product_id").references(() => products.id),
    personaId: varchar("persona_id").references(() => personas.id),
    briefText: text("brief_text").notNull(),
    referenceImageUrl: text("reference_image_url"),
    contextSnapshot: jsonb("context_snapshot").$type<TemplateStudioContextSnapshot>().notNull(),
    // The provider ID is the source of truth for generation. The name is an
    // audit snapshot and must never be used to resolve a provider system.
    designSystemId: varchar("design_system_id", { length: 255 }),
    designSystemName: varchar("design_system_name", { length: 255 }),
    mpEditorId: varchar("mp_editor_id", { length: 100 }),
    mpArtifactId: varchar("mp_artifact_id", { length: 100 }),
    mpRequestId: varchar("mp_request_id", { length: 100 }),
    generationAttemptId: varchar("generation_attempt_id", { length: 100 }),
    generationLeaseOwner: varchar("generation_lease_owner", { length: 100 }),
    generationLeaseExpiresAt: timestamp("generation_lease_expires_at"),
    generationSubmissionState: varchar("generation_submission_state", { length: 20 }).default("reserved"),
    html: text("html"),
    error: text("error"),
    generatingStartedAt: timestamp("generating_started_at"),
    pendingRevisionInstruction: text("pending_revision_instruction"),
    pendingRequestedBy: varchar("pending_requested_by"),
    pendingRevisionAttachmentIds: jsonb("pending_revision_attachment_ids").$type<string[] | null>(),
    publishedTemplateId: varchar("published_template_id").references(() => landingPageTemplates.id, { onDelete: "set null" }),
    publishedVersionId: varchar("published_version_id"),
    publishedAt: timestamp("published_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("template_studio_sessions_brand_idx").on(table.brandId, table.updatedAt),
    check(
      "template_studio_sessions_status_check",
      sql`${table.status} IN ('draft', 'generating', 'ready', 'failed')`,
    ),
    check(
      "template_studio_sessions_submission_state_check",
      sql`${table.generationSubmissionState} IS NULL OR ${table.generationSubmissionState} IN ('reserved', 'submitting', 'submitted', 'ambiguous', 'failed', 'complete')`,
    ),
  ],
);

export const templateStudioSessionBrands = pgTable(
  "template_studio_session_brands",
  {
    sessionId: varchar("session_id").notNull().references(() => templateStudioSessions.id, { onDelete: "cascade" }),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    assignedAt: timestamp("assigned_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("template_studio_session_brands_unique").on(table.sessionId, table.brandId),
    index("template_studio_session_brands_brand_idx").on(table.brandId),
  ],
);
export type TemplateStudioPromptStarter = typeof templateStudioPromptStarters.$inferSelect;

export type TemplateBrandVariant = typeof templateBrandVariants.$inferSelect;
export type AdLauncherLaunchDefault = typeof adLauncherLaunchDefaults.$inferSelect;

export type InsertShopifyProductImageCleanup = z.infer<typeof insertShopifyProductImageCleanupSchema>;

export const templateStudioRevisionImages = pgTable(
  "template_studio_revision_images",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull().references(() => templateStudioSessions.id, { onDelete: "cascade" }),
    uploadedBy: varchar("uploaded_by").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("uploading"),
    fileName: varchar("file_name", { length: 255 }).notNull(),
    contentType: varchar("content_type", { length: 100 }).notNull(),
    sizeBytes: integer("size_bytes").notNull(),
    // Reservations are created before multipart buffering/R2 upload, so the
    // durable object key is assigned only after the upload succeeds.
    objectKey: varchar("object_key", { length: 500 }),
    versionId: varchar("version_id").references(() => templateStudioVersions.id),
    claimedAt: timestamp("claimed_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("template_studio_revision_images_session_idx").on(table.sessionId, table.status),
    check(
      "template_studio_revision_images_status_check",
      sql`${table.status} IN ('uploading', 'pending', 'claimed', 'cancelled')`,
    ),
  ],
);

export type TemplateStudioRevisionImage = typeof templateStudioRevisionImages.$inferSelect;

export type ShopifyProductImageCleanup = typeof shopifyProductImageCleanup.$inferSelect;

export const insertShopifyProductImageCleanupSchema = createInsertSchema(shopifyProductImageCleanup).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const shopifyPerformanceRefreshRuns = pgTable("shopify_performance_refresh_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 128 }).notNull(),
  since: date("since").notNull(),
  until: date("until").notNull(),
  status: varchar("status", { length: 16 }).notNull().default("running"),
  requestedBy: varchar("requested_by").references(() => users.id, { onDelete: "set null" }),
  startedAt: timestamp("started_at").notNull().defaultNow(),
  completedAt: timestamp("completed_at"),
  claimToken: varchar("claim_token", { length: 80 }),
  claimExpiresAt: timestamp("claim_expires_at"),
  rowsPersisted: integer("rows_persisted").notNull().default(0),
  partial: boolean("partial").notNull().default(true),
  limitation: text("limitation"),
  error: text("error"),
}, (t) => [
  index("shopify_performance_refresh_latest_idx").on(t.brandId, t.accountId, t.startedAt),
  index("shopify_performance_refresh_claim_idx").on(t.status, t.claimExpiresAt),
  uniqueIndex("shopify_performance_one_active_account").on(t.brandId, t.accountId).where(sql`${t.status} = 'running'`),
  check("shopify_performance_refresh_status", sql`${t.status} in ('running','succeeded','failed')`),
]);

export const shopifyPerformanceRefreshDays = pgTable("shopify_performance_refresh_days", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 128 }).notNull(),
  day: date("day").notNull(),
  runId: varchar("run_id").notNull().references(() => shopifyPerformanceRefreshRuns.id, { onDelete: "cascade" }),
  partial: boolean("partial").notNull().default(true),
  limitation: text("limitation"),
  refreshedAt: timestamp("refreshed_at").notNull().defaultNow(),
}, (t) => [
  uniqueIndex("shopify_performance_refresh_day_uq").on(t.brandId, t.accountId, t.day),
  index("shopify_performance_refresh_day_range_idx").on(t.brandId, t.accountId, t.day),
]);

export const shopifyPerformanceProductFacts = pgTable("shopify_performance_product_facts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 128 }).notNull(),
  day: date("day").notNull(),
  orderId: varchar("order_id", { length: 128 }).notNull(),
  productId: varchar("product_id", { length: 128 }).notNull(),
  productName: text("product_name").notNull(),
  customerType: varchar("customer_type", { length: 16 }).notNull(),
  metaAttributed: boolean("meta_attributed").notNull(),
  netSales: numeric("net_sales", { precision: 18, scale: 6 }).notNull().default("0"),
  currency: varchar("currency", { length: 8 }),
  refreshedAt: timestamp("refreshed_at").notNull().defaultNow(),
}, (t) => [
  uniqueIndex("shopify_performance_product_fact_uq").on(t.brandId, t.accountId, t.day, t.orderId, t.productId, t.currency),
  index("shopify_performance_product_range_idx").on(t.brandId, t.accountId, t.day),
  check("shopify_performance_product_customer_type", sql`${t.customerType} in ('new','returning')`),
]);

export const insertNarrativeAngleWorkflowConfigSchema = createInsertSchema(
  narrativeAngleWorkflowConfigs,
).omit({ createdAt: true, updatedAt: true });

export type InsertNarrativeAngleWorkflowConfig = z.infer<typeof insertNarrativeAngleWorkflowConfigSchema>;

export type NarrativeAngleWorkflowConfig = typeof narrativeAngleWorkflowConfigs.$inferSelect;

export const narrativeV2Brands = pgTable("narrative_v2_brands", {
  brandId: varchar("brand_id").primaryKey().references(() => brands.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(false),
  updatedAt: timestamp("updated_at").defaultNow(),
});

/** Account-wide generation fences all ranges after accepted status/budget writes. */
export const adsManagerSnapshotAccounts = pgTable(
  "ads_manager_snapshot_accounts",
  {
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    accountId: varchar("account_id", { length: 64 }).notNull(),
    generation: integer("generation").notNull().default(0),
    connectionFingerprint: varchar("connection_fingerprint", { length: 256 }),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.brandId, table.accountId], name: "ads_manager_snapshot_accounts_pk" }),
  ],
);

export type AdsManagerSnapshot = typeof adsManagerSnapshots.$inferSelect;

/** Complete provider hierarchy snapshots; local associations and classifications stay live. */
export const adsManagerSnapshots = pgTable(
  "ads_manager_snapshots",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
    accountId: varchar("ad_account_id", { length: 64 }).notNull(),
    dateRangeKey: varchar("date_range_key", { length: 21 }).notNull(),
    rangeSince: date("range_since").notNull(),
    rangeUntil: date("range_until").notNull(),
    payload: jsonb("payload").$type<AdsManagerSnapshotPayload | null>(),
    fetchedAt: timestamp("fetched_at"),
    snapshotVersion: integer("snapshot_version").notNull().default(0),
    accountGeneration: integer("account_generation").notNull().default(0),
    refreshState: varchar("refresh_state", { length: 16 }).notNull().default("idle"),
    refreshToken: varchar("refresh_token", { length: 64 }),
    refreshLeaseExpiresAt: timestamp("refresh_lease_expires_at"),
    refreshStartedAt: timestamp("refresh_started_at"),
    manualRefreshRequestedAt: timestamp("manual_refresh_requested_at"),
    recovery: jsonb("recovery").$type<import("./ads-manager").AdsManagerRecovery | null>(),
    lastRefreshError: varchar("last_refresh_error", { length: 64 }),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("ads_manager_snapshots_account_range_uq").on(table.accountId, table.dateRangeKey),
    index("ads_manager_snapshots_refresh_lease_idx").on(table.refreshState, table.refreshLeaseExpiresAt),
    index("ads_manager_snapshots_retention_idx").on(table.fetchedAt),
    check("ads_manager_snapshots_refresh_state", sql`${table.refreshState} in ('idle','refreshing','error')`),
    check("ads_manager_snapshots_range_order", sql`${table.rangeSince} <= ${table.rangeUntil}`),
  ],
);

/** Separate from Brief facts: certified complete single-day Ads Manager reads.
 * Entity identities and hierarchy are retained in the versioned payload. */
export const adsManagerDailyReports = pgTable("ads_manager_daily_reports", {
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  day: date("day").notNull(),
  definition: varchar("definition", { length: 64 }).notNull(),
  accountGeneration: integer("account_generation").notNull(),
  payload: jsonb("payload").$type<import("./ads-manager").AdsManagerDailyStoredReport>().notNull(),
}, t => [
  primaryKey({ columns: [t.brandId, t.accountId, t.day, t.definition], name: "ads_manager_daily_reports_pk" }),
  index("ads_manager_daily_reports_retention_idx").on(t.day),
]);

/** Append-only recovery evidence for the Ads Manager older-source policy.
 * No FK to rolling facts: their retention must not delete these revisions. */
export const adsManagerPerformanceRevisions = pgTable("ads_manager_performance_revisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull(),
  accountId: varchar("account_id", { length: 64 }).notNull(),
  accountGeneration: integer("account_generation").notNull(),
  stage: varchar("stage", { length: 32 }).notNull(),
  reportingKey: varchar("reporting_key", { length: 64 }).notNull(),
  definition: varchar("definition", { length: 128 }).notNull(),
  refreshToken: varchar("refresh_token", { length: 64 }).notNull(),
  previousPayload: jsonb("previous_payload").notNull(),
  incomingPayload: jsonb("incoming_payload").notNull(),
  comparison: jsonb("comparison").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, t => [
  index("ads_manager_performance_revisions_scope_idx").on(t.brandId, t.accountId, t.accountGeneration, t.reportingKey),
]);

export type AdsManagerSnapshotSetting = typeof adsManagerSnapshotSettings.$inferSelect;

export type PersonaWorkflowConfig = typeof personaWorkflowConfigs.$inferSelect;

// Duplication has its own durable WAL. No local filesystem record is authoritative.
export const adsManagerDuplicationRuns = pgTable("ads_manager_duplication_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  actorId: varchar("actor_id").notNull().references(() => users.id),
  accountId: varchar("account_id").notNull(),
  requestKey: varchar("request_key", { length: 128 }).notNull(),
  requestHash: varchar("request_hash", { length: 64 }).notNull(),
  plan: jsonb("plan").$type<import("./ads-manager-duplication-engine").DuplicationPlan>().notNull(),
  live: boolean("live").notNull().default(false),
  status: varchar("status", { length: 24 }).notNull().default("queued"),
  result: jsonb("result").$type<import("./ads-manager-duplication-engine").DuplicationResult>(),
  error: text("error"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, t => [
  uniqueIndex("ads_manager_duplication_request_uq").on(t.brandId, t.actorId, t.requestKey),
  index("ads_manager_duplication_queue_idx").on(t.status, t.createdAt),
  check("ads_manager_duplication_run_status", sql`${t.status} IN ('queued','running','succeeded','partial','failed','needs_review')`),
]);

export const adsManagerDuplicationSteps = pgTable("ads_manager_duplication_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => adsManagerDuplicationRuns.id),
  stepKey: varchar("step_key").notNull(),
  action: varchar("action").notNull(),
  ordinal: integer("ordinal").notNull(),
  status: varchar("status", { length: 24 }).notNull().default("planned"),
  attempt: integer("attempt").notNull().default(0),
  params: jsonb("params").$type<Record<string, unknown>>(),
  // Append-only attempt evidence within the step; response saved before interpretation.
  outcomes: jsonb("outcomes").$type<import("./ads-manager-duplication-engine").DuplicationOutcome[]>().notNull().default([]),
  parsedId: varchar("parsed_id"),
  error: text("error"),
  submittedAt: timestamp("submitted_at"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, t => [
  uniqueIndex("ads_manager_duplication_step_uq").on(t.runId, t.stepKey),
  uniqueIndex("ads_manager_duplication_ordinal_uq").on(t.runId, t.ordinal),
  check("ads_manager_duplication_step_status", sql`${t.status} IN ('planned','submitting','received','success','rejected','needs_review')`),
]);

export const performanceBriefReports = pgTable("performance_brief_reports", {
  identityKey: text("identity_key").primaryKey(),
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: text("account_id").notNull(),
  identity: jsonb("identity").notNull().$type<Record<string, any>>(),
  generation: integer("generation").notNull(),
  payload: jsonb("payload").notNull().$type<Record<string, any>>(),
  assembledAt: timestamp("assembled_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [index("performance_brief_reports_brand_account").on(t.brandId, t.accountId)]);

export const performanceBriefJobs = pgTable("performance_brief_jobs", {
  brandId: varchar("brand_id").notNull().references(() => brands.id, { onDelete: "cascade" }),
  accountId: text("account_id").notNull(),
  runId: varchar("run_id"),
  configuration: text("configuration").notNull(),
  context: jsonb("context").notNull().$type<Record<string, any>>(),
  initialized: boolean("initialized").notNull().default(false),
  targetDay: text("target_day").notNull(),
  completedDay: text("completed_day"),
  pendingDays: jsonb("pending_days").notNull().$type<string[]>().default([]),
  state: text("state").notNull().default("queued"),
  token: text("token"),
  leaseUntil: timestamp("lease_until", { withTimezone: true }),
  retryAt: timestamp("retry_at", { withTimezone: true }).notNull().defaultNow(),
  lastAttemptAt: timestamp("last_attempt_at", { withTimezone: true }).notNull().defaultNow(),
  lastSuccessAt: timestamp("last_success_at", { withTimezone: true }),
  error: text("error"),
}, t => [primaryKey({ columns: [t.brandId, t.accountId] }), index("performance_brief_jobs_due").on(t.retryAt, t.lastAttemptAt)]);

export const textAgentSettings = pgTable("text_agent_settings", {
  id: integer("id").primaryKey().default(1),
  toolsEnabled: boolean("tools_enabled").notNull().default(false),
  sendsEnabled: boolean("sends_enabled").notNull().default(false),
}, t => [check("text_agent_singleton", sql`${t.id} = 1`)]);
export type SmbOrderEvent = typeof smbOrderEvents.$inferSelect;

export type InsertSmbOrder = z.infer<typeof insertSmbOrderSchema>;

export const smbOrders = pgTable("smb_orders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  externalId: varchar("external_id", { length: 160 }).notNull().unique(),
  // Drafts/invites and missing-default review holds have no package yet.
  packageId: varchar("package_id").references(() => smbPackages.id),
  brandId: varchar("brand_id").references(() => brands.id),
  onboardingRunId: varchar("onboarding_run_id").references(() => onboardingRuns.id),
  clientContactId: varchar("client_contact_id").references(() => clientContacts.id),
  rolloutUserId: varchar("rollout_user_id").references(() => users.id),
  status: varchar("status", { length: 32 }).$type<SmbOrderStatus>().notNull().default("awaiting_intake"),
  isTest: boolean("is_test").notNull().default(false),
  confidenceScore: real("confidence_score"),
  confidenceFlagsJson: jsonb("confidence_flags_json").$type<string[]>().notNull().default([]),
  templateOverridesJson: jsonb("template_overrides_json").$type<SmbTemplateOverrides>().notNull().default({ add: [], remove: [] }),
  enrichmentStateJson: jsonb("enrichment_state_json").$type<Record<string, unknown>>().notNull().default({}),
  stepStateJson: jsonb("step_state_json").$type<Record<string, unknown>>().notNull().default({}),
  packageSnapshotJson: jsonb("package_snapshot_json").$type<SmbManifest>(),
  costCapUsd: numeric("cost_cap_usd", { precision: 14, scale: 6 }),
  rawIntakeJson: jsonb("raw_intake_json").$type<Record<string, unknown>>(),
  error: text("error"),
  stageStartedAt: timestamp("stage_started_at", { withTimezone: true }).notNull().defaultNow(),
  leaseOwner: varchar("lease_owner", { length: 100 }),
  leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
  leaseVersion: integer("lease_version").notNull().default(0),
  attempt: integer("attempt").notNull().default(0),
  retryAt: timestamp("retry_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  index("smb_orders_package_idx").on(t.packageId),
  index("smb_orders_brand_idx").on(t.brandId),
  index("smb_orders_contact_idx").on(t.clientContactId),
  index("smb_orders_onboarding_idx").on(t.onboardingRunId),
  index("smb_orders_rollout_idx").on(t.rolloutUserId),
  index("smb_orders_status_age_idx").on(t.status, t.stageStartedAt),
  index("smb_orders_claim_idx").on(t.isTest, t.status, t.leaseExpiresAt, t.retryAt),
  index("smb_orders_list_idx").on(t.createdAt, t.id),
  check("smb_orders_status_check", sql`${t.status} IN ('awaiting_intake','intake_received','enriching','needs_review','generating','qa','ready','published','failed','cancelled')`),
  check("smb_orders_cost_check", sql`${t.costCapUsd} >= 0`),
  check("smb_orders_attempt_check", sql`${t.attempt} >= 0 AND ${t.leaseVersion} >= 0`),
  check("smb_orders_lease_check", sql`(${t.leaseOwner} IS NULL) = (${t.leaseExpiresAt} IS NULL)`),
  check("smb_orders_confidence_check", sql`${t.confidenceScore} BETWEEN 0 AND 1`),
]);

export const insertSmbPackageSchema = createInsertSchema(smbPackages).omit({
  id: true, createdAt: true, updatedAt: true, manifestJson: true, qaPolicyJson: true,
}).extend({
  manifestJson: smbManifestSchema, costCapUsd: smbMoneySchema,
  qaPolicyJson: smbJsonObjectSchema.nullable().optional(),
  maxRevisions: z.number().int().nonnegative().optional(),
  humanReviewMode: z.literal("always").optional(),
});

export type InsertClientContact = z.infer<typeof insertClientContactSchema>;

export type ClientContact = typeof clientContacts.$inferSelect;

export type InsertSmbPackage = z.infer<typeof insertSmbPackageSchema>;

export const clientContacts = pgTable("client_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email", { length: 320 }).notNull(),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [uniqueIndex("client_contacts_email_unique").on(sql`lower(${t.email})`)]);

export type SmbPackage = typeof smbPackages.$inferSelect;

export type InsertSmbOrderEvent = z.infer<typeof insertSmbOrderEventSchema>;

export const insertSmbOrderSchema = createInsertSchema(smbOrders).omit({
  id: true, createdAt: true, updatedAt: true, confidenceFlagsJson: true,
  templateOverridesJson: true, enrichmentStateJson: true, stepStateJson: true,
  packageSnapshotJson: true, rawIntakeJson: true,
}).extend({
  externalId: z.string().trim().min(8).max(160),
  status: smbOrderStatusSchema.optional(),
  confidenceFlagsJson: smbConfidenceFlagsSchema.optional(),
  templateOverridesJson: smbTemplateOverridesSchema.optional(),
  enrichmentStateJson: smbJsonObjectSchema.optional(),
  stepStateJson: smbStepStatesSchema.optional(),
  packageSnapshotJson: smbManifestSchema.nullable().optional(),
  costCapUsd: smbMoneySchema.nullable().optional(),
  rawIntakeJson: smbJsonObjectSchema.nullable().optional(),
  confidenceScore: z.number().min(0).max(1).nullable().optional(),
});

export type SmbOrder = typeof smbOrders.$inferSelect;

export const smbAssetReviews = pgTable("smb_asset_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id").notNull().references(() => smbOrders.id),
  runId: varchar("run_id").notNull().references(() => onboardingRuns.id),
  itemId: varchar("item_id").notNull().references(() => onboardingRunItems.id),
  personaId: varchar("persona_id").notNull().references(() => personas.id),
  assetKey: varchar("asset_key", { length: 64 }).notNull(),
  decision: text("decision").$type<"approved" | "dropped">().notNull(),
  reviewedBy: varchar("reviewed_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("smb_asset_reviews_order_asset_unique").on(t.orderId, t.assetKey),
  index("smb_asset_reviews_run_idx").on(t.runId),
  check("smb_asset_reviews_decision_check", sql`${t.decision} IN ('approved', 'dropped')`),
]);

// Immutable portal content is independent of live brands, personas and renders.
// Only lifecycle, storage progress and ZIP claim fields may change after admission.
export const portalDeliveries = pgTable("portal_deliveries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id").notNull().references(() => smbOrders.id),
  rolloutUserId: varchar("rollout_user_id").notNull().references(() => users.id),
  status: text("status").$type<"preparing" | "published" | "revoked">().notNull().default("preparing"),
  brandingJson: jsonb("branding_json").$type<z.infer<typeof portalBrandingSchema>>().notNull(),
  groupsJson: jsonb("groups_json").$type<z.infer<typeof portalGroupsSchema>>().notNull(),
  contactJson: jsonb("contact_json").$type<z.infer<typeof portalContactSchema>>(),
  storageMode: text("storage_mode").$type<"private_copies" | "public_originals">().notNull(),
  sourceBucket: text("source_bucket").notNull(),
  bucket: text("bucket").notNull(),
  copyState: text("copy_state").$type<"pending" | "running" | "failed" | "ready">().notNull().default("pending"),
  zipState: text("zip_state").$type<"pending" | "running" | "failed" | "ready">().notNull().default("pending"),
  completedAssets: integer("completed_assets").notNull().default(0),
  zipKey: text("zip_key"),
  zipBytes: bigint("zip_bytes", { mode: "number" }),
  errorCode: text("error_code"),
  leaseOwner: text("lease_owner"),
  leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
  // Increment on revoke. Every future session request must compare this value
  // and published status, rather than trusting a previously authorized cookie.
  accessVersion: integer("access_version").notNull().default(1),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("portal_deliveries_order_unique").on(t.orderId),
  index("portal_deliveries_work_idx").on(t.status, t.copyState, t.zipState, t.leaseExpiresAt),
  check("portal_deliveries_status_check", sql`${t.status} IN ('preparing','published','revoked')`),
  check("portal_deliveries_storage_check", sql`${t.storageMode} IN ('private_copies','public_originals') AND length(${t.bucket}) > 0 AND length(${t.sourceBucket}) > 0`),
  check("portal_deliveries_states_check", sql`${t.copyState} IN ('pending','running','failed','ready') AND ${t.zipState} IN ('pending','running','failed','ready')`),
  check("portal_deliveries_lease_check", sql`(${t.leaseOwner} IS NULL) = (${t.leaseExpiresAt} IS NULL)`),
  check("portal_deliveries_branding_check", sql`jsonb_typeof(${t.brandingJson}) = 'object' AND length(${t.brandingJson}->>'name') BETWEEN 1 AND 255 AND jsonb_typeof(${t.groupsJson}) = 'array' AND jsonb_array_length(${t.groupsJson}) BETWEEN 1 AND 30`),
]);
export const portalAssets = pgTable("portal_assets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryId: varchar("delivery_id").notNull().references(() => portalDeliveries.id),
  sourceAssetKey: text("source_asset_key").notNull(),
  sourceKey: text("source_key").notNull(),
  objectKey: text("object_key").notNull(),
  // Thumbnail keys share the delivery's bucket. Never use the public image
  // proxy for private copies; future portal reads sign this bucket/key.
  thumbnailKey: text("thumbnail_key"),
  personaId: text("persona_id"),
  tool: text("tool").notNull(),
  type: text("type").$type<"image" | "video" | "logo">().notNull(),
  name: text("name").notNull(),
  sortOrder: integer("sort_order").notNull(),
  sizeBytes: bigint("size_bytes", { mode: "number" }),
  contentType: text("content_type"),
  etag: text("etag"),
  copied: boolean("copied").notNull().default(false),
  zipExcluded: text("zip_excluded").$type<"asset_too_large" | "total_size_cap" | "branding">(),
}, t => [
  uniqueIndex("portal_assets_delivery_source_unique").on(t.deliveryId, t.sourceAssetKey),
  index("portal_assets_delivery_sort_idx").on(t.deliveryId, t.sortOrder),
  check("portal_assets_type_check", sql`${t.type} IN ('image','video','logo')`),
  check("portal_assets_size_check", sql`${t.sizeBytes} >= 0`),
  check("portal_assets_zip_excluded_check", sql`${t.zipExcluded} IN ('asset_too_large','total_size_cap','branding')`),
]);
export const portalLinks = pgTable("portal_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryId: varchar("delivery_id").notNull().references(() => portalDeliveries.id),
  tokenHash: text("token_hash").notNull(),
  // Snapshot recipient metadata; no dependency on live client contacts.
  contactJson: jsonb("contact_json").$type<z.infer<typeof portalContactSchema>>(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  exchangedAt: timestamp("exchanged_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("portal_links_token_unique").on(t.tokenHash),
  index("portal_links_delivery_idx").on(t.deliveryId, t.createdAt),
  index("portal_links_expiry_idx").on(t.expiresAt),
]);
export const portalSessions = pgTable("portal_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  linkId: varchar("link_id").notNull().references(() => portalLinks.id),
  deliveryId: varchar("delivery_id").notNull().references(() => portalDeliveries.id),
  tokenHash: text("token_hash").notNull(),
  accessVersion: integer("access_version").notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
}, t => [
  uniqueIndex("portal_sessions_token_unique").on(t.tokenHash),
  index("portal_sessions_delivery_idx").on(t.deliveryId),
  index("portal_sessions_expiry_idx").on(t.expiresAt),
]);
export const portalAccessLogs = pgTable("portal_access_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryId: varchar("delivery_id").references(() => portalDeliveries.id),
  // Invalid exchanges have no identifiable link.
  linkId: varchar("link_id").references(() => portalLinks.id),
  action: text("action").notNull(),
  outcome: text("outcome").notNull(),
  at: timestamp("at", { withTimezone: true }).notNull().defaultNow(),
}, t => [index("portal_access_logs_delivery_at_idx").on(t.deliveryId, t.at)]);
// Fixed windows use the database clock and an atomic upsert across instances.
export const portalRateLimits = pgTable("portal_rate_limits", {
  scope: text("scope").notNull(),
  subjectHash: text("subject_hash").notNull(),
  windowStart: timestamp("window_start", { withTimezone: true }).notNull(),
  attempts: integer("attempts").notNull(),
}, t => [
  primaryKey({ columns: [t.scope, t.subjectHash, t.windowStart] }),
  index("portal_rate_limits_cleanup_idx").on(t.windowStart),
  check("portal_rate_limits_attempts_check", sql`${t.attempts} > 0`),
]);
export const portalFeedback = pgTable("portal_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryId: varchar("delivery_id").notNull().references(() => portalDeliveries.id),
  assetId: varchar("asset_id").notNull().references(() => portalAssets.id),
  linkId: varchar("link_id").notNull().references(() => portalLinks.id),
  decision: text("decision").notNull(),
  comment: text("comment"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("portal_feedback_asset_link_unique").on(t.assetId, t.linkId),
  index("portal_feedback_delivery_idx").on(t.deliveryId),
  check("portal_feedback_decision_check", sql`${t.decision} IN ('approved','rejected')`),
  check("portal_feedback_comment_check", sql`length(${t.comment}) <= 4000`),
]);
// Persist the unique destination key BEFORE initiating multipart. An admission
// timeout/crash can then discover and abort uploads by exact key, even without ID.
export const portalZipAttempts = pgTable("portal_zip_attempts", {
  id: varchar("id").primaryKey(),
  deliveryId: varchar("delivery_id").notNull().references(() => portalDeliveries.id),
  bucket: text("bucket").notNull(),
  objectKey: text("object_key").notNull(),
  uploadId: text("upload_id"),
  state: text("state").$type<"active" | "cleanup_pending" | "aborted" | "completed">().notNull(),
  bytes: bigint("bytes", { mode: "number" }).notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  index("portal_zip_attempts_delivery_idx").on(t.deliveryId, t.state),
  check("portal_zip_attempts_state_check", sql`${t.state} IN ('active','cleanup_pending','aborted','completed')`),
]);
export const insertPortalDeliverySchema = createInsertSchema(portalDeliveries).extend({
  brandingJson: portalBrandingSchema, groupsJson: portalGroupsSchema, contactJson: portalContactSchema.optional(),
  storageMode: z.enum(["private_copies", "public_originals"]),
  status: z.enum(["preparing", "published", "revoked"]).optional(),
  copyState: z.enum(["pending", "running", "failed", "ready"]).optional(),
  zipState: z.enum(["pending", "running", "failed", "ready"]).optional(),
});
export type PortalDelivery = typeof portalDeliveries.$inferSelect;
export type PortalAsset = typeof portalAssets.$inferSelect;
export const smbOrderEvents = pgTable("smb_order_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id").notNull().references(() => smbOrders.id),
  type: varchar("type", { length: 80 }).notNull(),
  detailJson: jsonb("detail_json").$type<Record<string, unknown>>().notNull().default({}),
  at: timestamp("at", { withTimezone: true }).notNull().defaultNow(),
}, t => [index("smb_order_events_order_at_idx").on(t.orderId, t.at, t.id)]);
export const insertSmbOrderEventSchema = createInsertSchema(smbOrderEvents).omit({
  id: true, at: true, detailJson: true,
}).extend({
  detailJson: smbJsonObjectSchema, type: z.string().regex(/^[a-z][a-z0-9_]{0,79}$/),
});
export const insertClientContactSchema = createInsertSchema(clientContacts, {
  email: z.string().trim().toLowerCase().email().max(320),
  name: z.string().trim().min(1).max(255),
}).omit({ id: true, createdAt: true });

export const smbTemplateUploads = pgTable("smb_template_uploads", {
  id: varchar("id").primaryKey(),
  createdBy: varchar("created_by").notNull().references(() => users.id),
  inputDigest: text("input_digest").notNull(),
  results: jsonb("results").$type<unknown>(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [index("smb_template_uploads_creator_idx").on(t.createdBy)]);

export const textAgentEvents = pgTable("text_agent_events", {
  key: text("key").primaryKey(),
  type: varchar("type", { length: 40 }).notNull(),
  contactId: varchar("contact_id"),
  actorId: varchar("actor_id"),
  detail: varchar("detail", { length: 240 }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});

export const textAgentPhones = pgTable("text_agent_phones", {
  phone: varchar("phone", { length: 16 }).primaryKey(),
  optedOutAt: timestamp("opted_out_at", { withTimezone: true }),
});

export const textAgentOperations = pgTable("text_agent_operations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  trustedKey: text("trusted_key").notNull().unique(),
  contactId: varchar("contact_id").notNull().references(() => textAgentContacts.id),
  contactVersion: integer("contact_version").notNull(),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  accountId: text("account_id").notNull(),
  tool: varchar("tool", { length: 32 }).notNull(),
  jobId: text("job_id"),
  runId: varchar("run_id"),
  reportId: text("report_id"),
  state: varchar("state", { length: 24 }).notNull().default("queued"),
  resultId: varchar("result_id").references(() => textAgentResults.id),
  errorCode: varchar("error_code", { length: 64 }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  index("text_agent_operation_run").on(t.runId, t.state),
  check("text_agent_operation_state", sql`${t.state} IN ('queued','completed','failed','suppressed')`),
]);

export const textAgentContacts = pgTable("text_agent_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  phone: varchar("phone", { length: 16 }).notNull().references(() => textAgentPhones.phone),
  version: integer("version").notNull().default(1),
  active: boolean("active").notNull().default(true),
  verifiedAt: timestamp("verified_at", { withTimezone: true }).notNull(),
  consentAt: timestamp("consent_at", { withTimezone: true }).notNull(),
  verifiedBy: varchar("verified_by").notNull().references(() => users.id),
  verificationNote: varchar("verification_note", { length: 240 }).notNull(),
  readReports: boolean("read_reports").notNull().default(false),
  refreshReports: boolean("refresh_reports").notNull().default(false),
  conversationId: varchar("conversation_id", { length: 160 }).notNull().unique(),
  cleanupVerifiedAt: timestamp("cleanup_verified_at", { withTimezone: true }),
  lastResultId: varchar("last_result_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("text_agent_active_phone").on(t.phone).where(sql`${t.active}`),
  check("text_agent_e164", sql`${t.phone} ~ '^\\+[1-9][0-9]{7,14}$'`),
  check("text_agent_grants", sql`NOT ${t.refreshReports} OR ${t.readReports}`),
]);

export const textAgentOutbox = pgTable("text_agent_outbox", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  contactId: varchar("contact_id").notNull().references(() => textAgentContacts.id),
  contactVersion: integer("contact_version").notNull(),
  operationId: varchar("operation_id").notNull().references(() => textAgentOperations.id),
  eventKey: text("event_key").notNull(),
  state: varchar("state", { length: 24 }).notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0),
  claim: varchar("claim"),
  leaseUntil: timestamp("lease_until", { withTimezone: true }),
  retryAt: timestamp("retry_at", { withTimezone: true }).notNull().defaultNow(),
  providerMessageId: varchar("provider_message_id", { length: 160 }),
  errorCode: varchar("error_code", { length: 64 }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("text_agent_recipient_event").on(t.contactId, t.eventKey),
  uniqueIndex("text_agent_provider_message").on(t.providerMessageId),
  check("text_agent_delivery_state", sql`${t.state} IN ('pending','sending','accepted','delivered','failed','suppressed','unknown')`),
]);

export const textAgentResults = pgTable("text_agent_results", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull().references(() => brands.id),
  accountId: text("account_id").notNull(),
  reportId: text("report_id").notNull(),
  assemblyKey: text("assembly_key").notNull().unique(),
  identity: jsonb("identity").$type<Record<string, any>>().notNull(),
  payload: jsonb("payload").$type<Record<string, any>>().notNull(),
  assembledAt: timestamp("assembled_at", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});

export type SmbTemplateTest = typeof smbTemplateTests.$inferSelect;

export const insertSmbTemplateTestSchema = createInsertSchema(smbTemplateTests).omit({ id: true, createdAt: true });

export type SmbAssetReview = typeof smbAssetReviews.$inferSelect;
